import * as FlowControllerModule from "../flow/flow-controller.js";
import * as ControlSpecControllerModule from "../control-spec/controller.js";
import * as UnitsModule from "../timing/units.js";
import * as TimingStateModule from "../timing/timing-state.js";
import * as TimingGridControllerModule from "../timing/timing-grid-controller.js";
import * as MotionProfileModule from "../timing/motion-profile.js";
import * as FlowNodeHtmlModule from "../flow/flow-node-html.js";
import * as SubprocessPreviewModule from "../flow/subprocess-preview.js";
import * as AssistantClassifyModule from "../agent/classify.js";
import * as WorkspaceUiModule from "./workspace-controller.js";
import * as AppStateModule from "./app-state.js";
import { getFrontendDomRefs as getDomRefsFromModule } from "./dom-refs.js";
import * as GenerationModule from "../generation/index.js";
import * as AssistantUiRuntime from "../ui/assistant/runtime.js";
import * as AssistantConfigModule from "../ui/assistant/config.js";
import * as AccountSettingsApi from "../api-client/account-settings-api.js";
import * as ControlSpecHelper from "./control-spec-helper.js";
import * as PlcProfileModule from "../plc-profile/builtins.js";

"use strict";


function createChatThreadId() {
    const external = AppStateModule?.createChatThreadId;
    if (typeof external === "function") {
        return external();
    }
    return `thread-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function createChatThread() {
    const external = AppStateModule?.createChatThread;
    if (typeof external === "function") {
        return external({
            createThreadId: createChatThreadId
        });
    }
    const now = new Date().toISOString();
    return {
        id: createChatThreadId(),
        label: "新しいスレッド",
        messages: [],
        updatedAt: now
    };
}

const initialChatThread = createChatThread();

const DEFAULT_SIGNAL_CATEGORIES = Object.freeze([
    { id: "sensor", name: "センサ入力", direction: "input", addressPrefix: "%IX", startByte: 0 },
    { id: "actuator", name: "アクチュエータ出力", direction: "output", addressPrefix: "%QX", startByte: 0 },
    { id: "condition", name: "内部条件", direction: "internal", addressPrefix: "%MX", startByte: 0 },
    { id: "step", name: "工程ステップ", direction: "internal", addressPrefix: "%MX", startByte: 10 },
    { id: "alarm", name: "異常・インターロック", direction: "internal", addressPrefix: "%MX", startByte: 20 },
    { id: "plcopenCommand", name: "PLCopenコマンド", direction: "internal", addressPrefix: "%MX", startByte: 30 },
    { id: "plcopenStatus", name: "PLCopen状態", direction: "internal", addressPrefix: "%MX", startByte: 40 },
    { id: "plcopenParameter", name: "PLCopenパラメータ", direction: "internal", addressPrefix: "%MD", startByte: 50 },
    { id: "plcopenError", name: "PLCopenエラー", direction: "internal", addressPrefix: "%MD", startByte: 60 }
]);

function cloneDefaultSignalCategories() {
    return DEFAULT_SIGNAL_CATEGORIES.map((entry) => ({ ...entry }));
}

function normalizeSignalCategoryEntry(entry, index = 0) {
    return ControlSpecHelper.normalizeSignalCategoryEntry(entry, index);
}

function ensureSignalCategories(project = state.project) {
    if (!Array.isArray(project.signalCategories) || project.signalCategories.length === 0) {
        project.signalCategories = cloneDefaultSignalCategories();
        return project.signalCategories;
    }
    const seen = new Set();
    project.signalCategories = project.signalCategories
        .map((entry, index) => normalizeSignalCategoryEntry(entry, index))
        .filter((entry) => {
            const key = entry.id.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    return project.signalCategories;
}

function findSignalCategory(categoryId, project = state.project) {
    const id = String(categoryId || "").trim();
    return ensureSignalCategories(project).find((entry) => entry.id === id) || null;
}

const state = window.__appState || {
    accountSettings: null,
    project: emptyProject(),
    selectedAxisId: null,
    selectedMotionId: null,
    computed: {
        motionTimes: {},  // motionId -> { startSec, endSec, cycleTime, accelTime, cruiseTime, decelTime, adjustedMaxSpeed }
        subprocessTimes: {},  // nodeId -> { startSec, endSec, cycleTime, durationSec, subprocessId, subprocessName }
        errors: [],
        motionErrors: {}  // motionId -> message
    },
    drawflow: null,
    nodeIdToMotionId: {},  // drawflow node id (string) -> motionId
    motionIdToNodeId: {},  // motionId -> drawflow node id (string)
    selectedFlowNodeId: null,
    pendingFlowConnection: null,
    activeFlowModuleName: "Home",
    suspendFlowEvents: false,
    pxPerSec: 120,
    openDetailAxes: new Set(),
    ui: {
        activeView: "timing",
        sidebarCollapsed: false,
        axisPopoverOpen: false,
        flowConnectionMode: "drag",
        flowConnectionHoverText: ""
    },
    chat: {
        activeThreadId: initialChatThread.id,
        threads: {
            [initialChatThread.id]: initialChatThread
        },
        messages: initialChatThread.messages,
        busy: false,
        mode: "st",
        dialect: "generic-st",
        plcProfileId: "generic-st",
        lastResult: "",
        uiOpen: true,
        settingsOpen: false,
        statusText: "準備完了",
        settingsSummary: "Generic IEC ST / FlowContext v1.0 / IR v1.0 / 0 steps / Skill: 0件",
        inputPlaceholder: "追加指示を入力",
        plcProfileEditor: {
            open: false,
            draft: null,
            error: ""
        },
        taskHistoryOpen: false,
        historyItems: [],
        historyLoading: false,
        workLogCollapsed: {},
        questionDraftInput: "",
        questionCardError: "",
        activeHistorySessionId: "",
        lastResumeAnnouncementSessionId: "",
        agentSession: (() => {
            const session = createEmptyAgentSession();
            return session;
        })(),
        planSession: null
    }
};
window.__appState = state;

state.chat.planSession = state.chat.agentSession;

const controlSpecServerValidationState = {
    findings: [],
    errorMessage: "",
    busy: false,
    lastValidatedAt: "",
    lastAppliedKey: "",
    lastRequestedKey: "",
    requestSeq: 0,
    timerId: 0
};

const AGENT_SESSION_STORAGE_KEY = "timingChartAgentSessionRef";
const assistantUiSubscribers = new Set();

function notifyAssistantUiChanged() {
    for (const listener of assistantUiSubscribers) {
        try {
            listener();
        } catch (error) {
            console.warn("assistant ui subscriber failed", error);
        }
    }
}

function normalizePlcProfile(profile = {}, fallbackIndex = 0) {
    return PlcProfileModule.normalizePlcProfile(profile, fallbackIndex);
}

function createDefaultUserPlcProfiles() {
    return PlcProfileModule.createDefaultUserPlcProfiles();
}

function resolvePlcProfile(profileId = "", userProfiles = []) {
    return PlcProfileModule.resolvePlcProfile(profileId, userProfiles);
}

function buildPlcProfileOptions(userProfiles = []) {
    return PlcProfileModule.buildPlcProfileOptions(userProfiles);
}

function collectPlcProfileConsistencyHints(profile = {}) {
    return PlcProfileModule.collectPlcProfileConsistencyHints(profile);
}

function validatePlcProfileForSave(profile = {}) {
    return PlcProfileModule.validatePlcProfileForSave(profile);
}

function buildFrontendDomRefsFallback(doc = document) {
    return {
        timingGrid: doc.getElementById("timingGrid"),
        errorPanel: doc.getElementById("errorPanel"),
        btnAddFlowMotion: doc.getElementById("btnAddFlowMotion"),
        btnAddStartNode: doc.getElementById("btnAddStartNode"),
        btnAddConditionNode: doc.getElementById("btnAddConditionNode"),
        btnAddButtonNode: doc.getElementById("btnAddButtonNode"),
        btnAddErrorNode: doc.getElementById("btnAddErrorNode"),
        btnAddSignalOutputNode: doc.getElementById("btnAddSignalOutputNode"),
        btnAddWaitNode: doc.getElementById("btnAddWaitNode"),
        btnAddSubprocessNode: doc.getElementById("btnAddSubprocessNode"),
        btnAddEndNode: doc.getElementById("btnAddEndNode"),
        btnFlowConnectDrag: doc.getElementById("btnFlowConnectDrag"),
        btnFlowConnectPick: doc.getElementById("btnFlowConnectPick"),
        btnClearFlowPalette: doc.getElementById("btnClearFlowPalette"),
        btnExport: doc.getElementById("btnExport"),
        importFile: doc.getElementById("importFile"),
        projectNameInput: doc.getElementById("projectNameInput"),
        btnNewProject: doc.getElementById("btnNewProject"),
        btnCreateBlankProject: doc.getElementById("btnCreateBlankProject"),
        btnImportProjectFromFile: doc.getElementById("btnImportProjectFromFile"),
        btnOpenTemplateGalleryFromNewProject: doc.getElementById("btnOpenTemplateGalleryFromNewProject"),
        btnCloseNewProjectChoice: doc.getElementById("btnCloseNewProjectChoice"),
        newProjectChoiceModal: doc.getElementById("newProjectChoiceModal"),
        btnCloseTemplateGallery: doc.getElementById("btnCloseTemplateGallery"),
        templateGalleryModal: doc.getElementById("templateGalleryModal"),
        templateGalleryList: doc.getElementById("templateGalleryList"),
        btnProjectList: doc.getElementById("btnProjectList"),
        projectListPanel: doc.getElementById("projectListPanel"),
        workspaceTargetStatus: doc.getElementById("workspaceTargetStatus"),
        cloudAuthStatus: doc.getElementById("cloudAuthStatus"),
        btnCloudSave: doc.getElementById("btnCloudSave"),
        btnCloudLoad: doc.getElementById("btnCloudLoad"),
        cloudProjectSelect: doc.getElementById("cloudProjectSelect"),
        btnCloudSignIn: doc.getElementById("btnCloudSignIn"),
        btnCloudSignOut: doc.getElementById("btnCloudSignOut"),
        btnCloudManageMembers: doc.getElementById("btnCloudManageMembers"),
        cloudOrganizationSwitcher: doc.getElementById("cloudOrganizationSwitcher"),
        cloudUserButton: doc.getElementById("cloudUserButton"),
        authRequiredOverlay: doc.getElementById("authRequiredOverlay"),
        authRequiredMessage: doc.getElementById("authRequiredMessage"),
        btnAuthRequiredSignIn: doc.getElementById("btnAuthRequiredSignIn"),
        flowAxisSelect: doc.getElementById("flowAxisSelect"),
        distanceLinearUnit: doc.getElementById("distanceLinearUnit"),
        accelLinearUnit: doc.getElementById("accelLinearUnit"),
        speedLinearUnit: doc.getElementById("speedLinearUnit"),
        angleRotaryUnit: doc.getElementById("angleRotaryUnit"),
        accelRotaryUnit: doc.getElementById("accelRotaryUnit"),
        speedRotaryUnit: doc.getElementById("speedRotaryUnit"),
        btnSaveAccountSettings: doc.getElementById("btnSaveAccountSettings"),
        accountSettingsStatus: doc.getElementById("accountSettingsStatus"),
        btnReloadSkills: doc.getElementById("btnReloadSkills"),
        btnSkillsSidebarTabCatalog: doc.getElementById("btnSkillsSidebarTabCatalog"),
        btnSkillsSidebarTabProject: doc.getElementById("btnSkillsSidebarTabProject"),
        skillsSidebarCatalogPanel: doc.getElementById("skillsSidebarCatalogPanel"),
        skillsSidebarProjectPanel: doc.getElementById("skillsSidebarProjectPanel"),
        btnOpenSkillsCatalog: doc.getElementById("btnOpenSkillsCatalog"),
        btnCloseSkillsCatalog: doc.getElementById("btnCloseSkillsCatalog"),
        btnSkillsCatalogTabBuiltin: doc.getElementById("btnSkillsCatalogTabBuiltin"),
        btnSkillsCatalogTabCustom: doc.getElementById("btnSkillsCatalogTabCustom"),
        skillCatalogSearchInput: doc.getElementById("skillCatalogSearchInput"),
        skillCatalogSortSelect: doc.getElementById("skillCatalogSortSelect"),
        btnCreateCustomSkill: doc.getElementById("btnCreateCustomSkill"),
        skillsCatalogModal: doc.getElementById("skillsCatalogModal"),
        skillsCatalogBuiltinPanel: doc.getElementById("skillsCatalogBuiltinPanel"),
        skillsCatalogCustomPanel: doc.getElementById("skillsCatalogCustomPanel"),
        builtinSkillsCatalogListContainer: doc.getElementById("builtinSkillsCatalogListContainer"),
        customOwnSkillsListContainer: doc.getElementById("customOwnSkillsListContainer"),
        customSharedSkillsListContainer: doc.getElementById("customSharedSkillsListContainer"),
        customSharedSkillsHelp: doc.getElementById("customSharedSkillsHelp"),
        customSkillsStatus: doc.getElementById("customSkillsStatus"),
        skillsListContainer: doc.getElementById("skillsListContainer"),
        projectSkillsStatus: doc.getElementById("projectSkillsStatus"),
        skillsEditorTitle: doc.getElementById("skillsEditorTitle"),
        skillNameInput: doc.getElementById("skillNameInput"),
        skillDescriptionInput: doc.getElementById("skillDescriptionInput"),
        skillStatusSelect: doc.getElementById("skillStatusSelect"),
        skillVisibilityNote: doc.getElementById("skillVisibilityNote"),
        skillCreatedByNote: doc.getElementById("skillCreatedByNote"),
        skillUpdatedByNote: doc.getElementById("skillUpdatedByNote"),
        skillUpdatedAtNote: doc.getElementById("skillUpdatedAtNote"),
        skillContentEditor: doc.getElementById("skillContentEditor"),
        btnSaveSkillContent: doc.getElementById("btnSaveSkillContent"),
        btnApplySkillToProject: doc.getElementById("btnApplySkillToProject"),
        btnDuplicateSkillToPrivate: doc.getElementById("btnDuplicateSkillToPrivate"),
        btnUnshareSkillToPrivate: doc.getElementById("btnUnshareSkillToPrivate"),
        btnShareSkillToOrg: doc.getElementById("btnShareSkillToOrg"),
        btnDeleteSkill: doc.getElementById("btnDeleteSkill"),
        skillEditorStatus: doc.getElementById("skillEditorStatus")
    };
}

function getFrontendDomRefs() {
    return getDomRefsFromModule(document);
}

function getPhase3GenerationExport(name) {
    const preferredCandidate = getPreferredGenerationApi()?.[name];
    if (typeof preferredCandidate === "function") return preferredCandidate;
    return typeof GenerationModule[name] === "function" ? GenerationModule[name] : null;
}

function getAgentModule(name) {
    if (typeof window === "undefined") return null;
    return window.__agentModules?.[name] || null;
}

function getAgentModuleFunction(moduleName, functionName) {
    const directRuntimeFunction = moduleName === "runtime" && typeof window !== "undefined"
        ? window[functionName]
        : null;
    if (typeof directRuntimeFunction === "function") return directRuntimeFunction;
    const mod = getAgentModule(moduleName);
    const fn = mod?.[functionName];
    return typeof fn === "function" ? fn : null;
}

function callAgentModuleFunction(moduleName, functionName, fallback, ...args) {
    const external = getAgentModuleFunction(moduleName, functionName);
    if (typeof external === "function" && external !== fallback) {
        return external(...args);
    }
    return fallback(...args);
}

function createEmptyAgentSession() {
    const external = getAgentModuleFunction("session", "createEmptyAgentSession");
    if (typeof external === "function" && external !== createEmptyAgentSession) {
        return external();
    }
    const now = new Date().toISOString();
    return {
        active: false,
        mode: "flow",
        status: "idle",
        jobStatus: "queued",
        jobType: "flow_plan",
        canPoll: false,
        isTerminalJobState: false,
        stateAuthority: "client",
        goal: "",
        sourceText: "",
        plan: [],
        currentStepIndex: 0,
        assumptions: [],
        activityLog: [],
        observations: [],
        questions: [],
        answers: {},
        nextActions: [],
        nextAction: "",
        memory: {
            rejectedIdeas: [],
            confirmedFacts: [],
            warnings: []
        },
        remoteSessionId: "",
        remoteStatus: "idle",
        remoteEtag: "",
        serverRevisionCount: 0,
        serverUpdatedAt: "",
        workspaceId: "",
        projectId: "",
        generationRunId: "",
        clientRequestId: "",
        generationRunPersisted: false,
        generatedCode: "",
        draft: null,
        deliveredAt: "",
        currentQuestionIndex: 0,
        revisionCount: 0,
        createdAt: now,
        updatedAt: now,
        localUpdatedAt: now
    };
}


function assignChatAgentSession(session) {
    if (typeof isAgentPollDebugOn === "function" && isAgentPollDebugOn()) {
        console.info("[agent-poll] assignChatAgentSession", {
            active: session?.active,
            remoteSessionId: session?.remoteSessionId,
            status: session?.status,
            isNull: session == null,
            keys: session && typeof session === "object" ? Object.keys(session).length : null
        }, "\n", new Error().stack?.split("\n").slice(2, 6).join("\n"));
    }
    const external = getAgentModuleFunction("session", "assignChatAgentSession");
    if (typeof external === "function") {
        return external(state.chat, session);
    }
    const nextSession = session || createEmptyAgentSession();
    state.chat.agentSession = nextSession;
    state.chat.planSession = nextSession;
    return nextSession;
}

function ensureChatAgentSession() {
    const external = getAgentModuleFunction("session", "ensureChatAgentSession");
    if (typeof external === "function") {
        return external(state.chat);
    }
    if (!state.chat.agentSession) {
        return assignChatAgentSession(null);
    }
    if (state.chat.planSession !== state.chat.agentSession) {
        state.chat.planSession = state.chat.agentSession;
    }
    return state.chat.agentSession;
}

function emptyProject() {
    const external = AppStateModule?.createEmptyProject;
    if (typeof external === "function") {
        return external({
            cloneDefaultActuatorTypeCatalog,
            cloneDefaultSignalCategories
        });
    }
    return {
        version: 2,
        name: "新しいプロジェクト",
        selectedPlcProfileId: "generic-st",
        units: { distanceLinear: "mm", accelLinear: "m/s2", speedLinear: "mm/s" },
        axes: [],
        actuatorTypeCatalog: cloneDefaultActuatorTypeCatalog(),
        signalCategories: cloneDefaultSignalCategories(),
        actuators: [],
        signals: [],
        plcProfiles: [],
        plcProfilesSeeded: false,
        flow: { drawflowData: {}, subprocesses: [], internalFlags: [] }
    };
}

function ensureProjectFlowState(project = state.project) {
    if (!project.flow || typeof project.flow !== "object") project.flow = {};
    if (!project.flow.drawflowData || typeof project.flow.drawflowData !== "object") project.flow.drawflowData = {};
    if (!Array.isArray(project.flow.subprocesses)) project.flow.subprocesses = [];
    if (!Array.isArray(project.flow.internalFlags)) project.flow.internalFlags = [];
    return project.flow;
}

function getSubprocessList(project = state.project) {
    return ensureProjectFlowState(project).subprocesses;
}

function findSubprocessById(subprocessId, project = state.project) {
    return getSubprocessList(project).find((item) => item.id === subprocessId) || null;
}

function getInternalFlagList(project = state.project) {
    return ensureProjectFlowState(project).internalFlags;
}

function normalizeInternalFlagName(name) {
    return String(name || "").trim();
}

function findInternalFlagByName(name, project = state.project) {
    const normalizedName = normalizeInternalFlagName(name);
    return getInternalFlagList(project).find((item) => normalizeInternalFlagName(item.name) === normalizedName) || null;
}

function ensureInternalFlag(name, project = state.project) {
    const normalizedName = normalizeInternalFlagName(name);
    if (!normalizedName) return null;
    const existing = findInternalFlagByName(normalizedName, project);
    if (existing) return existing;
    const row = { name: normalizedName };
    getInternalFlagList(project).push(row);
    return row;
}

function syncInternalFlagsFromFlowSnapshot(snapshot = getFlowExportSnapshot(), project = state.project) {
    const names = [];
    for (const moduleData of Object.values(snapshot?.drawflow || {})) {
        for (const node of Object.values(moduleData?.data || {})) {
            const data = getNodeData(node);
            if (getNodeTypeFromData(data) !== "signal_output") continue;
            if (normalizeSignalScope(data.signalScope) !== "internal_flag") continue;
            const normalizedName = normalizeInternalFlagName(data.signalName);
            if (normalizedName) names.push(normalizedName);
        }
    }
    const uniqueNames = [...new Set(names)];
    const flow = ensureProjectFlowState(project);
    flow.internalFlags = uniqueNames.map((name) => ({ name }));
}

function commitInternalFlagName(name, project = state.project) {
    const normalizedName = normalizeInternalFlagName(name);
    if (!normalizedName) return null;
    return ensureInternalFlag(normalizedName, project);
}

function normalizeSubprocessName(name) {
    return String(name || "").trim() || "サブプロセス";
}

function findSubprocessByName(name, project = state.project) {
    const normalizedName = normalizeSubprocessName(name);
    return getSubprocessList(project).find((item) => normalizeSubprocessName(item.name) === normalizedName) || null;
}

function isSubprocessNameDuplicate(name, excludeId = "", project = state.project) {
    const found = findSubprocessByName(name, project);
    return Boolean(found && found.id !== excludeId);
}

function makeUniqueSubprocessName(baseName = "サブプロセス") {
    const base = normalizeSubprocessName(baseName);
    if (!isSubprocessNameDuplicate(base)) return base;
    for (let index = 1; index < 1000; index += 1) {
        const candidate = `${base}${index}`;
        if (!isSubprocessNameDuplicate(candidate)) return candidate;
    }
    return `${base}${Date.now().toString(36)}`;
}

function getSubprocessModuleName(subprocessId) {
    return "subprocess_" + String(subprocessId || "").replace(/[^a-zA-Z0-9_-]/g, "_");
}

function ensureSubprocessMeta(meta = {}) {
    const flow = ensureProjectFlowState(state.project);
    const subprocessId = String(meta.id || genId("subprocess"));
    let row = findSubprocessById(subprocessId);
    if (!row) {
        row = {
            id: subprocessId,
            name: makeUniqueSubprocessName(meta.name || meta.subprocessName || meta.label || "サブプロセス"),
            moduleName: String(meta.moduleName || getSubprocessModuleName(subprocessId)),
            durationSec: Math.max(0, numOrZero(meta.durationSec || 1))
        };
        flow.subprocesses.push(row);
    } else {
        const nextName = normalizeSubprocessName(meta.name || row.name || "サブプロセス");
        if (!isSubprocessNameDuplicate(nextName, row.id)) row.name = nextName;
        row.moduleName = String(row.moduleName || meta.moduleName || getSubprocessModuleName(subprocessId));
        row.durationSec = Math.max(0, numOrZero(meta.durationSec != null ? meta.durationSec : row.durationSec));
    }
    return row;
}

function getActiveFlowModuleName() {
    if (FlowControllerModule.getActiveFlowModuleName) {
        return FlowControllerModule.getActiveFlowModuleName({ state });
    }
}

function getDrawflowAdapter() {
    if (FlowControllerModule.getDrawflowAdapter) {
        return FlowControllerModule.getDrawflowAdapter({
            state,
            win: window,
            createDrawflowAdapter: window.__createDrawflowAdapter
        });
    }
}

function getFlowExportSnapshot() {
    if (FlowControllerModule.getFlowExportSnapshot) {
        return FlowControllerModule.getFlowExportSnapshot({ getDrawflowAdapter });
    }
}

function getFlowModuleData(snapshot = getFlowExportSnapshot(), moduleName = getActiveFlowModuleName()) {
    if (FlowControllerModule.getFlowModuleData) {
        return FlowControllerModule.getFlowModuleData({
            snapshot,
            moduleName,
            getFlowExportSnapshot,
            getActiveFlowModuleName,
            getDrawflowAdapter
        });
    }
}

function getMainFlowDataSnapshot() {
    if (FlowControllerModule.getMainFlowDataSnapshot) {
        return FlowControllerModule.getMainFlowDataSnapshot({
            getFlowExportSnapshot,
            getFlowModuleData: (snapshot, moduleName) => getFlowModuleData(snapshot, moduleName)
        });
    }
}

function getActiveFlowDataSnapshot() {
    if (FlowControllerModule.getActiveFlowDataSnapshot) {
        return FlowControllerModule.getActiveFlowDataSnapshot({ getDrawflowAdapter });
    }
}

const WORKSPACE_UI_STORAGE_KEY = "timingChartWorkspaceUi";
const WORKSPACE_VIEWS = WorkspaceUiModule?.WORKSPACE_VIEWS || {
    timing: {
        title: "タイムチャート",
        help: "",
        tooltip: "複数軸の動作を縦に並べてガント風に編集します。動作バーをクリックすると詳細編集が表示されます。"
    },
    flow: {
        title: "フロー作成",
        help: "",
        tooltip: "開始、終了、動作、サブプロセス、条件、操作入力、信号操作、エラーノードを接続して制御の流れを組み立てます。"
    },
    settings: {
        title: "設定",
        help: "単位など、計算に関わる共通設定を管理します。"
    },
    "control-spec": {
        title: "制御仕様",
        help: "",
        tooltip: "信号一覧・アクチュエータの定義、I/O表やST（構造化テキスト）等の成果物出力、設計検証結果を管理します。"
    },
    skills: {
        title: "Skills",
        help: "ワークスペース内の標準仕様（Skills）一覧です。プロジェクトで使用する仕様を選択・適用できます。"
    },
    project: {
        title: "プロジェクト",
        help: "JSONファイルの入出力、クラウド保存と読込を管理します。"
    }
};

function saveWorkspaceUiState() {
    try {
        const payload = WorkspaceUiModule?.buildWorkspaceUiStorageValue
            ? WorkspaceUiModule.buildWorkspaceUiStorageValue(state.ui)
            : {
                activeView: state.ui.activeView,
                sidebarCollapsed: state.ui.sidebarCollapsed
            };
        localStorage.setItem(WORKSPACE_UI_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
        console.warn("workspace ui save failed", e);
    }
}

function loadWorkspaceUiState() {
    try {
        const raw = localStorage.getItem(WORKSPACE_UI_STORAGE_KEY);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (WorkspaceUiModule?.applySavedWorkspaceUi) {
            WorkspaceUiModule.applySavedWorkspaceUi(state.ui, saved);
            return;
        }
        if (saved && WORKSPACE_VIEWS[saved.activeView]) state.ui.activeView = saved.activeView;
        state.ui.sidebarCollapsed = Boolean(saved?.sidebarCollapsed);
    } catch (e) {
        console.warn("workspace ui load failed", e);
    }
}

function afterWorkspaceViewShown(viewId) {
    const run = () => {
        if (viewId === "skills") {
            loadAndRenderSkills();
        }
        if (viewId === "settings") {
            renderSettingsView();
        }
        if (viewId === "control-spec") {
            renderControlSpecView({ immediateValidation: true });
            renderControlSpecActuators();
        }
        if (viewId === "flow") {
            syncFlowEditorHeight();
            scheduleFlowConnectionRefresh();
        } else if (viewId === "timing") {
            renderTimingGrid();
            renderSpeedChart();
        }
    };
    if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(run);
    } else {
        setTimeout(run, 0);
    }
}

function renderSettingsView() {
    renderControlSpecSignalCategories();
    renderActuatorTypeCatalogEditor();
}

function syncFlowEditorHeight() {
    if (FlowControllerModule.syncFlowEditorHeight) {
        return FlowControllerModule.syncFlowEditorHeight({
            doc: document,
            viewport: window
        });
    }
}

function renderHeaderActions() {
    if (WorkspaceUiModule?.applyHeaderActionsState) {
        WorkspaceUiModule.applyHeaderActionsState(document, {
            activeView: state.ui.activeView,
            assistantOpen: state.chat.uiOpen
        });
        return;
    }
    const header = document.querySelector(".app-header");
    if (header) header.classList.toggle("assistant-collapsed", !state.chat.uiOpen);
    const titleBar = document.querySelector(".project-title-bar");
    if (titleBar) titleBar.classList.toggle("assistant-collapsed", !state.chat.uiOpen);
    let hasVisibleGroup = false;
    document.querySelectorAll("[data-header-actions]").forEach(group => {
        const allowed = (group.getAttribute("data-header-actions") || "").split(/\s+/).filter(Boolean);
        const visible = allowed.includes(state.ui.activeView);
        group.classList.toggle("is-hidden", !visible);
        if (visible) hasVisibleGroup = true;
    });
    if (header) header.classList.toggle("is-hidden", !hasVisibleGroup);
}

function setActiveView(viewId, options = {}) {
    if (!WORKSPACE_VIEWS[viewId]) viewId = "timing";
    state.ui.activeView = viewId;

    const meta = WORKSPACE_VIEWS[viewId];
    if (WorkspaceUiModule?.applyActiveViewDom) {
        WorkspaceUiModule.applyActiveViewDom(document, viewId, meta);
    } else {
        document.querySelectorAll("[data-view-panel]").forEach(panel => {
            panel.classList.toggle("is-active", panel.dataset.viewPanel === viewId);
        });
        document.querySelectorAll(".sidebar-link[data-view]").forEach(button => {
            const active = button.dataset.view === viewId;
            button.classList.toggle("is-active", active);
            button.setAttribute("aria-current", active ? "page" : "false");
        });

        const title = document.getElementById("activeViewTitle");
        const help = document.getElementById("activeViewHelp");
        if (title) {
            title.textContent = meta.title;
            title.title = meta.tooltip || meta.help || "";
        }
        if (help) {
            help.textContent = meta.help || "";
            help.style.display = meta.help ? "" : "none";
        }
    }

    renderHeaderActions();
    if (!options.skipPersist) saveWorkspaceUiState();
    afterWorkspaceViewShown(viewId);
}

function setSidebarCollapsed(collapsed, options = {}) {
    state.ui.sidebarCollapsed = Boolean(collapsed);
    if (WorkspaceUiModule?.applySidebarCollapsedDom) {
        WorkspaceUiModule.applySidebarCollapsedDom(document, state.ui.sidebarCollapsed);
    } else {
        const shell = document.getElementById("appShell");
        const toggle = document.getElementById("btnSidebarToggle");
        if (shell) shell.classList.toggle("sidebar-collapsed", state.ui.sidebarCollapsed);
        if (toggle) {
            const label = state.ui.sidebarCollapsed ? "メニューを展開" : "メニューを折りたたむ";
            toggle.innerHTML = '<span class="sidebar-toggle-icon" aria-hidden="true"><svg class="sidebar-toggle-icon-open" viewBox="0 0 20 20" focusable="false"><rect x="3" y="4" width="14" height="12" rx="2"></rect><path d="M8 4v12"></path></svg><svg class="sidebar-toggle-icon-closed" viewBox="0 0 20 20" focusable="false"><rect x="3" y="4" width="14" height="12" rx="2"></rect><path d="M12 4v12"></path></svg></span>';
            toggle.title = label;
            toggle.setAttribute("aria-label", label);
            toggle.setAttribute("aria-expanded", String(!state.ui.sidebarCollapsed));
            toggle.setAttribute("data-sidebar-collapsed", String(state.ui.sidebarCollapsed));
        }
    }
    if (!options.skipPersist) saveWorkspaceUiState();
}

function renderControlSpecView(options = {}) {
    if (ControlSpecControllerModule?.renderControlSpecView) {
        const rendered = ControlSpecControllerModule.renderControlSpecView({
            project: state.project,
            getGenerationIr: () => {
                const generationApi = typeof getPreferredGenerationApi === "function" ? getPreferredGenerationApi() : null;
                return generationApi ? generationApi.getGenerationIr() : null;
            },
            ensureSignalCategories,
            renderSignalCategories: renderControlSpecSignalCategories,
            getSelectedSignalCategoryId: () => document.getElementById("selectSignalCategory")?.value || "",
            renderSignalCategoryOptions,
            renderSignals: renderControlSpecSignals,
            renderFlowSignalCandidates: renderControlSpecFlowSignalCandidates,
            renderOutputs: renderControlSpecOutputs,
            renderFindings: renderControlSpecFindings,
            activeView: state.ui.activeView,
            skipValidationRefresh: options.skipValidationRefresh,
            immediateValidation: options.immediateValidation,
            scheduleValidationRefresh: scheduleControlSpecServerValidationRefresh
        });
        if (rendered) return;
    }
    ensureSignalCategories();
    renderControlSpecSignalCategories();
    renderSignalCategoryOptions(document.getElementById("selectSignalCategory")?.value || "");
    renderControlSpecSignals(); // 信号辞書テーブルを再描画
    const generationApi = typeof getPreferredGenerationApi === "function" ? getPreferredGenerationApi() : null;
    const generationIr = generationApi ? generationApi.getGenerationIr() : null;
    
    const vm = ControlSpecHelper.buildControlSpecViewModel(state.project, generationIr);
    
    renderControlSpecFlowSignalCandidates();
    renderControlSpecOutputs(vm);
    renderControlSpecFindings(vm);
    if (state.ui.activeView === "control-spec" && !options.skipValidationRefresh) {
        scheduleControlSpecServerValidationRefresh({ immediate: Boolean(options.immediateValidation) });
    }
}

function buildControlSpecValidationPayload() {
    if (ControlSpecControllerModule?.buildControlSpecValidationPayload) {
        return ControlSpecControllerModule.buildControlSpecValidationPayload({
            getPreferredGenerationApi,
            getActivePlcProfile,
            state
        });
    }
    const generationApi = typeof getPreferredGenerationApi === "function" ? getPreferredGenerationApi() : null;
    if (!generationApi) return null;
    const flowContext = typeof generationApi.getFlowContext === "function" ? generationApi.getFlowContext() : null;
    const generationIr = typeof generationApi.getGenerationIr === "function" ? generationApi.getGenerationIr() : null;
    if (!flowContext && !generationIr) return null;
    const activeProfile = getActivePlcProfile();
    return {
        flowContext,
        generationIr,
        vendor: activeProfile.vendorTarget || state.chat.dialect,
        plcProfileId: activeProfile.id,
        plcProfile: activeProfile,
        mode: "validate"
    };
}

function buildControlSpecValidationRequestKey(payload) {
    if (ControlSpecControllerModule?.buildControlSpecValidationRequestKey) {
        return ControlSpecControllerModule.buildControlSpecValidationRequestKey(payload);
    }
    try {
        return JSON.stringify(payload || {});
    } catch {
        return "";
    }
}

function scheduleControlSpecServerValidationRefresh(options = {}) {
    if (ControlSpecControllerModule?.scheduleControlSpecServerValidationRefresh) {
        return ControlSpecControllerModule.scheduleControlSpecServerValidationRefresh({
            apiGenerationClient,
            validationState: controlSpecServerValidationState,
            clearTimer: (timerId) => clearTimeout(timerId),
            setTimer: (callback, delay) => setTimeout(callback, delay),
            refreshControlSpecServerValidation: (nextOptions) => refreshControlSpecServerValidation(nextOptions),
            logWarning: (...args) => console.warn(...args),
            options
        });
    }
    if (!apiGenerationClient || typeof apiGenerationClient.postValidate !== "function") return;
    if (controlSpecServerValidationState.timerId) {
        clearTimeout(controlSpecServerValidationState.timerId);
        controlSpecServerValidationState.timerId = 0;
    }
    const delay = options.immediate ? 0 : 120;
    controlSpecServerValidationState.timerId = setTimeout(() => {
        controlSpecServerValidationState.timerId = 0;
        refreshControlSpecServerValidation(options).catch((error) => {
            console.warn("[control-spec] /api/validate refresh failed:", error);
        });
    }, delay);
}

async function refreshControlSpecServerValidation(options = {}) {
    if (ControlSpecControllerModule?.refreshControlSpecServerValidation) {
        return ControlSpecControllerModule.refreshControlSpecServerValidation({
            apiGenerationClient,
            validationState: controlSpecServerValidationState,
            buildControlSpecValidationPayload,
            buildControlSpecValidationRequestKey,
            options,
            notifyAssistantUiChanged,
            activeView: state.ui.activeView,
            renderControlSpecView
        });
    }
    if (!apiGenerationClient || typeof apiGenerationClient.postValidate !== "function") {
        controlSpecServerValidationState.findings = [];
        controlSpecServerValidationState.errorMessage = "";
        controlSpecServerValidationState.busy = false;
        return;
    }

    const payload = buildControlSpecValidationPayload();
    if (!payload) {
        controlSpecServerValidationState.findings = [];
        controlSpecServerValidationState.errorMessage = "";
        controlSpecServerValidationState.busy = false;
        return;
    }

    const requestKey = buildControlSpecValidationRequestKey(payload);
    if (!options.force && requestKey && requestKey === controlSpecServerValidationState.lastAppliedKey) {
        return;
    }
    if (controlSpecServerValidationState.busy && requestKey === controlSpecServerValidationState.lastRequestedKey) {
        return;
    }

    controlSpecServerValidationState.busy = true;
    controlSpecServerValidationState.errorMessage = "";
    controlSpecServerValidationState.lastRequestedKey = requestKey;
    const requestSeq = ++controlSpecServerValidationState.requestSeq;

    const getAccessToken = getPhase3GenerationExport("getApiClientAccessToken");
    const accessToken = typeof getAccessToken === "function"
        ? await getAccessToken(apiGenerationClient)
        : null;

    try {
        const result = await apiGenerationClient.postValidate(payload, { accessToken });
        if (requestSeq !== controlSpecServerValidationState.requestSeq) return;
        controlSpecServerValidationState.findings = Array.isArray(result?.validationFindings)
            ? result.validationFindings
            : [];
        controlSpecServerValidationState.errorMessage = "";
        controlSpecServerValidationState.lastValidatedAt = new Date().toISOString();
        controlSpecServerValidationState.lastAppliedKey = requestKey;
        notifyAssistantUiChanged();
    } catch (error) {
        if (requestSeq !== controlSpecServerValidationState.requestSeq) return;
        controlSpecServerValidationState.findings = [];
        controlSpecServerValidationState.errorMessage = error?.message || "validate_failed";
        notifyAssistantUiChanged();
    } finally {
        if (requestSeq === controlSpecServerValidationState.requestSeq) {
            controlSpecServerValidationState.busy = false;
            if (state.ui.activeView === "control-spec") {
                renderControlSpecView({ skipValidationRefresh: true });
            }
            notifyAssistantUiChanged();
        }
    }
}

function getCombinedControlSpecFindings(vm) {
    if (ControlSpecControllerModule?.getCombinedControlSpecFindings) {
        return ControlSpecControllerModule.getCombinedControlSpecFindings(vm, controlSpecServerValidationState);
    }
    return ControlSpecHelper.mergeControlSpecFindings(vm?.findings, controlSpecServerValidationState.findings);
}

function findFlowModuleNameForNode(nodeId) {
    const snapshot = getFlowExportSnapshot();
    const modules = snapshot?.drawflow ? Object.keys(snapshot.drawflow) : ["Home"];
    for (const moduleName of modules) {
        const data = getFlowModuleData(snapshot, moduleName);
        if (data?.[nodeId]) return moduleName;
    }
    return "";
}

function openControlSpecFindingNode(nodeId) {
    if (ControlSpecControllerModule?.openControlSpecFindingNode) {
        return ControlSpecControllerModule.openControlSpecFindingNode({
            nodeId,
            findFlowModuleNameForNode,
            alertMessage: (message) => alert(message),
            setActiveView,
            setActiveFlowModule,
            state,
            renderFlowNodeEditor,
            requestAnimationFrameFn: (callback) => requestAnimationFrame(callback),
            getNodeElement: (id) => document.getElementById("node-" + id),
            setTimeoutFn: (callback, delay) => setTimeout(callback, delay)
        });
    }
    const moduleName = findFlowModuleNameForNode(nodeId);
    if (!moduleName) {
        alert(`対象ノード '${nodeId}' が見つかりません。`);
        return;
    }
    setActiveView("flow");
    setActiveFlowModule(moduleName);
    state.selectedFlowNodeId = String(nodeId);
    renderFlowNodeEditor();
    requestAnimationFrame(() => {
        const nodeEl = document.getElementById("node-" + nodeId);
        nodeEl?.scrollIntoView?.({ block: "center", inline: "center" });
        nodeEl?.classList?.add("flow-connection-source");
        setTimeout(() => nodeEl?.classList?.remove("flow-connection-source"), 1200);
    });
}

function openControlSpecFindingSignal(signalId) {
    if (ControlSpecControllerModule?.openControlSpecFindingSignal) {
        return ControlSpecControllerModule.openControlSpecFindingSignal({
            signalId,
            project: state.project,
            alertMessage: (message) => alert(message),
            setActiveView,
            setControlSpecTab,
            loadSignalToForm
        });
    }
    const signals = Array.isArray(state.project?.signals) ? state.project.signals : [];
    if (!signals.some(sig => sig.id === signalId)) {
        alert(`対象信号 '${signalId}' が見つかりません。`);
        return;
    }
    setActiveView("control-spec");
    setControlSpecTab("signals");
    loadSignalToForm(signalId);
}

function openControlSpecFindingActuator(actuatorId, actionId = "") {
    if (ControlSpecControllerModule?.openControlSpecFindingActuator) {
        return ControlSpecControllerModule.openControlSpecFindingActuator({
            actuatorId,
            actionId,
            project: state.project,
            alertMessage: (message) => alert(message),
            setActiveView,
            setControlSpecTab,
            loadActuatorToForm,
            loadActionToCard
        });
    }
    const actuators = Array.isArray(state.project?.actuators) ? state.project.actuators : [];
    const actuator = actuators.find(act => act.id === actuatorId);
    if (!actuator) {
        alert(`対象アクチュエータ '${actuatorId}' が見つかりません。`);
        return;
    }
    setActiveView("control-spec");
    setControlSpecTab("actuators");
    loadActuatorToForm(actuatorId);
    if (actionId) {
        const index = (Array.isArray(actuator.actions) ? actuator.actions : []).findIndex(action => action.id === actionId);
        if (index >= 0) loadActionToCard(index);
    }
}

function openControlSpecFindingTarget(button) {
    if (ControlSpecControllerModule?.openControlSpecFindingTarget) {
        return ControlSpecControllerModule.openControlSpecFindingTarget(button, {
            project: state.project,
            findFlowModuleNameForNode,
            alertMessage: (message) => alert(message),
            setActiveView,
            setActiveFlowModule,
            setControlSpecTab,
            state,
            renderFlowNodeEditor,
            requestAnimationFrameFn: (callback) => requestAnimationFrame(callback),
            getNodeElement: (id) => document.getElementById("node-" + id),
            setTimeoutFn: (callback, delay) => setTimeout(callback, delay),
            loadSignalToForm,
            loadActuatorToForm,
            loadActionToCard
        });
    }
    const target = button?.dataset?.findingTarget || "";
    if (target === "node") {
        openControlSpecFindingNode(button.dataset.nodeId || "");
    } else if (target === "signal") {
        openControlSpecFindingSignal(button.dataset.signalId || "");
    } else if (target === "actuator") {
        openControlSpecFindingActuator(button.dataset.actuatorId || "", button.dataset.actionId || "");
    }
}

function renderControlSpecFindings(vm) {
    if (ControlSpecControllerModule?.renderControlSpecFindings) {
        return ControlSpecControllerModule.renderControlSpecFindings(document, {
            vm,
            validationState: controlSpecServerValidationState,
            escapeHtml
        });
    }
    const tbody = document.getElementById("controlSpecFindingsTableBody");
    const meta = document.getElementById("controlSpecFindingsMeta");
    if (!tbody) return;

    const findings = getCombinedControlSpecFindings(vm);
    const findingsMeta = ControlSpecHelper.buildControlSpecFindingsMeta(findings, controlSpecServerValidationState);
    if (meta) {
        meta.textContent = findingsMeta.text;
    }

    if (!findings.length) {
        const emptyMessage = ControlSpecHelper.buildEmptyControlSpecFindingsMessage(controlSpecServerValidationState);
        tbody.innerHTML = `
            <tr>
                <td colspan="4" class="ctrl-spec-empty-cell">
                    ${escapeHtml(emptyMessage)}
                </td>
            </tr>
        `;
        return;
    }

    tbody.innerHTML = "";
    findings.forEach((finding) => {
        const tr = document.createElement("tr");
        tr.className = "ctrl-spec-tr";
        const display = ControlSpecHelper.buildControlSpecFindingRowModel(
            finding,
            ControlSpecHelper.localizeControlSpecFindingMessage
        );
        const buttons = (Array.isArray(display.actions) ? display.actions : []).map((action) => {
            const attrs = [`data-finding-target="${escapeHtml(action.target)}"`];
            Object.entries(action.dataset || {}).forEach(([key, value]) => {
                const attrName = `data-${key.replace(/[A-Z]/g, (ch) => "-" + ch.toLowerCase())}`;
                attrs.push(`${attrName}="${escapeHtml(value)}"`);
            });
            return `<button type="button" class="control-spec-action-btn" ${attrs.join(" ")}>${escapeHtml(action.label)}</button>`;
        });
        const actionsHtml = buttons.length ? `<div class="ctrl-action-target-btns">${buttons.join("")}</div>` : "";
        tr.innerHTML = `
            <td style="padding: 10px 8px; font-weight: bold; color: ${display.severityColor};">${escapeHtml(display.severity)}</td>
            <td class="ctrl-spec-td-mono-bold">${escapeHtml(display.code)}</td>
            <td class="ctrl-spec-td-desc-520">${escapeHtml(display.message)}</td>
            <td class="ctrl-spec-td-muted">
                <div class="ctrl-source-label">source: ${escapeHtml(display.sourceText)}</div>
                <div>${escapeHtml(display.targetText)}</div>
                ${actionsHtml}
            </td>
        `;
        tbody.appendChild(tr);
    });
}

function renderControlSpecOutputs(vm) {
    if (ControlSpecControllerModule?.renderControlSpecOutputs) {
        return ControlSpecControllerModule.renderControlSpecOutputs(document, {
            vm,
            escapeHtml
        });
    }
    const tbody = document.getElementById("controlSpecIoTableBody");
    const meta = document.getElementById("controlSpecIoTableMeta");
    const valveModelBody = document.getElementById("controlSpecValveModelBody");
    const valveModelMeta = document.getElementById("controlSpecValveModelMeta");
    const actionBody = document.getElementById("controlSpecActionStepsBody");
    const actionMeta = document.getElementById("controlSpecActionStepsMeta");
    const conditionBody = document.getElementById("controlSpecConditionStepsBody");
    const conditionMeta = document.getElementById("controlSpecConditionStepsMeta");
    const errorBody = document.getElementById("controlSpecErrorListBody");
    const errorMeta = document.getElementById("controlSpecErrorListMeta");
    if (!tbody) return;

    const sections = ControlSpecHelper.buildControlSpecOutputSections(vm);
    if (meta) meta.textContent = sections.io.metaText;
    renderControlSpecValveModels(sections.valveModel, valveModelBody, valveModelMeta);
    renderControlSpecActionSteps(sections.action, actionBody, actionMeta);
    renderControlSpecConditionSteps(sections.condition, conditionBody, conditionMeta);
    renderControlSpecErrorList(sections.error, errorBody, errorMeta);

    if (!sections.io.count) {
        tbody.innerHTML = `
            <tr>
                <td colspan="6" class="ctrl-spec-empty-cell">
                    ${escapeHtml(sections.io.emptyMessage)}
                </td>
            </tr>
        `;
        return;
    }

    tbody.innerHTML = "";
    sections.io.rows.forEach((entry) => {
        const tr = document.createElement("tr");
        tr.className = "ctrl-spec-tr";
        const row = ControlSpecHelper.buildControlSpecIoRowModel(
            entry,
            ControlSpecHelper.formatIoReferencedBy
        );
        tr.innerHTML = `
            <td class="ctrl-spec-td"><span class="control-spec-badge ${row.badgeClass}">${escapeHtml(row.direction)}</span></td>
            <td class="ctrl-spec-td-mono-bold-muted">${escapeHtml(row.address)}</td>
            <td class="ctrl-spec-td-mono-id">${escapeHtml(row.signalId)}</td>
            <td class="ctrl-spec-td">${escapeHtml(row.signalName)}</td>
            <td class="ctrl-spec-td-muted">${escapeHtml(row.usagesText)}</td>
            <td class="ctrl-spec-td-desc-320">${escapeHtml(row.refsText)}</td>
        `;
        tbody.appendChild(tr);
    });
}

function renderControlSpecValveModels(section, tbody, meta) {
    if (!tbody) return;
    if (meta) meta.textContent = section.metaText;

    if (!section.count) {
        tbody.innerHTML = `
            <tr>
                <td colspan="7" class="ctrl-spec-empty-cell">
                    ${escapeHtml(section.emptyMessage)}
                </td>
            </tr>
        `;
        return;
    }

    tbody.innerHTML = "";
    section.rows.forEach((entry) => {
        const tr = document.createElement("tr");
        tr.className = "ctrl-spec-tr";
        const row = ControlSpecHelper.buildControlSpecValveModelRowModel(entry);
        tr.innerHTML = `
            <td class="ctrl-spec-td-mono-bold">${escapeHtml(row.actuatorIdText)}</td>
            <td class="ctrl-spec-td">${escapeHtml(row.actuatorNameText)}</td>
            <td class="ctrl-spec-td-muted">${escapeHtml(row.valveModeText)}</td>
            <td class="ctrl-spec-td-desc-320">${escapeHtml(row.outputsText)}</td>
            <td class="ctrl-spec-td-desc-360">${escapeHtml(row.extendText)}</td>
            <td class="ctrl-spec-td-desc-360">${escapeHtml(row.retractText)}</td>
            <td class="ctrl-spec-td-desc-360">${escapeHtml(`${row.stopText} / 非常停止: ${row.emergencyText}`)}</td>
        `;
        tbody.appendChild(tr);
    });
}

function renderControlSpecErrorList(section, tbody, meta) {
    if (!tbody) return;
    if (meta) meta.textContent = section.metaText;

    if (!section.count) {
        tbody.innerHTML = `
            <tr>
                <td colspan="5" class="ctrl-spec-empty-cell">
                    ${escapeHtml(section.emptyMessage)}
                </td>
            </tr>
        `;
        return;
    }

    tbody.innerHTML = "";
    section.errorSteps.forEach((step) => {
        const tr = document.createElement("tr");
        tr.className = "ctrl-spec-tr";
        const row = ControlSpecHelper.buildControlSpecErrorStepRowModel(step);
        tr.innerHTML = `
            <td class="ctrl-spec-td">${escapeHtml(row.typeText)}</td>
            <td class="ctrl-spec-td-mono">${escapeHtml(row.stepNodeText)}</td>
            <td class="ctrl-spec-td-mono-bold">${escapeHtml(row.codeText)}</td>
            <td class="ctrl-spec-td-desc-360">${escapeHtml(row.messageText)}</td>
            <td class="ctrl-spec-td-muted">${escapeHtml(row.targetText)}</td>
        `;
        tbody.appendChild(tr);
    });

    section.timeoutFindings.forEach((finding) => {
        const tr = document.createElement("tr");
        tr.className = "ctrl-spec-tr";
        const row = ControlSpecHelper.buildControlSpecTimeoutFindingRowModel(finding);
        tr.innerHTML = `
            <td class="ctrl-spec-td">${escapeHtml(row.typeText)}</td>
            <td class="ctrl-spec-td-mono">${escapeHtml(row.stepNodeText)}</td>
            <td class="ctrl-spec-td-mono-bold">${escapeHtml(row.codeText)}</td>
            <td class="ctrl-spec-td-desc-360">${escapeHtml(row.messageText)}</td>
            <td class="ctrl-spec-td-muted">${escapeHtml(row.targetText)}</td>
        `;
        tbody.appendChild(tr);
    });
}

function renderControlSpecConditionSteps(section, tbody, meta) {
    if (!tbody) return;
    if (meta) meta.textContent = section.metaText;

    if (!section.count) {
        tbody.innerHTML = `
            <tr>
                <td colspan="6" class="ctrl-spec-empty-cell">
                    ${escapeHtml(section.emptyMessage)}
                </td>
            </tr>
        `;
        return;
    }

    tbody.innerHTML = "";
    section.rows.forEach((step) => {
        const tr = document.createElement("tr");
        tr.className = "ctrl-spec-tr";
        const row = ControlSpecHelper.buildControlSpecConditionRowModel(
            step,
            ControlSpecHelper.formatControlSpecConditionStepExpression,
            ControlSpecHelper.formatControlSpecConditionBranches
        );
        tr.innerHTML = `
            <td class="ctrl-spec-td-mono-bold-dark">${escapeHtml(row.stepText)}</td>
            <td class="ctrl-spec-td">${escapeHtml(row.nameText)}</td>
            <td class="ctrl-spec-td-muted">${escapeHtml(row.kindText)}</td>
            <td class="ctrl-spec-td-desc-320">${escapeHtml(row.expressionText)}</td>
            <td class="ctrl-spec-td-desc">${escapeHtml(row.branchesText)}</td>
            <td class="ctrl-spec-td-mono-muted">${escapeHtml(row.timeoutText)}</td>
        `;
        tbody.appendChild(tr);
    });
}

function renderControlSpecActionSteps(section, tbody, meta) {
    if (!tbody) return;
    if (meta) meta.textContent = section.metaText;

    if (!section.count) {
        tbody.innerHTML = `
            <tr>
                <td colspan="7" class="ctrl-spec-empty-cell">
                    ${escapeHtml(section.emptyMessage)}
                </td>
            </tr>
        `;
        return;
    }

    tbody.innerHTML = "";
    section.rows.forEach((step) => {
        const tr = document.createElement("tr");
        tr.className = "ctrl-spec-tr";
        const row = ControlSpecHelper.buildControlSpecActionRowModel(
            step,
            ControlSpecHelper.formatControlSpecCommands,
            ControlSpecHelper.formatControlSpecConditions
        );
        tr.innerHTML = `
            <td class="ctrl-spec-td-mono-bold-dark">${escapeHtml(row.stepText)}</td>
            <td class="ctrl-spec-td">${escapeHtml(row.labelText)}</td>
            <td class="ctrl-spec-td-muted">${escapeHtml(row.actuatorText)}</td>
            <td class="ctrl-spec-td-muted">${escapeHtml(row.actionText)}</td>
            <td class="ctrl-spec-td-desc">${escapeHtml(row.commandsText)}</td>
            <td class="ctrl-spec-td-desc">${escapeHtml(row.doneConditionsText)}</td>
            <td class="ctrl-spec-td-mono-muted">${escapeHtml(row.timeoutText)}</td>
        `;
        tbody.appendChild(tr);
    });
}

function getCurrentControlSpecViewModel() {
    if (ControlSpecControllerModule?.getCurrentControlSpecViewModel) {
        const generationApi = typeof getPreferredGenerationApi === "function" ? getPreferredGenerationApi() : null;
        const generationIr = generationApi ? generationApi.getGenerationIr() : null;
        return ControlSpecControllerModule.getCurrentControlSpecViewModel({
            project: state.project,
            generationIr
        });
    }
    const generationApi = typeof getPreferredGenerationApi === "function" ? getPreferredGenerationApi() : null;
    const generationIr = generationApi ? generationApi.getGenerationIr() : null;
    return ControlSpecHelper.buildControlSpecViewModel(state.project, generationIr);
}

function downloadControlSpecTextFile(filename, content, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

function exportControlSpecIoTable(format) {
    const vm = getCurrentControlSpecViewModel();
    const ioTable = Array.isArray(vm?.ioTable) ? vm.ioTable : [];
    if (!ioTable.length) {
        alert("出力できる I/O 表がありません。");
        return;
    }
    if (format === "json") {
        downloadControlSpecTextFile(
            "control-spec-io-table.json",
            JSON.stringify(ioTable, null, 2),
            "application/json"
        );
        return;
    }
    const csv = ControlSpecHelper.serializeIoTableCsv(ioTable);
    downloadControlSpecTextFile("control-spec-io-table.csv", csv, "text/csv;charset=utf-8");
}

// --- 信号カテゴリ UI CRUD & State Sync ---
let editingSignalCategoryId = null;

function renderSignalCategoryOptions(selectedId = "") {
    if (ControlSpecControllerModule?.renderSignalCategoryOptions) {
        return ControlSpecControllerModule.renderSignalCategoryOptions(document, {
            categories: ensureSignalCategories(),
            selectedId,
            escapeHtml
        });
    }
    const select = document.getElementById("selectSignalCategory");
    if (!select) return;
    const categories = ensureSignalCategories();
    const optionsState = ControlSpecHelper.buildSignalCategoryOptionsState(categories, selectedId, escapeHtml);
    select.innerHTML = optionsState.options;
}

function renderControlSpecSignalCategories() {
    if (ControlSpecControllerModule?.renderControlSpecSignalCategories) {
        return ControlSpecControllerModule.renderControlSpecSignalCategories(document, {
            categories: ensureSignalCategories(),
            escapeHtml,
            onEdit: loadSignalCategoryToForm,
            onDelete: deleteControlSpecSignalCategory
        });
    }
    const tbody = document.getElementById("controlSpecSignalCategoriesTableBody");
    if (!tbody) return;
    tbody.innerHTML = "";
    const listState = ControlSpecHelper.buildControlSpecSignalCategoryListState(ensureSignalCategories());

    if (listState.isEmpty) {
        tbody.innerHTML = `
            <tr>
                <td colspan="7" class="ctrl-spec-empty-cell">
                    ${escapeHtml(listState.emptyMessage)}
                </td>
            </tr>
        `;
        return;
    }

    listState.rows.forEach((category) => {
        const tr = document.createElement("tr");
        tr.className = "ctrl-spec-tr";
        const row = ControlSpecHelper.buildControlSpecSignalCategoryRowModel(category);
        tr.innerHTML = `
            <td class="ctrl-spec-td-bold">${escapeHtml(row.idText)}</td>
            <td class="ctrl-spec-td">${escapeHtml(row.nameText)}</td>
            <td class="ctrl-spec-td-help">${escapeHtml(row.directionText)}</td>
            <td class="ctrl-spec-td-mono-bold-muted">${escapeHtml(row.addressPrefixText)}</td>
            <td class="ctrl-spec-td-help">${escapeHtml(row.startByteText)}</td>
            <td class="ctrl-spec-td-btns">
                <button type="button" class="control-spec-action-btn edit-btn" data-category-id="${escapeHtml(row.idText)}">編集</button>
                <button type="button" class="control-spec-action-btn delete delete-btn" data-category-id="${escapeHtml(row.idText)}">削除</button>
            </td>
        `;
        tr.querySelector(".edit-btn").addEventListener("click", () => {
            loadSignalCategoryToForm(row.idText);
        });
        tr.querySelector(".delete-btn").addEventListener("click", () => {
            deleteControlSpecSignalCategory(row.idText);
        });
        tbody.appendChild(tr);
    });
}

function loadSignalCategoryToForm(categoryId) {
    const category = findSignalCategory(categoryId);
    if (!category) return;
    if (ControlSpecControllerModule?.loadSignalCategoryToForm) {
        editingSignalCategoryId = ControlSpecControllerModule.loadSignalCategoryToForm(document, {
            category,
            showError: showSignalCategoryFormError
        });
        return;
    }
    const formState = ControlSpecHelper.buildLoadedSignalCategoryFormState(category);
    editingSignalCategoryId = formState.editingSignalCategoryId;

    const title = document.getElementById("controlSpecSignalCategoryFormTitle");
    if (title) title.textContent = formState.titleText;

    const inputId = document.getElementById("inputSignalCategoryId");
    if (inputId) {
        inputId.value = formState.inputId;
        inputId.disabled = formState.inputIdDisabled;
        inputId.style.background = formState.inputIdBackground;
    }

    const inputName = document.getElementById("inputSignalCategoryName");
    if (inputName) inputName.value = formState.inputName;

    const selectDirection = document.getElementById("selectSignalCategoryDirection");
    if (selectDirection) selectDirection.value = formState.direction;

    const selectPrefix = document.getElementById("selectSignalCategoryAddressPrefix");
    if (selectPrefix) selectPrefix.value = formState.addressPrefix;

    const inputStartByte = document.getElementById("inputSignalCategoryStartByte");
    if (inputStartByte) inputStartByte.value = formState.startByte;

    showSignalCategoryFormError("");
}

function resetSignalCategoryForm() {
    if (ControlSpecControllerModule?.resetSignalCategoryForm) {
        editingSignalCategoryId = ControlSpecControllerModule.resetSignalCategoryForm(document, {
            showError: showSignalCategoryFormError
        });
        return;
    }
    const formState = ControlSpecHelper.buildResetSignalCategoryFormState();
    editingSignalCategoryId = formState.editingSignalCategoryId;

    const title = document.getElementById("controlSpecSignalCategoryFormTitle");
    if (title) title.textContent = formState.titleText;

    const inputId = document.getElementById("inputSignalCategoryId");
    if (inputId) {
        inputId.value = formState.inputId;
        inputId.disabled = formState.inputIdDisabled;
        inputId.style.background = formState.inputIdBackground;
    }

    const inputName = document.getElementById("inputSignalCategoryName");
    if (inputName) inputName.value = formState.inputName;

    const selectDirection = document.getElementById("selectSignalCategoryDirection");
    if (selectDirection) selectDirection.value = formState.direction;

    const selectPrefix = document.getElementById("selectSignalCategoryAddressPrefix");
    if (selectPrefix) selectPrefix.value = formState.addressPrefix;

    const inputStartByte = document.getElementById("inputSignalCategoryStartByte");
    if (inputStartByte) inputStartByte.value = formState.startByte;

    showSignalCategoryFormError("");
}

function saveControlSpecSignalCategory() {
    const inputId = document.getElementById("inputSignalCategoryId");
    const inputName = document.getElementById("inputSignalCategoryName");
    const selectDirection = document.getElementById("selectSignalCategoryDirection");
    const selectPrefix = document.getElementById("selectSignalCategoryAddressPrefix");
    const inputStartByte = document.getElementById("inputSignalCategoryStartByte");
    if (!inputId || !inputName || !selectDirection || !selectPrefix || !inputStartByte) return;
    if (ControlSpecControllerModule?.saveControlSpecSignalCategory) {
        return ControlSpecControllerModule.saveControlSpecSignalCategory({
            project: state.project,
            categories: ensureSignalCategories(),
            editingSignalCategoryId,
            formValues: {
                id: inputId.value,
                name: inputName.value,
                direction: selectDirection.value,
                addressPrefix: selectPrefix.value,
                startByte: inputStartByte.value
            },
            showError: showSignalCategoryFormError,
            renderSignalCategories: renderControlSpecSignalCategories,
            renderSignalCategoryOptions,
            getSelectedSignalCategoryId: () => document.getElementById("selectSignalCategory")?.value || "",
            renderSignals: renderControlSpecSignals,
            scheduleAutoSave,
            resetForm: resetSignalCategoryForm
        });
    }

    const formInput = ControlSpecHelper.buildControlSpecSignalCategoryFormInput({
        id: inputId.value,
        name: inputName.value,
        direction: selectDirection.value,
        addressPrefix: selectPrefix.value,
        startByte: inputStartByte.value
    });
    const draft = ControlSpecHelper.buildControlSpecSignalCategoryDraft(formInput);
    const categories = ensureSignalCategories();
    const validation = ControlSpecHelper.validateControlSpecSignalCategoryDraft(
        draft,
        editingSignalCategoryId,
        categories
    );
    if (!validation.ok) {
        showSignalCategoryFormError(validation.errorMessage);
        return;
    }
    state.project.signalCategories = ControlSpecHelper.buildSavedControlSpecSignalCategories(
        categories,
        draft,
        validation
    );

    renderControlSpecSignalCategories();
    renderSignalCategoryOptions(document.getElementById("selectSignalCategory")?.value || "");
    renderControlSpecSignals();
    scheduleAutoSave();
    resetSignalCategoryForm();
}

function deleteControlSpecSignalCategory(categoryId) {
    if (ControlSpecControllerModule?.deleteControlSpecSignalCategory) {
        return ControlSpecControllerModule.deleteControlSpecSignalCategory({
            project: state.project,
            categoryId,
            categories: ensureSignalCategories(),
            signals: state.project?.signals || [],
            confirmDelete: (message) => confirm(message),
            alertMessage: (message) => alert(message),
            renderSignalCategories: renderControlSpecSignalCategories,
            renderSignalCategoryOptions,
            renderSignals: renderControlSpecSignals,
            scheduleAutoSave,
            editingSignalCategoryId,
            resetForm: resetSignalCategoryForm
        });
    }
    const deleteState = ControlSpecHelper.buildDeleteControlSpecSignalCategoryState(
        categoryId,
        ensureSignalCategories(),
        state.project?.signals || []
    );
    if (!deleteState.ok) {
        alert(deleteState.alertMessage);
        return;
    }
    if (!confirm(deleteState.confirmMessage)) return;

    state.project.signalCategories = deleteState.nextCategories;
    state.project.signals = deleteState.nextSignals;
    renderControlSpecSignalCategories();
    renderSignalCategoryOptions();
    renderControlSpecSignals();
    scheduleAutoSave();
    if (editingSignalCategoryId === categoryId) resetSignalCategoryForm();
}

function showSignalCategoryFormError(msg) {
    const errBox = document.getElementById("controlSpecSignalCategoryFormError");
    if (!errBox) return;
    errBox.textContent = msg || "";
    errBox.style.display = msg ? "block" : "none";
}

function applySelectedSignalCategoryDefaults() {
    const categoryId = document.getElementById("selectSignalCategory")?.value || "";
    const category = findSignalCategory(categoryId);
    if (!category) return;
    const selectDirection = document.getElementById("selectSignalDirection");
    if (selectDirection) selectDirection.value = category.direction;
}

function autoAssignSignalAddress() {
    const selectCategory = document.getElementById("selectSignalCategory");
    const inputAddress = document.getElementById("inputSignalAddress");
    if (!selectCategory || !inputAddress) return;
    const category = findSignalCategory(selectCategory.value) || ensureSignalCategories()[0];
    const address = ControlSpecHelper.buildNextSignalAddress(category, state.project?.signals || []);
    if (address) inputAddress.value = address;
}

// --- 信号辞書 (Signals) UI CRUD & State Sync ---
let editingSignalId = null; // nullなら新規追加、文字列なら編集中のSignalID

function renderControlSpecSignals() {
    if (ControlSpecControllerModule?.renderControlSpecSignals) {
        return ControlSpecControllerModule.renderControlSpecSignals(document, {
            signals: state.project?.signals,
            findSignalCategory,
            escapeHtml,
            onEdit: loadSignalToForm,
            onDelete: deleteControlSpecSignal
        });
    }
    const tbody = document.getElementById("controlSpecSignalsTableBody");
    if (!tbody) return;

    tbody.innerHTML = "";
    const listState = ControlSpecHelper.buildControlSpecSignalListState(state.project?.signals);
    const signalsMeta = document.getElementById("controlSpecSignalsMeta");
    if (signalsMeta) signalsMeta.textContent = listState.metaText;

    if (listState.isEmpty) {
        tbody.innerHTML = `
            <tr>
                <td colspan="10" class="ctrl-spec-empty-cell">
                    ${escapeHtml(listState.emptyMessage)}
                </td>
            </tr>
        `;
        return;
    }

    listState.rows.forEach(sig => {
        const tr = document.createElement("tr");
        tr.className = "ctrl-spec-tr";
        const row = ControlSpecHelper.buildControlSpecSignalRowModel(sig, findSignalCategory);

        tr.innerHTML = `
            <td class="ctrl-spec-td-bold">${escapeHtml(row.idText)}</td>
            <td class="ctrl-spec-td">${escapeHtml(row.nameText)}</td>
            <td class="ctrl-spec-td-help">${escapeHtml(row.categoryText)}</td>
            <td class="ctrl-spec-td"><span class="control-spec-badge ${row.badgeClass}">${row.directionLabel}</span></td>
            <td class="ctrl-spec-td-mono-bold-muted" title="${escapeHtml(row.iecNameText)}">${escapeHtml(row.addressText)}</td>
            <td class="ctrl-spec-td-help">${escapeHtml(row.dataTypeText)}</td>
            <td class="ctrl-spec-td-help">${escapeHtml(row.activeStateText)}</td>
            <td class="ctrl-spec-td-help">${escapeHtml(row.actuatorIdText)}</td>
            <td class="ctrl-spec-td-truncate-150" title="${escapeHtml(row.descriptionText)}">${escapeHtml(row.descriptionText)}</td>
            <td class="ctrl-spec-td-btns">
                <button type="button" class="control-spec-action-btn edit-btn" data-sig-id="${escapeHtml(row.idText)}">編集</button>
                <button type="button" class="control-spec-action-btn delete delete-btn" data-sig-id="${escapeHtml(row.idText)}">削除</button>
            </td>
        `;

        tr.querySelector(".edit-btn").addEventListener("click", () => {
            loadSignalToForm(row.idText);
        });

        tr.querySelector(".delete-btn").addEventListener("click", () => {
            deleteControlSpecSignal(row.idText);
        });

        tbody.appendChild(tr);
    });
}

function loadSignalToForm(signalId) {
    const sig = state.project.signals.find(s => s.id === signalId);
    if (!sig) return;
    if (ControlSpecControllerModule?.loadSignalToForm) {
        editingSignalId = ControlSpecControllerModule.loadSignalToForm(document, {
            signal: sig,
            signalId,
            renderSignalCategoryOptions
        });
        return;
    }

    const formState = ControlSpecHelper.buildLoadedControlSpecSignalFormState(sig, signalId);
    editingSignalId = formState.editingSignalId;

    const title = document.getElementById("controlSpecSignalFormTitle");
    if (title) title.textContent = formState.titleText;

    const inputId = document.getElementById("inputSignalId");
    if (inputId) {
        inputId.value = formState.inputId;
        inputId.disabled = formState.inputIdDisabled; // 編集時はIDは読み取り専用
        inputId.style.background = formState.inputIdBackground;
    }

    const inputName = document.getElementById("inputSignalName");
    if (inputName) inputName.value = formState.inputName;

    const inputIecName = document.getElementById("inputSignalIecName");
    if (inputIecName) inputIecName.value = formState.inputIecName;

    renderSignalCategoryOptions(formState.category);

    const selectDirection = document.getElementById("selectSignalDirection");
    if (selectDirection) selectDirection.value = formState.direction;

    const inputAddress = document.getElementById("inputSignalAddress");
    if (inputAddress) inputAddress.value = formState.address;

    const selectDataType = document.getElementById("selectSignalDataType");
    if (selectDataType) selectDataType.value = formState.dataType;

    const selectActive = document.getElementById("selectSignalActiveState");
    if (selectActive) selectActive.value = formState.activeState;

    const inputActuatorId = document.getElementById("inputSignalActuatorId");
    if (inputActuatorId) inputActuatorId.value = formState.actuatorId;

    const textareaDesc = document.getElementById("textareaSignalDesc");
    if (textareaDesc) textareaDesc.value = formState.description;

    const errBox = document.getElementById("controlSpecSignalFormError");
    if (errBox && formState.hideError) errBox.style.display = "none";
}

function resetSignalForm() {
    if (ControlSpecControllerModule?.resetSignalForm) {
        editingSignalId = ControlSpecControllerModule.resetSignalForm(document, {
            defaultCategoryId: ensureSignalCategories()[0]?.id || "",
            renderSignalCategoryOptions,
            applySelectedSignalCategoryDefaults
        });
        return;
    }
    const formState = ControlSpecHelper.buildResetControlSpecSignalFormState(ensureSignalCategories()[0]?.id || "");
    editingSignalId = formState.editingSignalId;

    const title = document.getElementById("controlSpecSignalFormTitle");
    if (title) title.textContent = formState.titleText;

    const inputId = document.getElementById("inputSignalId");
    if (inputId) {
        inputId.value = formState.inputId;
        inputId.disabled = formState.inputIdDisabled;
        inputId.style.background = formState.inputIdBackground;
    }

    const inputName = document.getElementById("inputSignalName");
    if (inputName) inputName.value = formState.inputName;

    const inputIecName = document.getElementById("inputSignalIecName");
    if (inputIecName) inputIecName.value = formState.inputIecName;

    renderSignalCategoryOptions(formState.category);
    applySelectedSignalCategoryDefaults();

    const selectDirection = document.getElementById("selectSignalDirection");
    if (selectDirection && !selectDirection.value) selectDirection.value = formState.direction;

    const inputAddress = document.getElementById("inputSignalAddress");
    if (inputAddress) inputAddress.value = formState.address;

    const selectActive = document.getElementById("selectSignalActiveState");
    if (selectActive) selectActive.value = formState.activeState;

    const selectDataType = document.getElementById("selectSignalDataType");
    if (selectDataType) selectDataType.value = formState.dataType;

    const inputActuatorId = document.getElementById("inputSignalActuatorId");
    if (inputActuatorId) inputActuatorId.value = formState.actuatorId;

    const textareaDesc = document.getElementById("textareaSignalDesc");
    if (textareaDesc) textareaDesc.value = formState.description;

    const errBox = document.getElementById("controlSpecSignalFormError");
    if (errBox && formState.hideError) errBox.style.display = "none";
}

function saveControlSpecSignal() {
    const inputId = document.getElementById("inputSignalId");
    const inputName = document.getElementById("inputSignalName");
    const inputIecName = document.getElementById("inputSignalIecName");
    const selectCategory = document.getElementById("selectSignalCategory");
    const selectDirection = document.getElementById("selectSignalDirection");
    const inputAddress = document.getElementById("inputSignalAddress");
    const selectDataType = document.getElementById("selectSignalDataType");
    const selectActive = document.getElementById("selectSignalActiveState");
    const textareaDesc = document.getElementById("textareaSignalDesc");
    const inputActuatorId = document.getElementById("inputSignalActuatorId");
    const errBox = document.getElementById("controlSpecSignalFormError");

    if (!inputId || !inputName || !inputAddress || !selectDirection || !selectActive) return;
    if (ControlSpecControllerModule?.saveControlSpecSignal) {
        return ControlSpecControllerModule.saveControlSpecSignal({
            project: state.project,
            editingSignalId,
            formValues: {
                id: inputId.value,
                name: inputName.value,
                iecName: inputIecName?.value || "",
                category: selectCategory?.value || "",
                address: inputAddress.value,
                direction: selectDirection.value,
                dataType: selectDataType?.value || "BOOL",
                activeState: selectActive.value,
                actuatorId: inputActuatorId?.value || "",
                description: textareaDesc?.value || ""
            },
            showError: showFormError,
            recomputeAll,
            renderSignals: renderControlSpecSignals,
            renderView: renderControlSpecView,
            scheduleAutoSave,
            resetForm: resetSignalForm
        });
    }

    const formInput = ControlSpecHelper.buildControlSpecSignalFormInput({
        id: inputId.value,
        name: inputName.value,
        iecName: inputIecName?.value || "",
        category: selectCategory?.value || "",
        address: inputAddress.value,
        direction: selectDirection.value,
        dataType: selectDataType?.value || "BOOL",
        activeState: selectActive.value,
        actuatorId: inputActuatorId?.value || "",
        description: textareaDesc?.value || ""
    });
    const draft = ControlSpecHelper.buildControlSpecSignalDraft(formInput);

    if (!Array.isArray(state.project.signals)) {
        state.project.signals = [];
    }

    const validation = ControlSpecHelper.validateControlSpecSignalDraft(
        draft,
        editingSignalId,
        state.project.signals
    );
    if (!validation.ok) {
        showFormError(validation.errorMessage);
        return;
    }

    state.project.signals = ControlSpecHelper.buildSavedControlSpecSignals(
        state.project.signals,
        draft,
        validation
    );

    // 局所更新ルートの走査・実行
    recomputeAll(); // findings の再計算
    renderControlSpecSignals(); // 左側テーブルの再描画
    renderControlSpecView(); // ViewModelとカウンタ表示の更新
    scheduleAutoSave(); // 自動保存
    resetSignalForm(); // フォームを新規追加状態へリセット
}

function showFormError(msg) {
    const errBox = document.getElementById("controlSpecSignalFormError");
    if (errBox) {
        errBox.textContent = msg;
        errBox.style.display = "block";
    }
}

function isSignalReferencedInActuators(signalId) {
    return ControlSpecHelper.findControlSpecSignalReferenceInActuators(
        signalId,
        state.project?.actuators || []
    );
}

function deleteControlSpecSignal(signalId) {
    if (ControlSpecControllerModule?.deleteControlSpecSignal) {
        return ControlSpecControllerModule.deleteControlSpecSignal({
            project: state.project,
            signalId,
            actuators: state.project?.actuators || [],
            confirmDelete: (message) => confirm(message),
            editingSignalId,
            resetForm: resetSignalForm,
            recomputeAll,
            renderSignals: renderControlSpecSignals,
            renderView: renderControlSpecView,
            scheduleAutoSave
        });
    }
    if (!Array.isArray(state.project?.signals)) return;

    const refCheck = isSignalReferencedInActuators(signalId);
    const deleteState = ControlSpecHelper.buildDeleteControlSpecSignalState(
        signalId,
        refCheck,
        state.project.signals
    );

    if (!confirm(deleteState.confirmMessage)) return;

    state.project.signals = deleteState.nextSignals;

    // フォーム編集中にその信号が削除された場合はフォームをクリア
    if (editingSignalId === signalId) {
        resetSignalForm();
    }

    // 局所更新ルートの実行
    recomputeAll();
    renderControlSpecSignals();
    renderControlSpecView();
    scheduleAutoSave();
}

// --- 信号辞書 フロー候補取り込み機能 & State ---
let controlSpecFlowSignalCandidates = [];

function ensureConditionDraftSignal(kind, conditionName, activeValue, actuatorId = "") {
    const name = String(conditionName || "").trim();
    if (!name) return "";
    if (!Array.isArray(state.project.signals)) state.project.signals = [];

    const direction = kind === "internalFlag" ? "internal" : "input";
    const existing = ControlSpecHelper.findExistingSignalByName(name, direction, state.project?.signals || []);
    if (existing?.id) {
        if (kind === "internalFlag") commitInternalFlagName(existing.id);
        return existing.id;
    }

    const prefix = kind === "internalFlag" ? "M_" : "X_";
    const fallback = `${prefix}CONDITION`;
    const baseId = ControlSpecHelper.toSignalDraftId(name, fallback);
    const signalId = ControlSpecHelper.makeUniqueSignalDraftId(
        baseId.startsWith(prefix) ? baseId : `${prefix}${baseId}`,
        state.project?.signals || []
    );

    state.project.signals.push({
        id: signalId,
        name,
        iecName: "",
        category: kind === "internalFlag" ? "condition" : "sensor",
        direction,
        address: "",
        dataType: "BOOL",
        activeState: activeValue === false ? "FALSE" : "TRUE",
        actuatorId: actuatorId || "",
        description: kind === "internalFlag" ? `条件ノードから生成した内部条件: ${name}` : `条件ノードから生成した入力条件: ${name}`
    });
    if (kind === "internalFlag") commitInternalFlagName(signalId);
    renderControlSpecSignals();
    renderControlSpecView();
    scheduleAutoSave();
    return signalId;
}

function buildControlSpecFlowSignalCandidates() {
    const snapshot = getFlowExportSnapshot();
    const modules = snapshot?.drawflow ? Object.keys(snapshot.drawflow) : ["Home"];
    const existingSignals = Array.isArray(state.project?.signals) ? state.project.signals : [];
    let candidates = [];
    let seenKeys = [];

    for (const mod of modules) {
        const modData = getFlowModuleData(snapshot, mod);
        const nodes = Object.values(modData || {});

        for (const node of nodes) {
            const data = getNodeData(node);
            const nodeType = getNodeTypeFromData(data);

            if (nodeType === "condition") {
                const normalized = ensureConditionNodeData({ ...data });
                const rows = ControlSpecHelper.buildControlSpecFlowConditionCandidates(
                    mod,
                    normalized,
                    existingSignals,
                    candidates.length
                );
                const nextState = ControlSpecHelper.appendUniqueControlSpecFlowCandidates(
                    candidates,
                    seenKeys,
                    rows
                );
                candidates = nextState.candidates;
                seenKeys = nextState.seenKeys;
            }

            if (nodeType === "signal_output") {
                const normalized = ensureSignalOutputData({ ...data });
                const candidate = ControlSpecHelper.buildControlSpecFlowSignalOutputCandidate(
                    mod,
                    normalized,
                    existingSignals,
                    candidates.length
                );
                const nextState = ControlSpecHelper.appendUniqueControlSpecFlowCandidates(
                    candidates,
                    seenKeys,
                    candidate ? [candidate] : []
                );
                candidates = nextState.candidates;
                seenKeys = nextState.seenKeys;
            }
        }
    }

    return candidates;
}

function renderControlSpecFlowSignalCandidates() {
    if (ControlSpecControllerModule?.renderControlSpecFlowSignalCandidates) {
        controlSpecFlowSignalCandidates = buildControlSpecFlowSignalCandidates();
        return ControlSpecControllerModule.renderControlSpecFlowSignalCandidates(document, {
            candidates: controlSpecFlowSignalCandidates,
            escapeHtml
        });
    }
    const select = document.getElementById("selectControlSpecFlowSignalCandidate");
    if (!select) return;
    controlSpecFlowSignalCandidates = buildControlSpecFlowSignalCandidates();
    const optionsState = ControlSpecHelper.buildControlSpecFlowSignalCandidateOptionsState(
        controlSpecFlowSignalCandidates,
        escapeHtml
    );
    select.innerHTML = optionsState.options;
}

function applyControlSpecFlowSignalCandidate() {
    const select = document.getElementById("selectControlSpecFlowSignalCandidate");
    if (ControlSpecControllerModule?.applyControlSpecFlowSignalCandidate) {
        return ControlSpecControllerModule.applyControlSpecFlowSignalCandidate(document, {
            candidates: controlSpecFlowSignalCandidates,
            selectedIndex: select?.value || "",
            findSignalCategory,
            loadSignalToForm,
            resetSignalForm
        });
    }
    if (!select || select.value === "") return;
    const candidate = controlSpecFlowSignalCandidates[Number(select.value)];
    if (!candidate) return;

    const applyState = ControlSpecHelper.buildControlSpecFlowSignalCandidateApplyState(
        candidate,
        (categoryId) => Boolean(findSignalCategory(categoryId))
    );

    if (applyState.mode === "existing") {
        loadSignalToForm(applyState.existingSignalId);
        return;
    }

    resetSignalForm();
    const inputId = document.getElementById("inputSignalId");
    const inputName = document.getElementById("inputSignalName");
    const selectCategory = document.getElementById("selectSignalCategory");
    const selectDirection = document.getElementById("selectSignalDirection");
    const textareaDesc = document.getElementById("textareaSignalDesc");
    const inputActuatorId = document.getElementById("inputSignalActuatorId");
    const formState = applyState.formState;
    if (inputId) inputId.value = formState.signalId;
    if (inputName) inputName.value = formState.signalName;
    if (selectCategory && formState.categoryId) selectCategory.value = formState.categoryId;
    if (selectDirection) selectDirection.value = formState.direction;
    if (textareaDesc) textareaDesc.value = formState.description;
    if (inputActuatorId) inputActuatorId.value = formState.actuatorId;
}

// --- アクチュエータ・アクション (Actuators) UI CRUD & State Sync ---
let editingActuatorId = null;
let actuatorActions = [];
let selectedActionIndex = null;
let editingActuatorTypeCatalogId = null;
const ACTION_NAME_PRESET_CANDIDATES = [
    "前進", "後退", "上昇", "下降", "チャック", "アンチャック",
    "ON", "OFF", "正転", "逆転", "停止", "原点復帰", "位置決め", "吸着", "吸着破壊"
];

function syncMotorControlTypeField(typeSelectId, motorSelectId) {
    const typeSelect = document.getElementById(typeSelectId);
    const motorSelect = document.getElementById(motorSelectId);
    if (!typeSelect || !motorSelect) return;
    const fieldState = ControlSpecHelper.buildMotorControlTypeFieldState(
        normalizeAxisActuatorType(typeSelect.value)
    );
    motorSelect.disabled = fieldState.disabled;
    motorSelect.style.background = fieldState.background;
}

function syncPneumaticValveModeField(typeSelectId, valveSelectId) {
    const typeSelect = document.getElementById(typeSelectId);
    const valveSelect = document.getElementById(valveSelectId);
    if (!typeSelect || !valveSelect) return;
    const fieldState = ControlSpecHelper.buildPneumaticValveModeFieldState(
        normalizeAxisActuatorType(typeSelect.value)
    );
    valveSelect.disabled = fieldState.disabled;
    valveSelect.style.background = fieldState.background;
    valveSelect.value = normalizeAxisActuatorType(typeSelect.value) === "pneumaticCylinder"
        ? (valveSelect.value === "singleSolenoid" ? "singleSolenoid" : "doubleSolenoid")
        : "";
}

function buildActuatorValveModelFormValues() {
    const read = (id) => String(document.getElementById(id)?.value || "").trim();
    return {
        positiveOutput: read("inputActuatorValvePositiveOutput"),
        negativeOutput: read("inputActuatorValveNegativeOutput"),
        singleOutput: read("inputActuatorValveSingleOutput"),
        singleSolenoidActiveDirection: read("selectActuatorValveSingleActiveDirection"),
        stateTable: {
            extend: {
                positive: read("selectActuatorValveStateExtendPositive"),
                negative: read("selectActuatorValveStateExtendNegative"),
                single: read("selectActuatorValveStateExtendSingle")
            },
            retract: {
                positive: read("selectActuatorValveStateRetractPositive"),
                negative: read("selectActuatorValveStateRetractNegative"),
                single: read("selectActuatorValveStateRetractSingle")
            },
            stop: {
                positive: read("selectActuatorValveStateStopPositive"),
                negative: read("selectActuatorValveStateStopNegative"),
                single: read("selectActuatorValveStateStopSingle")
            },
            emergency: {
                positive: read("selectActuatorValveStateEmergencyPositive"),
                negative: read("selectActuatorValveStateEmergencyNegative"),
                single: read("selectActuatorValveStateEmergencySingle")
            }
        }
    };
}

function writeActuatorValveModelFormValues(valveModel = null) {
    const source = valveModel && typeof valveModel === "object" ? valveModel : {};
    const stateTable = source?.stateTable && typeof source.stateTable === "object" ? source.stateTable : {};
    const setValue = (id, value) => {
        const element = document.getElementById(id);
        if (element) element.value = String(value || "");
    };
    setValue("inputActuatorValvePositiveOutput", source.positiveOutput);
    setValue("inputActuatorValveNegativeOutput", source.negativeOutput);
    setValue("inputActuatorValveSingleOutput", source.singleOutput);
    setValue("selectActuatorValveSingleActiveDirection", source.singleSolenoidActiveDirection || "positive");
    setValue("selectActuatorValveStateExtendPositive", stateTable?.extend?.positive || "");
    setValue("selectActuatorValveStateExtendNegative", stateTable?.extend?.negative || "");
    setValue("selectActuatorValveStateExtendSingle", stateTable?.extend?.single || "");
    setValue("selectActuatorValveStateRetractPositive", stateTable?.retract?.positive || "");
    setValue("selectActuatorValveStateRetractNegative", stateTable?.retract?.negative || "");
    setValue("selectActuatorValveStateRetractSingle", stateTable?.retract?.single || "");
    setValue("selectActuatorValveStateStopPositive", stateTable?.stop?.positive || "");
    setValue("selectActuatorValveStateStopNegative", stateTable?.stop?.negative || "");
    setValue("selectActuatorValveStateStopSingle", stateTable?.stop?.single || "");
    setValue("selectActuatorValveStateEmergencyPositive", stateTable?.emergency?.positive || "");
    setValue("selectActuatorValveStateEmergencyNegative", stateTable?.emergency?.negative || "");
    setValue("selectActuatorValveStateEmergencySingle", stateTable?.emergency?.single || "");
}

function syncActuatorValvePresetOptions() {
    const presetSelect = document.getElementById("selectActuatorValvePreset");
    const typeSelect = document.getElementById("selectActuatorType");
    const valveModeSelect = document.getElementById("selectActuatorPneumaticValveMode");
    if (!presetSelect || !typeSelect || !valveModeSelect) return;
    const optionsState = ControlSpecHelper.buildPneumaticValvePresetOptionsState(
        normalizeAxisActuatorType(typeSelect.value),
        valveModeSelect.value || "doubleSolenoid",
        presetSelect.value
    );
    presetSelect.innerHTML = optionsState.options;
    presetSelect.value = optionsState.selectedPreset;
}

function applyActuatorValvePreset() {
    const presetSelect = document.getElementById("selectActuatorValvePreset");
    const typeSelect = document.getElementById("selectActuatorType");
    const valveModeSelect = document.getElementById("selectActuatorPneumaticValveMode");
    if (!presetSelect || !typeSelect || !valveModeSelect) return;
    const nextState = ControlSpecHelper.applyPneumaticActuatorValvePreset(
        presetSelect.value,
        normalizeAxisActuatorType(typeSelect.value),
        {
            pneumaticValveMode: valveModeSelect.value,
            ...buildActuatorValveModelFormValues()
        }
    );
    if (!nextState?.applied) return;
    valveModeSelect.value = nextState.pneumaticValveMode || valveModeSelect.value;
    writeActuatorValveModelFormValues(nextState.valveModel);
    syncPneumaticValveModeField("selectActuatorType", "selectActuatorPneumaticValveMode");
    syncActuatorValvePresetOptions();
    presetSelect.value = String(presetSelect.value || "");
    syncActuatorValveModelFields();
}

function syncActuatorValveModelFields() {
    const actuatorType = normalizeAxisActuatorType(
        document.getElementById("selectActuatorType")?.value || ""
    );
    const pneumaticValveMode = document.getElementById("selectActuatorPneumaticValveMode")?.value || "doubleSolenoid";
    const fieldState = ControlSpecHelper.buildActuatorValveModelFieldState(
        actuatorType,
        pneumaticValveMode,
        buildActuatorValveModelFormValues()
    );
    const valveModel = fieldState?.valveModel || {};
    const stateTable = valveModel?.stateTable || {};
    const setDisplay = (id, visible, displayValue = "block") => {
        const element = document.getElementById(id);
        if (element) element.style.display = visible ? displayValue : "none";
    };
    const setValue = (id, value) => {
        const element = document.getElementById(id);
        if (element) element.value = String(value || "");
    };
    const setDisabled = (ids, disabled) => {
        ids.forEach((id) => {
            const element = document.getElementById(id);
            if (!element) return;
            element.disabled = disabled;
            element.style.background = disabled ? "#f3f4f6" : "#fff";
        });
    };

    setDisplay("controlSpecActuatorValveModelPanel", fieldState.showPanel, "flex");
    setDisplay("controlSpecActuatorValvePresetRow", fieldState.showPanel);
    setDisplay("controlSpecActuatorValveDoubleOutputs", fieldState.showDoubleOutputs);
    setDisplay("controlSpecActuatorValveSingleOutput", fieldState.showSingleOutput);
    setDisplay("controlSpecActuatorValveSingleDirection", fieldState.showSingleOutput);
    syncActuatorValvePresetOptions();
    document.querySelectorAll(".control-spec-valve-double-cell").forEach((element) => {
        element.style.display = fieldState.showDoubleOutputs ? "table-cell" : "none";
    });
    document.querySelectorAll(".control-spec-valve-single-cell").forEach((element) => {
        element.style.display = fieldState.showSingleOutput ? "table-cell" : "none";
    });

    setDisabled(
        [
            "inputActuatorValvePositiveOutput",
            "inputActuatorValveNegativeOutput",
            "selectActuatorValveStateExtendPositive",
            "selectActuatorValveStateExtendNegative",
            "selectActuatorValveStateRetractPositive",
            "selectActuatorValveStateRetractNegative",
            "selectActuatorValveStateStopPositive",
            "selectActuatorValveStateStopNegative",
            "selectActuatorValveStateEmergencyPositive",
            "selectActuatorValveStateEmergencyNegative"
        ],
        !fieldState.showDoubleOutputs
    );
    setDisabled(
        [
            "inputActuatorValveSingleOutput",
            "selectActuatorValveSingleActiveDirection",
            "selectActuatorValveStateExtendSingle",
            "selectActuatorValveStateRetractSingle",
            "selectActuatorValveStateStopSingle",
            "selectActuatorValveStateEmergencySingle"
        ],
        !fieldState.showSingleOutput
    );
    setDisabled(["selectActuatorValvePreset"], !fieldState.showPanel);
    writeActuatorValveModelFormValues({
        ...valveModel,
        stateTable
    });
}

function buildDefaultAxisRef(name = "", actuatorId = "") {
    const source = name || actuatorId || "Axis";
    return `Axis_${ControlSpecHelper.buildControlSpecSlug(source, "Axis")}`;
}

function syncActuatorControlMethodDefault(force = false) {
    const selectType = document.getElementById("selectActuatorType");
    const selectControlMethod = document.getElementById("selectActuatorControlMethod");
    const inputAxisRef = document.getElementById("inputActuatorAxisRef");
    if (!selectType || !selectControlMethod) return;
    const type = normalizeAxisActuatorType(selectType.value);
    const fieldState = ControlSpecHelper.buildActuatorControlMethodFieldState(
        {
            actuatorType: type,
            controlMethodValue: selectControlMethod.value,
            force,
            axisRefValue: inputAxisRef?.value || "",
            defaultAxisRef: buildDefaultAxisRef(
                document.getElementById("inputActuatorName")?.value || "",
                document.getElementById("inputActuatorId")?.value || ""
            )
        },
        normalizeActuatorControlMethod
    );
    selectControlMethod.value = fieldState.selectedControlMethod;
    if (inputAxisRef) {
        inputAxisRef.disabled = fieldState.axisRefDisabled;
        inputAxisRef.style.background = fieldState.axisRefBackground;
        inputAxisRef.value = fieldState.axisRefValue;
    }
}

function createEmptyActuatorAction(name = "前進") {
    return ControlSpecHelper.createEmptyControlSpecActuatorAction(name, {
        makeUniqueActionId: (baseId) => makeUniqueActionId(baseId, actuatorActions)
    });
}

function getNextActionPresetName() {
    return ControlSpecHelper.getNextControlSpecActionPresetName(
        actuatorActions,
        ACTION_NAME_PRESET_CANDIDATES
    );
}

function renderControlSpecActuators() {
    if (ControlSpecControllerModule?.renderControlSpecActuators) {
        return ControlSpecControllerModule.renderControlSpecActuators(document, {
            actuators: state.project?.actuators,
            escapeHtml,
            getActuatorTypeLabel: (actuatorType = "pneumaticCylinder") => {
                const normalized = normalizeAxisActuatorType(actuatorType);
                return ControlSpecHelper.getActuatorTypeLabel(
                    getActuatorTypeCatalogEntry(normalized)?.label || "", normalized
                );
            },
            getMotorControlTypeLabel: (motorControlType = "", actuatorType = "motor") =>
                ControlSpecHelper.getMotorControlTypeLabel(
                    normalizeMotorControlType(motorControlType, actuatorType)
                ),
            getPneumaticValveModeLabel: (v = "") =>
                ControlSpecHelper.getPneumaticValveModeLabel(v),
            normalizeActuatorControlMethod,
            normalizeAxisActuatorType,
            onEdit: loadActuatorToForm,
            onDelete: deleteControlSpecActuator,
            renderActuatorTypeCatalogEditor
        });
    }
    const tbody = document.getElementById("controlSpecActuatorsTableBody");
    if (!tbody) return;

    tbody.innerHTML = "";
    const listState = ControlSpecHelper.buildControlSpecActuatorListState(state.project?.actuators);

    // メタ情報（カウンタ）の更新
    const actuatorsMeta = document.getElementById("controlSpecActuatorsMeta");
    if (actuatorsMeta) {
        actuatorsMeta.textContent = listState.metaText;
    }

    if (listState.isEmpty) {
        tbody.innerHTML = `
            <tr>
                <td colspan="6" class="ctrl-spec-empty-cell">
                    ${escapeHtml(listState.emptyMessage)}
                </td>
            </tr>
        `;
        renderActuatorTypeCatalogEditor();
        return;
    }

    listState.rows.forEach(act => {
        const tr = document.createElement("tr");
        tr.className = "ctrl-spec-tr";
        const row = ControlSpecHelper.buildControlSpecActuatorRowModel(
            act,
            (actuatorType = "pneumaticCylinder") => {
                const normalized = normalizeAxisActuatorType(actuatorType);
                return ControlSpecHelper.getActuatorTypeLabel(
                    getActuatorTypeCatalogEntry(normalized)?.label || "", normalized
                );
            },
            (motorControlType = "", actuatorType = "motor") =>
                ControlSpecHelper.getMotorControlTypeLabel(
                    normalizeMotorControlType(motorControlType, actuatorType)
                ),
            (v = "") => ControlSpecHelper.getPneumaticValveModeLabel(v),
            normalizeActuatorControlMethod,
            normalizeAxisActuatorType
        );

        tr.innerHTML = `
            <td class="ctrl-spec-td-bold">${escapeHtml(row.idText)}</td>
            <td class="ctrl-spec-td">${escapeHtml(row.nameText)}</td>
            <td class="ctrl-spec-td-muted">${escapeHtml(row.typeText)}</td>
            <td class="ctrl-spec-td-help">${escapeHtml(row.controlMethodText)}</td>
            <td class="ctrl-spec-td-help">${escapeHtml(row.valveSummaryText || "-")}</td>
            <td class="ctrl-spec-td-bold-help">${escapeHtml(row.actionCountText)}</td>
            <td class="ctrl-spec-td-btns">
                <button type="button" class="control-spec-action-btn edit-btn" data-act-id="${escapeHtml(row.idText)}">編集</button>
                <button type="button" class="control-spec-action-btn delete delete-btn" data-act-id="${escapeHtml(row.idText)}">削除</button>
            </td>
        `;

        tr.querySelector(".edit-btn").addEventListener("click", () => {
            loadActuatorToForm(row.idText);
        });

        tr.querySelector(".delete-btn").addEventListener("click", () => {
            deleteControlSpecActuator(row.idText);
        });

        tbody.appendChild(tr);
    });

    renderActuatorTypeCatalogEditor();
}

function buildActuatorTypeOptions(selectedType = "pneumaticCylinder", includeBlank = false) {
    const normalizedSelected = normalizeAxisActuatorType(selectedType);
    const entries = ensureActuatorTypeCatalog(state.project);
    return ControlSpecHelper.buildActuatorTypeOptionsState(
        entries,
        normalizedSelected,
        includeBlank,
        escapeHtml
    ).options;
}

function refreshActuatorTypeSelectOptions() {
    const mappings = [
        ["selectActuatorType", "pneumaticCylinder"],
        ["axisAddActuatorTypeSelect", "pneumaticCylinder"],
        ["flowAxisAddActuatorTypeSelect", "pneumaticCylinder"]
    ];
    const entries = ensureActuatorTypeCatalog(state.project);
    mappings.forEach(([id, fallback]) => {
        const select = document.getElementById(id);
        if (!select) return;
        const selectState = ControlSpecHelper.buildActuatorTypeSelectRefreshState(
            {
                currentValue: select.value,
                fallback,
                entries
            },
            normalizeAxisActuatorType,
            escapeHtml
        );
        select.innerHTML = selectState.options;
        select.value = selectState.current;
    });
    syncMotorControlTypeField("selectActuatorType", "selectActuatorMotorControlType");
}

function resetActuatorTypeCatalogForm(typeId = null) {
    const entries = ensureActuatorTypeCatalog(state.project);
    const target = typeId ? entries.find((entry) => entry.id === typeId) : null;
    const isCore = Boolean(target && DEFAULT_ACTUATOR_TYPE_CATALOG.some((entry) => entry.id === target.id));
    const formState = ControlSpecHelper.buildActuatorTypeCatalogFormState(target, isCore);
    const inputId = document.getElementById("inputActuatorTypeCatalogId");
    const inputLabel = document.getElementById("inputActuatorTypeCatalogLabel");
    const inputActions = document.getElementById("inputActuatorTypeCatalogActions");
    const meta = document.getElementById("controlSpecActuatorTypeCatalogMeta");
    const deleteButton = document.getElementById("btnControlSpecDeleteActuatorTypeCatalog");
    const select = document.getElementById("selectActuatorTypeCatalogEntry");
    editingActuatorTypeCatalogId = formState.editingActuatorTypeCatalogId;
    if (inputId) {
        inputId.value = formState.inputId;
        inputId.disabled = formState.inputIdDisabled;
        inputId.style.background = formState.inputIdBackground;
    }
    if (inputLabel) inputLabel.value = formState.inputLabel;
    if (inputActions) inputActions.value = formState.inputActions;
    if (meta) meta.textContent = formState.metaText;
    if (deleteButton) {
        deleteButton.disabled = formState.deleteDisabled;
        deleteButton.style.opacity = formState.deleteOpacity;
        deleteButton.title = formState.deleteTitle;
    }
    if (select) select.value = formState.selectValue;
}

function renderActuatorTypeCatalogEditor() {
    const select = document.getElementById("selectActuatorTypeCatalogEntry");
    if (!select) return;
    const entries = ensureActuatorTypeCatalog(state.project);
    const editorState = ControlSpecHelper.buildActuatorTypeCatalogEditorState(
        entries,
        editingActuatorTypeCatalogId,
        escapeHtml
    );
    select.innerHTML = editorState.options;
    resetActuatorTypeCatalogForm(editorState.selectedTypeId);
}

function getActuatorTypeReferenceSummary(typeId) {
    return ControlSpecHelper.buildActuatorTypeReferenceSummary(
        typeId,
        state.project?.axes || [],
        state.project?.actuators || [],
        normalizeAxisActuatorType
    );
}

function saveActuatorTypeCatalogEntry() {
    const inputId = document.getElementById("inputActuatorTypeCatalogId");
    const inputLabel = document.getElementById("inputActuatorTypeCatalogLabel");
    const inputActions = document.getElementById("inputActuatorTypeCatalogActions");
    if (!inputId || !inputLabel || !inputActions) return;
    const catalog = ensureActuatorTypeCatalog(state.project);
    const formInput = ControlSpecHelper.buildActuatorTypeCatalogFormInput({
        rawId: inputId.value,
        label: inputLabel.value,
        actionsText: inputActions.value
    });
    const rawId = formInput.rawId.trim();
    const id = ControlSpecHelper.normalizeControlSpecText(
        rawId,
        ControlSpecHelper.buildControlSpecSlug(formInput.label || "type", "type").toLowerCase()
    );
    const draft = ControlSpecHelper.buildActuatorTypeCatalogDraft(formInput, id);
    const validation = ControlSpecHelper.validateActuatorTypeCatalogDraft(
        draft,
        editingActuatorTypeCatalogId,
        catalog
    );
    if (!validation.ok) {
        alert(validation.errorMessage);
        return;
    }
    const nextEntry = normalizeActuatorTypeCatalogEntry(draft, catalog.length);
    const nextState = ControlSpecHelper.buildSavedActuatorTypeCatalogState(
        catalog,
        editingActuatorTypeCatalogId,
        nextEntry
    );
    state.project.actuatorTypeCatalog = nextState.catalog;
    editingActuatorTypeCatalogId = nextState.editingActuatorTypeCatalogId;
    refreshActuatorTypeSelectOptions();
    recomputeAll();
    renderControlSpecActuators();
    renderControlSpecView();
    scheduleAutoSave();
}

function deleteActuatorTypeCatalogEntry() {
    const deleteState = ControlSpecHelper.buildDeleteActuatorTypeCatalogState(
        editingActuatorTypeCatalogId,
        DEFAULT_ACTUATOR_TYPE_CATALOG.map((entry) => entry.id),
        getActuatorTypeReferenceSummary(editingActuatorTypeCatalogId),
        ensureActuatorTypeCatalog(state.project)
    );
    if (!deleteState.ok) {
        if (deleteState.alertMessage) alert(deleteState.alertMessage);
        return;
    }
    if (!confirm(deleteState.confirmMessage)) return;
    state.project.actuatorTypeCatalog = deleteState.nextCatalog;
    editingActuatorTypeCatalogId = deleteState.editingActuatorTypeCatalogId;
    refreshActuatorTypeSelectOptions();
    recomputeAll();
    renderControlSpecActuators();
    renderControlSpecView();
    scheduleAutoSave();
}

function loadActuatorToForm(actuatorId) {
    cancelControlSpecActuatorAutoSave();
    const act = state.project.actuators.find(a => a.id === actuatorId);
    if (!act) return;
    if (ControlSpecControllerModule?.loadActuatorToForm) {
        const nextState = ControlSpecControllerModule.loadActuatorToForm(document, {
            actuator: act,
            actuatorId,
            buildActuatorTypeOptions,
            normalizeAxisActuatorType,
            normalizeMotorControlType,
            normalizeActuatorControlMethod,
            syncMotorControlTypeField,
            syncPneumaticValveModeField,
            syncActuatorValveModelFields,
            syncActuatorControlMethodDefault,
            populateActionSignalOptions,
            renderControlSpecActionList: () => {}
        });
        if (!nextState) return;
        editingActuatorId = nextState.editingActuatorId;
        actuatorActions = nextState.actuatorActions;
        selectedActionIndex = nextState.selectedActionIndex;
        renderControlSpecActionList();
        return;
    }

    const formState = ControlSpecHelper.buildLoadedControlSpecActuatorFormState(act, actuatorId);
    editingActuatorId = formState.editingActuatorId;
    actuatorActions = formState.actuatorActions;
    selectedActionIndex = formState.selectedActionIndex;

    const title = document.getElementById("controlSpecActuatorFormTitle");
    if (title) title.textContent = formState.titleText;

    const inputId = document.getElementById("inputActuatorId");
    if (inputId) {
        inputId.value = formState.inputId;
        inputId.disabled = formState.inputIdDisabled;
        inputId.style.background = formState.inputIdBackground;
    }

    const inputName = document.getElementById("inputActuatorName");
    if (inputName) inputName.value = formState.inputName;

    const selectType = document.getElementById("selectActuatorType");
    if (selectType) {
        selectType.innerHTML = buildActuatorTypeOptions(formState.actuatorType);
        selectType.value = normalizeAxisActuatorType(formState.actuatorType);
    }

    const selectMotorType = document.getElementById("selectActuatorMotorControlType");
    if (selectMotorType) selectMotorType.value = normalizeMotorControlType(formState.motorControlType, formState.actuatorType);
    const selectValveMode = document.getElementById("selectActuatorPneumaticValveMode");
    if (selectValveMode) selectValveMode.value = formState.pneumaticValveMode || "doubleSolenoid";

    const selectControlMethod = document.getElementById("selectActuatorControlMethod");
    if (selectControlMethod) selectControlMethod.value = normalizeActuatorControlMethod(formState.controlMethod, formState.actuatorType);
    const inputAxisRef = document.getElementById("inputActuatorAxisRef");
    if (inputAxisRef) inputAxisRef.value = formState.axisRef;
    writeActuatorValveModelFormValues(formState.valveModel || null);
    syncMotorControlTypeField("selectActuatorType", "selectActuatorMotorControlType");
    syncPneumaticValveModeField("selectActuatorType", "selectActuatorPneumaticValveMode");
    syncActuatorValveModelFields();
    syncActuatorControlMethodDefault(false);

    const errBox = document.getElementById("controlSpecActuatorFormError");
    if (errBox && formState.hideError) errBox.style.display = "none";

    // 信号選択肢ドロップダウンの動的生成
    populateActionSignalOptions();

    // アクション一覧を描画
    renderControlSpecActionList();

    // 詳細カードを非表示にする
    const detailCard = document.getElementById("controlSpecActionDetailCard");
    if (detailCard && formState.hideDetailCard) detailCard.style.display = "none";
}

function resetActuatorForm() {
    cancelControlSpecActuatorAutoSave();
    if (ControlSpecControllerModule?.resetActuatorForm) {
        const nextState = ControlSpecControllerModule.resetActuatorForm(document, {
            defaultActions: createDefaultActuatorActions("pneumaticCylinder", ""),
            buildActuatorTypeOptions,
            normalizeAxisActuatorType,
            normalizeMotorControlType,
            normalizeActuatorControlMethod,
            syncMotorControlTypeField,
            syncPneumaticValveModeField,
            syncActuatorValveModelFields,
            syncActuatorControlMethodDefault,
            populateActionSignalOptions,
            renderControlSpecActionList: () => {}
        });
        if (!nextState) return;
        editingActuatorId = nextState.editingActuatorId;
        actuatorActions = nextState.actuatorActions;
        selectedActionIndex = nextState.selectedActionIndex;
        renderControlSpecActionList();
        return;
    }
    const formState = ControlSpecHelper.buildResetControlSpecActuatorFormState(
        createDefaultActuatorActions("pneumaticCylinder", "")
    );
    editingActuatorId = formState.editingActuatorId;
    actuatorActions = formState.actuatorActions;
    selectedActionIndex = formState.selectedActionIndex;

    const title = document.getElementById("controlSpecActuatorFormTitle");
    if (title) title.textContent = formState.titleText;

    const inputId = document.getElementById("inputActuatorId");
    if (inputId) {
        inputId.value = formState.inputId;
        inputId.disabled = formState.inputIdDisabled;
        inputId.style.background = formState.inputIdBackground;
    }

    const inputName = document.getElementById("inputActuatorName");
    if (inputName) inputName.value = formState.inputName;

    const selectType = document.getElementById("selectActuatorType");
    if (selectType) {
        selectType.innerHTML = buildActuatorTypeOptions(formState.actuatorType);
        selectType.value = formState.actuatorType;
    }

    const selectMotorType = document.getElementById("selectActuatorMotorControlType");
    if (selectMotorType) selectMotorType.value = formState.motorControlType;
    const selectValveMode = document.getElementById("selectActuatorPneumaticValveMode");
    if (selectValveMode) selectValveMode.value = formState.pneumaticValveMode;

    const selectControlMethod = document.getElementById("selectActuatorControlMethod");
    if (selectControlMethod) selectControlMethod.value = formState.controlMethod;
    const inputAxisRef = document.getElementById("inputActuatorAxisRef");
    if (inputAxisRef) inputAxisRef.value = formState.axisRef;
    writeActuatorValveModelFormValues(formState.valveModel || null);
    syncMotorControlTypeField("selectActuatorType", "selectActuatorMotorControlType");
    syncPneumaticValveModeField("selectActuatorType", "selectActuatorPneumaticValveMode");
    syncActuatorValveModelFields();
    syncActuatorControlMethodDefault(false);

    const errBox = document.getElementById("controlSpecActuatorFormError");
    if (errBox && formState.hideError) errBox.style.display = "none";

    const detailCard = document.getElementById("controlSpecActionDetailCard");
    if (detailCard && formState.hideDetailCard) detailCard.style.display = "none";

    renderControlSpecActionList();
}

function populateActionSignalOptions() {
    if (ControlSpecControllerModule?.populateActionSignalOptions) {
        return ControlSpecControllerModule.populateActionSignalOptions(document, {
            signals: state.project?.signals,
            escapeHtml
        });
    }
    const setOutputSelect = document.getElementById("selectActionSetOutput");
    const resetOutputSelect = document.getElementById("selectActionResetOutput");
    const doneSignalSelect = document.getElementById("selectActionDoneSignal");
    const faultSignalSelect = document.getElementById("selectActionFaultSignal");

    if (!setOutputSelect || !resetOutputSelect || !doneSignalSelect || !faultSignalSelect) return;

    const signals = Array.isArray(state.project?.signals) ? state.project.signals : [];
    const optionsState = ControlSpecHelper.buildControlSpecActionSignalOptionsState(signals, escapeHtml);
    setOutputSelect.innerHTML = optionsState.setOutputOptions;
    resetOutputSelect.innerHTML = optionsState.resetOutputOptions;
    doneSignalSelect.innerHTML = optionsState.doneSignalOptions;
    faultSignalSelect.innerHTML = optionsState.faultSignalOptions;
}

function syncActionCompletionModeFields() {
    if (ControlSpecControllerModule?.syncActionCompletionModeFields) {
        return ControlSpecControllerModule.syncActionCompletionModeFields(document);
    }
    const selectCompletionMode = document.getElementById("selectActionCompletionMode");
    const inputTimeout = document.getElementById("inputActionTimeoutSec");
    const selectDoneSig = document.getElementById("selectActionDoneSignal");
    const selectDoneVal = document.getElementById("selectActionDoneValue");
    if (!selectCompletionMode || !inputTimeout || !selectDoneSig || !selectDoneVal) return null;

    const fieldState = ControlSpecHelper.buildControlSpecActionCompletionModeFieldState(
        selectCompletionMode.value,
        inputTimeout.value
    );
    selectCompletionMode.value = fieldState.completionMode;
    inputTimeout.disabled = fieldState.timeoutDisabled;
    inputTimeout.style.background = fieldState.timeoutDisabled ? "#f3f4f6" : "#fff";
    inputTimeout.value = String(fieldState.timeoutValue ?? inputTimeout.value ?? "");
    selectDoneSig.disabled = fieldState.doneSignalDisabled;
    selectDoneSig.style.background = fieldState.doneSignalDisabled ? "#f3f4f6" : "#fff";
    selectDoneVal.disabled = fieldState.doneValueDisabled;
    selectDoneVal.style.background = fieldState.doneValueDisabled ? "#f3f4f6" : "#fff";
    return fieldState;
}

function renderControlSpecActionList() {
    if (ControlSpecControllerModule?.renderControlSpecActionList) {
        return ControlSpecControllerModule.renderControlSpecActionList(document, {
            actuatorActions,
            selectedActionIndex,
            escapeHtml,
            onEdit: loadActionToCard,
            onDelete: deleteActionFromList
        });
    }
    const listContainer = document.getElementById("controlSpecActionList");
    if (!listContainer) return;

    listContainer.innerHTML = "";
    const listState = ControlSpecHelper.buildControlSpecActionListState(actuatorActions);

    if (listState.isEmpty) {
        listContainer.innerHTML = `
            <div style="text-align: center; color: #888; font-size: 0.85em; padding: 10px; border: 1px dashed #ddd; border-radius: 4px; background: #fafafa;">
                ${escapeHtml(listState.emptyMessage)}
            </div>
        `;
        return;
    }

    listState.rows.forEach((action, index) => {
        const row = ControlSpecHelper.buildControlSpecActionListRowModel(action, index, selectedActionIndex);
        const div = document.createElement("div");
        div.className = `control-spec-action-item ${row.isActive ? "is-active" : ""}`;

        div.innerHTML = `
            <div class="ctrl-action-header">
                <span class="ctrl-action-title">${escapeHtml(row.idText)} (${escapeHtml(row.nameText)})</span>
                <div class="ctrl-action-btns">
                    <button type="button" class="control-spec-action-btn ctrl-action-btn-sm edit-action-btn" data-idx="${index}">編集</button>
                    <button type="button" class="control-spec-action-btn delete ctrl-action-btn-sm delete-action-btn" data-idx="${index}">削除</button>
                </div>
            </div>
            <div class="control-spec-action-meta ctrl-action-meta">
                <span>出力: <strong class="text-blue">${escapeHtml(row.setOutputText)}</strong></span>
                <span>リセット: <strong class="text-blue">${escapeHtml(row.resetOutputText)}</strong></span>
                <span>完了方式: <strong>${escapeHtml(row.completionModeText)}</strong></span>
                <span>完了条件: <strong class="text-green">${escapeHtml(row.doneConditionText)}</strong></span>
                <span>異常条件: <strong class="text-danger">${escapeHtml(row.faultConditionText)}</strong></span>
                <span>タイムアウト: <strong>${escapeHtml(row.timeoutText)}</strong></span>
                ${row.plcopenText ? `<span>FB: <strong class="text-purple">${escapeHtml(row.plcopenText)}</strong></span>` : ""}
            </div>
        `;

        div.querySelector(".edit-action-btn").addEventListener("click", (e) => {
            e.stopPropagation();
            loadActionToCard(index);
        });

        div.querySelector(".delete-action-btn").addEventListener("click", (e) => {
            e.stopPropagation();
            deleteActionFromList(index);
        });

        div.addEventListener("click", () => {
            loadActionToCard(index);
        });

        listContainer.appendChild(div);
    });
}

function loadActionToCard(index) {
    const action = actuatorActions[index];
    if (!action) return;
    if (ControlSpecControllerModule?.loadActionToCard) {
        selectedActionIndex = ControlSpecControllerModule.loadActionToCard(document, {
            action,
            index
        });
        renderControlSpecActionList();
        return;
    }
    const detailState = ControlSpecHelper.buildLoadedControlSpecActionDetailState(action, index);
    selectedActionIndex = detailState.selectedActionIndex;

    // 一覧の選択状態を更新するため再描画
    renderControlSpecActionList();

    const title = document.getElementById("controlSpecActionDetailTitle");
    if (title) title.textContent = detailState.titleText;

    const inputId = document.getElementById("inputActionId");
    if (inputId) {
        inputId.value = detailState.inputId;
        inputId.disabled = detailState.inputIdDisabled; // アクションIDは編集不可
        inputId.style.background = detailState.inputIdBackground;
    }

    const inputName = document.getElementById("inputActionName");
    if (inputName) inputName.value = detailState.inputName;

    const selectCompletionMode = document.getElementById("selectActionCompletionMode");
    if (selectCompletionMode) selectCompletionMode.value = detailState.completionMode;

    const inputTimeout = document.getElementById("inputActionTimeoutSec");
    if (inputTimeout) inputTimeout.value = detailState.timeoutSec;

    const selectPlcopenFamily = document.getElementById("selectActionPlcopenFamily");
    if (selectPlcopenFamily) selectPlcopenFamily.value = detailState.plcopenFamily;

    const inputPlcopenFbName = document.getElementById("inputActionPlcopenFbName");
    if (inputPlcopenFbName) inputPlcopenFbName.value = detailState.plcopenFbName;

    const inputExecuteSignal = document.getElementById("inputActionExecuteSignal");
    if (inputExecuteSignal) inputExecuteSignal.value = detailState.executeSignal;

    const inputDoneStatusSignal = document.getElementById("inputActionDoneStatusSignal");
    if (inputDoneStatusSignal) inputDoneStatusSignal.value = detailState.doneStatusSignal;

    const inputBusySignal = document.getElementById("inputActionBusySignal");
    if (inputBusySignal) inputBusySignal.value = detailState.busySignal;

    const inputErrorSignal = document.getElementById("inputActionErrorSignal");
    if (inputErrorSignal) inputErrorSignal.value = detailState.errorSignal;

    const inputErrorIdSignal = document.getElementById("inputActionErrorIdSignal");
    if (inputErrorIdSignal) inputErrorIdSignal.value = detailState.errorIdSignal;

    const inputPositionParam = document.getElementById("inputActionPositionParam");
    if (inputPositionParam) inputPositionParam.value = detailState.positionParam;

    const inputVelocityParam = document.getElementById("inputActionVelocityParam");
    if (inputVelocityParam) inputVelocityParam.value = detailState.velocityParam;

    const selectSetOutput = document.getElementById("selectActionSetOutput");
    if (selectSetOutput) selectSetOutput.value = detailState.setOutput;

    const selectResetOutput = document.getElementById("selectActionResetOutput");
    if (selectResetOutput) selectResetOutput.value = detailState.resetOutput;

    const selectDoneSig = document.getElementById("selectActionDoneSignal");
    const selectDoneVal = document.getElementById("selectActionDoneValue");
    if (selectDoneSig && selectDoneVal) {
        selectDoneSig.value = detailState.doneSignal;
        selectDoneVal.value = detailState.doneValue;
    }

    const selectFaultSig = document.getElementById("selectActionFaultSignal");
    const selectFaultVal = document.getElementById("selectActionFaultValue");
    if (selectFaultSig && selectFaultVal) {
        selectFaultSig.value = detailState.faultSignal;
        selectFaultVal.value = detailState.faultValue;
    }

    syncActionCompletionModeFields();

    const detailCard = document.getElementById("controlSpecActionDetailCard");
    if (detailCard && detailState.showDetailCard) detailCard.style.display = "flex";
}

function deleteActionFromList(index) {
    const action = actuatorActions[index];
    if (!action) return;
    if (ControlSpecControllerModule?.deleteActionFromList) {
        const nextState = ControlSpecControllerModule.deleteActionFromList(document, {
            index,
            action,
            selectedActionIndex,
            editingActuatorId,
            actuatorActions,
            findReference: isActuatorReferencedInFlow,
            confirmDelete: (message) => confirm(message)
        });
        if (!nextState?.ok) return;
        actuatorActions = nextState.actuatorActions;
        selectedActionIndex = nextState.selectedActionIndex;
        renderControlSpecActionList();
        return;
    }

    const check = editingActuatorId ? isActuatorReferencedInFlow(editingActuatorId, action.id) : { referenced: false };
    const deleteState = ControlSpecHelper.buildDeleteControlSpecActionState(
        index,
        action,
        selectedActionIndex,
        editingActuatorId,
        check,
        actuatorActions
    );
    if (!confirm(deleteState.confirmMessage)) return;

    actuatorActions = deleteState.nextActions;
    selectedActionIndex = deleteState.nextSelectedActionIndex;
    const detailCard = document.getElementById("controlSpecActionDetailCard");
    if (detailCard && deleteState.hideDetailCard) detailCard.style.display = "none";

    renderControlSpecActionList();
}

function saveActionDetail() {
    if (selectedActionIndex === null) return;
    if (ControlSpecControllerModule?.saveActionDetail) {
        const nextState = ControlSpecControllerModule.saveActionDetail(document, {
            selectedActionIndex,
            actuatorActions,
            normalizePlcopenActionMeta,
            normalizeActionParameters,
            normalizeActionStatus,
            alertMessage: (message) => alert(message)
        });
        if (!nextState?.ok) return;
        actuatorActions = nextState.actuatorActions;
        selectedActionIndex = nextState.selectedActionIndex;
        renderControlSpecActionList();
        return;
    }

    const inputName = document.getElementById("inputActionName");
    const inputTimeout = document.getElementById("inputActionTimeoutSec");
    const selectPlcopenFamily = document.getElementById("selectActionPlcopenFamily");
    const inputPlcopenFbName = document.getElementById("inputActionPlcopenFbName");
    const inputExecuteSignal = document.getElementById("inputActionExecuteSignal");
    const inputDoneStatusSignal = document.getElementById("inputActionDoneStatusSignal");
    const inputBusySignal = document.getElementById("inputActionBusySignal");
    const inputErrorSignal = document.getElementById("inputActionErrorSignal");
    const inputErrorIdSignal = document.getElementById("inputActionErrorIdSignal");
    const inputPositionParam = document.getElementById("inputActionPositionParam");
    const inputVelocityParam = document.getElementById("inputActionVelocityParam");
    const selectSetOutput = document.getElementById("selectActionSetOutput");
    const selectResetOutput = document.getElementById("selectActionResetOutput");
    const selectCompletionMode = document.getElementById("selectActionCompletionMode");
    const selectDoneSig = document.getElementById("selectActionDoneSignal");
    const selectDoneVal = document.getElementById("selectActionDoneValue");
    const selectFaultSig = document.getElementById("selectActionFaultSignal");
    const selectFaultVal = document.getElementById("selectActionFaultValue");

    if (!inputName || !inputTimeout || !selectSetOutput || !selectResetOutput || !selectCompletionMode || !selectDoneSig || !selectDoneVal || !selectFaultSig || !selectFaultVal) return;

    const formInput = ControlSpecHelper.buildControlSpecActionDetailFormInput({
        name: inputName.value,
        completionMode: selectCompletionMode.value,
        timeoutSec: inputTimeout.value,
        plcopen: normalizePlcopenActionMeta({
            family: selectPlcopenFamily?.value || "",
            fbName: inputPlcopenFbName?.value || ""
        }),
        executeSignal: inputExecuteSignal?.value || "",
        setOutputSig: selectSetOutput.value,
        resetOutputSig: selectResetOutput.value,
        parameters: normalizeActionParameters({
            position: inputPositionParam?.value || "",
            velocity: inputVelocityParam?.value || ""
        }),
        status: normalizeActionStatus({
            busySignal: inputBusySignal?.value || "",
            doneSignal: inputDoneStatusSignal?.value || "",
            errorSignal: inputErrorSignal?.value || "",
            errorIdSignal: inputErrorIdSignal?.value || ""
        }),
        doneSig: selectDoneSig.value,
        doneVal: selectDoneVal.value === "TRUE",
        faultSig: selectFaultSig.value,
        faultVal: selectFaultVal.value === "TRUE"
    });
    const draft = ControlSpecHelper.buildControlSpecActionDetailDraft(formInput);
    const validation = ControlSpecHelper.validateControlSpecActionDetailDraft(draft);
    if (!validation.ok) {
        alert(validation.errorMessage);
        return;
    }

    const nextState = ControlSpecHelper.buildSavedControlSpecActionDetailState(
        actuatorActions,
        selectedActionIndex,
        draft
    );
    actuatorActions = nextState.actions;
    selectedActionIndex = nextState.selectedActionIndex;
    const detailCard = document.getElementById("controlSpecActionDetailCard");
    if (detailCard && nextState.hideDetailCard) detailCard.style.display = "none";

    renderControlSpecActionList();
}

function saveControlSpecActuator(options = {}) {
    cancelControlSpecActuatorAutoSave();
    const keepEditing = options.keepEditing === true;
    const silentValidation = options.silentValidation === true;
    const inputId = document.getElementById("inputActuatorId");
    const inputName = document.getElementById("inputActuatorName");
    const selectType = document.getElementById("selectActuatorType");
    const selectMotorType = document.getElementById("selectActuatorMotorControlType");
    const selectValveMode = document.getElementById("selectActuatorPneumaticValveMode");
    const selectControlMethod = document.getElementById("selectActuatorControlMethod");
    const inputAxisRef = document.getElementById("inputActuatorAxisRef");
    const errBox = document.getElementById("controlSpecActuatorFormError");

    if (!inputId || !inputName || !selectType || !selectMotorType || !selectValveMode || !selectControlMethod) return;
    if (ControlSpecControllerModule?.saveControlSpecActuator) {
        const actuatorType = selectType.value;
        const motorControlType = normalizeMotorControlType(selectMotorType.value, actuatorType);
        const controlMethod = normalizeActuatorControlMethod(selectControlMethod.value, actuatorType);
        const draftInput = ControlSpecHelper.buildControlSpecActuatorFormInput(
            {
                typedId: inputId.value,
                name: inputName.value,
                actuatorType,
                motorControlType,
                pneumaticValveMode: selectValveMode.value,
                controlMethod,
                axisRef: inputAxisRef ? inputAxisRef.value : "",
                ...buildActuatorValveModelFormValues(),
                actions: actuatorActions
            },
            "",
            state.project?.actuators,
            editingActuatorId
        );
        const id = draftInput.typedId || makeUniqueActuatorId(`${(actuatorType || "act").replace(/[^A-Za-z0-9_]+/g, "")}_${ControlSpecHelper.buildControlSpecSlug(draftInput.name || "actuator", "actuator")}`);
        return ControlSpecControllerModule.saveControlSpecActuator({
            project: state.project,
            editingActuatorId,
            formValues: {
                typedId: draftInput.typedId,
                name: draftInput.name,
                actuatorType: draftInput.actuatorType,
                motorControlType: draftInput.motorControlType,
                pneumaticValveMode: draftInput.pneumaticValveMode,
                valveModel: draftInput.valveModel,
                controlMethod: draftInput.controlMethod,
                axisRef: draftInput.axisRef,
                actions: draftInput.actions
            },
            fallbackId: id,
            showError: showActuatorFormError,
            clearError: clearActuatorFormError,
            recomputeAll,
            renderActuators: renderControlSpecActuators,
            renderView: renderControlSpecView,
            refreshFlowNodes: () => updateAllFlowNodeLabels(),
            scheduleAutoSave,
            loadForm: loadActuatorToForm,
            keepEditing,
            silentValidation,
            resetForm: resetActuatorForm
        });
    }

    const actuatorType = selectType.value;
    const motorControlType = normalizeMotorControlType(selectMotorType.value, actuatorType);
    const controlMethod = normalizeActuatorControlMethod(selectControlMethod.value, actuatorType);
    const draftInput = ControlSpecHelper.buildControlSpecActuatorFormInput(
        {
            typedId: inputId.value,
            name: inputName.value,
            actuatorType,
            motorControlType,
            pneumaticValveMode: selectValveMode.value,
            controlMethod,
            axisRef: inputAxisRef ? inputAxisRef.value : "",
            ...buildActuatorValveModelFormValues(),
            actions: actuatorActions
        },
        "",
        state.project?.actuators,
        editingActuatorId
    );
    const id = draftInput.typedId || makeUniqueActuatorId(`${(actuatorType || "act").replace(/[^A-Za-z0-9_]+/g, "")}_${ControlSpecHelper.buildControlSpecSlug(draftInput.name || "actuator", "actuator")}`);
    const draft = ControlSpecHelper.buildControlSpecActuatorDraft(draftInput, id);

    if (!Array.isArray(state.project.actuators)) {
        state.project.actuators = [];
    }
    const validation = ControlSpecHelper.validateControlSpecActuatorDraft(
        draft,
        editingActuatorId,
        state.project.actuators
    );
    if (!validation.ok) {
        if (!silentValidation) showActuatorFormError(validation.errorMessage);
        return { ok: false, errorMessage: validation.errorMessage };
    }
    state.project.actuators = ControlSpecHelper.buildSavedControlSpecActuators(
        state.project.actuators,
        draft,
        validation
    );

    // 局所的再計算・更新
    recomputeAll();
    renderControlSpecActuators();
    renderControlSpecView();
    updateAllFlowNodeLabels();
    scheduleAutoSave();
    clearActuatorFormError();
    if (keepEditing) {
        loadActuatorToForm(draft.id);
    } else {
        resetActuatorForm();
    }
    return { ok: true, savedActuatorId: draft.id };
}

function showActuatorFormError(msg) {
    const errBox = document.getElementById("controlSpecActuatorFormError");
    if (errBox) {
        errBox.textContent = msg;
        errBox.style.display = "block";
    }
}

function clearActuatorFormError() {
    const errBox = document.getElementById("controlSpecActuatorFormError");
    if (errBox) {
        errBox.textContent = "";
        errBox.style.display = "none";
    }
}

let controlSpecActuatorAutoSaveTimer = null;

function cancelControlSpecActuatorAutoSave() {
    if (controlSpecActuatorAutoSaveTimer !== null) {
        clearTimeout(controlSpecActuatorAutoSaveTimer);
        controlSpecActuatorAutoSaveTimer = null;
    }
}

function scheduleControlSpecActuatorAutoSave(delayMs = 300) {
    cancelControlSpecActuatorAutoSave();
    controlSpecActuatorAutoSaveTimer = setTimeout(() => {
        controlSpecActuatorAutoSaveTimer = null;
        saveControlSpecActuator({ keepEditing: true, silentValidation: true });
    }, Math.max(0, Number(delayMs) || 0));
}

function bindControlSpecActuatorAutoSaveFields() {
    const textFieldIds = [
        "inputActuatorId",
        "inputActuatorName",
        "inputActuatorAxisRef",
        "inputActuatorValvePositiveOutput",
        "inputActuatorValveNegativeOutput",
        "inputActuatorValveSingleOutput"
    ];
    const selectFieldIds = [
        "selectActuatorType",
        "selectActuatorMotorControlType",
        "selectActuatorPneumaticValveMode",
        "selectActuatorValvePreset",
        "selectActuatorValveSingleActiveDirection",
        "selectActuatorValveStateExtendPositive",
        "selectActuatorValveStateExtendNegative",
        "selectActuatorValveStateExtendSingle",
        "selectActuatorValveStateRetractPositive",
        "selectActuatorValveStateRetractNegative",
        "selectActuatorValveStateRetractSingle",
        "selectActuatorValveStateStopPositive",
        "selectActuatorValveStateStopNegative",
        "selectActuatorValveStateStopSingle",
        "selectActuatorValveStateEmergencyPositive",
        "selectActuatorValveStateEmergencyNegative",
        "selectActuatorValveStateEmergencySingle",
        "selectActuatorControlMethod"
    ];
    textFieldIds.forEach((id) => {
        document.getElementById(id)?.addEventListener("input", () => {
            scheduleControlSpecActuatorAutoSave(350);
        });
        document.getElementById(id)?.addEventListener("change", () => {
            scheduleControlSpecActuatorAutoSave(0);
        });
    });
    selectFieldIds.forEach((id) => {
        document.getElementById(id)?.addEventListener("change", () => {
            scheduleControlSpecActuatorAutoSave(0);
        });
    });
}

function isActuatorReferencedInFlow(actuatorId, actionId = null) {
    const snapshot = getFlowExportSnapshot();
    const modules = snapshot?.drawflow ? Object.keys(snapshot.drawflow) : ["Home"];
    const moduleEntries = [];
    for (const mod of modules) {
        const modData = getFlowModuleData(snapshot, mod);
        const nodes = Object.values(modData || {});
        moduleEntries.push({
            moduleName: mod,
            nodes: nodes.map((node) => {
                const data = getNodeData(node);
                return {
                    nodeId: node.id,
                    nodeLabel: data?.label || node.name || `Node ${node.id}`,
                    actuatorId: data?.actuatorId,
                    actionId: data?.actionId
                };
            })
        });
    }
    return ControlSpecHelper.findControlSpecActuatorReferenceInFlow(
        actuatorId,
        actionId,
        moduleEntries
    );
}

function deleteControlSpecActuator(actuatorId) {
    if (!Array.isArray(state.project?.actuators)) return;
    if (ControlSpecControllerModule?.deleteControlSpecActuator) {
        return ControlSpecControllerModule.deleteControlSpecActuator({
            project: state.project,
            actuatorId,
            findReference: isActuatorReferencedInFlow,
            confirmDelete: (message) => confirm(message),
            editingActuatorId,
            resetForm: resetActuatorForm,
            recomputeAll,
            renderActuators: renderControlSpecActuators,
            renderView: renderControlSpecView,
            scheduleAutoSave
        });
    }

    // フロー内の動作ノード参照チェック
    const check = isActuatorReferencedInFlow(actuatorId, null);
    const deleteState = ControlSpecHelper.buildDeleteControlSpecActuatorState(
        actuatorId,
        check,
        state.project.actuators
    );

    if (!confirm(deleteState.confirmMessage)) return;

    state.project.actuators = deleteState.nextActuators;

    if (editingActuatorId === actuatorId) {
        resetActuatorForm();
    }

    recomputeAll();
    renderControlSpecActuators();
    renderControlSpecView();
    scheduleAutoSave();
}

function bindControlSpecTabs() {
    document.querySelectorAll(".app-header .control-spec-tab-btn").forEach(button => {
        button.addEventListener("click", () => {
            setControlSpecTab(button.dataset.tab);
        });
    });
}

function setControlSpecTab(tabId) {
    if (!tabId) return;
    document.querySelectorAll(".app-header .control-spec-tab-btn").forEach(btn => {
        const active = btn.dataset.tab === tabId;
        btn.classList.toggle("is-active", active);
        btn.setAttribute("aria-selected", String(active));
    });

    document.querySelectorAll("#view-control-spec .control-spec-tabpanel").forEach(panel => {
        const active = panel.id === `control-spec-tabpanel-${tabId}`;
        panel.classList.toggle("is-active", active);
        panel.setAttribute("aria-hidden", String(!active));
        panel.style.display = active ? "flex" : "none";
    });
}

function bindWorkspaceUi() {
    document.querySelectorAll(".sidebar-link[data-view]").forEach(button => {
        button.addEventListener("click", () => setActiveView(button.dataset.view || "timing"));
    });
    document.getElementById("sidebarLogo")?.addEventListener("click", () => {
        if (state.ui.sidebarCollapsed) setSidebarCollapsed(false);
    });
    document.getElementById("btnSidebarToggle")?.addEventListener("click", () => {
        setSidebarCollapsed(!state.ui.sidebarCollapsed);
    });
    bindControlSpecTabs();
    refreshActuatorTypeSelectOptions();
    renderSettingsView();
    
    // 信号辞書UI関連のイベントバインド
    document.getElementById("btnControlSpecAddSignal")?.addEventListener("click", () => {
        resetSignalForm();
        // フォームパネルのタイトルを追加に戻す
        const title = document.getElementById("controlSpecSignalFormTitle");
        if (title) title.textContent = "信号を追加";
    });
    
    document.getElementById("btnControlSpecCancelSignal")?.addEventListener("click", () => {
        resetSignalForm();
    });
    
    document.getElementById("btnControlSpecSaveSignal")?.addEventListener("click", () => {
        saveControlSpecSignal();
    });
    document.getElementById("btnControlSpecApplyFlowCandidate")?.addEventListener("click", () => {
        applyControlSpecFlowSignalCandidate();
    });
    document.getElementById("selectSignalCategory")?.addEventListener("change", () => {
        applySelectedSignalCategoryDefaults();
    });
    document.getElementById("btnControlSpecAutoAssignAddress")?.addEventListener("click", () => {
        autoAssignSignalAddress();
    });
    document.getElementById("btnControlSpecAddSignalCategory")?.addEventListener("click", () => {
        resetSignalCategoryForm();
    });
    document.getElementById("btnControlSpecCancelSignalCategory")?.addEventListener("click", () => {
        resetSignalCategoryForm();
    });
    document.getElementById("btnControlSpecSaveSignalCategory")?.addEventListener("click", () => {
        saveControlSpecSignalCategory();
    });
    
    // アクチュエータUI関連のイベントバインド
    document.getElementById("btnControlSpecAddActuator")?.addEventListener("click", () => {
        resetActuatorForm();
    });

    document.getElementById("btnControlSpecAddActuatorTypeCatalog")?.addEventListener("click", () => {
        resetActuatorTypeCatalogForm(null);
    });

    document.getElementById("selectActuatorTypeCatalogEntry")?.addEventListener("change", (e) => {
        resetActuatorTypeCatalogForm(e.target.value || null);
    });

    document.getElementById("btnControlSpecSaveActuatorTypeCatalog")?.addEventListener("click", () => {
        saveActuatorTypeCatalogEntry();
    });

    document.getElementById("btnControlSpecDeleteActuatorTypeCatalog")?.addEventListener("click", () => {
        deleteActuatorTypeCatalogEntry();
    });

    document.getElementById("btnControlSpecCancelActuator")?.addEventListener("click", () => {
        resetActuatorForm();
    });

    document.getElementById("btnControlSpecSaveActuator")?.addEventListener("click", () => {
        saveControlSpecActuator();
    });
    bindControlSpecActuatorAutoSaveFields();

    document.getElementById("selectActuatorType")?.addEventListener("change", (e) => {
        syncMotorControlTypeField("selectActuatorType", "selectActuatorMotorControlType");
        syncPneumaticValveModeField("selectActuatorType", "selectActuatorPneumaticValveMode");
        syncActuatorValveModelFields();
        syncActuatorControlMethodDefault(true);
        if (ControlSpecControllerModule?.resetActuatorActionsForTypeChange) {
            const motorControlType = document.getElementById("selectActuatorMotorControlType")?.value || "";
            const controlMethod = document.getElementById("selectActuatorControlMethod")?.value || "";
            const nextState = ControlSpecControllerModule.resetActuatorActionsForTypeChange(document, {
                editingActuatorId,
                actuatorType: e.target.value || "pneumaticCylinder",
                motorControlType,
                controlMethod,
                mergeActuatorActionsWithDefaults
            });
            if (!nextState) return;
            actuatorActions = nextState.actuatorActions;
            selectedActionIndex = nextState.selectedActionIndex;
            renderControlSpecActionList();
            return;
        }
        if (editingActuatorId !== null) return;
        const motorControlType = document.getElementById("selectActuatorMotorControlType")?.value || "";
        const controlMethod = document.getElementById("selectActuatorControlMethod")?.value || "";
        const nextState = ControlSpecHelper.buildResetControlSpecActuatorActionState(
            mergeActuatorActionsWithDefaults([], e.target.value || "pneumaticCylinder", "", "", motorControlType, controlMethod)
        );
        actuatorActions = nextState.actuatorActions;
        selectedActionIndex = nextState.selectedActionIndex;
        const detailCard = document.getElementById("controlSpecActionDetailCard");
        if (detailCard && nextState.hideDetailCard) detailCard.style.display = "none";
        renderControlSpecActionList();
    });

    document.getElementById("selectActuatorControlMethod")?.addEventListener("change", () => {
        syncActuatorControlMethodDefault(false);
    });

    document.getElementById("selectActuatorPneumaticValveMode")?.addEventListener("change", () => {
        syncPneumaticValveModeField("selectActuatorType", "selectActuatorPneumaticValveMode");
        syncActuatorValveModelFields();
    });

    document.getElementById("selectActuatorValvePreset")?.addEventListener("change", (event) => {
        if (!event.target?.value) return;
        applyActuatorValvePreset();
    });

    document.getElementById("selectActuatorMotorControlType")?.addEventListener("change", (e) => {
        if (ControlSpecControllerModule?.resetActuatorActionsForMotorControlTypeChange) {
            const actuatorType = document.getElementById("selectActuatorType")?.value || "pneumaticCylinder";
            const controlMethod = document.getElementById("selectActuatorControlMethod")?.value || "";
            const nextState = ControlSpecControllerModule.resetActuatorActionsForMotorControlTypeChange(document, {
                editingActuatorId,
                actuatorType,
                motorControlType: e.target.value || "",
                controlMethod,
                mergeActuatorActionsWithDefaults
            });
            if (!nextState) return;
            actuatorActions = nextState.actuatorActions;
            selectedActionIndex = nextState.selectedActionIndex;
            renderControlSpecActionList();
            return;
        }
        if (editingActuatorId !== null) return;
        const actuatorType = document.getElementById("selectActuatorType")?.value || "pneumaticCylinder";
        const controlMethod = document.getElementById("selectActuatorControlMethod")?.value || "";
        const nextState = ControlSpecHelper.buildResetControlSpecActuatorActionState(
            mergeActuatorActionsWithDefaults([], actuatorType, "", "", e.target.value || "", controlMethod)
        );
        actuatorActions = nextState.actuatorActions;
        selectedActionIndex = nextState.selectedActionIndex;
        const detailCard = document.getElementById("controlSpecActionDetailCard");
        if (detailCard && nextState.hideDetailCard) detailCard.style.display = "none";
        renderControlSpecActionList();
    });

    document.getElementById("btnControlSpecAddAction")?.addEventListener("click", () => {
        if (ControlSpecControllerModule?.addControlSpecAction) {
            const nextState = ControlSpecControllerModule.addControlSpecAction({
                actuatorActions,
                createEmptyActuatorAction,
                getNextActionPresetName
            });
            actuatorActions = nextState.actuatorActions;
            renderControlSpecActionList();
            loadActionToCard(nextState.selectedActionIndex);
            return;
        }
        const nextState = ControlSpecHelper.buildAddedControlSpecActionState(
            actuatorActions,
            createEmptyActuatorAction(getNextActionPresetName())
        );
        actuatorActions = nextState.actuatorActions;
        renderControlSpecActionList();
        loadActionToCard(nextState.selectedActionIndex);
    });

    document.getElementById("btnControlSpecSaveActionDetail")?.addEventListener("click", () => {
        saveActionDetail();
    });
    document.getElementById("selectActionCompletionMode")?.addEventListener("change", () => {
        syncActionCompletionModeFields();
    });

    document.getElementById("btnControlSpecExportIoCsv")?.addEventListener("click", () => {
        exportControlSpecIoTable("csv");
    });

    document.getElementById("btnControlSpecExportIoJson")?.addEventListener("click", () => {
        exportControlSpecIoTable("json");
    });

    document.getElementById("controlSpecFindingsTableBody")?.addEventListener("click", (event) => {
        const button = event.target?.closest?.("[data-finding-target]");
        if (!button) return;
        openControlSpecFindingTarget(button);
    });

    syncMotorControlTypeField("selectActuatorType", "selectActuatorMotorControlType");
    syncPneumaticValveModeField("selectActuatorType", "selectActuatorPneumaticValveMode");
    syncActuatorValveModelFields();
    syncActuatorControlMethodDefault(false);
}

function loadAssistantWidth() {
    try {
        const savedWidth = localStorage.getItem("timingChartAssistantWidth");
        if (savedWidth) {
            const width = parseInt(savedWidth);
            if (width >= 280 && width <= window.innerWidth * 0.6) {
                document.documentElement.style.setProperty('--assistant-width', `${width}px`);
            }
        }
    } catch (e) {
        console.warn("Failed to load assistant width", e);
    }
}

function bindAssistantResize() {
    const handle = document.querySelector(".assistant-resize-handle");
    const sidebar = document.querySelector(".assistant-sidebar");
    if (!handle || !sidebar) return;

    let isDragging = false;
    let startX = 0;
    let startWidth = 0;

    handle.addEventListener("mousedown", (e) => {
        if (e.button !== 0) return;
        isDragging = true;
        startX = e.clientX;
        startWidth = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--assistant-width')) || 360;
        
        handle.classList.add("is-dragging");
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        
        e.preventDefault();
    });

    document.addEventListener("mousemove", (e) => {
        if (!isDragging) return;
        
        const dx = startX - e.clientX;
        let newWidth = startWidth + dx;
        
        const minWidth = 280;
        const maxWidth = window.innerWidth * 0.6;
        if (newWidth < minWidth) newWidth = minWidth;
        if (newWidth > maxWidth) newWidth = maxWidth;
        
        document.documentElement.style.setProperty('--assistant-width', `${newWidth}px`);
        
        if (typeof syncFlowEditorHeight === "function") {
            syncFlowEditorHeight();
        }
    });

    document.addEventListener("mouseup", () => {
        if (!isDragging) return;
        isDragging = false;
        handle.classList.remove("is-dragging");
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        
        try {
            const currentWidth = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--assistant-width')) || 360;
            localStorage.setItem("timingChartAssistantWidth", currentWidth);
        } catch (e) {
            console.warn("Failed to save assistant width", e);
        }
    });
}

function getNextAxisDefaultName() {
    const idx = state.project.axes.length + 1;
    return "軸" + idx;
}

function getDefaultActuatorTypeCatalogSeed() {
    return [
        { id: "pneumaticCylinder", label: "空圧シリンダ", defaultActionNames: ["前進", "後退", "停止"] },
        { id: "hydraulicCylinder", label: "油圧シリンダ", defaultActionNames: ["原点復帰", "位置決め", "停止"] },
        { id: "electricCylinder", label: "電動シリンダ", defaultActionNames: ["前進", "後退", "停止", "リセット"] },
        { id: "motor", label: "モータ", defaultActionNames: ["正転", "逆転", "停止", "原点復帰", "位置決め"] },
        { id: "chuck", label: "チャック", defaultActionNames: ["チャック", "アンチャック"] },
        { id: "vacuum", label: "バキューム", defaultActionNames: ["吸着", "吸着破壊"] },
        { id: "heater", label: "ヒータ", defaultActionNames: ["ON", "OFF"] },
        { id: "generic", label: "汎用I/O", defaultActionNames: ["ON", "OFF"] }
    ];
}

const DEFAULT_ACTUATOR_TYPE_CATALOG = Object.freeze(getDefaultActuatorTypeCatalogSeed());

function normalizeAxisActuatorType(value) {
    return ControlSpecHelper.normalizeAxisActuatorType(value);
}
function normalizeMotorControlType(value, actuatorType = "") {
    return ControlSpecHelper.normalizeMotorControlType(value, actuatorType);
}
function normalizeActuatorControlMethod(value, actuatorType = "") {
    return ControlSpecHelper.normalizeActuatorControlMethod(value, actuatorType);
}
function cloneDefaultActuatorTypeCatalog() {
    return getDefaultActuatorTypeCatalogSeed().map((entry) => ({
        id: entry.id,
        label: entry.label,
        defaultActionNames: entry.defaultActionNames.slice()
    }));
}
function normalizeActuatorTypeCatalogEntry(entry, index = 0) {
    return ControlSpecHelper.normalizeActuatorTypeCatalogEntry(entry, index);
}

function ensureActuatorTypeCatalog(project = state.project) {
    if (!project || typeof project !== "object") return cloneDefaultActuatorTypeCatalog();
    if (!Array.isArray(project.actuatorTypeCatalog) || !project.actuatorTypeCatalog.length) {
        project.actuatorTypeCatalog = cloneDefaultActuatorTypeCatalog();
    } else {
        const seen = new Set();
        project.actuatorTypeCatalog = project.actuatorTypeCatalog
            .map((entry, index) => normalizeActuatorTypeCatalogEntry(entry, index))
            .filter((entry) => {
                const key = entry.id.toLowerCase();
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
    }
    return project.actuatorTypeCatalog;
}

function getActuatorTypeCatalogEntry(typeId, project = state.project) {
    const normalizedId = String(typeId || "").trim();
    return ensureActuatorTypeCatalog(project).find((entry) => entry.id === normalizedId) || null;
}

function getSelectValue(id, fallback = "pneumaticCylinder") {
    return document.getElementById(id)?.value || fallback;
}

function renderAxisAddPopover() {
    const popover = document.getElementById("axisAddPopover");
    const button = document.getElementById("btnAddAxis");
    const input = document.getElementById("axisAddNameInput");
    if (popover) popover.classList.toggle("is-open", state.ui.axisPopoverOpen);
    if (button) button.setAttribute("aria-expanded", String(state.ui.axisPopoverOpen));
    if (state.ui.axisPopoverOpen && input) {
        if (!input.value) input.value = getNextAxisDefaultName();
        setTimeout(() => {
            input.focus();
            input.select();
        }, 0);
    }
}

function setAxisAddPopoverOpen(open) {
    state.ui.axisPopoverOpen = Boolean(open);
    if (!open) {
        const input = document.getElementById("axisAddNameInput");
        if (input) input.value = "";
        const typeSelect = document.getElementById("axisAddActuatorTypeSelect");
        if (typeSelect) typeSelect.value = "pneumaticCylinder";
        const motorSelect = document.getElementById("axisAddMotorControlTypeSelect");
        if (motorSelect) motorSelect.value = "speedControl";
    }
    renderAxisAddPopover();
}

function submitAxisFromPopover(name) {
    const axisName = String(name || "").trim();
    addAxis(axisName || null, {
        actuatorType: getSelectValue("axisAddActuatorTypeSelect"),
        motorControlType: getSelectValue("axisAddMotorControlTypeSelect", "speedControl")
    });
    setAxisAddPopoverOpen(false);
}

let flowAxisPopoverOpen = false;

function bindFlowAxisSelector() {
    if (FlowControllerModule.bindFlowAxisSelector) {
        return FlowControllerModule.bindFlowAxisSelector({
            doc: document,
            state,
            getFlowAxisPopoverOpen: () => flowAxisPopoverOpen,
            setFlowAxisPopoverOpenState: (open) => { flowAxisPopoverOpen = open; },
            addAxis,
            getSelectValue,
            getNextAxisDefaultName,
            removeAxis,
            setTimeoutFn: (fn, delay) => setTimeout(fn, delay)
        });
    }
}

function bindAxisAddPopover() {
    const button = document.getElementById("btnAddAxis");
    const input = document.getElementById("axisAddNameInput");
    button?.addEventListener("click", (e) => {
        e.stopPropagation();
        setAxisAddPopoverOpen(!state.ui.axisPopoverOpen);
    });
    document.getElementById("btnAxisAddCancel")?.addEventListener("click", () => {
        setAxisAddPopoverOpen(false);
    });
    document.getElementById("btnAxisAddConfirm")?.addEventListener("click", () => {
        submitAxisFromPopover(input?.value || "");
    });
    document.querySelectorAll("[data-axis-preset]").forEach(buttonEl => {
        buttonEl.addEventListener("click", () => {
            submitAxisFromPopover(buttonEl.getAttribute("data-axis-preset") || "");
        });
    });
    input?.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            submitAxisFromPopover(input.value || "");
        } else if (e.key === "Escape") {
            setAxisAddPopoverOpen(false);
        }
    });
    document.addEventListener("click", (e) => {
        if (!state.ui.axisPopoverOpen) return;
        const popover = document.getElementById("axisAddPopover");
        const trigger = document.getElementById("btnAddAxis");
        if (popover?.contains(e.target) || trigger?.contains(e.target)) return;
        setAxisAddPopoverOpen(false);
    });
    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && state.ui.axisPopoverOpen) {
            setAxisAddPopoverOpen(false);
        }
    });
}

function ensureProjectPlcProfiles(project = state.project) {
    return PlcProfileModule.ensureProjectPlcProfiles(project);
}

function getActivePlcProfile(profileId = state.chat?.plcProfileId || state.chat?.dialect, project = state.project) {
    return PlcProfileModule.getActivePlcProfile(profileId, project);
}

function getPlcProfileOptions() {
    return buildPlcProfileOptions(ensureProjectPlcProfiles());
}

function buildPlcProfileRuleLines(profile = {}) {
    return PlcProfileModule.buildPlcProfileRuleLines(profile);
}

function buildPlcProfileEditorHints(profile = {}) {
    return PlcProfileModule.buildPlcProfileEditorHints(profile);
}

function buildPlcProfileSummary(profile = {}) {
    return PlcProfileModule.buildPlcProfileSummary(profile);
}

function buildPlcProfileBaseLabel(profile = {}) {
    return PlcProfileModule.buildPlcProfileBaseLabel(profile, ensureProjectPlcProfiles());
}

function formatPlcProfileFieldLabel(path = "") {
    return PlcProfileModule.formatPlcProfileFieldLabel(path);
}

function collectActivePlcProfileWarnings(profile = {}) {
    return PlcProfileModule.collectActivePlcProfileWarnings(profile, controlSpecServerValidationState.findings);
}

function syncChatDialectFromProfile() {
    const profile = getActivePlcProfile();
    return PlcProfileModule.syncChatDialectFromProfile(state.chat, state.project, profile);
}

function getDialectMeta(dialect) {
    return PlcProfileModule.resolveDialectMeta(dialect, state.chat, state.project);
}

function buildInstructionWithPlcProfile(userInstruction = "", profile = getActivePlcProfile()) {
    return PlcProfileModule.buildInstructionWithPlcProfile(userInstruction, profile);
}

let _idCounter = 0;
function genId(prefix) {
    const external = window.__appStateGenId;
    if (typeof external === "function" && external !== genId) {
        return external(prefix);
    }
    _idCounter += 1;
    return prefix + "-" + Date.now().toString(36) + "-" + _idCounter;
}

function makeDefaultMotion(idx, last) {
    return {
        id: genId("motion"),
        name: "動作" + idx,
        distance: 100,
        accel: 1,
        decel: 1,
        maxSpeed: 500,
        waitTime: 0,
        dependency: last ? { sourceMotionId: last.id, anchor: "end", offsetSec: 0 } : null,
        absoluteStartSec: last ? null : 0,
        startConditionText: "",
        comment: ""
    };
}

function makeStartNodeData() {
    return FlowNodeHtmlModule.makeStartNodeData();
}

function makeButtonNodeData() {
    return FlowNodeHtmlModule.makeButtonNodeData();
}

function normalizeButtonMode(value) {
    return FlowNodeHtmlModule.normalizeButtonMode(value);
}


function ensureButtonNodeData(data = {}) {
    data.nodeType = "button";
    data.label = String(data.label || "押しボタン").trim() || "押しボタン";
    data.signalName = String(data.signalName || "X_PB1").trim() || "X_PB1";
    data.mode = normalizeButtonMode(data.mode);
    if (!data.trueLabel) data.trueLabel = "ON";
    if (!data.falseLabel) data.falseLabel = "OFF";
    return data;
}

function makeConditionNodeData() {
    return FlowNodeHtmlModule.makeConditionNodeData();
}

function normalizeConditionEntry(entry = {}, index = 0, fallbackName = "条件") {
    return FlowNodeHtmlModule.normalizeConditionEntry(entry, index, fallbackName);
}

function getConditionMode(data = {}) {
    return FlowNodeHtmlModule.getConditionMode(data);
}

function getConditionOperator(data = {}) {
    return FlowNodeHtmlModule.getConditionOperator(data);
}

function normalizeConditionEntries(entries = [], fallbackName = "条件", mode = "single") {
    return FlowNodeHtmlModule.normalizeConditionEntries(entries, fallbackName, mode);
}

function ensureConditionNodeData(data = {}) {
    return FlowNodeHtmlModule.ensureConditionNodeData(data);
}

function getConditionKind(data = {}) {
    return FlowNodeHtmlModule.getConditionKind(data);
}


function buildConditionNodeMeta(data = {}) {
    return FlowNodeHtmlModule.buildConditionNodeMeta(data);
}

function makeErrorNodeData() {
    return FlowNodeHtmlModule.makeErrorNodeData();
}

function makeSignalOutputNodeData() {
    return FlowNodeHtmlModule.makeSignalOutputNodeData();
}

function makeWaitNodeData() {
    return FlowNodeHtmlModule.makeWaitNodeData();
}

function normalizeSignalScope(value) {
    return FlowNodeHtmlModule.normalizeSignalScope(value);
}

function ensureSignalOutputData(data = {}) {
    return FlowNodeHtmlModule.ensureSignalOutputData(data);
}

function makeSubprocessNodeData(meta = null) {
    if (FlowControllerModule.makeSubprocessNodeData) {
        return FlowControllerModule.makeSubprocessNodeData({
            meta,
            ensureSubprocessMeta,
            makeUniqueSubprocessName,
            timingModeExpand: SUBPROCESS_TIMING_EXPAND
        });
    }
}

function makeEndNodeData() {
    return FlowNodeHtmlModule.makeEndNodeData();
}

function getNodeData(node) {
    return FlowNodeHtmlModule.getNodeData(node);
}

function getNodeTypeFromData(data) {
    return FlowNodeHtmlModule.getNodeTypeFromData(data);
}

function getFlowNodeLabelFromData(data, node = null) {
    return FlowNodeHtmlModule.getFlowNodeLabelFromData(data, node, { findMotion });
}

function buildSubprocessPreviewState(subprocessId) {
    return SubprocessPreviewModule.buildSubprocessPreviewState({
        moduleName: findSubprocessById(subprocessId)?.moduleName || getSubprocessModuleName(subprocessId),
        moduleData: getFlowModuleData(getFlowExportSnapshot(), findSubprocessById(subprocessId)?.moduleName || getSubprocessModuleName(subprocessId)),
        getNodeData,
        getNodeTypeFromData,
        resolveNodeLabel: getFlowNodeLabelFromData
    });
}


function buildFlowNodeHtml(data, axis, motion) {
    return FlowNodeHtmlModule.buildFlowNodeHtml(data, axis, motion, {
        subprocessList: getSubprocessList(state.project),
        subprocessPreview: getNodeTypeFromData(data) === "subprocess" ? buildSubprocessPreviewState(data.subprocessId) : null,
        motionTimes: state.computed?.motionTimes ?? {},
        actuators: Array.isArray(state.project?.actuators) ? state.project.actuators : []
    });
}

function isFlowNodeActionTarget(target) {
    if (FlowControllerModule.isFlowNodeActionTarget) {
        return FlowControllerModule.isFlowNodeActionTarget({ target });
    }
}

function toggleSubprocessNodeCollapse(nodeId) {
    if (FlowControllerModule.toggleSubprocessNodeCollapse) {
        return FlowControllerModule.toggleSubprocessNodeCollapse({
            nodeId,
            getFlowNodeById,
            getNodeData,
            getNodeTypeFromData,
            refreshFlowNodeHtml,
            selectFlowNode
        });
    }
}

function handleFlowNodeAction(buttonEl, event) {
    if (FlowControllerModule.handleFlowNodeAction) {
        return FlowControllerModule.handleFlowNodeAction({
            buttonEl,
            event,
            toggleSubprocessNodeCollapse
        });
    }
}

function bindFlowNodeSelectionTargets(root = document) {
    if (FlowControllerModule.bindFlowNodeSelectionTargets) {
        return FlowControllerModule.bindFlowNodeSelectionTargets({
            root,
            isFlowNodeActionTarget,
            selectFlowNodeByElement
        });
    }
}

function bindFlowNodeActions(root = document) {
    if (FlowControllerModule.bindFlowNodeActions) {
        return FlowControllerModule.bindFlowNodeActions({
            root,
            handleFlowNodeAction
        });
    }
}

function bindConditionNodeSelection(root = document) {
    if (FlowControllerModule.bindConditionNodeSelection) {
        return FlowControllerModule.bindConditionNodeSelection({
            root,
            bindFlowNodeSelectionTargets,
            bindFlowNodeActions
        });
    }
}

function selectFlowNodeByElement(target) {
    if (FlowControllerModule.selectFlowNodeByElement) {
        return FlowControllerModule.selectFlowNodeByElement({
            target,
            getFlowNodeIdFromElement,
            getFlowNodeById,
            selectFlowNode
        });
    }
}

function selectFlowNode(nodeId, options = {}) {
    if (FlowControllerModule.selectFlowNode) {
        return FlowControllerModule.selectFlowNode({
            state,
            nodeId,
            options,
            getFlowNodeById,
            renderFlowNodeEditor
        });
    }
}

function closeFlowNodeEditor() {
    if (FlowControllerModule.closeFlowNodeEditor) {
        return FlowControllerModule.closeFlowNodeEditor({
            state,
            renderFlowNodeEditor
        });
    }
}

function getFlowNodeIdFromElement(target) {
    if (FlowControllerModule.getFlowNodeIdFromElement) {
        return FlowControllerModule.getFlowNodeIdFromElement({ target });
    }
}

function getFlowPortName(target, prefix) {
    if (FlowControllerModule.getFlowPortName) {
        return FlowControllerModule.getFlowPortName({ target, prefix });
    }
}

function getFlowNodeLabel(nodeId) {
    if (FlowControllerModule.getFlowNodeLabel) {
        return FlowControllerModule.getFlowNodeLabel({
            state,
            nodeId,
            getActiveFlowModuleName,
            getNodeData,
            resolveFlowNodeLabelFromData: getFlowNodeLabelFromData
        });
    }
}

function updateFlowConnectionSelectionState() {
    if (FlowControllerModule.updateFlowConnectionSelectionState) {
        return FlowControllerModule.updateFlowConnectionSelectionState({
            state,
            doc: document
        });
    }
}

function setFlowConnectionHoverText(text = "") {
    if (FlowControllerModule.setFlowConnectionHoverText) {
        return FlowControllerModule.setFlowConnectionHoverText({
            state,
            text,
            renderFlowConnectionAssist
        });
    }
}

function renderFlowConnectionAssist() {
    if (FlowControllerModule.renderFlowConnectionAssist) {
        return FlowControllerModule.renderFlowConnectionAssist({
            state,
            doc: document,
            getFlowNodeLabel,
            updateFlowConnectionSelectionState
        });
    }
}

function clearPendingFlowConnection() {
    if (FlowControllerModule.clearPendingFlowConnection) {
        return FlowControllerModule.clearPendingFlowConnection({
            state,
            renderFlowConnectionAssist
        });
    }
}

function setFlowConnectionMode(mode) {
    if (FlowControllerModule.setFlowConnectionMode) {
        return FlowControllerModule.setFlowConnectionMode({
            state,
            mode,
            renderFlowConnectionAssist
        });
    }
}

function beginFlowConnection(nodeId, outputName) {
    if (FlowControllerModule.beginFlowConnection) {
        return FlowControllerModule.beginFlowConnection({
            state,
            nodeId,
            outputName,
            getActiveFlowModuleName,
            clearPendingFlowConnection,
            renderFlowConnectionAssist
        });
    }
}

function completeFlowConnection(targetNodeId, inputName) {
    if (FlowControllerModule.completeFlowConnection) {
        return FlowControllerModule.completeFlowConnection({
            state,
            targetNodeId,
            inputName,
            getActiveFlowModuleName,
            clearPendingFlowConnection,
            renderFlowConnectionAssist,
            addConnection: (...args) => getDrawflowAdapter().addConnection(...args),
            alertMessage: (message) => alert(message),
            warn: (...args) => console.warn(...args)
        });
    }
}

function bindFlowNodeEditorClose(panel) {
    if (FlowControllerModule.bindFlowNodeEditorClose) {
        return FlowControllerModule.bindFlowNodeEditorClose({
            panel,
            closeFlowNodeEditor
        });
    }
}

function positionFlowNodeEditor(nodeId) {
    if (FlowControllerModule.positionFlowNodeEditor) {
        return FlowControllerModule.positionFlowNodeEditor({
            nodeId,
            doc: document,
            viewport: window
        });
    }
}

function flowNodeEditorHeader(title) {
    if (FlowControllerModule.flowNodeEditorHeader) {
        return FlowControllerModule.flowNodeEditorHeader({
            title,
            escapeHtml
        });
    }
}

function finishFlowNodeEditor(panel, nodeId) {
    if (FlowControllerModule.finishFlowNodeEditor) {
        return FlowControllerModule.finishFlowNodeEditor({
            panel,
            nodeId,
            bindFlowNodeEditorClose,
            requestAnimationFrame,
            positionFlowNodeEditor
        });
    }
}

function buildFlowActuatorTypeOptions(selectedType = "pneumaticCylinder") {
    if (FlowControllerModule.buildFlowActuatorTypeOptions) {
        return FlowControllerModule.buildFlowActuatorTypeOptions({
            selectedType,
            buildActuatorTypeOptions
        });
    }
}


function makeUniqueActuatorId(baseId) {
    if (FlowControllerModule.makeUniqueActuatorId) {
        return FlowControllerModule.makeUniqueActuatorId({
            state,
            baseId
        });
    }
}

function makeUniqueActionId(baseId, actions = []) {
    if (FlowControllerModule.makeUniqueActionId) {
        return FlowControllerModule.makeUniqueActionId({
            baseId,
            actions
        });
    }
}

function normalizePlcopenActionMeta(plcopen = {}) {
    return ControlSpecHelper.normalizePlcopenActionMeta(plcopen);
}
function normalizeActionCommand(command = {}) {
    return ControlSpecHelper.normalizeActionCommand(command);
}
function normalizeActionParameters(parameters = {}) {
    return ControlSpecHelper.normalizeActionParameters(parameters);
}
function normalizeActionStatus(status = {}) {
    return ControlSpecHelper.normalizeActionStatus(status);
}
function createDefaultPlcopenPins(actionId = "") {
    return ControlSpecHelper.createDefaultPlcopenPins(actionId);
}

function getDefaultActuatorActionTemplates(actuatorType = "pneumaticCylinder", motorControlType = "", controlMethod = "") {
    return ControlSpecHelper.getDefaultActuatorActionTemplates(
        actuatorType, motorControlType, controlMethod,
        getActuatorTypeCatalogEntry(normalizeAxisActuatorType(actuatorType))
    );
}
function createDefaultActuatorActions(actuatorType = "pneumaticCylinder", motorControlType = "", controlMethod = "") {
    return ControlSpecHelper.createDefaultActuatorActions(
        actuatorType, motorControlType, controlMethod,
        getActuatorTypeCatalogEntry(normalizeAxisActuatorType(actuatorType))
    );
}
function mergeActuatorActionsWithDefaults(actions = [], actuatorType = "pneumaticCylinder", motionName = "", motionId = "", motorControlType = "", controlMethod = "") {
    return ControlSpecHelper.mergeActuatorActionsWithDefaults(
        actions, actuatorType, motionName, motionId, motorControlType, controlMethod,
        getActuatorTypeCatalogEntry(normalizeAxisActuatorType(actuatorType))
    );
}

function findActuatorById(actuatorId = "") {
    if (FlowControllerModule.findActuatorById) {
        return FlowControllerModule.findActuatorById({
            state,
            actuatorId
        });
    }
}

function createFlowDerivedActuator(axis, motion) {
    if (FlowControllerModule.createFlowDerivedActuator) {
        return FlowControllerModule.createFlowDerivedActuator({
            state,
            axis,
            motion,
            normalizeAxisActuatorType,
            normalizeControlSpecText: (v, fb = "") => ControlSpecHelper.normalizeControlSpecText(v, fb),
            buildControlSpecSlug: (v, fb = "item") => ControlSpecHelper.buildControlSpecSlug(v, fb),
            makeUniqueActuatorId,
            normalizeMotorControlType,
            normalizeActuatorControlMethod,
            buildDefaultAxisRef,
            mergeActuatorActionsWithDefaults
        });
    }
}

function ensureFlowMotionActuatorBinding(data, found) {
    if (FlowControllerModule.ensureFlowMotionActuatorBinding) {
        return FlowControllerModule.ensureFlowMotionActuatorBinding({
            state,
            data,
            found,
            findActuatorById,
            createFlowDerivedActuator,
            mergeActuatorActionsWithDefaults,
            normalizeControlSpecText: (v, fb = "") => ControlSpecHelper.normalizeControlSpecText(v, fb),
            escapeHtml
        });
    }
}

function resolveFlowMotionActuatorType(data, found) {
    if (FlowControllerModule.resolveFlowMotionActuatorType) {
        return FlowControllerModule.resolveFlowMotionActuatorType({
            data,
            found,
            findActuatorById,
            normalizeAxisActuatorType
        });
    }
}

function applyFlowMotionActuatorType(data, found, nextType) {
    if (FlowControllerModule.applyFlowMotionActuatorType) {
        return FlowControllerModule.applyFlowMotionActuatorType({
            data,
            found,
            nextType,
            normalizeAxisActuatorType,
            findActuatorById,
            createFlowDerivedActuator,
            normalizeMotorControlType,
            normalizeActuatorControlMethod,
            buildDefaultAxisRef,
            mergeActuatorActionsWithDefaults
        });
    }
}

function refreshHomeMotionNodeMaps(snapshot = getFlowExportSnapshot()) {
    if (FlowControllerModule.refreshHomeMotionNodeMaps) {
        return FlowControllerModule.refreshHomeMotionNodeMaps({
            state,
            snapshot,
            getFlowModuleData
        });
    }
}

function ensureDrawflowModule(moduleName) {
    if (FlowControllerModule.ensureDrawflowModule) {
        return FlowControllerModule.ensureDrawflowModule({
            state,
            moduleName,
            getFlowExportSnapshot
        });
    }
}

function clearCustomDrawflowModules() {
    if (FlowControllerModule.clearCustomDrawflowModules) {
        return FlowControllerModule.clearCustomDrawflowModules({
            state,
            getFlowExportSnapshot
        });
    }
}

function setActiveFlowModule(moduleName = "Home") {
    if (FlowControllerModule.setActiveFlowModule) {
        return FlowControllerModule.setActiveFlowModule({
            moduleName,
            state,
            ensureDrawflowModule,
            renderFlowTabs,
            renderFlowNodeEditor,
            renderFlowConnectionAssist,
            bindConditionNodeSelection,
            scheduleFlowConnectionRefresh,
            drawflowRoot: document.getElementById("drawflow-container") || document,
            doc: document
        });
    }
}

function renderFlowTabs() {
    if (FlowControllerModule.renderFlowTabs) {
        return FlowControllerModule.renderFlowTabs(document, {
            getActiveFlowModuleName,
            getSubprocessList,
            getSubprocessModuleName,
            escapeHtml,
            setActiveFlowModule,
            ensureSubprocessMeta,
            makeUniqueSubprocessName,
            ensureDrawflowModule,
            removeSubprocessMeta,
            getFlowExportSnapshot,
            getFlowModuleData,
            hasFlowModuleNodes: FlowControllerModule.hasFlowModuleNodes,
            confirmMessage: (message) => window.confirm(message)
        });
    }
}

function getFlowNodeById(nodeId) {
    if (FlowControllerModule.getFlowNodeById) {
        return FlowControllerModule.getFlowNodeById({
            state,
            nodeId,
            getActiveFlowModuleName
        });
    }
}

function refreshAllFlowConnections() {
    if (FlowControllerModule.refreshAllFlowConnections) {
        return FlowControllerModule.refreshAllFlowConnections({ getDrawflowAdapter });
    }
}

function scheduleFlowConnectionRefresh() {
    if (FlowControllerModule.scheduleFlowConnectionRefresh) {
        return FlowControllerModule.scheduleFlowConnectionRefresh({ getDrawflowAdapter });
    }
}


function renderFlowNodeEditor() {
    if (FlowControllerModule.renderFlowNodeEditor) {
        return FlowControllerModule.renderFlowNodeEditor({
            state,
            doc: document,
            getActiveFlowModuleName,
            getNodeData,
            getNodeTypeFromData,
            prepareFlowNodeEditor: FlowControllerModule.prepareFlowNodeEditor,
            renderSelectedFlowNodeEditor: FlowControllerModule.renderSelectedFlowNodeEditor,
            escapeHtml,
            flowNodeEditorHeader,
            finishFlowNodeEditor,
            refreshFlowNodeHtml,
            ensureButtonNodeData,
            normalizeButtonMode,
            ensureSignalOutputData,
            syncInternalFlagsFromFlowSnapshot,
            getInternalFlagList,
            normalizeSignalScope,
            commitInternalFlagName,
            renderFlowTabs,
            renderFlowNodeEditor,
            findSubprocessById,
            ensureSubprocessMeta,
            getSubprocessList,
            isSubprocessNameDuplicate,
            refreshSubprocessReferenceNodes,
            ensureDrawflowModule,
            setActiveFlowModule,
            findMotion,
            applyFlowMotionActuatorType,
            ensureFlowMotionActuatorBinding,
            resolveFlowMotionActuatorType,
            findActuatorById,
            buildFlowActuatorTypeOptions,
            renderControlSpecActuators,
            recomputeAll,
            renderControlSpecView,
            scheduleAutoSave,
            renderErrors,
            renderTimingGrid,
            renderSpeedChart,
            updateAllFlowNodeLabels,
            isControlSpecViewActive: state.ui.activeView === "control-spec",
            getConditionMode,
            getConditionKind,
            getConditionOperator,
            ensureConditionNodeData,
            ensureConditionDraftSignal,
            normalizeConditionEntries,
            normalizeConditionEntry,
            actuators: state.project?.actuators || []
        });
    }
}

function refreshFlowNodeHtml(nodeId) {
    if (FlowControllerModule.refreshFlowNodeHtml) {
        return FlowControllerModule.refreshFlowNodeHtml({
            nodeId,
            state,
            doc: document,
            getActiveFlowModuleName,
            getNodeData,
            findMotion,
            buildFlowNodeHtml,
            bindConditionNodeSelection,
            scheduleFlowConnectionRefresh,
            positionFlowNodeEditor,
            rebuildStateFromFlow
        });
    }
}

function syncActiveFlowNodeEditorState(options = {}) {
    if (FlowControllerModule.syncActiveFlowNodeEditorState) {
        return FlowControllerModule.syncActiveFlowNodeEditorState({
            state,
            doc: document,
            getFlowNodeById,
            getNodeData,
            getNodeTypeFromData,
            normalizeConditionEntry,
            normalizeConditionEntries,
            ensureConditionDraftSignal,
            ensureConditionNodeData,
            normalizeSignalScope,
            ensureSignalOutputData,
            commitInternalFlagName,
            findSubprocessById,
            isSubprocessNameDuplicate,
            normalizeButtonMode,
            refreshFlowNodeHtml,
            options
        });
    }
}

function refreshSubprocessReferenceNodes(subprocess) {
    if (FlowControllerModule.refreshSubprocessReferenceNodes) {
        return FlowControllerModule.refreshSubprocessReferenceNodes({
            subprocess,
            state,
            getActiveFlowModuleName,
            getNodeData,
            getNodeTypeFromData,
            refreshFlowNodeHtml
        });
    }
}

function updateFlowAxisSelect() {
    if (FlowControllerModule.updateFlowAxisSelect) {
        return FlowControllerModule.updateFlowAxisSelect({
            state,
            doc: document,
            escapeHtml
        });
    }
}

// Mirrors src/generation/summary.js during the single-file migration phase.
function buildFlowSummaryText(context = null) {
    const external = getPhase3GenerationExport("buildFlowSummaryFromContext");
    if (typeof external === "function" && external !== buildFlowSummaryText) {
        return external(context || {});
    }
}

const FLOW_CONTEXT_VERSION = "1.0";
const GENERATION_IR_VERSION = "1.0";

// =====================================================================
// Generation workspace: FlowContext extraction
// =====================================================================
function getFlowDataSnapshot() {
    const external = getPhase3GenerationExport("getFlowDataSnapshot");
    if (typeof external === "function" && external !== getFlowDataSnapshot) {
        return external({ getMainFlowDataSnapshot });
    }
}

// Single source for the helpers required by extract*FromSnapshot /
// extractFlowAxesFromProject. Routes the runtime-bound helpers through
// `buildFlowContextFromSources`, so the canonical FlowContext path always uses
// explicit injection (instead of falling back to global function references).
// See `src/generation/README.md` 共通の移行パターン (node helper / 軸単位 helper の helpers 注入).
function buildFlowExtractionHelpers() {
    const external = getPhase3GenerationExport("buildFlowExtractionHelpers");
    if (typeof external === "function" && external !== buildFlowExtractionHelpers) {
        return external({
            getNodeData,
            getNodeTypeFromData,
            state,
            findMotion,
            getAxisDistanceUnit,
            getAxisAccelUnit,
            getAxisSpeedUnit,
            findSubprocessById
        });
    }
}

function extractFlowConnectionsFromSnapshot(flowData = {}) {
    const external = getPhase3GenerationExport("extractFlowConnectionsFromSnapshot");
    if (typeof external === "function" && external !== extractFlowConnectionsFromSnapshot) {
        return external(flowData);
    }
}

const SUBPROCESS_TIMING_EXPAND = "expand";
const SUBPROCESS_TIMING_FIXED = "fixed_duration";

function normalizeSubprocessTimingMode({ explicitTimingMode, subprocessId, subprocesses, rawModulesNonEmpty, modulesByName }) {
    const external = getPhase3GenerationExport("normalizeSubprocessTimingMode");
    if (typeof external === "function" && external !== normalizeSubprocessTimingMode) {
        return external({ explicitTimingMode, subprocessId, subprocesses, rawModulesNonEmpty, modulesByName });
    }
}

// Mirrors src/generation/context.js during the single-file migration phase.
function extractFlowNodesFromSnapshot(flowData, helpers = {}) {
    const external = getPhase3GenerationExport("extractFlowNodesFromSnapshot");
    if (typeof external === "function" && external !== extractFlowNodesFromSnapshot) {
        return external(flowData, helpers);
    }
}


function extractFlowAxesFromProject(project = state.project, helpers = {}) {
    const external = getPhase3GenerationExport("extractFlowAxesFromProject");
    if (typeof external === "function" && external !== extractFlowAxesFromProject) {
        return external(project, helpers);
    }
}


function extractFlowConditionsFromSnapshot(flowData, helpers = {}) {
    const external = getPhase3GenerationExport("extractFlowConditionsFromSnapshot");
    if (typeof external === "function" && external !== extractFlowConditionsFromSnapshot) {
        return external(flowData, helpers);
    }
}


function extractFlowErrorsFromSnapshot(flowData, helpers = {}) {
    const external = getPhase3GenerationExport("extractFlowErrorsFromSnapshot");
    if (typeof external === "function" && external !== extractFlowErrorsFromSnapshot) {
        return external(flowData, helpers);
    }
}


function buildFlowContextFromSources({ project = state.project, flowData = getFlowDataSnapshot(), dialect = syncChatDialectFromProfile().vendorTarget, assistantPipeline = null, plcProfile = null } = {}, helpers = {}) {
    const external = getPhase3GenerationExport("buildFlowContextFromSources");
    if (typeof external === "function" && external !== buildFlowContextFromSources) {
        return external({ project, flowData, dialect, assistantPipeline, plcProfile }, helpers);
    }
}

function prepareProjectForGeneration(project = state.project) {
    return ControlSpecHelper.assignDeferredPneumaticValveOutputs(project);
}

function buildFlowContext() {
    const external = getPhase3GenerationExport("buildFlowContext");
    if (typeof external === "function" && external !== buildFlowContext) {
        return external({
            state,
            getActivePlcProfile,
            getFlowDataSnapshot,
            buildFlowContextFromSources,
            buildFlowExtractionHelpers,
            prepareProjectForGeneration
        });
    }
}

function validateFlowContextContract(context) {
    const external = getPhase3GenerationExport("validateFlowContextContract");
    if (typeof external === "function" && external !== validateFlowContextContract) {
        return external(context);
    }
    return Boolean(
        context &&
        typeof context === "object" &&
        typeof context.contractVersion === "string" &&
        Array.isArray(context.axes) &&
        Array.isArray(context.flowNodes) &&
        Array.isArray(context.conditions) &&
        Array.isArray(context.errors)
    );
}

function getFlowContext() {
    const external = getPhase3GenerationExport("getFlowContext");
    if (typeof external === "function" && external !== getFlowContext) {
        return external({
            buildFlowContext,
            validateFlowContextContract
        });
    }
}

function buildStGenerationContext() {
    const context = getFlowContext();
    return {
        version: context.projectVersion,
        contractVersion: context.contractVersion,
        vendorTarget: context.vendorTarget,
        dialect: context.dialect,
        units: context.units,
        axes: context.axes,
        flowNodes: context.flowNodes,
        connections: context.connections,
        conditions: context.conditions,
        errors: context.errors,
        actuators: context.actuators,
        signals: context.signals,
        internalFlags: context.internalFlags,
        subprocesses: context.subprocesses,
        modules: context.modules,
        assistantPipeline: context.assistantPipeline,
        summary: context.summary
    };
}

// =====================================================================
// Generation workspace: GenerationIR assembly
// =====================================================================
function nextChatStepNumber(index) {
    return (index + 1) * 10;
}

function toStIdentifier(value, fallback) {
    const base = String(value || "")
        .replace(/[^A-Za-z0-9_]+/g, "_")
        .replace(/^_+|_+$/g, "");
    const normalized = base || fallback;
    return /^[A-Za-z_]/.test(normalized) ? normalized : "_" + normalized;
}

function toStableStIdentifier(value, fallback, minCoreLength = 3) {
    const raw = String(value || "");
    const candidate = toStIdentifier(raw, fallback);
    const candidateCore = candidate.replace(/_/g, "");
    const rawHasNonAscii = /[^\x00-\x7F]/.test(raw);
    if (rawHasNonAscii && candidateCore.length < minCoreLength) {
        return toStIdentifier(fallback, fallback);
    }
    return candidate;
}

function asArray(value) {
    return Array.isArray(value) ? value : [];
}

function parseStartConditionSpec(text) {
    const raw = String(text || "").trim();
    if (!raw) return null;
    const parts = raw.split("/").map(part => part.trim()).filter(Boolean);
    const tail = parts[parts.length - 1] || raw;
    const match = tail.match(/^(.*)\[([^\]]+)\]$/);
    if (!match) {
        return {
            raw,
            conditionName: tail,
            branchLabel: "",
            placeholder: "",
            includesStart: parts.some(part => part === "開始")
        };
    }
    return {
        raw,
        conditionName: match[1].trim(),
        branchLabel: match[2].trim(),
        placeholder: "",
        includesStart: parts.some(part => part === "開始")
    };
}

function buildMotionLookup(context) {
    const byMotionId = new Map();
    const byNodeKey = new Map();
    const axes = asArray(context?.axes);
    for (const axis of axes) {
        for (const motion of asArray(axis?.motions)) {
            if (motion?.id) byMotionId.set(motion.id, { axis, motion });
            if (axis?.name && motion?.name) {
                byNodeKey.set(`${axis.name}::${motion.name}`, { axis, motion });
            }
        }
    }
    return { byMotionId, byNodeKey };
}

function normalizePortOrder(portName) {
    if (portName === "output_1") return 1;
    if (portName === "output_2") return 2;
    if (portName === "output_3") return 3;
    return 99;
}

function getPrimaryTarget(connectionsByFrom, nodeId, outputName = "output_1") {
    const edge = (connectionsByFrom.get(nodeId) || []).find((item) => item.output === outputName);
    return edge?.to || null;
}

function buildGraphMaps(context) {
    const flowNodes = asArray(context?.flowNodes);
    const connections = asArray(context?.connections)
        .map((connection) => ({
            from: String(connection?.from || ""),
            to: String(connection?.to || ""),
            output: String(connection?.output || "output_1"),
            input: String(connection?.input || "input_1")
        }))
        .filter((connection) => connection.from && connection.to);

    const nodesById = new Map(flowNodes.map((node) => [String(node.id), node]));
    const connectionsByFrom = new Map();
    const connectionsByTo = new Map();
    for (const connection of connections) {
        if (!connectionsByFrom.has(connection.from)) connectionsByFrom.set(connection.from, []);
        connectionsByFrom.get(connection.from).push(connection);
        if (!connectionsByTo.has(connection.to)) connectionsByTo.set(connection.to, []);
        connectionsByTo.get(connection.to).push(connection);
    }

    for (const list of connectionsByFrom.values()) {
        list.sort((a, b) => {
            const byPort = normalizePortOrder(a.output) - normalizePortOrder(b.output);
            if (byPort !== 0) return byPort;
            return String(a.to).localeCompare(String(b.to));
        });
    }

    return {
        nodesById,
        connections,
        connectionsByFrom,
        connectionsByTo
    };
}

function enumerateReachableNodeIds(startId, connectionsByFrom) {
    const reachable = new Set();
    const queue = startId ? [startId] : [];
    while (queue.length) {
        const nodeId = queue.shift();
        if (!nodeId || reachable.has(nodeId)) continue;
        reachable.add(nodeId);
        for (const connection of connectionsByFrom.get(nodeId) || []) {
            if (!reachable.has(connection.to)) queue.push(connection.to);
        }
    }
    return reachable;
}

function buildConditionExpression(node, stepNumber) {
    const conditions = asArray(node?.conditions);
    if (!conditions.length) {
        return {
            expression: `Cond${stepNumber}`,
            terms: []
        };
    }
    const terms = conditions.map((entry, index) => {
        const raw = String(entry?.expression || entry?.label || "").trim();
        return {
            raw,
            ident: toStableStIdentifier(
                raw || entry?.label || node?.conditionText || `Cond${stepNumber}_${index + 1}`,
                `Cond${stepNumber}_${index + 1}`,
                3
            )
        };
    });
    const joiner = node?.operator === "OR" ? " OR " : " AND ";
    return {
        expression: terms.map((item) => item.ident).join(joiner) || `Cond${stepNumber}`,
        terms
    };
}

function enrichMotionNode(node, motionLookup) {
    const hit =
        motionLookup.byMotionId.get(node.motionId) ||
        motionLookup.byNodeKey.get(`${node.axisName || ""}::${node.motionName || ""}`) ||
        null;
    return {
        ...node,
        axisName: node.axisName || hit?.axis?.name || "",
        motionName: node.motionName || hit?.motion?.name || "",
        motionRef: hit
            ? {
                  axis: hit.axis,
                  motion: hit.motion
              }
            : null
    };
}

function buildTransitionsForNode(node, connectionsByFrom, stepByNodeId) {
    const edges = connectionsByFrom.get(node.id) || [];
    if (node.type === "condition") {
        return {
            true: stepByNodeId.get(getPrimaryTarget(connectionsByFrom, node.id, "output_1")) ?? null,
            false: stepByNodeId.get(getPrimaryTarget(connectionsByFrom, node.id, "output_2")) ?? null,
            timeout: stepByNodeId.get(getPrimaryTarget(connectionsByFrom, node.id, "output_3")) ?? null
        };
    }
    if (node.type === "end" || node.type === "error") {
        return {};
    }
    const nextEdge = edges[0];
    return {
        next: nextEdge ? stepByNodeId.get(nextEdge.to) ?? null : null
    };
}

function buildConditionPlaceholder(spec, fallbackIndex, conditionIndex = null) {
    if (!spec || !spec.conditionName || !spec.branchLabel) return "";
    const branchKey = toStableStIdentifier(spec.branchLabel, `Branch${fallbackIndex}`, 2);
    const conditionKey = conditionIndex != null ? `Cond${conditionIndex}` : `Cond${fallbackIndex}`;
    return toStableStIdentifier(`${spec.conditionName}_${spec.branchLabel}`, `${conditionKey}_${branchKey}`, 5);
}

function buildConditionIndexMap(conditions) {
    return new Map((conditions || []).map((condition, index) => [condition.conditionText, index + 1]));
}

function resolveMatchedCondition(context, conditionSpec) {
    if (!conditionSpec?.conditionName) return null;
    return (context.conditions || []).find(cond => cond.conditionText === conditionSpec.conditionName) || null;
}

function buildGenerationIrFromContext(context) {
    const external = getPhase3GenerationExport("buildGenerationIrFromContext");
    if (typeof external === "function" && external !== buildGenerationIrFromContext) {
        return external(context);
    }
    return buildGenerationGraphIrFromContext(context);
}

function buildGenerationGraphIrFromContext(context) {
    const external = getPhase3GenerationExport("buildGenerationGraphIrFromContext");
    if (typeof external === "function" && external !== buildGenerationGraphIrFromContext) {
        return external(context);
    }
    const { nodesById, connections, connectionsByFrom } = buildGraphMaps(context);
    const motionLookup = buildMotionLookup(context);
    const startNodes = asArray(context?.flowNodes).filter((node) => node?.type === "start");
    const startNode = startNodes[0] || null;
    const reachableNodeIds = enumerateReachableNodeIds(startNode?.id || "", connectionsByFrom);
    const orderedReachableIds = [...reachableNodeIds];
    const stepByNodeId = new Map(
        orderedReachableIds.map((nodeId, index) => [nodeId, nextChatStepNumber(index)])
    );
    const subprocessById = new Map(
        asArray(context?.subprocesses).map((item) => [String(item?.id || ""), item])
    );

    const steps = orderedReachableIds
        .map((nodeId) => nodesById.get(nodeId))
        .filter(Boolean)
        .map((rawNode) => {
            const node = rawNode.type === "motion" ? enrichMotionNode(rawNode, motionLookup) : rawNode;
            const step = stepByNodeId.get(node.id);
            const base = {
                step,
                nodeId: node.id,
                type: node.type,
                name: node.name || node.label || "",
                transitions: {}
            };

            if (node.type === "motion") {
                const axis = node.motionRef?.axis || null;
                const motion = node.motionRef?.motion || null;
                return {
                    ...base,
                    axisId: node.axisId || "",
                    motionId: node.motionId || "",
                    axisName: node.axisName || axis?.name || "",
                    motionName: node.motionName || motion?.name || "",
                    axis,
                    motion,
                    commandName: toStableStIdentifier(
                        `${node.axisName || axis?.name || "Axis"}_${node.motionName || motion?.name || "Motion"}`,
                        `Step${step}_Motion`,
                        5
                    ),
                    doneCondition: toStableStIdentifier(
                        `${node.axisName || axis?.name || "Axis"}_${node.motionName || motion?.name || "Motion"}_Done`,
                        `Step${step}_Done`,
                        8
                    ),
                    transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
                };
            }

            if (node.type === "condition") {
                const cond = buildConditionExpression(node, step);
                return {
                    ...base,
                    conditionText: node.conditionText || node.name || "条件",
                    conditionMode: node.conditionMode || "single",
                    operator: node.operator || "AND",
                    conditions: asArray(node.conditions),
                    ...(node.conditionKind ? {
                        conditionKind: node.conditionKind,
                        conditionName: node.conditionName || "",
                        conditionValue: node.conditionValue === false ? false : true,
                        conditionActuatorId: node.conditionActuatorId || "",
                        conditionSignalId: node.conditionSignalId || ""
                    } : {}),
                    conditionExpression: cond.expression,
                    conditionTerms: cond.terms,
                    timerName: toStableStIdentifier(node.conditionText || `Condition_${step}`, `CondTimer_${step}`, 5),
                    trueLabel: node.trueLabel || "ON",
                    falseLabel: node.falseLabel || "OFF",
                    timeoutLabel: node.timeoutLabel || "TIMEOUT",
                    timeoutSec: numOrZero(node.timeoutSec),
                    transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
                };
            }

            if (node.type === "signal_output") {
                return {
                    ...base,
                    signalScope: node.signalScope || "external",
                    signalName: node.signalName || "",
                    action: node.action || "ON",
                    signalIdent: toStableStIdentifier(node.signalName || node.name || `Signal_${step}`, `Signal_${step}`, 5),
                    transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
                };
            }

            if (node.type === "subprocess") {
                const definition = subprocessById.get(String(node.subprocessId || ""));
                return {
                    ...base,
                    subprocessId: node.subprocessId || "",
                    subprocessName: node.subprocessName || node.name || "サブプロセス",
                    moduleName: definition?.moduleName || "",
                    durationSec: numOrZero(node.durationSec ?? definition?.durationSec ?? 1),
                    timerName: toStableStIdentifier(node.subprocessName || `Subprocess_${step}`, `SubTimer_${step}`, 5),
                    transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
                };
            }

            if (node.type === "error") {
                return {
                    ...base,
                    errorCode: node.errorCode || "E001",
                    errorMessage: node.errorMessage || "エラー",
                    errorIdent: toStableStIdentifier(node.errorCode || `Error_${step}`, `Error_${step}`, 5),
                    transitions: {}
                };
            }

            if (node.type === "end") {
                return {
                    ...base,
                    label: node.label || "終了",
                    result: node.result || "normal",
                    transitions: {}
                };
            }

            return {
                ...base,
                label: node.label || node.name || "",
                transitions: buildTransitionsForNode(node, connectionsByFrom, stepByNodeId)
            };
        });

    return {
        contractVersion: GENERATION_IR_VERSION,
        flowContextVersion: context?.contractVersion,
        dialect: context?.dialect,
        vendorTarget: context?.vendorTarget,
        summary: context?.summary,
        startNodeId: startNode?.id || "",
        startStep: startNode ? stepByNodeId.get(startNode.id) || 0 : 0,
        steps,
        connections,
        reachableNodeIds: orderedReachableIds,
        conditions: context?.conditions || [],
        errors: context?.errors || []
    };
}

function validateGenerationIrContract(ir) {
    const external = getPhase3GenerationExport("validateGenerationIrContract");
    if (typeof external === "function" && external !== validateGenerationIrContract) {
        return external(ir);
    }
    return Boolean(
        ir &&
        typeof ir.contractVersion === "string" &&
        Array.isArray(ir.steps) &&
        Array.isArray(ir.conditions) &&
        Array.isArray(ir.errors)
    );
}

function getGenerationIr() {
    const external = getPhase3GenerationExport("getGenerationIr");
    if (typeof external === "function" && external !== getGenerationIr) {
        return external({
            buildGenerationIrFromContext,
            buildStGenerationContext,
            validateGenerationIrContract
        });
    }
}

// =====================================================================
// Generation workspace: Prompt / local generation
// =====================================================================
function getGenerationSnapshot() {
    const external = getPhase3GenerationExport("getGenerationSnapshot");
    if (typeof external === "function" && external !== getGenerationSnapshot) {
        return external({
            getFlowContext,
            getGenerationIr,
            getActivePlcProfile,
            state,
            getDialectMeta
        });
    }
}

// Single source for the helpers consumed by buildLocalStDraftFromSnapshot.
// Mirrors `src/generation/st-draft.js` の helpers シグネチャ
// (`findMotion` / `getAxisDistanceUnit` / `getAxisSpeedUnit`).
// 詳細は docs/PHASE2_ST_DRAFT_DEPENDENCY_PLAN.md を参照。
function buildStDraftHelpers() {
    return {
        findMotion,
        getAxisDistanceUnit,
        getAxisSpeedUnit
    };
}

function buildBooleanAssignment(action) {
    return action === "RESET" || action === "OFF" ? "FALSE" : "TRUE";
}

function formatTimeLiteral(seconds) {
    const n = Math.max(numOrZero(seconds), 0);
    if (n === 0) return "T#0S";
    return `T#${n}S`;
}

function pushTransition(lines, nextStep, stepVar, indent = "        ") {
    if (nextStep == null) {
        lines.push(`${indent}// TODO: 接続先が未定義です`);
        return;
    }
    lines.push(`${indent}${stepVar} := ${nextStep};`);
}

function buildSignalIdentifierMap(ir) {
    const map = new Map();
    const signals = Array.isArray(ir?.signals) ? ir.signals : [];
    for (const sig of signals) {
        if (!sig?.id) continue;
        map.set(sig.id, sig.iecName || sig.id);
    }
    return map;
}

function getSignalIdentifier(signalMap, signalId = "") {
    return signalMap.get(signalId) || signalId;
}

function collectGlobalSignalDeclarations(ir) {
    const signals = Array.isArray(ir?.signals) ? ir.signals : [];
    return signals
        .filter((sig) => sig?.iecName)
        .map((sig) => ({
            iecName: sig.iecName,
            address: sig.address || "",
            dataType: sig.dataType || "BOOL"
        }));
}

function renderPlcopenMotionStep(lines, step, dialectMeta) {
    const action = step.action || {};
    const fbName = action.plcopen?.fbName || "";
    const axisRef = step.actuator?.axisRef || "";
    const instanceName = toStIdentifier(`fb_${step.actuator?.id || "Actuator"}_${action.id || step.step}`, `fbStep${step.step}`);
    const executeSignal = action.command?.executeSignal || "TRUE";
    const params = action.parameters || {};
    const status = action.status || {};
    const callArgs = [
        axisRef ? `Axis := ${axisRef}` : "",
        `Execute := ${executeSignal}`,
        params.position ? `Position := ${params.position}` : "",
        params.velocity ? `Velocity := ${params.velocity}` : ""
    ].filter(Boolean);

    lines.push(`        // PLCopen ${action.plcopen?.family || "motion"}: ${step.actuator?.name || "-"} / ${action.name || "-"} / ${fbName || "-"}`);
    if (!fbName) {
        lines.push("        // TODO: PLCopen FB名が未設定です");
        pushTransition(lines, step.transitions.next, dialectMeta.stepVar);
        return;
    }
    if (!axisRef) {
        lines.push("        // TODO: Axis参照が未設定です");
    }
    lines.push(`        ${instanceName}(${callArgs.join(", ")});`);
    if (status.busySignal) lines.push(`        ${status.busySignal} := ${instanceName}.Busy;`);
    if (status.doneSignal) lines.push(`        ${status.doneSignal} := ${instanceName}.Done;`);
    if (status.errorSignal) lines.push(`        ${status.errorSignal} := ${instanceName}.Error;`);
    if (status.errorIdSignal) lines.push(`        ${status.errorIdSignal} := ${instanceName}.ErrorID;`);
    lines.push(`        IF ${instanceName}.Done THEN`);
    pushTransition(lines, step.transitions.next, dialectMeta.stepVar, "            ");
    lines.push(`        ELSIF ${instanceName}.Error THEN`);
    lines.push("            // TODO: PLCopen FBエラー処理");
    if (step.transitions.timeout != null) {
        pushTransition(lines, step.transitions.timeout, dialectMeta.stepVar, "            ");
    } else {
        lines.push(`            ${dialectMeta.stepVar} := 9999; // エラー用ダミーステップ`);
    }
    lines.push("        END_IF;");
}

function collectPlcopenFbDeclarations(steps = []) {
    const declarations = [];
    const seen = new Set();
    for (const step of steps) {
        const action = step?.action || {};
        const fbName = action.plcopen?.fbName || "";
        if (!fbName) continue;
        const instanceName = toStIdentifier(`fb_${step.actuator?.id || "Actuator"}_${action.id || step.step}`, `fbStep${step.step}`);
        if (seen.has(instanceName)) continue;
        seen.add(instanceName);
        declarations.push({ instanceName, fbName });
    }
    return declarations;
}

function renderMotionStep(lines, step, dialectMeta, helpers, signalMap) {
    const axis = step.axis || null;
    const motion = step.motion || null;
    const getAxisDistanceUnit = helpers.getAxisDistanceUnit || (() => "-");
    const getAxisSpeedUnit = helpers.getAxisSpeedUnit || (() => "-");

    if (
        step.actuator &&
        (step.actuator.controlMethod === "plcopenMotion" || step.actuator.controlMethod === "plcopenFluidPower")
    ) {
        renderPlcopenMotionStep(lines, step, dialectMeta);
        return;
    }

    lines.push(`        // ${step.axisName || "-"} / ${step.motionName || "-"}`);
    lines.push(
        `        // Distance=${motion?.distance ?? "-"} ${axis ? getAxisDistanceUnit(axis) : "-"}, MaxSpeed=${motion?.maxSpeed ?? "-"} ${axis ? getAxisSpeedUnit(axis) : "-"}`
    );
    lines.push(`        ${step.commandName}();`);
    lines.push(`        IF ${step.doneCondition} THEN`);
    if (numOrZero(motion?.waitTime) > 0) {
        lines.push(`            // Wait ${numOrZero(motion.waitTime).toFixed(2)}s before next step`);
    }
    pushTransition(lines, step.transitions.next, dialectMeta.stepVar, "            ");
    lines.push("        END_IF;");
}

function renderConditionStep(lines, step, dialectMeta) {
    const condExpr = step.conditionExpression || `Cond_${step.step}`;
    if (step.timeoutSec > 0 && step.transitions.timeout != null) {
        lines.push(`        ${step.timerName}(IN := NOT (${condExpr}), PT := ${formatTimeLiteral(step.timeoutSec)});`);
    }
    lines.push(`        IF ${condExpr} THEN`);
    pushTransition(lines, step.transitions.true, dialectMeta.stepVar, "            ");
    if (step.timeoutSec > 0 && step.transitions.timeout != null) {
        lines.push(`        ELSIF ${step.timerName}.Q THEN`);
        pushTransition(lines, step.transitions.timeout, dialectMeta.stepVar, "            ");
        lines.push("        ELSE");
        lines.push(`            ${step.timerName}(IN := TRUE, PT := ${formatTimeLiteral(step.timeoutSec)});`);
        lines.push("        END_IF;");
        return;
    }
    if (step.transitions.false != null) {
        lines.push("        ELSE");
        pushTransition(lines, step.transitions.false, dialectMeta.stepVar, "            ");
        lines.push("        END_IF;");
        return;
    }
    lines.push("        END_IF;");
}

function renderSignalOutputStep(lines, step, dialectMeta, signalMap) {
    lines.push(`        ${getSignalIdentifier(signalMap, step.signalName) || step.signalIdent} := ${buildBooleanAssignment(step.action)};`);
    pushTransition(lines, step.transitions.next, dialectMeta.stepVar);
}

function renderSubprocessStep(lines, step, dialectMeta) {
    lines.push(`        ${step.timerName}(IN := TRUE, PT := ${formatTimeLiteral(step.durationSec)});`);
    lines.push(`        IF ${step.timerName}.Q THEN`);
    lines.push(`            ${step.timerName}(IN := FALSE, PT := ${formatTimeLiteral(step.durationSec)});`);
    pushTransition(lines, step.transitions.next, dialectMeta.stepVar, "            ");
    lines.push("        END_IF;");
}

function renderErrorStep(lines, step) {
    lines.push(`        ${step.errorIdent} := TRUE; // ${step.errorMessage}`);
    lines.push("        // TODO: add alarm reset / acknowledge flow");
}

function renderEndStep(lines, step, dialectMeta) {
    lines.push(`        // ${step.label || "終了"} (${step.result || "normal"})`);
    lines.push(`        ${dialectMeta.stepVar} := 0;`);
}

// Mirrors src/generation/st-draft.js during the single-file migration phase.
// Phase 2 では「snapshot のみで足りる範囲 / helpers 注入が必要な範囲 / runtime に
// 残すべき範囲」を引数境界に反映済み。ロジック本体は次フェーズで
// `src/generation/st-draft.js` を正に切り替える前提で、ここは構造ミラーに留める。
// 依存分解: docs/PHASE2_ST_DRAFT_DEPENDENCY_PLAN.md.
function buildLocalStDraftFromSnapshot(
    snapshot = getGenerationSnapshot(),
    dialectMeta = getDialectMeta(snapshot.assistant.plcProfileId || snapshot.assistant.dialect),
    helpers = buildStDraftHelpers()
) {
    const external = getPhase3GenerationExport("buildLocalStDraftFromSnapshot");
    if (typeof external === "function" && external !== buildLocalStDraftFromSnapshot) {
        return external(snapshot, dialectMeta, helpers);
    }
    const { generationIr: ir } = snapshot;
    const steps = Array.isArray(ir.steps) ? ir.steps : [];
    const signalMap = buildSignalIdentifierMap(ir);
    const lines = [];
    lines.push(`(* Auto-generated draft for ${dialectMeta.label} *)`);
    lines.push(`(* ${dialectMeta.comment} *)`);
    const globalSignals = collectGlobalSignalDeclarations(ir);
    if (globalSignals.length > 0) {
        lines.push("VAR_GLOBAL");
        for (const sig of globalSignals) {
            const address = sig.address ? ` AT ${sig.address}` : "";
            lines.push(`    ${sig.iecName}${address} : ${sig.dataType};`);
        }
        lines.push("END_VAR");
        lines.push("");
    }
    const plcopenDeclarations = collectPlcopenFbDeclarations(steps);
    if (plcopenDeclarations.length > 0) {
        lines.push("VAR");
        for (const decl of plcopenDeclarations) {
            lines.push(`    ${decl.instanceName} : ${decl.fbName};`);
        }
        lines.push("END_VAR");
        lines.push("");
    }
    lines.push(`CASE ${dialectMeta.stepVar} OF`);
    lines.push("    0:");
    lines.push(`        IF ${dialectMeta.startSignal} THEN`);
    lines.push(`            ${dialectMeta.stepVar} := ${ir.startStep || (steps[0]?.step ?? 900)};`);
    lines.push("        END_IF;");
    lines.push("");

    for (const step of steps) {
        lines.push(`    ${step.step}:`);
        switch (step.type) {
            case "motion":
                renderMotionStep(lines, step, dialectMeta, helpers, signalMap);
                break;
            case "condition":
                renderConditionStep(lines, step, dialectMeta);
                break;
            case "signal_output":
                renderSignalOutputStep(lines, step, dialectMeta, signalMap);
                break;
            case "subprocess":
                renderSubprocessStep(lines, step, dialectMeta);
                break;
            case "error":
                renderErrorStep(lines, step);
                break;
            case "end":
                renderEndStep(lines, step, dialectMeta);
                break;
            case "start":
                pushTransition(lines, step.transitions.next, dialectMeta.stepVar);
                break;
            default:
                lines.push(`        // Unsupported node type: ${step.type}`);
                pushTransition(lines, step.transitions?.next ?? null, dialectMeta.stepVar);
                break;
        }
        lines.push("");
    }

    lines.push("END_CASE;");
    return lines.join("\n");
}

// Mirrors src/generation/prompt.js during the single-file migration phase.
function buildAiPromptFromSnapshot(mode, snapshot, userMessage, dialectLabel) {
    const external = getPhase3GenerationExport("buildAiPromptFromSnapshot");
    if (typeof external === "function" && external !== buildAiPromptFromSnapshot) {
        return external(mode, snapshot, userMessage, dialectLabel);
    }
    if (mode === "flow") {
        return [
            "PLC制御フローを作成してください。",
            "回答は flowDraftVersion / axes / nodes / connections を持つJSONだけにしてください。",
            "",
            "追加指示:",
            userMessage || "フローを作成",
            "",
            "現在のフロー要約:",
            snapshot.flowContext.summary
        ].join("\n");
    }
    const taskLine = mode === "summary"
        ? "現在のフローを自然文で要約してください。"
        : mode === "review"
            ? "ST生成に向けて不足情報、曖昧さ、リスク、IEC 61131-3観点の注意点をレビューしてください。"
            : "Structured Text のたたき台を生成してください。";
    return [
        `対象PLCプロファイル: ${dialectLabel}`,
        taskLine,
        "回答形式:",
        "1. 前提",
        "2. 不足情報",
        "3. 生成結果",
        "4. 注意点",
        "",
        "フロー要約:",
        snapshot.flowContext.summary,
        "",
        "FlowContext(JSON):",
        JSON.stringify(snapshot.flowContext, null, 2),
        "",
        "GenerationIR(JSON):",
        JSON.stringify(snapshot.generationIr, null, 2),
        "",
        userMessage ? `追加指示:\n${userMessage}` : "追加指示: なし"
    ].join("\n");
}

function buildLocalSummaryResponseFromSnapshot(snapshot) {
    const external = getPhase3GenerationExport("buildLocalSummaryResponseFromSnapshot");
    if (typeof external === "function" && external !== buildLocalSummaryResponseFromSnapshot) {
        return external(snapshot);
    }
    const summary = snapshot.flowContext.summary || "要約できるフロー情報がありません。";
    return [
        "1. 前提",
        "現在のチャートとフロー情報からローカル要約を作成しました。",
        "",
        "2. 不足情報",
        "開始信号名、完了信号名、命令FB名は未指定です。",
        "",
        "3. 生成結果",
        summary,
        "",
        "4. 注意点",
        "API 未設定のためローカル要約のみです。"
    ].join("\n");
}

function buildLocalReviewResponseFromSnapshot(snapshot, findings, userMessage) {
    const external = getPhase3GenerationExport("buildLocalReviewResponseFromSnapshot");
    if (typeof external === "function" && external !== buildLocalReviewResponseFromSnapshot) {
        return external(snapshot, findings, userMessage);
    }
    return [
        "1. 前提",
        `ローカルレビューを実行しました。対象プロファイルは ${snapshot.assistant.plcProfileName || snapshot.assistant.dialectLabel} です。`,
        "",
        "2. 不足情報",
        findings.length ? findings.join("\n") : "大きな不足は見当たりません。",
        "",
        "3. 生成結果",
        snapshot.flowContext.summary || "フロー要約なし",
        "",
        "4. 注意点",
        userMessage ? `追加指示は未反映です: ${userMessage}` : "API 未設定のため、IEC 61131-3観点を含む簡易レビューです。"
    ].join("\n");
}

function buildLocalStResponseFromDraft(snapshot, draftText, startSignal, userMessage) {
    const external = getPhase3GenerationExport("buildLocalStResponseFromDraft");
    if (typeof external === "function" && external !== buildLocalStResponseFromDraft) {
        return external(snapshot, draftText, startSignal, userMessage);
    }
    return [
        "1. 前提",
        `ローカル ST ドラフトを生成しました。対象プロファイルは ${snapshot.assistant.plcProfileName || snapshot.assistant.dialectLabel} です。`,
        "",
        "2. 不足情報",
        `- ${startSignal} の実信号名`,
        "- 各軸完了信号名",
        "- 実際に呼び出す軸制御 FB 名",
        "",
        "3. 生成結果",
        draftText,
        "",
        "4. 注意点",
        userMessage ? `追加指示: ${userMessage}` : "API 未設定のためローカルテンプレート出力です。"
    ].join("\n");
}

function buildLocalStBlockedResponse(errors = []) {
    const external = getPhase3GenerationExport("buildLocalStBlockedResponse");
    if (typeof external === "function" && external !== buildLocalStBlockedResponse) {
        return external(errors);
    }
    return [
        "1. 前提",
        "ローカル ST 生成を中止しました。",
        "",
        "2. 不足情報 / エラー",
        errors.map(f => `- [エラー] ${f.message} (Path: ${f.path || ""})`).join("\n"),
        "",
        "3. 生成結果",
        "エラーを解決するまで Structured Text は生成されません。",
        "",
        "4. 注意点",
        "サブプロセスの定義や接続に不整合があります。Drawflow エディタ上の接続やサブプロセスモジュールを確認してください。"
    ].join("\n");
}

function buildLocalStResponse(snapshot, userMessage) {
    const errors = (snapshot.generationIr?.expansionFindings || []).filter(f => f.severity === "error");
    if (errors.length > 0) {
        return buildLocalStBlockedResponse(errors);
    }
    const dialectMeta = getDialectMeta(snapshot.assistant.plcProfileId || snapshot.assistant.dialect);
    return buildLocalStResponseFromDraft(
        snapshot,
        buildLocalStDraftFromSnapshot(snapshot, dialectMeta, buildStDraftHelpers()),
        dialectMeta.startSignal,
        userMessage
    );
}

function ensureActiveChatThread() {
    if (AppStateModule?.ensureActiveChatThread) {
        return AppStateModule.ensureActiveChatThread(state.chat, createChatThread);
    }
    const activeId = state.chat.activeThreadId;
    const threads = state.chat.threads && typeof state.chat.threads === "object"
        ? state.chat.threads
        : (state.chat.threads = {});
    if (activeId && threads[activeId]) {
        if (!Array.isArray(threads[activeId].messages)) {
            threads[activeId].messages = [];
        }
        state.chat.messages = threads[activeId].messages;
        return threads[activeId];
    }
    const thread = createChatThread();
    threads[thread.id] = thread;
    state.chat.activeThreadId = thread.id;
    state.chat.messages = thread.messages;
    return thread;
}

function getActiveChatMessages() {
    if (AppStateModule?.getActiveChatMessages) {
        return AppStateModule.getActiveChatMessages(state.chat, createChatThread);
    }
    return ensureActiveChatThread().messages;
}

function buildFallbackThreadTitle(text) {
    return AssistantClassifyModule.buildFallbackThreadTitle(text);
}

function touchActiveChatThread(label = "") {
    const thread = ensureActiveChatThread();
    thread.updatedAt = new Date().toISOString();
    if (label && (!thread.label || thread.label === "新規チャット")) {
        thread.label = label;
    }
    return thread;
}

function resetQuestionCardState() {
    state.chat.questionDraftInput = "";
    state.chat.questionCardError = "";
}

function appendChatMessage(role, content, meta = "", extra = {}) {
    const thread = touchActiveChatThread(role === "user" ? String(content || "").trim().slice(0, 32) : "");
    thread.messages.push({
        id: `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role,
        content,
        meta,
        ts: new Date().toLocaleString("ja-JP"),
        actions: Array.isArray(extra.actions) ? extra.actions : [],
        question: extra.question || null,
        faVerification: extra.faVerification || null,
        appliedSkills: Array.isArray(extra.appliedSkills) ? extra.appliedSkills : [],
        sessionId: extra.sessionId || "",
        groupKey: extra.groupKey || ""
    });
    state.chat.messages = thread.messages;
    renderChatUi();
}

function hasChatMessage(match = {}) {
    return getActiveChatMessages().some((item) => {
        if (match.role && item.role !== match.role) return false;
        if (match.meta && item.meta !== match.meta) return false;
        if (match.sessionId && item.sessionId !== match.sessionId) return false;
        if (match.groupKey && item.groupKey !== match.groupKey) return false;
        if (match.content && item.content !== match.content) return false;
        return true;
    });
}

function toFiniteNumber(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
}

function normalizeFaVerificationState({
    status = "",
    verdict = "",
    verdictScope = "L1",
    iterations = [],
    passCount = null,
    failCount = null,
    finalSt = "",
    reason = "",
    report = ""
} = {}) {
    const normalizedIterations = Array.isArray(iterations) ? iterations : [];
    const normalizedStatus = status || (verdict === "skipped" ? "skipped" : "completed");
    const finalReport = report
        || (normalizedIterations.length ? String(normalizedIterations[normalizedIterations.length - 1]?.report || "") : "");
    return {
        status: normalizedStatus,
        verdict: verdict || (normalizedStatus === "skipped" ? "skipped" : ""),
        verdictScope: verdictScope || "L1",
        iterations: normalizedIterations,
        passCount: toFiniteNumber(passCount),
        failCount: toFiniteNumber(failCount),
        finalSt: typeof finalSt === "string" ? finalSt : "",
        reason: typeof reason === "string" ? reason : "",
        report: finalReport
    };
}

function describeFaVerificationReason(reason) {
    if (reason === "worker_unavailable") return "FA worker に接続できなかったため、L1 検証はスキップしました。";
    if (reason === "api_unbound") return "API client が未接続のため、自動検証を開始できませんでした。";
    if (reason === "worker_invalid_response") return "FA worker の応答を解釈できなかったため、L1 検証はスキップしました。";
    return reason ? `L1 検証はスキップされました (${reason})。` : "L1 検証はスキップされました。";
}

function buildFaVerificationMessageContent(faVerification) {
    const lines = [];
    if (faVerification.status === "skipped" || faVerification.verdict === "skipped") {
        lines.push(describeFaVerificationReason(faVerification.reason));
        return lines.join("\n");
    }
    if (faVerification.status === "queued" || faVerification.verdict === "queued") {
        lines.push(`${faVerification.verdictScope || "L1"}検証をキューへ投入しました。`);
        lines.push("結果は後続の worker 処理で反映します。");
        return lines.join("\n");
    }
    if (faVerification.verdict === "PASS") {
        lines.push(`${faVerification.verdictScope || "L1"}検証通過（納品確認は未）`);
    } else {
        lines.push(`${faVerification.verdictScope || "L1"}検証で差分が残っています。`);
    }
    const metrics = [];
    if (faVerification.iterations.length) metrics.push(`iter ${faVerification.iterations.length}`);
    if (faVerification.passCount !== null) metrics.push(`pass ${faVerification.passCount}`);
    if (faVerification.failCount !== null) metrics.push(`fail ${faVerification.failCount}`);
    if (metrics.length) lines.push(metrics.join(" / "));
    if (faVerification.report) {
        lines.push("", "最終 judge report:", faVerification.report);
    }
    if (faVerification.finalSt) {
        lines.push("", "最終 ST:", "```st", faVerification.finalSt, "```");
    }
    return lines.join("\n");
}

function buildFaVerificationRequest(mode, result) {
    const sr = result?.serverResponse;
    if (mode !== "st") return null;
    if (!sr?.verificationPending || !sr?.faVerificationRequest) return null;
    if (typeof result?.reply !== "string" || !result.reply.trim()) return null;
    return {
        verificationId: sr.verificationId || `fa-${Date.now()}`,
        st: typeof sr.faVerificationRequest.st === "string" && sr.faVerificationRequest.st.trim()
            ? sr.faVerificationRequest.st
            : result.reply,
        expectedTrace: sr.faVerificationRequest.expectedTrace,
        plantConfig: sr.faVerificationRequest.plantConfig || {},
        promptContext: sr.faVerificationRequest.promptContext || {},
        options: sr.faVerificationRequest.options || {}
    };
}

async function startFaVerificationFollowUp(mode, result) {
    const request = buildFaVerificationRequest(mode, result);
    if (!request || !apiGenerationClient || typeof apiGenerationClient.postFaAgentVerify !== "function") {
        return false;
    }
    setChatStatus("L1検証中...");
    let accessToken = null;
    if (typeof apiGenerationClient.getAccessToken === "function") {
        try {
            accessToken = await apiGenerationClient.getAccessToken();
        } catch (error) {
            console.warn("[fa-agent] access token lookup failed before verify", error);
        }
    }
    try {
        const response = await apiGenerationClient.postFaAgentVerify({
            verificationId: request.verificationId,
            st: request.st,
            expectedTrace: request.expectedTrace,
            plantConfig: request.plantConfig,
            promptContext: request.promptContext,
            options: request.options
        }, { accessToken });
        if (response?.status === "queued" || response?.verificationPending === true) {
            const faVerification = normalizeFaVerificationState({
                status: "queued",
                verdict: "queued",
                verdictScope: response?.verdictScope || "L1"
            });
            appendChatMessage(
                "assistant",
                buildFaVerificationMessageContent(faVerification),
                "fa-verification",
                {
                    groupKey: `fa-verification:${request.verificationId}:queued`,
                    faVerification
                }
            );
            setChatStatus("L1検証キュー投入");
            void pollFaVerificationResult(request, accessToken);
            return true;
        }
        const repairedSt = typeof response?.finalSt === "string" ? response.finalSt.trim() : "";
        const originalSt = request.st.trim();
        const faVerification = normalizeFaVerificationState({
            verdict: response?.verdict || "FAIL",
            verdictScope: response?.verdictScope || "L1",
            iterations: response?.iterations,
            passCount: response?.passCount,
            failCount: response?.failCount,
            finalSt: repairedSt && repairedSt !== originalSt ? response.finalSt : ""
        });
        appendChatMessage(
            "assistant",
            buildFaVerificationMessageContent(faVerification),
            "fa-verification",
            {
                groupKey: `fa-verification:${request.verificationId}:final`,
                faVerification
            }
        );
        setChatStatus(faVerification.verdict === "PASS" ? "L1検証通過" : "L1検証差分あり");
    } catch (error) {
        const faVerification = normalizeFaVerificationState({
            status: "skipped",
            verdict: "skipped",
            verdictScope: "L1",
            reason: error?.code || error?.name || "verification_failed"
        });
        appendChatMessage(
            "assistant",
            buildFaVerificationMessageContent(faVerification),
            "fa-verification",
            {
                groupKey: `fa-verification:${request.verificationId}:skipped`,
                faVerification
            }
        );
        setChatStatus("L1検証スキップ");
    }
    return true;
}

async function pollFaVerificationResult(request, accessToken) {
    if (!request?.verificationId || !apiGenerationClient || typeof apiGenerationClient.getFaAgentVerifyStatus !== "function") {
        return false;
    }
    const maxAttempts = 10;
    const delayMs = 1000;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        const response = await apiGenerationClient.getFaAgentVerifyStatus(request.verificationId, { accessToken });
        if (response?.status === "queued" || response?.verificationPending === true) {
            continue;
        }
        const repairedSt = typeof response?.finalSt === "string" ? response.finalSt.trim() : "";
        const originalSt = typeof request.st === "string" ? request.st.trim() : "";
        const faVerification = normalizeFaVerificationState({
            verdict: response?.verdict || "FAIL",
            verdictScope: response?.verdictScope || "L1",
            iterations: response?.iterations,
            passCount: response?.passCount,
            failCount: response?.failCount,
            finalSt: repairedSt && repairedSt !== originalSt ? response.finalSt : ""
        });
        appendChatMessage(
            "assistant",
            buildFaVerificationMessageContent(faVerification),
            "fa-verification",
            {
                groupKey: `fa-verification:${request.verificationId}:final`,
                faVerification
            }
        );
        setChatStatus(faVerification.verdict === "PASS" ? "L1検証通過" : "L1検証差分あり");
        return true;
    }
    return false;
}

function setChatStatus(text) {
    state.chat.statusText = text;
    notifyAssistantUiChanged();
}

function renderChatShell() {
    const layout = document.getElementById("workspaceLayout");
    const panel = document.getElementById("assistantPanel");
    const shell = document.getElementById("chatShell");
    const settings = document.getElementById("chatSettingsPanel");
    const toggle = document.getElementById("btnAssistantToggle");
    if (layout) layout.classList.toggle("assistant-collapsed", !state.chat.uiOpen);
    if (panel) panel.classList.toggle("is-collapsed", !state.chat.uiOpen);
    if (shell) shell.classList.toggle("is-hidden", !state.chat.uiOpen);
    if (settings) settings.classList.toggle("is-open", state.chat.settingsOpen && state.chat.uiOpen);
    if (toggle) {
        const label = state.chat.uiOpen ? "生成アシスタントを折りたたむ" : "生成アシスタントを開く";
        toggle.innerHTML = '<span class="assistant-toggle-icon" aria-hidden="true"><svg class="assistant-toggle-icon-open" viewBox="0 0 20 20" focusable="false"><rect x="3" y="4" width="14" height="12" rx="2"></rect><path d="M12 4v12"></path></svg><svg class="assistant-toggle-icon-closed" viewBox="0 0 20 20" focusable="false"><rect x="3" y="4" width="14" height="12" rx="2"></rect><path d="M8 4v12"></path></svg></span>';
        toggle.title = label;
        toggle.setAttribute("aria-label", label);
        toggle.setAttribute("aria-expanded", String(state.chat.uiOpen));
        toggle.setAttribute("data-assistant-collapsed", String(!state.chat.uiOpen));
    }
    renderHeaderActions();
    if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(syncFlowEditorHeight);
    } else {
        setTimeout(syncFlowEditorHeight, 0);
    }
}

const COLLAPSIBLE_WORKLOG_META = new Set(["agent-action", "agent-observation", "fallback", "info"]);

function isCollapsibleWorklogMessage(message) {
    return COLLAPSIBLE_WORKLOG_META.has(String(message?.meta || ""));
}

function summarizeWorklogBlock(messages) {
    const actionCount = messages.filter((item) => item.meta === "agent-action").length;
    const observationCount = messages.filter((item) => item.meta === "agent-observation").length;
    const fallbackCount = messages.filter((item) => item.meta === "fallback").length;
    const infoCount = messages.filter((item) => item.meta === "info").length;
    if (actionCount > 0 && observationCount === 0 && fallbackCount === 0 && infoCount === 0) {
        return `${messages.length} 件の作業を実行`;
    }
    if (observationCount > 0 && actionCount === 0 && fallbackCount === 0 && infoCount === 0) {
        return `${messages.length} 件の観測ログ`;
    }
    return `${messages.length} 件の作業ログ`;
}

function buildChatRenderBlocks(messages) {
    const blocks = [];
    for (let index = 0; index < messages.length; index += 1) {
        const message = messages[index];
        if (!isCollapsibleWorklogMessage(message)) {
            blocks.push({ kind: "message", message, index });
            continue;
        }
        const rows = [message];
        const start = index;
        while (index + 1 < messages.length && isCollapsibleWorklogMessage(messages[index + 1])) {
            rows.push(messages[index + 1]);
            index += 1;
        }
        blocks.push({
            kind: "worklog",
            id: `worklog-${start}-${index}-${message.meta || "system"}`,
            messages: rows
        });
    }
    return blocks;
}

function ensureWorklogCollapsedState(groupId) {
    const threadId = state.chat.activeThreadId || "default";
    if (!state.chat.workLogCollapsed[threadId]) state.chat.workLogCollapsed[threadId] = {};
    if (!Object.prototype.hasOwnProperty.call(state.chat.workLogCollapsed[threadId], groupId)) {
        state.chat.workLogCollapsed[threadId][groupId] = true;
    }
    return state.chat.workLogCollapsed[threadId][groupId];
}

function buildQuestionCardOptions(question, actions = []) {
    const normalizedActions = Array.isArray(actions)
        ? actions.filter((action) => action?.kind === "plan-answer" || action?.kind === "agent-answer")
        : [];
    if (normalizedActions.length) return normalizedActions;
    if (Array.isArray(question?.options) && question.options.length) {
        return question.options.map((option) => ({
            kind: "agent-answer",
            questionId: question.id,
            value: option.value,
            label: option.label || option.value
        }));
    }
    if (Array.isArray(question?.quickOptions) && question.quickOptions.length) {
        return question.quickOptions.map((option) => ({
            kind: "agent-answer",
            questionId: question.id,
            value: option,
            label: option
        }));
    }
    return [];
}

function createQuestionCardPayload(question, questionUi) {
    if (!question) return null;
    return {
        id: question.id,
        kind: question.kind || "text",
        text: question.text || "",
        reason: buildPlanReasonText(question),
        placeholder: question.placeholder || "回答を入力",
        options: buildQuestionCardOptions(question, questionUi?.actions || [])
    };
}

function formatAgentSessionTimestamp(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("ja-JP", {
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    });
}

function toggleWorklogGroup(groupId) {
    const threadId = state.chat.activeThreadId || "default";
    if (!state.chat.workLogCollapsed[threadId]) state.chat.workLogCollapsed[threadId] = {};
    state.chat.workLogCollapsed[threadId][groupId] = !ensureWorklogCollapsedState(groupId);
    renderChatUi();
}

function getAssistantMessageBlocks() {
    return buildChatRenderBlocks(getActiveChatMessages()).map((block) => {
        if (block.kind === "worklog") {
            return {
                kind: "worklog",
                id: block.id,
                collapsed: ensureWorklogCollapsedState(block.id),
                summary: summarizeWorklogBlock(block.messages),
                countLabel: ensureWorklogCollapsedState(block.id) ? "表示" : "非表示",
                messages: block.messages.map((row) => ({
                    id: row.id,
                    role: row.role,
                    meta: row.meta,
                    ts: row.ts,
                    content: row.content
                }))
            };
        }
        return {
            kind: "message",
            message: {
                id: block.message.id,
                role: block.message.role,
                meta: block.message.meta,
                ts: block.message.ts,
                content: block.message.content,
                actions: Array.isArray(block.message.actions) ? block.message.actions : [],
                question: block.message.question || null,
                faVerification: block.message.faVerification || null,
                appliedSkills: Array.isArray(block.message.appliedSkills) ? block.message.appliedSkills : []
            }
        };
    });
}

function getAssistantTaskHistoryItems() {
    const activeSessionId = state.chat.agentSession?.remoteSessionId || state.chat.activeHistorySessionId || "";
    return state.chat.historyItems.map((item) => {
        const summaryParts = [];
        if (Number.isFinite(Number(item.questionCount))) summaryParts.push(`Q ${Number(item.questionCount)}`);
        if (Number.isFinite(Number(item.revisionCount))) summaryParts.push(`Rev ${Number(item.revisionCount)}`);
        const queueWaitText = formatAgentSessionDuration(item.queueWaitMs);
        const executionText = formatAgentSessionDuration(item.executionDurationMs);
        if (queueWaitText) summaryParts.push(`Queue ${queueWaitText}`);
        if (executionText) summaryParts.push(`Run ${executionText}`);
        const goalText = String(item.goal || "");
        const titleSource = goalText.split(/\r?\n/).map((line) => line.trim()).find((line) => line) || "";
        return {
            sessionId: item.sessionId,
            goal: titleSource ? buildFallbackThreadTitle(titleSource) : "(no goal)",
            goalFull: goalText,
            jobStatus: item.jobStatus || item.status || "",
            updatedLabel: formatAgentSessionTimestamp(item.updatedAt || item.createdAt || ""),
            mode: item.mode || "flow",
            summary: summaryParts.join(" / "),
            isActive: Boolean(activeSessionId && item.sessionId === activeSessionId)
        };
    });
}

function renderChatUi() {
    updateChatInputPlaceholder();
    renderChatShell();
    renderAssistantInsights();
    notifyAssistantUiChanged();
}

function updateChatInputPlaceholder() {
    const external = getAgentModuleFunction("session", "buildChatInputPlaceholder");
    state.chat.inputPlaceholder = typeof external === "function" && external !== updateChatInputPlaceholder
        ? external(getAgentSession(), "追加指示を入力")
        : "追加指示を入力";
}

// Mirrors src/generation/facade.js during the single-file migration phase.
const GENERATION_FACADE_KEYS = Object.freeze([
    "validateFlowContextContract",
    "validateGenerationIrContract",
    "buildFlowSummaryFromContext",
    "extractFlowNodesFromSnapshot",
    "extractFlowAxesFromProject",
    "extractFlowConditionsFromSnapshot",
    "extractFlowErrorsFromSnapshot",
    "buildFlowContextFromSources",
    "buildGenerationGraphIrFromContext",
    "buildGenerationIrFromContext",
    "buildAiPromptFromSnapshot",
    "buildLocalSummaryResponseFromSnapshot",
    "buildLocalReviewResponseFromSnapshot",
    "buildLocalStDraftFromSnapshot",
    "buildLocalStResponseFromDraft",
    "buildLocalStBlockedResponse",
    "getFlowContext",
    "getGenerationIr",
    "getGenerationSnapshot",
    "requestGeneration",
    "exportFlowContext",
    "exportGenerationIr"
]);

const GENERATION_RUNTIME_KEYS = Object.freeze([
    "getFlowContext",
    "getGenerationIr",
    "getGenerationSnapshot",
    "requestGeneration",
    "exportFlowContext",
    "exportGenerationIr"
]);

function validateGenerationKeySet(expectedKeys, target) {
    const expected = [...expectedKeys].sort();
    const actual = Object.keys(target || {}).sort();
    if (expected.length !== actual.length) return false;
    for (let i = 0; i < expected.length; i += 1) {
        if (expected[i] !== actual[i]) return false;
    }
    return true;
}

function createGenerationFacade(runtime = {}) {
    return {
        validateFlowContextContract,
        validateGenerationIrContract,
        buildFlowSummaryFromContext: buildFlowSummaryText,
        extractFlowNodesFromSnapshot,
        extractFlowAxesFromProject,
        extractFlowConditionsFromSnapshot,
        extractFlowErrorsFromSnapshot,
        buildFlowContextFromSources,
        buildGenerationGraphIrFromContext,
        buildGenerationIrFromContext,
        buildAiPromptFromSnapshot,
        buildLocalSummaryResponseFromSnapshot,
        buildLocalReviewResponseFromSnapshot,
        buildLocalStDraftFromSnapshot,
        buildLocalStResponseFromDraft,
        buildLocalStBlockedResponse,
        getFlowContext: runtime.getFlowContext,
        getGenerationIr: runtime.getGenerationIr,
        getGenerationSnapshot: runtime.getGenerationSnapshot,
        requestGeneration: runtime.requestGeneration,
        exportFlowContext: runtime.exportFlowContext,
        exportGenerationIr: runtime.exportGenerationIr
    };
}

function validateGenerationRuntimeKeys(runtime) {
    return validateGenerationKeySet(GENERATION_RUNTIME_KEYS, runtime);
}

function buildGenerationRuntimeBindings() {
    return {
        getFlowContext,
        getGenerationIr,
        getGenerationSnapshot,
        requestGeneration,
        exportFlowContext: exportFlowContextJson,
        exportGenerationIr: exportGenerationIrJson
    };
}

const generationRuntimeBindings = Object.freeze(
    buildGenerationRuntimeBindings()
);

if (!validateGenerationRuntimeKeys(generationRuntimeBindings)) {
    console.warn("generation runtime keys are out of sync with GENERATION_RUNTIME_KEYS");
}

function createGenerationRuntime() {
    return generationRuntimeBindings;
}


function createPreferredGenerationApi() {
    return Object.freeze(
        createGenerationFacade(createGenerationRuntime())
    );
}

// Phase 3 step #4: preferredGenerationApi must be reassignable so the ESM
// bootstrap at the end of this file can swap the facade body to
// src/generation/facade.js once the module has loaded. Until then (and as a
// graceful fallback if the swap fails), the HTML mirror facade is used.
let preferredGenerationApi = createPreferredGenerationApi();
let generationFacadeSource = "html-mirror";

function validateGenerationFacadeKeys(api) {
    return validateGenerationKeySet(GENERATION_FACADE_KEYS, api);
}

if (!validateGenerationFacadeKeys(getPreferredGenerationApi())) {
    console.warn("preferredGenerationApi keys are out of sync with GENERATION_FACADE_KEYS");
}

// Mirrors the export names of src/generation/index.js. Drift here means the
// HTML facade and the module facade have diverged; see
// docs/PHASE2_PREFERRED_API_SWITCH_PLAN.md for the swap plan.
const GENERATION_INDEX_EXPECTED_EXPORTS = Object.freeze([
    "FLOW_CONTEXT_VERSION",
    "GENERATION_IR_VERSION",
    "FLOW_PATCH_VERSION",
    "validateFlowContextContract",
    "validateGenerationIrContract",
    "validateFlowPatchContract",
    "buildFlowSummaryFromContext",
    "extractFlowNodesFromSnapshot",
    "extractFlowConnectionsFromSnapshot",
    "extractFlowAxesFromProject",
    "extractFlowConditionsFromSnapshot",
    "extractFlowErrorsFromSnapshot",
    "buildFlowContextFromSources",
    "buildGenerationGraphIrFromContext",
    "nextGenerationStepNumber",
    "parseStartConditionSpec",
    "buildConditionPlaceholder",
    "buildConditionIndexMap",
    "resolveMatchedCondition",
    "buildGenerationIrFromContext",
    "buildAiPromptFromSnapshot",
    "buildLocalSummaryResponseFromSnapshot",
    "buildLocalReviewResponseFromSnapshot",
    "buildLocalStBlockedResponse",
    "buildLocalStResponseFromDraft",
    "buildLocalStDraftFromSnapshot",
    "buildRawFlowIndex",
    "buildRawFlowIndexFromFlowContext",
    "getFlowNeighborhood",
    "getFlowNode",
    "searchFlowNodes",
    "GENERATION_FACADE_KEYS",
    "GENERATION_RUNTIME_KEYS",
    "validateGenerationRuntimeKeys",
    "createGenerationFacade"
]);

function validateGenerationFacadeKeysSubsetOf(expectedExports) {
    const exportSet = new Set(expectedExports);
    return GENERATION_FACADE_KEYS.every(key => {
        if (key === "getFlowContext" || key === "getGenerationIr"
            || key === "getGenerationSnapshot" || key === "requestGeneration"
            || key === "exportFlowContext" || key === "exportGenerationIr") {
            return true; // runtime-supplied keys live outside the module facade
        }
        return exportSet.has(key);
    });
}

if (!validateGenerationFacadeKeysSubsetOf(GENERATION_INDEX_EXPECTED_EXPORTS)) {
    console.warn("GENERATION_FACADE_KEYS contains a non-runtime key not exported by src/generation/index.js");
}

function getPreferredGenerationApi() {
    return preferredGenerationApi;
}

function renderAssistantInsights() {
    const snapshot = getPreferredGenerationApi().getGenerationSnapshot();
    const external = AssistantUiRuntime?.buildAssistantSettingsSummary;
    state.chat.settingsSummary = typeof external === "function"
        ? external(snapshot)
        : `${snapshot.assistant.plcProfileName || snapshot.assistant.dialectLabel} / FlowContext v${snapshot.flowContext.contractVersion} / IR v${snapshot.generationIr.contractVersion} / ${snapshot.generationIr.steps.length} steps`;
}

function cloneJsonValue(value) {
    return JSON.parse(JSON.stringify(value));
}

const AGENT_SESSION_POLL_INTERVAL_MS = 5000;
let agentSessionPollTimer = 0;
let agentSessionPollInFlight = false;
let agentSessionPollRequestSerial = 0;
let agentSessionPollInFlightSince = 0;

// Diagnostic logging for the "frozen on サーバーエージェント実行中" issue.
// Enable in the browser console with:  localStorage.setItem("agentPollDebug","1")
// (or window.__AGENT_POLL_DEBUG = true), then reload and reproduce. Disable with
// localStorage.removeItem("agentPollDebug").
function isAgentPollDebugOn() {
    try {
        return window.__AGENT_POLL_DEBUG === true || localStorage.getItem("agentPollDebug") === "1";
    } catch { return window.__AGENT_POLL_DEBUG === true; }
}
function agentPollLog(...args) {
    if (isAgentPollDebugOn()) console.info("[agent-poll]", ...args);
}

function clearAgentSessionPolling() {
    if (agentSessionPollTimer) {
        clearInterval(agentSessionPollTimer);
        agentSessionPollTimer = 0;
    }
    if (agentSessionPollInFlight) agentPollLog("clear: was in-flight, resetting lock");
    agentSessionPollInFlight = false;
    agentSessionPollInFlightSince = 0;
}

function shouldPollAgentSession(session = getAgentSession()) {
    return Boolean(
        session?.active &&
        session?.remoteSessionId &&
        session?.canPoll !== false &&
        apiAgentSessionClient &&
        typeof apiAgentSessionClient.getSession === "function"
    );
}

function resetPlanSession() {
    clearAgentSessionPolling();
    clearPersistedAgentSessionReference();
    resetQuestionCardState();
    assignChatAgentSession(createEmptyAgentSession());
    updateChatInputPlaceholder();
}

function getAgentSession() {
    return ensureChatAgentSession();
}

function clearPersistedAgentSessionReference() {
    const external = getAgentModuleFunction("session", "clearPersistedAgentSessionReference");
    if (typeof external === "function" && external !== clearPersistedAgentSessionReference) {
        return external(localStorage);
    }
    try {
        localStorage.removeItem(AGENT_SESSION_STORAGE_KEY);
    } catch (error) {
        console.warn("agent session ref clear failed", error);
    }
}

function persistAgentSessionReference(session = getAgentSession()) {
    const external = getAgentModuleFunction("session", "persistAgentSessionReference");
    if (typeof external === "function" && external !== persistAgentSessionReference) {
        return external(session, localStorage);
    }
    try {
        if (!session?.active || !session.remoteSessionId || session.isTerminalJobState) {
            clearPersistedAgentSessionReference();
            return;
        }
        localStorage.setItem(AGENT_SESSION_STORAGE_KEY, JSON.stringify({
            remoteSessionId: session.remoteSessionId,
            goal: session.goal || session.sourceText || "",
            mode: session.mode || "flow",
            updatedAt: session.updatedAt || new Date().toISOString()
        }));
    } catch (error) {
        console.warn("agent session ref save failed", error);
    }
}

function inferAgentSessionReferenceFromChatMessages() {
    const messages = getActiveChatMessages();
    for (let index = messages.length - 1; index >= 0; index -= 1) {
        const item = messages[index] || {};
        const sessionId = String(item.sessionId || "").trim()
            || String(item.content || "").match(/sessionId:\s*([0-9a-f-]{36})/i)?.[1]
            || "";
        if (sessionId) {
            return {
                remoteSessionId: sessionId,
                goal: "",
                mode: "st"
            };
        }
    }
    return null;
}

function touchAgentSession(session = getAgentSession(), options = {}) {
    if (!session) return;
    session.localUpdatedAt = new Date().toISOString();
    const preserveUpdatedAt = options.preserveUpdatedAt === true || session.stateAuthority === "server";
    if (!preserveUpdatedAt) {
        session.updatedAt = session.localUpdatedAt;
    }
    assignChatAgentSession(session);
    persistAgentSessionReference(session);
}

function buildAgentSessionMemoryPayload(session = getAgentSession()) {
    const memory = session?.memory || {};
    return {
        assumptions: Array.isArray(session?.assumptions) ? [...session.assumptions] : [],
        rejectedIdeas: Array.isArray(memory.rejectedIdeas) ? [...memory.rejectedIdeas] : [],
        confirmedFacts: Array.isArray(memory.confirmedFacts) ? [...memory.confirmedFacts] : [],
        warnings: Array.isArray(memory.warnings) ? [...memory.warnings] : []
    };
}

function pushAgentMemory(kind, value) {
    const session = getAgentSession();
    if (!session?.memory?.[kind] || !value) return;
    if (!session.memory[kind].includes(value)) session.memory[kind].push(value);
    touchAgentSession(session);
}

function pushPlanActivity(message, meta = "agent-action") {
    const session = getAgentSession();
    if (session?.active) session.activityLog.push(message);
    touchAgentSession(session, { preserveUpdatedAt: true });
    appendChatMessage("system", message, meta);
}

function setAgentStatus(status) {
    const session = getAgentSession();
    if (!session) return;
    session.status = status;
    syncAgentJobState(session);
    touchAgentSession(session);
}

function syncAgentJobState(session = getAgentSession()) {
    if (!session) return;
    const deriveJobStatus = getAgentModuleFunction("session", "deriveAgentJobStatus");
    const nextJobStatus = typeof deriveJobStatus === "function"
        ? deriveJobStatus(session.status)
        : (session.status === "asking" || session.status === "waiting_answer")
            ? "waiting_user"
            : (session.status === "ready" || session.status === "applied")
                ? "completed"
                : session.status === "failed"
                    ? "failed"
                    : session.status === "cancelled"
                        ? "cancelled"
                        : session.status === "idle"
                            ? "queued"
                            : "running";
    session.jobStatus = nextJobStatus;
    session.jobType = session.jobType || "flow_plan";
    session.canPoll = nextJobStatus !== "cancelled";
    session.isTerminalJobState = ["completed", "failed", "cancelled"].includes(nextJobStatus);
}

function updateChatStatusFromAgentJob(session = getAgentSession()) {
    if (!session?.active) return;
    const text = session.jobStatus === "queued"
        ? "エージェントジョブ待機中..."
        : session.jobStatus === "running"
            ? "サーバーエージェント実行中..."
            : session.jobStatus === "waiting_user"
                ? "エージェント回答待ち"
                : session.jobStatus === "completed"
                    ? "エージェントジョブ完了"
                    : session.jobStatus === "failed"
                        ? "エージェントジョブ失敗"
                        : session.jobStatus === "cancelled"
                        ? "エージェントジョブ停止"
                            : "";
    if (!text) return;
    const metrics = [];
    const queueWaitText = formatAgentSessionDuration(session.queueWaitMs);
    const executionText = formatAgentSessionDuration(session.executionDurationMs);
    if (queueWaitText) metrics.push(`queue ${queueWaitText}`);
    if (executionText) metrics.push(`run ${executionText}`);
    setChatStatus(metrics.length ? `${text} (${metrics.join(" / ")})` : text);
}

function formatAgentSessionDuration(value) {
    const ms = Number(value);
    if (!Number.isFinite(ms) || ms < 0) return "";
    if (ms < 1000) return `${Math.round(ms)}ms`;
    const sec = ms / 1000;
    if (sec < 60) return `${sec.toFixed(sec < 10 ? 1 : 0)}s`;
    const minutes = Math.floor(sec / 60);
    const remainSec = Math.round(sec % 60);
    return remainSec > 0 ? `${minutes}m ${remainSec}s` : `${minutes}m`;
}

function maybeDeliverAgentSessionOutput(session = getAgentSession(), modeOverride = "") {
    if (!session?.active) return false;
    const sessionMode = modeOverride || session.mode || "flow";
    if (sessionMode === "flow") return false;
    if (!session.isTerminalJobState) return false;
    const output = String(session.generatedCode || "").trim();
    if (!output) return false;
    const deliveredAt = session.deliveredAt || "";
    const updatedAt = session.updatedAt || "";
    if (deliveredAt && updatedAt && deliveredAt === updatedAt) return false;
    const sessionId = session.remoteSessionId || "";
    const groupKey = sessionId ? `agent-output:${sessionId}:${updatedAt || "latest"}` : "";
    if (
        (groupKey && hasChatMessage({ role: "assistant", groupKey }))
        || (sessionId && hasChatMessage({ role: "assistant", meta: sessionMode, sessionId }))
        || hasChatMessage({ role: "assistant", meta: sessionMode, content: output })
    ) {
        session.deliveredAt = updatedAt || new Date().toISOString();
        touchAgentSession(session);
        setChatStatus(session.jobStatus === "failed" ? "エージェントジョブ失敗" : "エージェントジョブ完了");
        return false;
    }
    state.chat.lastResult = output;
    appendChatMessage("assistant", output, sessionMode, { sessionId, groupKey });
    session.deliveredAt = updatedAt || new Date().toISOString();
    touchAgentSession(session);
    setChatStatus(session.jobStatus === "failed" ? "エージェントジョブ失敗" : "エージェントジョブ完了");
    return true;
}

async function pollRemoteAgentSessionNow(options = {}) {
    const session = getAgentSession();
    if (!shouldPollAgentSession(session)) {
        const stored = state.chat?.agentSession;
        agentPollLog("skip: shouldPoll=false", {
            active: session?.active,
            remoteSessionId: session?.remoteSessionId,
            canPoll: session?.canPoll,
            status: session?.status,
            jobStatus: session?.jobStatus,
            mode: session?.mode,
            keys: session && typeof session === "object" ? Object.keys(session).length : null,
            sameRefAsStored: session === stored,
            storedActive: stored?.active,
            storedRemoteSessionId: stored?.remoteSessionId,
            hasClient: Boolean(apiAgentSessionClient && typeof apiAgentSessionClient.getSession === "function")
        });
        return false;
    }
    if (agentSessionPollInFlight) {
        const stuckMs = agentSessionPollInFlightSince ? Date.now() - agentSessionPollInFlightSince : 0;
        // Watchdog: a hung request must not wedge the loop forever. If a poll has
        // been "in flight" far longer than any real request should take, release
        // the lock so the next tick can retry instead of returning early forever.
        if (stuckMs > AGENT_SESSION_POLL_INTERVAL_MS * 3) {
            agentPollLog("watchdog: releasing stuck in-flight lock after", stuckMs, "ms");
            agentSessionPollInFlight = false;
        } else {
            agentPollLog("skip: poll already in-flight for", stuckMs, "ms");
            return false;
        }
    }
    agentSessionPollInFlight = true;
    agentSessionPollInFlightSince = Date.now();
    const pollRequestSerial = ++agentSessionPollRequestSerial;
    agentPollLog("poll start", {
        serial: pollRequestSerial,
        sessionId: session.remoteSessionId,
        jobStatus: session.jobStatus,
        forceFull: options.forceFull === true,
        etag: session.remoteEtag || "",
        sinceRevision: session.serverRevisionCount ?? session.revisionCount
    });
    let accessToken = null;
    try {
        const getAccessToken = getAgentModuleFunction("runtime", "getApiClientAccessToken");
        accessToken = typeof getAccessToken === "function"
            ? await getAccessToken(apiAgentSessionClient)
            : null;
        const previousUpdatedAt = session.updatedAt || "";
        const previousJobStatus = session.jobStatus || "";
        const forceFull = options.forceFull === true;
        const remote = await apiAgentSessionClient.getSession(session.remoteSessionId, {
            accessToken,
            ifNoneMatch: forceFull ? "" : (session.remoteEtag || ""),
            view: forceFull ? "full" : "delta",
            sinceRevision: forceFull
                ? undefined
                : Number.isFinite(Number(session.serverRevisionCount))
                    ? Number(session.serverRevisionCount)
                    : Number.isFinite(Number(session.revisionCount))
                        ? Number(session.revisionCount)
                        : undefined
        });
        if (pollRequestSerial !== agentSessionPollRequestSerial) {
            agentPollLog("stale response dropped", { serial: pollRequestSerial, latest: agentSessionPollRequestSerial });
            return false;
        }
        agentPollLog("response", {
            serial: pollRequestSerial,
            notModified: Boolean(remote?.notModified),
            remoteJobStatus: remote?.jobStatus,
            remoteTerminal: remote?.isTerminalJobState,
            remoteRevision: remote?.revisionCount,
            generatedCodeLen: typeof remote?.generatedCode === "string" ? remote.generatedCode.length : null
        });
        const applyPollResult = getAgentModuleFunction("runtime", "applyRemoteAgentSessionPollResult");
        const pollResult = typeof applyPollResult === "function"
            ? applyPollResult(session, remote, {
                previousUpdatedAt,
                previousJobStatus,
                updateStatus: options.updateStatus
            })
            : (() => {
                if (remote?.notModified) {
                    if (typeof remote.etag === "string" && remote.etag) {
                        session.remoteEtag = remote.etag;
                    }
                    return {
                        changed: false,
                        shouldUpdateStatus: options.updateStatus !== false,
                        shouldRunFlowLoop: false,
                        shouldDeliverOutput: false,
                        shouldStopPolling: Boolean(session.isTerminalJobState || session.jobStatus === "waiting_user")
                    };
                }
                const applyRemoteState = getAgentModuleFunction("session", "applyRemoteAgentSessionState");
                if (typeof applyRemoteState === "function") {
                    applyRemoteState(session, remote, {
                        shouldAcceptRemoteAgentSessionState: getAgentModuleFunction("session", "shouldAcceptRemoteAgentSessionState")
                    });
                }
                syncAgentJobState(session);
                const changed = previousUpdatedAt !== (session.updatedAt || "") || previousJobStatus !== (session.jobStatus || "");
                const isFlowMode = (session.mode || "flow") === "flow";
                const hasGeneratedOutput = typeof session.generatedCode === "string" && session.generatedCode.trim().length > 0;
                return {
                    changed,
                    shouldUpdateStatus: changed || options.updateStatus !== false,
                    shouldRunFlowLoop: changed && isFlowMode,
                    shouldDeliverOutput: !isFlowMode && Boolean(session.isTerminalJobState && hasGeneratedOutput),
                    shouldStopPolling: Boolean(session.isTerminalJobState || session.jobStatus === "waiting_user")
                };
            })();
        agentPollLog("applied", {
            serial: pollRequestSerial,
            sessionJobStatus: session.jobStatus,
            sessionTerminal: session.isTerminalJobState,
            changed: pollResult.changed,
            shouldDeliverOutput: pollResult.shouldDeliverOutput,
            shouldRunFlowLoop: pollResult.shouldRunFlowLoop,
            shouldStopPolling: pollResult.shouldStopPolling
        });
        touchAgentSession(session, { preserveUpdatedAt: true });
        if (pollResult.shouldUpdateStatus) {
            updateChatStatusFromAgentJob(session);
        }
        if (pollResult.shouldRunFlowLoop) {
            runAgentLoopRuntime(session, { emitOverview: false, preferExistingState: true });
        } else if (pollResult.shouldDeliverOutput) {
            maybeDeliverAgentSessionOutput(session);
        }
        if (pollResult.shouldStopPolling) {
            clearAgentSessionPolling();
        }
        return pollResult.changed;
    } finally {
        agentSessionPollInFlight = false;
        agentSessionPollInFlightSince = 0;
        agentPollLog("poll end", { serial: pollRequestSerial });
    }
}

function ensureAgentSessionPolling(session = getAgentSession()) {
    if (!shouldPollAgentSession(session)) {
        agentPollLog("ensurePolling: not pollable, clearing", {
            active: session?.active,
            remoteSessionId: session?.remoteSessionId,
            canPoll: session?.canPoll
        });
        clearAgentSessionPolling();
        return;
    }
    clearAgentSessionPolling();
    if (session.isTerminalJobState || session.jobStatus === "waiting_user") {
        agentPollLog("ensurePolling: terminal/waiting, no interval", {
            jobStatus: session.jobStatus,
            terminal: session.isTerminalJobState
        });
        return;
    }
    agentPollLog("ensurePolling: starting interval", {
        sessionId: session.remoteSessionId,
        intervalMs: AGENT_SESSION_POLL_INTERVAL_MS
    });
    agentSessionPollTimer = setInterval(() => {
        pollRemoteAgentSessionNow({ updateStatus: false }).catch((error) => {
            console.warn("[phase4] agent session poll failed", error);
        });
    }, AGENT_SESSION_POLL_INTERVAL_MS);
    setTimeout(() => {
        pollRemoteAgentSessionNow({ updateStatus: true, forceFull: true }).catch((error) => {
            console.warn("[phase4] agent session immediate poll failed", error);
        });
    }, 0);
    setTimeout(() => {
        const current = getAgentSession();
        if (!current?.active || current.remoteSessionId !== session.remoteSessionId || current.isTerminalJobState) return;
        pollRemoteAgentSessionNow({ updateStatus: true, forceFull: true }).catch((error) => {
            console.warn("[phase4] agent session rescue poll failed", error);
        });
    }, AGENT_SESSION_POLL_INTERVAL_MS + 1000);
}

function buildAgentRevisionInstruction(session) {
    const external = getAgentModuleFunction("session", "buildAgentRevisionInstruction");
    if (typeof external === "function" && external !== buildAgentRevisionInstruction) {
        return external(session);
    }
    return "";
}

async function requestAgentFlowDraft(userInstruction, options = {}) {
    const stage = options.stage || "initial";
    const result = await getPreferredGenerationApi().requestGeneration("flow", userInstruction);
    if (!result.ok) {
        if (result.quotaExceeded) {
            return {
                ok: false,
                draft: null,
                source: "quota",
                error: result.error,
                usedFallback: false,
                fallbackReason: null
            };
        }
        throw result.error;
    }
    const draft = extractJsonObjectFromText(result.reply);
    return {
        ok: true,
        draft,
        source: result.usedFallback ? "local" : "api",
        stage,
        usedFallback: Boolean(result.usedFallback),
        fallbackReason: result.fallbackReason ?? null,
        serverResponse: result.serverResponse ?? null
    };
}

async function requestRemoteAgentSessionCreate(userInstruction, answers = {}) {
    if (!apiAgentSessionClient || typeof apiAgentSessionClient.createSession !== "function") return null;
    const facade = getPreferredGenerationApi();
    const flowContext = facade.getFlowContext();
    const generationIr = facade.getGenerationIr();
    const activeProfile = getActivePlcProfile();
    const effectiveInstruction = buildInstructionWithPlcProfile(userInstruction, activeProfile);
    const buildClientRequestId = getAgentModuleFunction("runtime", "createAgentClientRequestId");
    const clientRequestId = typeof buildClientRequestId === "function"
        ? buildClientRequestId(genId, Date.now)
        : (typeof genId === "function" ? genId() : `agent-${Date.now()}`);
    const activeSession = getAgentSession();
    const getAccessToken = getAgentModuleFunction("runtime", "getApiClientAccessToken");
    const accessToken = typeof getAccessToken === "function"
        ? await getAccessToken(apiAgentSessionClient)
        : null;
    const buildPayload = getAgentModuleFunction("runtime", "buildRemoteAgentSessionCreatePayload");
    const payload = typeof buildPayload === "function"
        ? buildPayload({
            facade: {
                getFlowContext: () => flowContext,
                getGenerationIr: () => generationIr
            },
            activeProfile,
            state,
            cloudProjectId,
            userInstruction,
            effectiveInstruction,
            clientRequestId,
            answers,
            activeSession,
            buildAgentSessionMemoryPayload
        })
        : {
            mode: "flow",
            flowContext,
            generationIr,
            vendor: activeProfile.vendorTarget || state.chat.dialect,
            projectId: cloudProjectId || undefined,
            userInstruction: effectiveInstruction,
            clientRequestId,
            answers,
            memory: buildAgentSessionMemoryPayload(activeSession)
        };
    return apiAgentSessionClient.createSession(payload, { accessToken });
}

async function requestRemoteAgentSessionAnswer(sessionId, userInstruction, answers = {}) {
    if (!sessionId || !apiAgentSessionClient || typeof apiAgentSessionClient.answerSession !== "function") return null;
    const activeSession = getAgentSession();
    const buildClientRequestId = getAgentModuleFunction("runtime", "createAgentClientRequestId");
    const clientRequestId = typeof buildClientRequestId === "function"
        ? buildClientRequestId(genId, Date.now)
        : (typeof genId === "function" ? genId() : `agent-${Date.now()}`);
    const buildPayload = getAgentModuleFunction("runtime", "buildRemoteAgentSessionAnswerPayload");
    const payload = typeof buildPayload === "function"
        ? buildPayload({
            activeSession,
            userInstruction,
            clientRequestId,
            answers,
            buildAgentSessionMemoryPayload
        })
        : {
            userInstruction,
            clientRequestId,
            expectedRevisionCount: Number.isFinite(Number(activeSession?.serverRevisionCount))
                ? Number(activeSession.serverRevisionCount)
                : Number.isFinite(Number(activeSession?.revisionCount))
                    ? Math.max(0, Number(activeSession.revisionCount) - 1)
                    : 0,
            expectedUpdatedAt:
                !Number.isFinite(Number(activeSession?.serverRevisionCount))
                && !Number.isFinite(Number(activeSession?.revisionCount))
                && typeof activeSession?.serverUpdatedAt === "string"
                && activeSession.serverUpdatedAt
                    ? activeSession.serverUpdatedAt
                    : (typeof activeSession?.updatedAt === "string" ? activeSession.updatedAt : ""),
            answers,
            memory: buildAgentSessionMemoryPayload(activeSession)
        };
    const getAccessToken = getAgentModuleFunction("runtime", "getApiClientAccessToken");
    const accessToken = typeof getAccessToken === "function"
        ? await getAccessToken(apiAgentSessionClient)
        : null;
    return apiAgentSessionClient.answerSession(sessionId, payload, { accessToken });
}

async function requestRemoteAgentJobCreate(mode, userInstruction = "") {
    if (!apiAgentSessionClient || typeof apiAgentSessionClient.createSession !== "function") return null;
    const facade = getPreferredGenerationApi();
    const flowContext = facade.getFlowContext();
    const generationIr = facade.getGenerationIr();
    const rawFlowIndex = buildRawFlowIndexForApi();
    const activeProfile = getActivePlcProfile();
    const effectiveInstruction = buildInstructionWithPlcProfile(userInstruction, activeProfile);
    const buildClientRequestId = getAgentModuleFunction("runtime", "createAgentClientRequestId");
    const clientRequestId = typeof buildClientRequestId === "function"
        ? buildClientRequestId(genId, Date.now)
        : (typeof genId === "function" ? genId() : `agent-${Date.now()}`);
    const getAccessToken = getAgentModuleFunction("runtime", "getApiClientAccessToken");
    const accessToken = typeof getAccessToken === "function"
        ? await getAccessToken(apiAgentSessionClient)
        : null;
    const buildPayload = getAgentModuleFunction("runtime", "buildRemoteAgentJobCreatePayload");
    const payload = typeof buildPayload === "function"
        ? buildPayload({
            facade: {
                getFlowContext: () => flowContext,
                getGenerationIr: () => generationIr
            },
            buildRawFlowIndexForApi: () => rawFlowIndex,
            activeProfile,
            state,
            cloudProjectId,
            mode,
            userInstruction,
            effectiveInstruction,
            clientRequestId
        })
        : {
            mode,
            flowContext,
            generationIr,
            rawFlowIndex,
            vendor: activeProfile.vendorTarget || state.chat.dialect,
            projectId: cloudProjectId || undefined,
            userInstruction: effectiveInstruction,
            clientRequestId
        };
    return apiAgentSessionClient.createSession(payload, { accessToken });
}

async function requestRemoteAgentSessionCancel(sessionId) {
    if (!sessionId || !apiAgentSessionClient || typeof apiAgentSessionClient.cancelSession !== "function") return null;
    const getAccessToken = getAgentModuleFunction("runtime", "getApiClientAccessToken");
    const accessToken = typeof getAccessToken === "function"
        ? await getAccessToken(apiAgentSessionClient)
        : null;
    return apiAgentSessionClient.cancelSession(sessionId, { accessToken });
}

async function requestRemoteAgentSessionHistory(limit = 10) {
    if (!apiAgentSessionClient || typeof apiAgentSessionClient.listSessions !== "function") return [];
    const getAccessToken = getAgentModuleFunction("runtime", "getApiClientAccessToken");
    const accessToken = typeof getAccessToken === "function"
        ? await getAccessToken(apiAgentSessionClient)
        : null;
    const result = await apiAgentSessionClient.listSessions({ accessToken, limit });
    return Array.isArray(result?.items) ? result.items : [];
}

async function resumeAgentSessionFromHistory(sessionId) {
    if (!sessionId || !apiAgentSessionClient || typeof apiAgentSessionClient.getSession !== "function") return false;
    if (state.chat.agentSession?.remoteSessionId === sessionId && state.chat.activeHistorySessionId === sessionId) {
        state.chat.taskHistoryOpen = true;
        renderChatUi();
        setChatStatus("同じセッションを表示中です");
        return true;
    }
    const getAccessToken = getAgentModuleFunction("runtime", "getApiClientAccessToken");
    const accessToken = typeof getAccessToken === "function"
        ? await getAccessToken(apiAgentSessionClient)
        : null;
    const remote = await apiAgentSessionClient.getSession(sessionId, { accessToken });
    const restoredSessionBuilder = getAgentModuleFunction("session", "createRestoredAgentSession");
    const session = typeof restoredSessionBuilder === "function"
        ? restoredSessionBuilder(remote, remote.goal || "")
        : null;
    if (!session) return false;
    const title = buildFallbackThreadTitle(remote.title || remote.goal || "再開したタスク");
    const thread = ensureActiveChatThread();
    thread.updatedAt = new Date().toISOString();
    thread.label = title;
    assignChatAgentSession(session);
    state.chat.activeHistorySessionId = remote.sessionId || sessionId;
    state.chat.taskHistoryOpen = true;
    syncAgentJobState(session);
    touchAgentSession(session, { preserveUpdatedAt: true });
    const resolvedSessionId = remote.sessionId || sessionId;
    const resumeGroupKey = `agent-resume:${resolvedSessionId}`;
    if (
        state.chat.lastResumeAnnouncementSessionId !== resolvedSessionId
        && !hasChatMessage({ role: "system", meta: "agent-resume", groupKey: resumeGroupKey })
        && !hasChatMessage({ role: "system", meta: "agent-resume", sessionId: resolvedSessionId })
    ) {
        state.chat.lastResumeAnnouncementSessionId = resolvedSessionId;
        appendChatMessage("system", `履歴からエージェントセッションを再開しました。sessionId: ${remote.sessionId}`, "agent-resume", {
            sessionId: resolvedSessionId,
            groupKey: resumeGroupKey
        });
    } else {
        renderChatUi();
    }
    if ((session.mode || "flow") === "flow") {
        runAgentLoopRuntime(session, { emitOverview: true, preferExistingState: true });
    } else {
        maybeDeliverAgentSessionOutput(session, session.mode);
    }
    updateChatStatusFromAgentJob(session);
    ensureAgentSessionPolling(session);
    return true;
}

async function showAgentSessionHistory(limit = 10) {
    state.chat.taskHistoryOpen = true;
    state.chat.historyLoading = true;
    renderChatUi();
    try {
        const items = await requestRemoteAgentSessionHistory(limit);
        state.chat.historyItems = items;
        state.chat.historyLoading = false;
        renderChatUi();
        setChatStatus(items.length ? "履歴を取得しました" : "履歴なし");
        return items.length > 0;
    } catch (error) {
        state.chat.historyLoading = false;
        state.chat.historyItems = [];
        renderChatUi();
        throw error;
    }
}

function buildAssumptionsFromAnswers(answers = {}) {
    const external = getAgentModuleFunction("session", "buildAssumptionsFromAnswers");
    if (typeof external === "function" && external !== buildAssumptionsFromAnswers) {
        return external(answers);
    }
    return [];
}

async function restoreAgentSessionFromStorage() {
    if (!apiAgentSessionClient || typeof apiAgentSessionClient.getSession !== "function") return false;
    const currentSession = getAgentSession();
    if (currentSession?.active) {
        syncAgentJobState(currentSession);
        if ((currentSession.mode || "flow") !== "flow") {
            maybeDeliverAgentSessionOutput(currentSession, currentSession.mode);
        }
        if (!currentSession.isTerminalJobState && currentSession.remoteSessionId) {
            ensureAgentSessionPolling(currentSession);
            await pollRemoteAgentSessionNow({ updateStatus: true });
        } else {
            updateChatStatusFromAgentJob(currentSession);
        }
        return true;
    }
    const readPersistedReference = getAgentModuleFunction("session", "readPersistedAgentSessionReference");
    let parsed = typeof readPersistedReference === "function"
        ? readPersistedReference(localStorage)
        : (() => {
            try {
                const raw = localStorage.getItem(AGENT_SESSION_STORAGE_KEY) || "";
                return raw ? JSON.parse(raw) : null;
            } catch (error) {
                console.warn("agent session ref load failed", error);
                return null;
            }
        })();
    if (!parsed?.remoteSessionId) {
        parsed = inferAgentSessionReferenceFromChatMessages();
    }
    if (!parsed?.remoteSessionId) {
        clearPersistedAgentSessionReference();
        return false;
    }
    const getAccessToken = getAgentModuleFunction("runtime", "getApiClientAccessToken");
    const accessToken = typeof getAccessToken === "function"
        ? await getAccessToken(apiAgentSessionClient)
        : null;
    let remote = null;
    try {
        remote = await apiAgentSessionClient.getSession(parsed.remoteSessionId, { accessToken });
    } catch (error) {
        if (error?.status === 404) clearPersistedAgentSessionReference();
        throw error;
    }
    if (!remote?.sessionId) {
        clearPersistedAgentSessionReference();
        return false;
    }
    const restoredSessionBuilder = getAgentModuleFunction("session", "createRestoredAgentSession");
    const session = typeof restoredSessionBuilder === "function"
        ? restoredSessionBuilder(remote, parsed.goal || "")
        : null;
    if (!session) {
        clearPersistedAgentSessionReference();
        return false;
    }
    assignChatAgentSession(session);
    state.chat.activeHistorySessionId = remote.sessionId || "";
    syncAgentJobState(session);
    touchAgentSession(session, { preserveUpdatedAt: true });
    const resolvedSessionId = remote.sessionId || "";
    const resumeGroupKey = resolvedSessionId ? `agent-resume:${resolvedSessionId}` : "";
    if (
        resolvedSessionId
        && state.chat.lastResumeAnnouncementSessionId !== resolvedSessionId
        && !hasChatMessage({ role: "system", meta: "agent-resume", groupKey: resumeGroupKey })
        && !hasChatMessage({ role: "system", meta: "agent-resume", sessionId: resolvedSessionId })
    ) {
        state.chat.lastResumeAnnouncementSessionId = resolvedSessionId;
        appendChatMessage("system", `前回のエージェントセッションを再開しました。sessionId: ${remote.sessionId}`, "agent-resume", {
            sessionId: resolvedSessionId,
            groupKey: resumeGroupKey
        });
    } else {
        state.chat.lastResumeAnnouncementSessionId = resolvedSessionId;
        renderChatUi();
    }
    if ((session.mode || "flow") === "flow") {
        runAgentLoopRuntime(session, { emitOverview: true });
    } else {
        maybeDeliverAgentSessionOutput(session, session.mode);
    }
    updateChatStatusFromAgentJob(session);
    ensureAgentSessionPolling(session);
    return true;
}

if (typeof window !== "undefined") {
    window.restoreAgentSessionFromStorage = restoreAgentSessionFromStorage;
}

function getCurrentPlanQuestion() {
    const external = getAgentModuleFunction("session", "getCurrentPlanQuestion");
    if (typeof external === "function" && external !== getCurrentPlanQuestion) {
        return external(getAgentSession());
    }
    return null;
}

function detectPlanCreationIntent(text) {
    return AssistantClassifyModule.detectPlanCreationIntent(text);
}

function detectFlowCreationIntent(text) {
    return AssistantClassifyModule.detectFlowCreationIntent(text);
}

function isQuestionLikeText(text) {
    return AssistantClassifyModule.isQuestionLikeText(text);
}

function detectReviewIntent(text) {
    return AssistantClassifyModule.detectReviewIntent(text);
}

function detectSettingsHelpIntent(text) {
    return AssistantClassifyModule.detectSettingsHelpIntent(text);
}

function classifyAssistantIntent(mode, text) {
    return AssistantClassifyModule.classifyAssistantIntent(mode, text);
}

function extractQuestionLookupTokens(text) {
    const value = String(text || "");
    const tokens = new Set();
    const addressMatches = value.match(/%[IQM][XWD][0-9]+(?:\.[0-9]+)?/gi) || [];
    for (const token of addressMatches) tokens.add(token.toUpperCase());
    const identMatches = value.match(/[A-Za-z_][A-Za-z0-9_]*/g) || [];
    for (const token of identMatches) {
        if (token.length >= 2) tokens.add(token.toUpperCase());
    }
    return [...tokens];
}

function lookupAssistantQuestionContext(userMessage = "", snapshot = null) {
    const value = String(userMessage || "").trim();
    const currentSnapshot = snapshot || getPreferredGenerationApi().getGenerationSnapshot();
    const tokens = extractQuestionLookupTokens(value);
    const signalMap = new Map();
    const projectSignals = Array.isArray(state.project?.signals) ? state.project.signals : [];
    const ioTable = Array.isArray(currentSnapshot?.generationIr?.ioTable) ? currentSnapshot.generationIr.ioTable : [];
    const irSignals = Array.isArray(currentSnapshot?.generationIr?.signals) ? currentSnapshot.generationIr.signals : [];
    const flowNodes = Array.isArray(currentSnapshot?.flowContext?.flowNodes) ? currentSnapshot.flowContext.flowNodes : [];
    const keyOf = (entry = {}) => entry.id || entry.signalId || entry.iecName || entry.name || entry.signalName || entry.address || "";
    const ensureSignalEntry = (seed = {}) => {
        const key = keyOf(seed);
        if (!key) return null;
        const existing = signalMap.get(key) || {
            key,
            id: seed.id || seed.signalId || "",
            name: seed.name || seed.signalName || "",
            iecName: seed.iecName || "",
            address: seed.address || "",
            direction: seed.direction || "",
            dataType: seed.dataType || "",
            description: seed.description || "",
            usages: [],
            referencedBy: [],
            flowNodes: []
        };
        signalMap.set(key, existing);
        return existing;
    };
    for (const sig of projectSignals) {
        const entry = ensureSignalEntry(sig);
        if (!entry) continue;
        entry.id = entry.id || sig.id || "";
        entry.name = entry.name || sig.name || "";
        entry.iecName = entry.iecName || sig.iecName || "";
        entry.address = entry.address || sig.address || "";
        entry.direction = entry.direction || sig.direction || "";
        entry.dataType = entry.dataType || sig.dataType || "";
        entry.description = entry.description || sig.description || "";
    }
    for (const sig of irSignals) {
        const entry = ensureSignalEntry(sig);
        if (!entry) continue;
        entry.id = entry.id || sig.id || "";
        entry.name = entry.name || sig.name || "";
        entry.iecName = entry.iecName || sig.iecName || "";
        entry.address = entry.address || sig.address || "";
        entry.direction = entry.direction || sig.direction || "";
        entry.dataType = entry.dataType || sig.dataType || "";
        entry.description = entry.description || sig.description || "";
    }
    for (const row of ioTable) {
        const entry = ensureSignalEntry(row);
        if (!entry) continue;
        entry.id = entry.id || row.signalId || "";
        entry.name = entry.name || row.signalName || "";
        entry.address = entry.address || row.address || "";
        entry.direction = entry.direction || row.direction || "";
        entry.dataType = entry.dataType || row.dataType || "";
        if (Array.isArray(row.usages)) entry.usages = [...new Set([...entry.usages, ...row.usages.filter(Boolean)])];
        if (Array.isArray(row.referencedBy)) entry.referencedBy = row.referencedBy.slice(0, 5);
    }
    for (const node of flowNodes) {
        const signalName = String(node?.signalName || "").trim();
        if (!signalName) continue;
        const entry = [...signalMap.values()].find((item) =>
            item.name === signalName || item.iecName === signalName || item.id === signalName
        ) || ensureSignalEntry({ signalName });
        if (!entry) continue;
        entry.flowNodes.push({
            id: node.id || "",
            type: node.type || "",
            label: node.label || node.name || signalName
        });
    }
    const matches = [...signalMap.values()].map((entry) => {
        const haystack = [
            entry.id,
            entry.name,
            entry.iecName,
            entry.address,
            entry.description
        ].join(" ").toUpperCase();
        const score = tokens.reduce((sum, token) => sum + (haystack.includes(token) ? 1 : 0), 0);
        return { ...entry, score };
    }).filter((entry) => entry.score > 0).sort((a, b) => b.score - a.score).slice(0, 5);
    const lines = [];
    if (matches.length > 0) {
        lines.push("信号検索結果:");
        for (const entry of matches) {
            const fields = [
                entry.name || entry.id || entry.iecName || "(unnamed)",
                entry.iecName ? `iecName=${entry.iecName}` : "",
                entry.direction ? `direction=${entry.direction}` : "",
                entry.dataType ? `dataType=${entry.dataType}` : "",
                entry.address ? `address=${entry.address}` : "",
                entry.description ? `description=${entry.description}` : ""
            ].filter(Boolean);
            lines.push(`- ${fields.join(" / ")}`);
            if (entry.usages.length) lines.push(`  usages: ${entry.usages.join(", ")}`);
            if (entry.referencedBy.length) {
                lines.push(`  referencedBy: ${entry.referencedBy.map((ref) => `step ${ref.step} ${ref.usage}${ref.label ? ` (${ref.label})` : ""}`).join(", ")}`);
            }
            if (entry.flowNodes.length) {
                lines.push(`  flowNodes: ${entry.flowNodes.map((node) => `${node.type || "node"}:${node.label}`).join(", ")}`);
            }
        }
    }
    return {
        tokens,
        matches,
        text: lines.join("\n")
    };
}

function buildQuestionGenerationInstruction(userMessage = "", snapshot = null) {
    const lookup = lookupAssistantQuestionContext(userMessage, snapshot);
    return {
        lookup,
        instruction: lookup.text
            ? `${String(userMessage || "").trim()}\n\n[ローカル検索結果]\n${lookup.text}`
            : String(userMessage || "").trim()
    };
}

function buildLocalQuestionResponse(userMessage = "", snapshot = null, questionContext = "") {
    const lookup = questionContext || lookupAssistantQuestionContext(userMessage, snapshot).text;
    if (lookup) {
        return [
            "AI に接続できなかったため、ローカル検索結果をそのまま返します。",
            "",
            lookup
        ].join("\n");
    }
    return [
        "AI に接続できなかったため、ローカル検索だけでは十分な根拠を見つけられませんでした。",
        "",
        "質問対象の信号名、アドレス、またはノード名をもう少し具体的に入れると検索精度が上がります。"
    ].join("\n");
}

function extractAxisNameFromText(text) {
    return AssistantClassifyModule.extractAxisNameFromText(text);
}

function detectOffBranchFromText(text) {
    return AssistantClassifyModule.detectOffBranchFromText(text);
}

function detectAfterMotionFromText(text) {
    return AssistantClassifyModule.detectAfterMotionFromText(text);
}

function extractTimeoutSecFromText(text) {
    return AssistantClassifyModule.extractTimeoutSecFromText(text);
}

function extractErrorCodeFromText(text) {
    return AssistantClassifyModule.extractErrorCodeFromText(text);
}

function extractJsonObjectFromText(text) {
    return AssistantClassifyModule.extractJsonObjectFromText(text);
}


function buildLocalFlowDraft(userMessage = "") {
    const external = getAgentModuleFunction("draft", "buildLocalFlowDraft");
    if (typeof external === "function" && external !== buildLocalFlowDraft) {
        return external(userMessage);
    }
    return null;
}

function removeDraftNodeById(draft, nodeId) {
    const external = getAgentModuleFunction("draft", "removeDraftNodeById");
    if (typeof external === "function" && external !== removeDraftNodeById) {
        return external(draft, nodeId);
    }
    return undefined;
}

function upsertDraftNode(draft, nextNode) {
    const external = getAgentModuleFunction("draft", "upsertDraftNode");
    if (typeof external === "function" && external !== upsertDraftNode) {
        return external(draft, nextNode);
    }
    return undefined;
}

function removeDraftConnections(draft, predicate) {
    const external = getAgentModuleFunction("draft", "removeDraftConnections");
    if (typeof external === "function" && external !== removeDraftConnections) {
        return external(draft, predicate);
    }
    return undefined;
}

function upsertDraftConnection(draft, nextConnection) {
    const external = getAgentModuleFunction("draft", "upsertDraftConnection");
    if (typeof external === "function" && external !== upsertDraftConnection) {
        return external(draft, nextConnection);
    }
    return undefined;
}

function findDraftAxis(draft, axisIndex = 0) {
    const external = getAgentModuleFunction("draft", "findDraftAxis");
    if (typeof external === "function" && external !== findDraftAxis) {
        return external(draft, axisIndex);
    }
    return null;
}

function findDraftMotionById(draft, motionId) {
    const external = getAgentModuleFunction("draft", "findDraftMotionById");
    if (typeof external === "function" && external !== findDraftMotionById) {
        return external(draft, motionId);
    }
    return null;
}

function createAgentObservation(code, severity, message, options = {}) {
    const external = getAgentModuleFunction("planning", "createAgentObservation");
    if (typeof external === "function" && external !== createAgentObservation) {
        return external(code, severity, message, options);
    }
    return {
        code,
        severity,
        path: options.path || "",
        message,
        questionId: options.questionId || "",
        suggestedAction: options.suggestedAction || ""
    };
}

function hasDraftConnection(draft, predicate) {
    const external = getAgentModuleFunction("draft", "hasDraftConnection");
    if (typeof external === "function" && external !== hasDraftConnection) {
        return external(draft, predicate);
    }
    return false;
}

function findDraftConditionNode(draft) {
    const external = getAgentModuleFunction("draft", "findDraftConditionNode");
    if (typeof external === "function" && external !== findDraftConditionNode) {
        return external(draft);
    }
    return null;
}

function buildAgentPlanSteps() {
    const external = getAgentModuleFunction("session", "buildAgentPlanSteps");
    if (typeof external === "function" && external !== buildAgentPlanSteps) {
        return external();
    }
    return [];
}

function buildAgentObservations(goalText, draft, answers = {}) {
    const external = getAgentModuleFunction("planning", "buildAgentObservations");
    if (typeof external === "function" && external !== buildAgentObservations) {
        return external(goalText, draft, answers, {
            findDraftConditionNode,
            hasDraftConnection,
            findDraftMotionById,
            extractAxisNameFromText,
            detectOffBranchFromText,
            detectAfterMotionFromText,
            extractTimeoutSecFromText,
            extractErrorCodeFromText
        });
    }
    return [];
}

function buildAgentQuestionsFromObservations(observations) {
    const external = getAgentModuleFunction("planning", "buildAgentQuestionsFromObservations");
    if (typeof external === "function" && external !== buildAgentQuestionsFromObservations) {
        return external(observations);
    }
    return [];
}

function validateAgentDraft(draft) {
    const external = getAgentModuleFunction("planning", "validateAgentDraft");
    if (typeof external === "function" && external !== validateAgentDraft) {
        return external(draft, { findDraftMotionById });
    }
    return [];
}

function applyPlanAnswerToDraft(draft, questionId, rawValue) {
    const external = getAgentModuleFunction("draft", "applyPlanAnswerToDraft");
    if (typeof external === "function" && external !== applyPlanAnswerToDraft) {
        return external(draft, questionId, rawValue, { extractErrorCodeFromText });
    }
    return draft;
}

function applyAnswersToDraft(draft, answers = {}) {
    const external = getAgentModuleFunction("draft", "applyAnswersToDraft");
    if (typeof external === "function" && external !== applyAnswersToDraft) {
        return external(draft, answers, { extractErrorCodeFromText });
    }
    return draft;
}

function ensureTimeoutErrorBranch(draft, errorCode) {
    const external = getAgentModuleFunction("draft", "ensureTimeoutErrorBranch");
    if (typeof external === "function" && external !== ensureTimeoutErrorBranch) {
        return external(draft, errorCode);
    }
    return undefined;
}

function applyOffBranchToDraft(draft, branchValue) {
    const external = getAgentModuleFunction("draft", "applyOffBranchToDraft");
    if (typeof external === "function" && external !== applyOffBranchToDraft) {
        return external(draft, branchValue);
    }
    return undefined;
}

function applyAfterMotionPlanToDraft(draft, nextStep) {
    const external = getAgentModuleFunction("draft", "applyAfterMotionPlanToDraft");
    if (typeof external === "function" && external !== applyAfterMotionPlanToDraft) {
        return external(draft, nextStep);
    }
    return undefined;
}

function buildFlowPlanOverview(session) {
    const external = getAgentModuleFunction("session", "buildFlowPlanOverview");
    if (typeof external === "function" && external !== buildFlowPlanOverview) {
        return external(session);
    }
    return "";
}

function buildPlanReasonText(question) {
    const external = getAgentModuleFunction("session", "buildAgentQuestionReasonText");
    if (typeof external === "function" && external !== buildPlanReasonText) {
        return external(question);
    }
    return "";
}

function presentCurrentPlanQuestion() {
    return AssistantUiRuntime.presentCurrentPlanQuestion({
        session: getAgentSession(),
        question: getCurrentPlanQuestion(),
        resetQuestionCardState,
        buildQuestionUi: getAgentModuleFunction("session", "buildAgentQuestionUi"),
        buildPlanReasonText,
        setAgentStatus,
        appendChatMessage,
        createQuestionCardPayload,
        setChatStatus,
        touchAgentSession
    });
}

function decideNextAgentAction(session) {
    const external = getAgentModuleFunction("planning", "decideNextAgentAction");
    if (typeof external === "function" && external !== decideNextAgentAction) {
        return external(session);
    }
    return "validate";
}


function finalizeAgentSessionUi(session, options = {}) {
    const finalizeResult = options.skipValidation
        ? {
            session,
            status: session?.status === "failed" ? "failed" : "ready",
            validationFindings: session?.observations || []
        }
        : getAgentModule("runtime")?.finalizeAgentSessionDraft?.(session, {
            validateAgentDraft
        }) || { session, status: "ready", validationFindings: [] };
    const activeSession = finalizeResult.session || session;
    const finalizeUiBuilder = getAgentModuleFunction("session", "buildAgentFinalizeUi");
    const finalizeUi = typeof finalizeUiBuilder === "function"
        ? finalizeUiBuilder(finalizeResult)
        : {
            agentStatus: finalizeResult.status === "failed" ? "failed" : "ready",
            chatStatus: finalizeResult.status === "failed" ? "Validation needs attention" : "Plan ready",
            message: "",
            meta: finalizeResult.status === "failed" ? "agent-error" : "agent-ready",
            activities: [],
            warnings: [],
            actions: []
        };

    if (finalizeResult.status !== "failed") {
        state.chat.lastResult = JSON.stringify(activeSession.draft, null, 2);
    }
    for (const activity of finalizeUi.activities || []) {
        pushPlanActivity(activity.message, activity.meta || "agent-observation");
    }
    for (const warning of finalizeUi.warnings || []) {
        pushAgentMemory("warnings", warning);
    }
    setAgentStatus(finalizeUi.agentStatus || (finalizeResult.status === "failed" ? "failed" : "ready"));
    if (finalizeUi.message) {
        appendChatMessage("assistant", finalizeUi.message, finalizeUi.meta || (finalizeResult.status === "failed" ? "agent-error" : "agent-ready"), finalizeUi.actions?.length ? {
            actions: finalizeUi.actions
        } : undefined);
    }
    setChatStatus(finalizeUi.chatStatus || (finalizeResult.status === "failed" ? "Validation needs attention" : "Plan ready"));
    touchAgentSession(activeSession);
}

function runAgentLoopRuntime(session, options = {}) {
    if (!session?.active) return;
    const shouldPreferRemoteAgentState = getAgentModuleFunction("runtime", "shouldPreferRemoteAgentState");
    const preferExistingState = typeof shouldPreferRemoteAgentState === "function"
        ? shouldPreferRemoteAgentState(session, options)
        : options.preferExistingState === true || session?.stateAuthority === "server";
    const deriveNextActionFromSessionState = getAgentModuleFunction("runtime", "deriveNextActionFromSessionState");
    const advanceResult = preferExistingState
        ? { session, nextAction: typeof deriveNextActionFromSessionState === "function" ? deriveNextActionFromSessionState(session) : decideNextAgentAction(session) }
        : getAgentModule("runtime")?.advanceAgentSession?.(session, {
            buildAgentObservations,
            buildAgentQuestionsFromObservations,
            decideNextAgentAction
        }) || { session, nextAction: "validate" };
    const activeSession = advanceResult.session || session;
    const nextAction = advanceResult.nextAction || "validate";

    touchAgentSession(activeSession);
    setAgentStatus(activeSession.status || "observing");
    const loopUiBuilder = getAgentModuleFunction("session", "buildAgentLoopUi");
    const loopUi = typeof loopUiBuilder === "function"
        ? loopUiBuilder(activeSession, nextAction, options)
        : {
            overviewText: "",
            agentStatus: activeSession.status || "observing",
            shouldPresentQuestion: nextAction === "ask_user",
            shouldFinalize: nextAction !== "ask_user" && nextAction !== "failed" && nextAction !== "wait",
            activities: [],
            warnings: [],
            message: "",
            meta: nextAction === "failed" ? "agent-error" : "",
            chatStatus: nextAction === "failed" ? "Need corrections" : ""
        };

    if (loopUi.overviewText) {
        appendChatMessage("assistant", loopUi.overviewText, "agent-plan");
    }
    for (const activity of loopUi.activities || []) {
        pushPlanActivity(activity.message, activity.meta || "agent-observation");
    }
    for (const warning of loopUi.warnings || []) {
        pushAgentMemory("warnings", warning);
    }
    setAgentStatus(loopUi.agentStatus || activeSession.status || "observing");
    if (loopUi.chatStatus && nextAction !== "failed") {
        setChatStatus(loopUi.chatStatus);
    }

    if (nextAction === "failed") {
        if (loopUi.message) {
            appendChatMessage("assistant", loopUi.message, loopUi.meta || "agent-error");
        }
        setChatStatus(loopUi.chatStatus || "Need corrections");
        touchAgentSession(activeSession);
        return;
    }

    if (loopUi.shouldPresentQuestion) {
        presentCurrentPlanQuestion();
        touchAgentSession(activeSession);
        return;
    }

    if (nextAction === "ready" && preferExistingState) {
        finalizeAgentSessionUi(activeSession, { skipValidation: true });
        return;
    }

    if (loopUi.shouldFinalize) {
        finalizeAgentSessionUi(activeSession);
    }
}

async function refreshAgentDraftFromSources(session, instruction, options = {}) {
    const external = getAgentModuleFunction("runtime", "refreshAgentDraftFromSources");
    if (typeof external === "function" && external !== refreshAgentDraftFromSources) {
        return external(session, instruction, {
            requestRemoteAgentSessionCreate,
            requestRemoteAgentSessionAnswer,
            requestAgentFlowDraft,
            cloneJsonValue,
            applyAnswersToDraft,
            applyRemoteAgentSessionState: getAgentModuleFunction("session", "applyRemoteAgentSessionState"),
            pushPlanActivity
        }, options);
    }
    return {
        source: "noop",
        usedServerState: false
    };
}

async function startFlowPlanSession(userMessage = "") {
    const planningSessionBuilder = getAgentModuleFunction("session", "createPlanningAgentSession");
    const session = typeof planningSessionBuilder === "function"
        ? planningSessionBuilder(userMessage)
        : createEmptyAgentSession();
    assignChatAgentSession(session);
    syncAgentJobState(session);
    touchAgentSession(session, { preserveUpdatedAt: true });
    const prepareInitialDraft = getAgentModuleFunction("session", "prepareAgentSessionForInitialDraft");
    const initialConfigBuilder = getAgentModuleFunction("session", "buildAgentDraftRefreshConfig");
    const initialPreparation = typeof prepareInitialDraft === "function"
        ? prepareInitialDraft(session)
        : {
            session,
            agentStatus: "acting",
            activities: []
        };
    setAgentStatus(initialPreparation.agentStatus || "acting");
    for (const activity of initialPreparation.activities || []) {
        pushPlanActivity(activity.message, activity.meta || "agent-action");
    }
    const initialRefreshConfig = typeof initialConfigBuilder === "function"
        ? initialConfigBuilder("initial", Boolean(apiGenerationClient))
        : {
            chatStatus: apiGenerationClient ? "Requesting initial flow draft from AI..." : "Using local initial draft..."
        };
    setChatStatus(initialRefreshConfig.chatStatus);
    await refreshAgentDraftFromSources(session, userMessage || "Create flow plan", {
        stage: "initial",
        ...initialRefreshConfig
    });

    runAgentLoopRuntime(session, { emitOverview: true });
    ensureAgentSessionPolling(session);
}

async function answerPlanQuestion(questionId, rawValue, answerLabel = "") {
    return AssistantUiRuntime.answerPlanQuestion({
        questionId,
        rawValue,
        answerLabel,
        chat: state.chat,
        session: getAgentSession(),
        question: getCurrentPlanQuestion(),
        hasApiGenerationClient: Boolean(apiGenerationClient),
        renderChatUi,
        setChatStatus,
        applyAnswerToSession: getAgentModuleFunction("session", "applyAgentAnswerToSession"),
        applyPlanAnswerToDraft,
        syncAgentJobState,
        buildAnswerUi: getAgentModuleFunction("session", "buildAgentAnswerUiUpdate"),
        setAgentStatus,
        appendChatMessage,
        pushPlanActivity,
        pushAgentMemory,
        buildAgentRevisionInstruction,
        refreshAgentDraftFromSources,
        touchAgentSession,
        getLatestQuestion: getCurrentPlanQuestion,
        runAgentLoopRuntime,
        ensureAgentSessionPolling
    });
}

async function submitPlanAnswerFromInput(text) {
    return AssistantUiRuntime.submitPlanAnswerFromInput({
        text,
        chat: state.chat,
        question: getCurrentPlanQuestion(),
        answerPlanQuestion
    });
}

function applyPlanDraftNow() {
    const session = getAgentSession();
    if (!session?.active || !session.draft || session.status !== "ready") return;
    setAgentStatus("acting");
    pushPlanActivity("Action: Applying the plan to Drawflow.", "agent-action");
    const applied = applyFlowDraftToEditor(session.draft, session.sourceText);
    setAgentStatus("applied");
    appendChatMessage("assistant", `${applied.summary}
Nodes: ${applied.nodeCount} / Motions: ${applied.motionCount}`, "agent-ready");
    setChatStatus("Plan applied");
    state.chat.lastResult = JSON.stringify(session.draft, null, 2);
    resetPlanSession();
}

function cancelFlowPlanSession() {
    const session = getAgentSession();
    if (!session?.active) return;
    const remoteSessionId = session.remoteSessionId || "";
    const finish = async () => {
        resetPlanSession();
        appendChatMessage("system", "Plan session canceled.", "agent-observation", {
            actions: [{ kind: "agent-history", label: "履歴を見る" }]
        });
        setChatStatus("Plan canceled");
    };
    if (!remoteSessionId) {
        Promise.resolve(finish()).catch((error) => console.warn("[phase4] local cancel finalize failed", error));
        return;
    }
    Promise.resolve(requestRemoteAgentSessionCancel(remoteSessionId))
        .catch((error) => {
            console.warn("[phase4] remote cancel failed", error);
        })
        .finally(() => {
            Promise.resolve(finish()).catch((error) => console.warn("[phase4] cancel finalize failed", error));
        });
}

function asFiniteNumber(value, fallback) {
    const external = getAgentModuleFunction("editorApply", "asFiniteNumber");
    if (typeof external === "function" && external !== asFiniteNumber) {
        return external(value, fallback);
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function findDraftMotion(draft, node) {
    const external = getAgentModuleFunction("editorApply", "findDraftMotion");
    if (typeof external === "function" && external !== findDraftMotion) {
        return external(draft, node);
    }
    const axisName = String(node.axisName || "").trim();
    const motionId = String(node.motionId || node.motionDraftId || node.id || "").trim();
    const motionName = String(node.motionName || node.name || "").trim();
    for (const axis of draft.axes || []) {
        if (axisName && axis.name !== axisName) continue;
        for (const motion of axis.motions || []) {
            if ((motionId && motion.id === motionId) || (motionName && motion.name === motionName)) {
                return { axis, motion };
            }
        }
    }
    return null;
}

function applyFlowDraftToEditor(draft, userMessage = "") {
    if (!state.drawflow) throw new Error("Drawflow が初期化されていません。");
    const prepared = getAgentModule("editorApply")?.prepareFlowDraftApplyData?.(draft, userMessage, {
        buildLocalFlowDraft,
        genId
    }) || {
        axesDraft: Array.isArray(draft.axes) && draft.axes.length ? draft.axes : buildLocalFlowDraft(userMessage).axes,
        nodesDraft: Array.isArray(draft.nodes) && draft.nodes.length ? draft.nodes : buildLocalFlowDraft(userMessage).nodes,
        connectionsDraft: Array.isArray(draft.connections) ? draft.connections : [],
        runtimeAxes: [],
        summary: draft.summary || "Flow draft applied."
    };
    const axesDraft = prepared.axesDraft;
    const nodesDraft = prepared.nodesDraft;
    const connectionsDraft = prepared.connectionsDraft;
    state.project.axes = prepared.runtimeAxes;

    state.suspendFlowEvents = true;
    try {
        state.drawflow.clear();
        state.nodeIdToMotionId = {};
        state.motionIdToNodeId = {};
    } finally {
        state.suspendFlowEvents = false;
    }

    const nodeIdMap = {};
    const unresolvedMotionNodes = [];
    const nodeSpecs = getAgentModule("editorApply")?.buildDrawflowNodeSpecs?.(nodesDraft, axesDraft, {
        genId,
        findDraftMotion
    }) || [];
    for (const nodeSpec of nodeSpecs) {
        const id = String(nodeSpec.id || genId("draft-node"));
        const type = String(nodeSpec.type || "").toLowerCase();
        const x = asFiniteNumber(nodeSpec.x, 80);
        const y = asFiniteNumber(nodeSpec.y, 80);
        if (type === "start") {
            nodeIdMap[id] = addStartNode({ x, y, data: nodeSpec.data || { label: "Start" } });
        } else if (type === "condition") {
            nodeIdMap[id] = addConditionNode({ x, y, data: nodeSpec.data || {} });
        } else if (type === "error") {
            nodeIdMap[id] = addErrorNode({ x, y, data: nodeSpec.data || {} });
        } else if (type === "signal_output") {
            nodeIdMap[id] = addSignalOutputNode({ x, y, data: nodeSpec.data || {} });
        } else if (type === "end") {
            nodeIdMap[id] = addEndNode({ x, y, data: nodeSpec.data || {} });
        } else if (type === "motion") {
            const found = nodeSpec.runtimeMotionId ? findMotion(nodeSpec.runtimeMotionId) : null;
            if (found) nodeIdMap[id] = addFlowNode(found.axis, found.motion, null, { x, y });
            else unresolvedMotionNodes.push(id);
        }
    }

    state.suspendFlowEvents = true;
    try {
        for (const connection of connectionsDraft) {
            const from = nodeIdMap[String(connection.from || "")];
            const to = nodeIdMap[String(connection.to || "")];
            if (!from || !to) continue;
            try {
                state.drawflow.addConnection(from, to, connection.output || "output_1", connection.input || "input_1");
            } catch (e) {
                console.warn("flow draft connection skipped", connection, e);
            }
        }
    } finally {
        state.suspendFlowEvents = false;
    }

    state.selectedAxisId = state.project.axes[0]?.id ?? null;
    state.selectedMotionId = state.project.axes[0]?.motions[0]?.id ?? null;
    setActiveView("flow");
    scheduleFlowConnectionRefresh();
    rebuildStateFromFlow();
    if (unresolvedMotionNodes.length > 0) {
        // No longer a silent drop: a motion node that did not resolve to a
        // runtime motion is reported so the齟齬 between summary/counts and the
        // rendered flow becomes visible instead of disappearing.
        console.warn("flow draft: unresolved motion nodes dropped", unresolvedMotionNodes);
    }
    const summary =
        unresolvedMotionNodes.length > 0
            ? `${prepared.summary}\n⚠ 反映できなかった動作ノードが ${unresolvedMotionNodes.length} 件あります（軸の動作定義と一致しません）。`
            : prepared.summary;
    return {
        nodeCount: Object.keys(nodeIdMap).length,
        motionCount: state.project.axes.reduce((sum, axis) => sum + axis.motions.length, 0),
        unresolvedMotionCount: unresolvedMotionNodes.length,
        summary
    };
}

function buildLocalAssistantResponse(mode, snapshot, userMessage, currentFlowDraft = null, questionContext = "") {
    const facade = getPreferredGenerationApi();
    if (mode === "flow") {
        if (currentFlowDraft) {
            // Edit request but no server/AI available: return a no-op patch so
            // the existing flow is never destroyed by a blind regeneration.
            return JSON.stringify({
                flowPatchVersion: "1.0",
                summary: "AI に接続できないため、フローの増分編集を実行できませんでした。接続後にもう一度お試しください。",
                operations: []
            }, null, 2);
        }
        return JSON.stringify(buildLocalFlowDraft(userMessage), null, 2);
    }
    if (mode === "summary") {
        return facade.buildLocalSummaryResponseFromSnapshot(snapshot);
    }
    if (mode === "review") {
        const findings = [];
        if (!state.project.axes.length) findings.push("- 軸が未定義です。");
        for (const axis of state.project.axes) {
            for (const motion of axis.motions) {
                if (!motion.maxSpeed) findings.push(`- ${axis.name}/${motion.name}: 最高速度が未設定です。`);
                if (!motion.accel) findings.push(`- ${axis.name}/${motion.name}: 加速度が未設定です。`);
                if (!motion.decel) findings.push(`- ${axis.name}/${motion.name}: 減速度が未設定です。`);
            }
        }
        return facade.buildLocalReviewResponseFromSnapshot(snapshot, findings, userMessage);
    }
    if (mode === "qa") {
        return buildLocalQuestionResponse(userMessage, snapshot, questionContext);
    }
    const errors = (snapshot.generationIr?.expansionFindings || []).filter(f => f.severity === "error");
    if (errors.length > 0) {
        return buildLocalStBlockedResponse(errors);
    }
    const dialectMeta = getDialectMeta(snapshot.assistant.plcProfileId || snapshot.assistant.dialect);
    return facade.buildLocalStResponseFromDraft(
        snapshot,
        facade.buildLocalStDraftFromSnapshot(snapshot, dialectMeta, buildStDraftHelpers()),
        dialectMeta.startSignal,
        userMessage
    );
}

// Phase D: incremental flow edit. When the editor already holds a flow and
// the instruction is not an explicit "start over", the request carries the
// extracted current flow draft and the server returns a FlowPatch (delta)
// instead of a full draft. Flag-gated so it can be fully disabled.
const FLOW_INCREMENTAL_EDIT_ENABLED = true;

function isStGenerationMode(mode) {
    return AssistantClassifyModule.isStGenerationMode(mode);
}

function isFlowResetIntent(text) {
    return AssistantClassifyModule.isFlowResetIntent(text);
}

function confirmControlSpecBlockingFindings(mode) {
    if (!isStGenerationMode(mode)) return true;
    const generationIr = getPreferredGenerationApi().getGenerationIr();
    const blockers = ControlSpecHelper.getBlockingControlSpecFindings(generationIr);
    if (!blockers.length) return true;
    const details = ControlSpecHelper.formatControlSpecBlockingFindings(blockers);
    const more = blockers.length > 5 ? `\n...他 ${blockers.length - 5} 件` : "";
    return window.confirm([
        `制御仕様に error が ${blockers.length} 件あります。`,
        "このまま ST 生成を続行しますか？",
        "",
        details + more
    ].join("\n"));
}

// Returns the canonical current-flow draft when this flow request should be
// treated as an incremental edit, otherwise null (create-from-scratch path).
function buildCurrentFlowDraftForEdit(userMessage) {
    if (!FLOW_INCREMENTAL_EDIT_ENABLED) return null;
    if (!state.drawflow) return null;
    const extractFn = window.buildFlowDraftFromCurrentEditor;
    if (typeof extractFn !== "function") return null;
    let exported = null;
    try {
        exported = state.drawflow.export();
    } catch {
        return null;
    }
    const draft = extractFn(exported, state.project);
    const hasExisting =
        Array.isArray(draft?.nodes) && draft.nodes.some((n) => n && n.type && n.type !== "start");
    if (!hasExisting) return null;
    if (isFlowResetIntent(userMessage)) return null;
    return draft;
}

async function requestGeneration(mode, userMessage = "", options = {}) {
    const facade = getPreferredGenerationApi();
    const snapshot = facade.getGenerationSnapshot();
    const questionContext = String(options.questionContext || "");
    const activeProfile = getActivePlcProfile();
    const prompt = facade.buildAiPromptFromSnapshot(
        mode,
        snapshot,
        userMessage,
        activeProfile.name || getDialectMeta(state.chat.dialect).label,
        [],
        questionContext,
        activeProfile.promptRules || ""
    );

    const currentFlowDraft = mode === "flow" ? buildCurrentFlowDraftForEdit(userMessage) : null;

    let reply = null;
    let usedFallback = false;
    let fallbackReason = null;
    let serverResponse = null;
    let quotaExceededError = null;

    if (apiGenerationClient && typeof apiGenerationClient.postGenerate === "function") {
        const buildApiRequestPayload = getPhase3GenerationExport("buildGenerationApiRequestPayload");
        const getAccessToken = getPhase3GenerationExport("getApiClientAccessToken");
        const accessToken = typeof getAccessToken === "function"
            ? await getAccessToken(apiGenerationClient)
            : null;
        try {
            const requestPayload = typeof buildApiRequestPayload === "function" && buildApiRequestPayload !== requestGeneration
                ? buildApiRequestPayload({
                    facade,
                    buildRawFlowIndexForApi,
                    activeProfile,
                    state,
                    mode,
                    userMessage,
                    questionContext,
                    currentFlowDraft
                })
                : {
                    flowContext: facade.getFlowContext(),
                    generationIr: facade.getGenerationIr(),
                    rawFlowIndex: buildRawFlowIndexForApi(),
                    vendor: activeProfile.vendorTarget || state.chat.dialect,
                    mode,
                    userInstruction: userMessage,
                    ...(questionContext ? { questionContext } : {}),
                    plcProfileId: activeProfile.id,
                    plcProfileName: activeProfile.name,
                    plcProfilePromptRules: activeProfile.promptRules || "",
                    plcProfile: activeProfile,
                    skillIds: Array.isArray(state.project?.skillIds) ? state.project.skillIds : [],
                    ...(currentFlowDraft ? { currentFlowDraft } : {})
                };
            serverResponse = await apiGenerationClient.postGenerate(
                requestPayload,
                { accessToken }
            );
            if (serverResponse?.generatedCode) {
                reply = serverResponse.generatedCode;
                usedFallback = Boolean(serverResponse.usedFallback);
                fallbackReason = serverResponse.fallbackReason ?? null;
            }
        } catch (apiError) {
            const isDailyLimit =
                apiError?.name === "DailyLimitExceededError" ||
                apiError?.code === "daily_limit_exceeded";
            if (isDailyLimit) {
                quotaExceededError = apiError;
            } else {
                console.warn("[phase4] /api/generate failed, falling back to local:", apiError);
                fallbackReason = apiError?.code || apiError?.name || "api_call_failed";
            }
        }
    } else {
        fallbackReason = "api_unbound";
    }

    if (quotaExceededError) {
        return {
            ok: false,
            error: quotaExceededError,
            quotaExceeded: true,
            prompt,
            snapshot,
            serverResponse: quotaExceededError.details ?? null
        };
    }

    if (!reply) {
        try {
            reply = buildLocalAssistantResponse(mode, snapshot, userMessage, currentFlowDraft, questionContext);
            usedFallback = true;
            if (!fallbackReason) fallbackReason = "local_only";
        } catch (e) {
            return {
                ok: false,
                error: e,
                prompt,
                snapshot,
                serverResponse
            };
        }
    }

    return {
        ok: true,
        reply,
        usedFallback,
        fallbackReason,
        prompt,
        snapshot,
        serverResponse,
        currentFlowDraft
    };
}

async function runRemoteAgentSessionJob(mode, userMessage = "") {
    const remote = await requestRemoteAgentJobCreate(mode, userMessage);
    if (!remote?.sessionId) return false;
    const restoredSessionBuilder = getAgentModuleFunction("session", "createRestoredAgentSession");
    const session = typeof restoredSessionBuilder === "function"
        ? restoredSessionBuilder(remote, userMessage || remote.goal || "")
        : null;
    if (!session) return false;
    assignChatAgentSession(session);
    syncAgentJobState(session);
    touchAgentSession(session);
    updateChatStatusFromAgentJob(session);
    scheduleAutoSave(); // persist the current project when an assistant job starts
    if (remote.usedFallback) {
        const reasonLabel = remote.fallbackReason === "ai_unavailable"
            ? "サーバー側に AI Provider が設定されていません"
            : remote.fallbackReason || "理由不明";
        appendChatMessage("system", `サーバー側でフォールバックしました (${reasonLabel})。`, "fallback");
    }
    if (!maybeDeliverAgentSessionOutput(session, mode)) {
        appendChatMessage("system", `エージェントジョブを開始しました。sessionId: ${remote.sessionId}`, "agent-action");
    }
    ensureAgentSessionPolling(session);
    return true;
}

async function runChatAction(mode, userMessage = "") {
    if (mode !== "plan" && mode !== "flow" && detectPlanCreationIntent(userMessage)) mode = "plan";
    if (mode !== "flow" && mode !== "plan" && detectFlowCreationIntent(userMessage)) mode = "flow";
    const classified = classifyAssistantIntent(mode, userMessage);
    mode = classified.mode;
    if (!confirmControlSpecBlockingFindings(mode)) {
        const msg = "制御仕様の error があるため、ST 生成を中止しました。検証結果タブで内容を確認してください。";
        setActiveView("control-spec");
        setControlSpecTab("findings");
        appendChatMessage("system", msg, "error");
        state.chat.lastResult = msg;
        setChatStatus("ST生成中止");
        renderChatUi();
        return;
    }
    appendChatMessage("user", userMessage || (mode === "summary" ? "現在のフローを要約" : mode === "review" ? "ST生成前レビュー" : mode === "flow" ? "フロー作成" : mode === "plan" ? "フロー作成プラン" : "STコード生成"), mode);
    if (mode === "plan") {
        state.chat.busy = true;
        setChatStatus(apiGenerationClient ? "エージェントプランを準備中..." : "ローカルエージェントプランを準備中...");
        renderChatUi();
        try {
            resetPlanSession();
            await startFlowPlanSession(userMessage || "フローのプランを作成");
        } finally {
            state.chat.busy = false;
            renderChatUi();
        }
        return;
    }
    state.chat.busy = true;
    const questionRequest = mode === "qa" ? buildQuestionGenerationInstruction(userMessage) : null;
    setChatStatus(
        mode === "flow"
            ? (apiGenerationClient ? "API 経由でフロー作成中..." : "ローカルでフロー作成中...")
            : mode === "qa"
                ? (apiGenerationClient ? "検索結果を基に AI が回答中..." : "検索結果を整理中...")
                : (apiGenerationClient ? "API 経由で生成中..." : "ローカル生成中...")
    );
    renderChatUi();
    try {
        if (["summary", "review", "st", "vendor-conversion"].includes(mode) && apiAgentSessionClient?.createSession) {
            resetPlanSession();
            const started = await runRemoteAgentSessionJob(mode, userMessage);
            if (started) return;
        }
        const result = await getPreferredGenerationApi().requestGeneration(
            mode,
            userMessage,
            mode === "qa" ? { questionContext: questionRequest?.lookup?.text || "" } : {}
        );
        if (!result.ok) {
            if (result.quotaExceeded) {
                const limit = result.error?.details?.limit ?? "?";
                const used = result.error?.details?.usedToday ?? "?";
                const msg = `本日の生成回数の上限 (${used}/${limit}) に到達しました。明日 UTC 00:00 にリセットされます。`;
                appendChatMessage("system", msg, "error");
                state.chat.lastResult = msg;
                setChatStatus("クォータ上限");
                return;
            }
            throw result.error;
        }
        if (result.usedFallback) {
            const reasonLabel =
                result.fallbackReason === "api_unbound"
                    ? "API client が未バインドです"
                    : result.fallbackReason === "ai_call_failed" || result.fallbackReason === "api_call_failed"
                        ? "API 呼び出しに失敗しました"
                        : result.fallbackReason === "AI_PROVIDER unset" || result.fallbackReason === "ai_unavailable"
                            ? "サーバー側に AI Provider が設定されていません"
                            : result.fallbackReason || "理由不明";
            appendChatMessage("system", `ローカル生成へフォールバックしました (${reasonLabel})。`, "fallback");
        }
        const persistMsgParts = [];
        const sr = result.serverResponse;
        if (sr?.persisted) persistMsgParts.push("クラウドに保存済み");
        if (sr?.quota) persistMsgParts.push(`本日 ${sr.quota.usedToday}/${sr.quota.limit}`);
        if (persistMsgParts.length > 0) {
            appendChatMessage("system", persistMsgParts.join(" / "), "info");
        }
        if (mode === "flow") {
            const parsed = extractJsonObjectFromText(result.reply);
            const isPatch = parsed && Array.isArray(parsed.operations);
            if (result.currentFlowDraft && isPatch) {
                // Incremental edit path: merge a FlowPatch onto the extracted
                // current flow, then reuse the existing apply pipeline. On any
                // failure the editor is left untouched (never worse than now).
                const applyPatch = window.applyFlowPatchToDraft;
                if (typeof applyPatch !== "function") {
                    appendChatMessage("system", "フロー編集エンジンが読み込まれていません。", "error");
                    setChatStatus("フロー編集不可");
                } else {
                    const outcome = applyPatch(result.currentFlowDraft, parsed);
                    if (!outcome.ok) {
                        const detail = (outcome.errors || []).map((e) => e.message).join(" / ") || "不明なエラー";
                        appendChatMessage("system", `フロー編集を適用できませんでした（既存フローは変更していません）: ${detail}`, "error");
                        state.chat.lastResult = JSON.stringify(outcome.errors, null, 2);
                        setChatStatus("フロー編集失敗");
                    } else if ((outcome.applied?.length ?? 0) === 0) {
                        appendChatMessage("assistant", parsed.summary || "適用する変更がありませんでした。", mode);
                        setChatStatus("変更なし");
                    } else {
                        const applied = applyFlowDraftToEditor(outcome.draft, userMessage);
                        const msg = `${outcome.summary || parsed.summary || "フローを更新しました。"}\nノード ${applied.nodeCount} 件 / 動作 ${applied.motionCount} 件（増分反映）。`;
                        state.chat.lastResult = JSON.stringify(outcome.draft, null, 2);
                        appendChatMessage("assistant", msg, mode);
                        setChatStatus(result.usedFallback ? "ローカル増分反映完了" : "API 増分反映完了");
                    }
                }
            } else {
                const draft = parsed;
                const applied = applyFlowDraftToEditor(draft, userMessage);
                const msg = `${applied.summary}\nノード ${applied.nodeCount} 件 / 動作 ${applied.motionCount} 件をフローへ反映しました。`;
                state.chat.lastResult = JSON.stringify(draft, null, 2);
                appendChatMessage("assistant", msg, mode);
                setChatStatus(result.usedFallback ? "ローカル反映完了" : "API 反映完了");
            }
        } else {
            state.chat.lastResult = result.reply;
            appendChatMessage("assistant", result.reply, mode, {
                appliedSkills: Array.isArray(sr?.appliedSkills) ? sr.appliedSkills : []
            });
            setChatStatus(mode === "qa" ? (result.usedFallback ? "検索回答完了" : "AI回答完了") : (result.usedFallback ? "ローカル完了" : "API 完了"));
            Promise.resolve(startFaVerificationFollowUp(mode, result))
                .catch((error) => console.warn("[fa-agent] verification follow-up failed", error));
        }
    } catch (e) {
        const msg = `生成に失敗しました: ${e.message}`;
        appendChatMessage("system", msg, "error");
        state.chat.lastResult = msg;
        setChatStatus("エラー");
    } finally {
        state.chat.busy = false;
        renderChatUi();
    }
}

function clearChatHistory() {
    const thread = ensureActiveChatThread();
    thread.messages.length = 0;
    state.chat.messages = thread.messages;
    state.chat.lastResult = "";
    resetPlanSession();
    renderChatUi();
    setChatStatus("履歴をクリアしました");
}

function startNewChatThread() {
    resetPlanSession();
    const thread = createChatThread();
    state.chat.threads[thread.id] = thread;
    state.chat.activeThreadId = thread.id;
    state.chat.messages = thread.messages;
    state.chat.lastResult = "";
    state.chat.activeHistorySessionId = "";
    resetQuestionCardState();
    renderChatUi();
    setChatStatus("新しいチャットを開始しました");
}

function copyChatResult() {
    const text = state.chat.lastResult || "";
    if (!text) return;
    navigator.clipboard.writeText(text)
        .then(() => setChatStatus("結果をコピーしました"))
        .catch(() => setChatStatus("コピーに失敗しました"));
}

function exportFlowContextJson() {
    const external = getPhase3GenerationExport("exportFlowContext");
    if (typeof external === "function" && external !== exportFlowContextJson) {
        return external({
            getFlowContext: () => getPreferredGenerationApi().getFlowContext(),
            BlobCtor: Blob,
            URLApi: URL,
            doc: document,
            setChatStatus,
            consoleError: (error) => console.error(error)
        });
    }
}

function exportGenerationIrJson() {
    const external = getPhase3GenerationExport("exportGenerationIr");
    if (typeof external === "function" && external !== exportGenerationIrJson) {
        return external({
            getGenerationIr: () => getPreferredGenerationApi().getGenerationIr(),
            BlobCtor: Blob,
            URLApi: URL,
            doc: document,
            setChatStatus,
            consoleError: (error) => console.error(error)
        });
    }
}

// Compatibility shim only — do not add new logic here. New generation entries
// belong in createGenerationFacade (preferredGenerationApi) or in the runtime
// bindings. All HTML callers already go through getPreferredGenerationApi();
// generationWorkspace exists purely as a single named handle that future module
// migrations and external tooling can pin against. Old aliases (callApi /
// buildPrompt / buildLocalSummaryResponse / buildLocalReviewResponse /
// buildLocalStDraft / buildLocalStResponse / buildLocalResponse / request)
// were removed once their last in-HTML caller was replaced with the preferred
// *FromSnapshot / *FromDraft / requestGeneration entry; see
// docs/PHASE2_PREFERRED_API_SWITCH_PLAN.md and the HTML 対応表 in
// src/generation/README.md.
//
// Phase 2 final policy (locked 2026-05-09):
//   - Keep `generationWorkspace` as a frozen mirror of `preferredGenerationApi`
//     through Phase 5 (Managed Agents Integration). This gives migration
//     tooling and any out-of-tree script that pinned `generationWorkspace.*`
//     a stable handle while the import-side facade is finalized.
//   - Do NOT add new keys here. New keys must enter via
//     `createGenerationFacade` (pure side) or `generationRuntimeBindings`
//     (runtime side); the spread above propagates them automatically.
//   - Re-evaluate (and likely delete) at Phase 6 entry, once `preferredGenerationApi`
//     reads `src/generation/index.js` directly and external tooling has had at
//     least one major release to migrate to `getPreferredGenerationApi()`.
//   - Until that re-evaluation, code review must reject any patch that adds
//     properties or logic to this object (see SAAS_MIGRATION_PLAN.md Phase 2 残項目 #3).
function createGenerationWorkspaceMirror() {
    return Object.freeze({
        ...getPreferredGenerationApi()
    });
}

let generationWorkspace = createGenerationWorkspaceMirror();

// Phase 3 step #4: swap preferredGenerationApi (and generationWorkspace) to
// the facade exported by src/generation/facade.js. The ESM bootstrap at the
// bottom of this file calls this once GenerationModule.createGenerationFacade
// is available. Runtime keys (getFlowContext / requestGeneration / ...) are
// still wired from the HTML side via createGenerationRuntime(); only the pure
// helpers that live on the facade move to the module side.
//
// The HTML mirror functions (buildFlowContextFromSources, ...) remain in
// place for direct in-HTML callers; the swap only changes what
// getPreferredGenerationApi() and generationWorkspace expose.
function swapToModulePreferredGenerationApi(moduleFacadeFactory) {
    if (typeof moduleFacadeFactory !== "function") {
        console.warn("swapToModulePreferredGenerationApi: factory is not a function");
        return false;
    }
    let next;
    try {
        next = Object.freeze(moduleFacadeFactory(createGenerationRuntime()));
    } catch (e) {
        console.warn("swapToModulePreferredGenerationFacade: factory threw", e);
        return false;
    }
    if (!validateGenerationFacadeKeys(next)) {
        console.warn("swapToModulePreferredGenerationFacade: module facade keys do not match GENERATION_FACADE_KEYS");
        return false;
    }
    preferredGenerationApi = next;
    generationWorkspace = createGenerationWorkspaceMirror();
    generationFacadeSource = "src/generation/facade.js";
    console.info("[phase3] preferredGenerationApi swapped to src/generation/facade.js");
    return true;
}

if (typeof window !== "undefined") {
    // Compatibility / debug probes for the Phase 3 facade swap.
    window.__swapToModuleGenerationFacade = swapToModulePreferredGenerationApi;
    window.__getGenerationFacadeSource = () => generationFacadeSource;
    window.__GENERATION_INDEX_EXPECTED_EXPORTS = GENERATION_INDEX_EXPECTED_EXPORTS;
}

// Phase 4 step #10: late-binding for the /api/generate client. The module
// script at the bottom of this file imports `src/api-client/generation-api.js`
// and `src/api-client/supabase-browser.js`, then calls
// `window.__bindGenerationClient(...)` to populate the slot below.
//
// `requestGeneration()` checks the slot at call time. If the API client is
// bound (the normal case once Vite has loaded the modules), it tries
// /api/generate first and falls back to the local template path on failure.
// If the slot is null (file:// fallback or early calls before module load),
// requestGeneration() goes straight to the local templates and `usedFallback`
// is reported as true with reason `"api_unbound"`.
let apiGenerationClient = null;
if (typeof window !== "undefined") {
    // Runtime bind hook used by the module bootstrap to connect /api/generate.
    window.__bindGenerationClient = (client) => {
        apiGenerationClient = client || null;
    };
}

let rawFlowIndexBuilder = null;
if (typeof window !== "undefined") {
    window.__bindRawFlowIndexBuilder = (builder) => {
        rawFlowIndexBuilder = typeof builder === "function" ? builder : null;
    };
}

function buildRawFlowIndexForApi(snapshot = getFlowExportSnapshot()) {
    return typeof rawFlowIndexBuilder === "function" ? rawFlowIndexBuilder(snapshot) : null;
}

let apiAgentSessionClient = null;
if (typeof window !== "undefined") {
    // Runtime bind hook used by the module bootstrap to connect /api/agent-sessions.
    window.__bindAgentSessionClient = (client) => {
        apiAgentSessionClient = client || null;
    };
}

// Phase 4 step #10 後段: cloud project save/load.
// `apiProjectsClient` exposes /api/projects helpers + Supabase auth so the UI
// can save / load projects against the user's workspace. `null` while the
// late-bind script has not yet imported `src/api-client/projects-api.js` and
// `src/api-client/supabase-browser.js` — UI state then stays "未ログイン" and
// the cloud buttons remain disabled.
let apiProjectsClient = null;
let apiAssistantTitleClient = null;
let cloudProjectId = null; // set after a successful save / load
let cloudSignedIn = false; // mirrors the cloud auth session (sync flag for autosave)
const LAST_CLOUD_PROJECT_ID_KEY = "timingChartLastCloudProjectId";
let bootProjectRestoreDone = false;
let lastWorkspaceSessionScopeKey = undefined;

function readRememberedLastCloudProjectId(scopeKey = "") {
    try {
        const raw = localStorage.getItem(LAST_CLOUD_PROJECT_ID_KEY) || "";
        if (WorkspaceUiModule?.readWorkspaceScopedProjectId) {
            return WorkspaceUiModule.readWorkspaceScopedProjectId(raw, scopeKey);
        }
        return scopeKey ? null : (raw || null);
    } catch {
        return null;
    }
}

function rememberLastCloudProjectId(id, scopeKey = lastWorkspaceSessionScopeKey || "") {
    try {
        const raw = localStorage.getItem(LAST_CLOUD_PROJECT_ID_KEY) || "";
        const nextRaw = WorkspaceUiModule?.writeWorkspaceScopedProjectId
            ? WorkspaceUiModule.writeWorkspaceScopedProjectId(raw, scopeKey, id)
            : (id || "");
        if (nextRaw) localStorage.setItem(LAST_CLOUD_PROJECT_ID_KEY, nextRaw);
        else localStorage.removeItem(LAST_CLOUD_PROJECT_ID_KEY);
    } catch { /* storage unavailable */ }
}

function projectHasContent() {
    const p = state.project;
    if (p && Array.isArray(p.axes) && p.axes.length > 0) return true;
    const home = getFlowModuleData(p?.flow?.drawflowData || {}, "Home");
    return Boolean(home && Object.keys(home).length > 0);
}
let autoSaveTimer = null;

function updateCloudSaveStatus(msg) {
    const el = document.getElementById("cloudSaveStatus");
    if (el) el.textContent = msg;
}

function clearWorkspaceScopedSkillUi(refs = {}) {
    if (refs.skillsListContainer) {
        refs.skillsListContainer.innerHTML = '<p class="help">ワークスペースを切り替えました。再読み込み中...</p>';
    }
    if (refs.projectSkillsStatus) refs.projectSkillsStatus.textContent = "";
    if (refs.skillsEditorTitle) refs.skillsEditorTitle.textContent = "SKILL.md エディタ";
    if (refs.skillContentEditor) refs.skillContentEditor.value = "";
    if (refs.skillEditorStatus) refs.skillEditorStatus.textContent = "";
}

function handleCloudSessionChange(session) {
    if (!WorkspaceUiModule?.handleWorkspaceScopeChange) return false;
    return WorkspaceUiModule.handleWorkspaceScopeChange({
        session,
        getPreviousScopeKey: () => lastWorkspaceSessionScopeKey,
        setPreviousScopeKey: (value) => {
            lastWorkspaceSessionScopeKey = value;
        },
        clearAutoSaveTimer: () => {
            clearTimeout(autoSaveTimer);
            autoSaveTimer = null;
        },
        setCloudProjectId: (value) => {
            cloudProjectId = value;
        },
        rememberLastCloudProjectId,
        updateCloudSaveStatus,
        refs: getFrontendDomRefs(),
        clearAccountSettings: () => {
            state.accountSettings = null;
        },
        clearSkillUi: () => {
            clearWorkspaceScopedSkillUi(getFrontendDomRefs());
        },
        reloadSkills: () => {
            if (typeof window !== "undefined" && typeof window.loadAndRenderSkills === "function") {
                void window.loadAndRenderSkills();
            }
        }
    });
}

function scheduleAutoSave() {
    // Save an already-tracked project, or auto-create one once the user has done
    // something meaningful (edit / assistant run) on a signed-in session. Merely
    // opening a sample does not save: importData() cancels its own autosave timer.
    if (!cloudProjectId && !(cloudSignedIn && projectHasContent())) return;
    clearTimeout(autoSaveTimer);
    updateCloudSaveStatus("…");
    autoSaveTimer = setTimeout(async () => {
        await saveProjectToCloud();
    }, 3000);
}
if (typeof window !== "undefined") {
    // Runtime bind hook used by the module bootstrap to connect /api/projects.
    window.__bindProjectsClient = (client) => {
        apiProjectsClient = client || null;
        try { refreshCloudAuthUi(); }
        catch { /* UI not ready yet; later auth state changes will re-render */ }
    };
    window.__handleCloudSessionChange = (session) => {
        try { handleCloudSessionChange(session); }
        catch (e) { console.warn("[phase4] handleCloudSessionChange failed", e); }
    };
    window.__bindAssistantTitleClient = (client) => {
        apiAssistantTitleClient = client || null;
    };
}

// Phase 3 step #7: project I/O facade. Mirrors src/project/facade.js for the
// late-bind swap. The HTML mirror exposes the in-HTML normalize / migrate /
// serialize functions; the swap replaces them with the module versions on
// ESM bootstrap. importData() / exportData() (defined later in this file) call
// through `preferredProjectIo` so the swap takes effect everywhere.
const PROJECT_IO_FACADE_KEYS = Object.freeze([
    "PROJECT_VERSION",
    "DEFAULT_PROJECT_UNITS",
    "numOrNull",
    "numOrZero",
    "emptyProject",
    "normalizeImportedMotion",
    "normalizeImportedAxis",
    "migrateLegacyV1",
    "normalizeProjectImport",
    "serializeProjectExport"
]);

const PROJECT_VERSION = 2;
const DEFAULT_PROJECT_UNITS = Object.freeze({
    distanceLinear: "mm",
    accelLinear: "m/s2",
    speedLinear: "mm/s",
    angleRotary: "deg",
    accelRotary: "deg/s2",
    speedRotary: "deg/s"
});

function createProjectIoFacade() {
    return {
        PROJECT_VERSION,
        DEFAULT_PROJECT_UNITS,
        numOrNull,
        numOrZero,
        emptyProject,
        normalizeImportedMotion,
        normalizeImportedAxis,
        migrateLegacyV1,
        normalizeProjectImport: normalizeProjectImportHtmlMirror,
        serializeProjectExport: serializeProjectExportHtmlMirror
    };
}

let preferredProjectIo = Object.freeze(createProjectIoFacade());
let projectIoSource = "html-mirror";

if (!validateGenerationKeySet(PROJECT_IO_FACADE_KEYS, preferredProjectIo)) {
    console.warn("preferredProjectIo keys are out of sync with PROJECT_IO_FACADE_KEYS");
}

function getPreferredProjectIo() {
    return preferredProjectIo;
}

function swapToModulePreferredProjectIo(moduleFacadeFactory) {
    if (typeof moduleFacadeFactory !== "function") {
        console.warn("swapToModulePreferredProjectIo: factory is not a function");
        return false;
    }
    let next;
    try {
        next = Object.freeze(moduleFacadeFactory());
    } catch (e) {
        console.warn("swapToModulePreferredProjectIo: factory threw", e);
        return false;
    }
    if (!validateGenerationKeySet(PROJECT_IO_FACADE_KEYS, next)) {
        console.warn("swapToModulePreferredProjectIo: module facade keys do not match PROJECT_IO_FACADE_KEYS");
        return false;
    }
    preferredProjectIo = next;
    projectIoSource = "src/project/facade.js";
    console.info("[phase3] preferredProjectIo swapped to src/project/facade.js");
    return true;
}

if (typeof window !== "undefined") {
    // Compatibility / debug probes for the Phase 3 project I/O swap.
    window.__swapToModuleProjectFacade = swapToModulePreferredProjectIo;
    window.__getProjectIoSource = () => projectIoSource;
    window.__PROJECT_IO_FACADE_KEYS_EXPECTED = PROJECT_IO_FACADE_KEYS;
}

// =====================================================================
// Unit conversion (display -> internal)
// internal: distance mm, accel mm/s2, speed mm/s for linear
//           distance deg, accel deg/s2, speed deg/s for rotary
// =====================================================================
function toInternalDistanceLinear(v) {
    return UnitsModule.toInternalDistanceLinear(v, state.project.units);
}
function fromInternalDistanceLinear(v) {
    return UnitsModule.fromInternalDistanceLinear(v, state.project.units);
}
function fromInternalAccelLinear(v) {
    return UnitsModule.fromInternalAccelLinear(v, state.project.units);
}
function toInternalAccelLinear(v) {
    return UnitsModule.toInternalAccelLinear(v, state.project.units);
}
function toInternalSpeedLinear(v) {
    return UnitsModule.toInternalSpeedLinear(v, state.project.units);
}
function fromInternalSpeedLinear(v) {
    return UnitsModule.fromInternalSpeedLinear(v, state.project.units);
}

function toInternalDistanceLinearByUnit(v, unit) {
    return UnitsModule.toInternalDistanceLinearByUnit(v, unit);
}
function fromInternalDistanceLinearByUnit(v, unit) {
    return UnitsModule.fromInternalDistanceLinearByUnit(v, unit);
}
function toInternalAccelLinearByUnit(v, unit) {
    return UnitsModule.toInternalAccelLinearByUnit(v, unit);
}
function fromInternalAccelLinearByUnit(v, unit) {
    return UnitsModule.fromInternalAccelLinearByUnit(v, unit);
}
function toInternalSpeedLinearByUnit(v, unit) {
    return UnitsModule.toInternalSpeedLinearByUnit(v, unit);
}
function fromInternalSpeedLinearByUnit(v, unit) {
    return UnitsModule.fromInternalSpeedLinearByUnit(v, unit);
}

function toInternalAngleRotary(v) {
    return UnitsModule.toInternalAngleRotary(v, state.project.units);
}
function fromInternalAngleRotary(v) {
    return UnitsModule.fromInternalAngleRotary(v, state.project.units);
}
function fromInternalAccelRotary(v) {
    return UnitsModule.fromInternalAccelRotary(v, state.project.units);
}
function toInternalAccelRotary(v) {
    return UnitsModule.toInternalAccelRotary(v, state.project.units);
}
function toInternalSpeedRotary(v) {
    return UnitsModule.toInternalSpeedRotary(v, state.project.units);
}
function fromInternalSpeedRotary(v) {
    return UnitsModule.fromInternalSpeedRotary(v, state.project.units);
}

function toInternalAngleRotaryByUnit(v, unit) {
    return UnitsModule.toInternalAngleRotaryByUnit(v, unit);
}
function fromInternalAngleRotaryByUnit(v, unit) {
    return UnitsModule.fromInternalAngleRotaryByUnit(v, unit);
}
function toInternalAccelRotaryByUnit(v, unit) {
    return UnitsModule.toInternalAccelRotaryByUnit(v, unit);
}
function fromInternalAccelRotaryByUnit(v, unit) {
    return UnitsModule.fromInternalAccelRotaryByUnit(v, unit);
}
function toInternalSpeedRotaryByUnit(v, unit) {
    return UnitsModule.toInternalSpeedRotaryByUnit(v, unit);
}
function fromInternalSpeedRotaryByUnit(v, unit) {
    return UnitsModule.fromInternalSpeedRotaryByUnit(v, unit);
}

function convertProjectRotaryUnits(kind, nextUnit) {
    UnitsModule.convertProjectRotaryUnits(state.project, kind, nextUnit);
}

function convertProjectLinearUnits(kind, nextUnit) {
    UnitsModule.convertProjectLinearUnits(state.project, kind, nextUnit);
}

const CYLINDER_PRESETS = {
    slow: { label: "遅い", maxSpeed: 100, accel: 0.1 * 9.80665 * 1000 },
    normal: { label: "普通", maxSpeed: 200, accel: 0.2 * 9.80665 * 1000 },
    fast: { label: "速い", maxSpeed: 300, accel: 0.3 * 9.80665 * 1000 }
};

function applyCylinderPreset(motion, presetKey) {
    UnitsModule.applyCylinderPreset(motion, presetKey, state.project.units);
}

function detectCylinderPreset(axis, motion) {
    return UnitsModule.detectCylinderPreset(axis, motion, state.project.units);
}

// =====================================================================
// Axis / Motion helpers
// =====================================================================
function addAxis(name = null, options = {}) {
    const axisName = String(name || "").trim() || getNextAxisDefaultName();
    const axis = {
        id: genId("axis"),
        name: axisName,
        actuatorType: normalizeAxisActuatorType(options.actuatorType),
        motorControlType: normalizeMotorControlType(options.motorControlType, options.actuatorType),
        motionType: "linear",
        axisMaxSpeed: null,
        motions: []
    };
    state.project.axes.push(axis);
    state.selectedAxisId = axis.id;
    rebuildAll();
}

function removeAxis(axisId) {
    if (!confirm("この軸を削除しますか？関連する動作と参照も削除されます。")) return;
    const axis = findAxis(axisId);
    if (!axis) return;
    const removedMotionIds = axis.motions.map(m => m.id);
    state.project.axes = state.project.axes.filter(a => a.id !== axisId);
    // Clear dependencies that referenced removed motions
    for (const a of state.project.axes) {
        for (const m of a.motions) {
            if (m.dependency && removedMotionIds.includes(m.dependency.sourceMotionId)) {
                m.dependency = null;
                if (m.absoluteStartSec == null) m.absoluteStartSec = 0;
            }
        }
    }
    if (state.selectedAxisId === axisId) state.selectedAxisId = state.project.axes[0]?.id ?? null;
    if (removedMotionIds.includes(state.selectedMotionId)) state.selectedMotionId = null;
    // Remove drawflow nodes
    for (const motionId of removedMotionIds) {
        const nodeId = state.motionIdToNodeId[motionId];
        if (nodeId) removeFlowNode(nodeId);
    }
    rebuildAll();
}

function addMotion(axisId, options = {}) {
    let axis = findAxis(axisId);
    if (!axis) {
        if (state.project.axes.length === 0) {
            addAxis();
            axis = state.project.axes[0];
        } else {
            axis = findAxis(state.selectedAxisId) || state.project.axes[0];
        }
    }
    const idx = axis.motions.length + 1;
    const last = axis.motions[axis.motions.length - 1];
    const motion = makeDefaultMotion(idx, last);
    axis.motions.push(motion);
    state.selectedAxisId = axis.id;
    state.selectedMotionId = motion.id;
    const defaultPrevNodeId = getActiveFlowModuleName() === "Home" && last ? state.motionIdToNodeId[last.id] : null;
    addFlowNode(axis, motion, options.flowConnectionSourceNodeId ?? defaultPrevNodeId);
    rebuildAll();
    return motion;
}

function removeMotion(motionId) {
    const found = findMotion(motionId);
    if (!found) return;
    if (!confirm("この動作を削除しますか？")) return;
    found.axis.motions = found.axis.motions.filter(m => m.id !== motionId);
    // Clear dependencies that referenced this motion
    for (const a of state.project.axes) {
        for (const m of a.motions) {
            if (m.dependency && m.dependency.sourceMotionId === motionId) {
                m.dependency = null;
                if (m.absoluteStartSec == null) m.absoluteStartSec = 0;
                pushTransientWarning(m.id, "参照元が削除されたため未設定に戻しました");
            }
        }
    }
    if (state.selectedMotionId === motionId) state.selectedMotionId = null;
    const nodeId = state.motionIdToNodeId[motionId];
    if (nodeId) removeFlowNode(nodeId);
    rebuildAll();
}

function findAxis(axisId) {
    return TimingStateModule.findAxis(axisId, state.project);
}
function findMotion(motionId) {
    return TimingStateModule.findMotion(motionId, state.project);
}

const _transientWarnings = [];
function pushTransientWarning(motionId, msg) {
    _transientWarnings.push({ motionId, msg });
}

// =====================================================================
// Calculation
// =====================================================================
function computeAxisMaxSpeedMmPerSec(axis) {
    return TimingStateModule.computeAxisMaxSpeedMmPerSec(axis, state.project.units);
}

function getAxisMotionMaxSpeed(axis) {
    return TimingStateModule.getAxisMotionMaxSpeed(axis);
}

function computeMotionCycleTime(motion, axis) {
    return MotionProfileModule.computeMotionCycleTime(motion, axis, state.project.units);
}

function isFinitePositive(v) {
    return typeof v === "number" && isFinite(v) && v > 0;
}
function isFiniteNonZero(v) {
    return typeof v === "number" && isFinite(v) && v !== 0;
}

function recomputeAll() {
    const nextComputed = TimingStateModule.recomputeAll({
        project: state.project,
        transientWarnings: _transientWarnings,
        flowDataSnapshot: getMainFlowDataSnapshot(),
        nodeIdToMotionId: state.nodeIdToMotionId
    });
    _transientWarnings.length = 0; // 一時警告を消費
    
    // in-place 更新で参照を保護
    state.computed.motionTimes = nextComputed.motionTimes || {};
    state.computed.subprocessTimes = nextComputed.subprocessTimes || {};
    state.computed.errors = nextComputed.errors || [];
    state.computed.motionErrors = nextComputed.motionErrors || {};
}

function totalCycleSec() {
    return TimingStateModule.totalCycleSec(state.computed);
}

function getSubprocessNodeDuration(data) {
    return TimingStateModule.getSubprocessNodeDuration(data, state.project);
}

function getWaitNodeDuration(data) {
    return TimingStateModule.getWaitNodeDuration(data);
}

function buildTimingGridMetrics(totalSec, pxPerSec) {
    return TimingStateModule.buildTimingGridMetrics(totalSec, pxPerSec);
}

function buildTimingGridViewState(pxPerSec) {
    return TimingStateModule.buildTimingGridViewState(state.project, state.computed, state.selectedAxisId, pxPerSec);
}

function buildTimingRulerMarks(totalSec, pxPerSec) {
    return TimingStateModule.buildTimingRulerMarks(totalSec, pxPerSec);
}

function buildTrackGridStyle(pxPerSec) {
    return TimingStateModule.buildTrackGridStyle(pxPerSec);
}

function buildSubprocessTimingItems() {
    return TimingStateModule.buildSubprocessTimingItems(
        state.computed.subprocessTimes || {},
        state.project.flow?.subprocesses || []
    );
}

function buildSubprocessTimingRowState(pxPerSec) {
    return TimingStateModule.buildSubprocessTimingRowState(
        state.computed.subprocessTimes || {},
        state.project.flow?.subprocesses || [],
        pxPerSec
    );
}

function buildAxisTimingCurve(axis) {
    return TimingStateModule.buildAxisTimingCurve(axis, state.computed, state.project);
}

function buildMotionDetailRowState(axis, motion) {
    return TimingStateModule.buildMotionDetailRowState(axis, motion, state.computed, state.project);
}

function buildAxisCurveTrackState(axis, pxPerSec) {
    return TimingStateModule.buildAxisCurveTrackState(axis, state.computed, state.project, pxPerSec, TRACK_HEIGHT);
}


// =====================================================================
// Rendering
// =====================================================================
// Resolved lazily in DOMContentLoaded: the timing view markup is injected by a
// deferred module script that runs after this classic script parses, so these
// elements do not exist yet at parse time.
let { timingGrid, errorPanel } = getFrontendDomRefs();

function renderTimingGrid() {
    if (TimingGridControllerModule?.renderTimingGrid) {
        return TimingGridControllerModule.renderTimingGrid(document, {
            timingGrid,
            state,
            buildTimingGridViewState,
            escapeHtml,
            applyTrackGridBackground,
            renderAxisCurveTrack,
            renderSubprocessTimingRow,
            buildDetailContent,
            renderSpeedChart,
            addMotion,
            removeAxis,
            updateAllFlowNodeLabels
        });
    }
    timingGrid.innerHTML = "";
    const pxPerSec = state.pxPerSec;
    const gridState = buildTimingGridViewState(pxPerSec);
    const { totalSec, trackWidth } = gridState;

    // Ruler row
    const ruler = document.createElement("div");
    ruler.className = "ruler";
    const rulerLabel = document.createElement("div");
    rulerLabel.className = "axis-side";
    rulerLabel.textContent = "時間軸 (秒)";
    ruler.appendChild(rulerLabel);
    const rulerTrack = document.createElement("div");
    rulerTrack.className = "axis-track";
    rulerTrack.style.minWidth = trackWidth + "px";
    applyTrackGridBackground(rulerTrack, pxPerSec);
    const scaleControl = document.createElement("label");
    scaleControl.className = "ruler-scale-control";
    scaleControl.innerHTML = `目盛り <input type="number" id="pxPerSec" value="${escapeHtml(String(state.pxPerSec))}" min="20" max="600" step="10">`;
    rulerTrack.appendChild(scaleControl);
    for (const mark of gridState.rulerMarks) {
        const m = document.createElement("div");
        m.className = "ruler-label";
        m.style.left = mark.leftPx + "px";
        m.textContent = mark.label;
        rulerTrack.appendChild(m);
    }
    ruler.appendChild(rulerTrack);
    timingGrid.appendChild(ruler);

    for (const rowState of gridState.axisRows) {
        const { axis } = rowState;
        // Axis row
        const row = document.createElement("div");
        row.className = "axis-row";

        const side = document.createElement("div");
        side.className = "axis-side" + (rowState.isSelected ? " selected" : "");
        side.addEventListener("click", (e) => {
            if (e.target.tagName === "INPUT" || e.target.tagName === "BUTTON" || e.target.tagName === "SELECT") return;
            state.selectedAxisId = axis.id;
            state.selectedMotionId = null;
            renderTimingGrid();
            renderSpeedChart();
        });
        const nameInput = document.createElement("input");
        nameInput.type = "text";
        nameInput.className = "axis-name-input";
        nameInput.value = axis.name;
        nameInput.addEventListener("input", () => { axis.name = nameInput.value; updateAllFlowNodeLabels(axis.id); });
        side.appendChild(nameInput);

        const meta = document.createElement("div");
        meta.className = "axis-meta";
        meta.innerHTML = `
            <div>軸最高速度: ${rowState.axisMaxSpeedText}</div>
        `;
        side.appendChild(meta);

        const actions = document.createElement("div");
        actions.className = "axis-actions";
        const addBtn = document.createElement("button");
        addBtn.textContent = "＋動作";
        addBtn.addEventListener("click", () => addMotion(axis.id));
        const delBtn = document.createElement("button");
        delBtn.textContent = "軸削除";
        delBtn.className = "danger";
        delBtn.addEventListener("click", () => removeAxis(axis.id));
        actions.appendChild(addBtn);
        actions.appendChild(delBtn);
        side.appendChild(actions);

        row.appendChild(side);

        const track = document.createElement("div");
        track.className = "axis-track";
        track.style.minWidth = trackWidth + "px";

        renderAxisCurveTrack(axis, track, totalSec, pxPerSec, trackWidth);

        row.appendChild(track);
        timingGrid.appendChild(row);

        // Detail section if this axis is selected
        if (rowState.showDetail) {
            const detail = document.createElement("div");
            detail.className = "detail-section";
            detail.appendChild(buildDetailContent(axis));
            timingGrid.appendChild(detail);
        }
    }

    renderSubprocessTimingRow(totalSec, pxPerSec, trackWidth);
}

const SVG_NS = "http://www.w3.org/2000/svg";
const TRACK_HEIGHT = 100;

function renderSubprocessTimingRow(totalSec, pxPerSec, trackWidth) {
    if (TimingGridControllerModule?.renderSubprocessTimingRow) {
        return TimingGridControllerModule.renderSubprocessTimingRow(document, {
            timingGrid,
            state,
            trackHeight: TRACK_HEIGHT,
            totalSec,
            pxPerSec,
            trackWidth,
            buildSubprocessTimingRowState,
            applyTrackGridBackground,
            renderFlowNodeEditor,
            renderSpeedChart
        });
    }
    const items = buildSubprocessTimingRowState(pxPerSec);
    if (!items.length) return;

    const row = document.createElement("div");
    row.className = "axis-row";

    const side = document.createElement("div");
    side.className = "axis-side";
    side.innerHTML = `<div style="font-weight:700;">サブプロセス</div><div class="axis-meta"><div>固定秒数工程</div></div>`;
    row.appendChild(side);

    const track = document.createElement("div");
    track.className = "axis-track";
    track.style.minWidth = trackWidth + "px";
    applyTrackGridBackground(track, pxPerSec);

    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("class", "curve-svg");
    svg.setAttribute("width", trackWidth);
    svg.setAttribute("height", TRACK_HEIGHT);

    for (const item of items) {
        const rect = document.createElementNS(SVG_NS, "rect");
        rect.setAttribute("x", item.x);
        rect.setAttribute("y", 18);
        rect.setAttribute("width", item.width);
        rect.setAttribute("height", TRACK_HEIGHT - 36);
        rect.setAttribute("rx", 6);
        rect.setAttribute("fill", "#f6ad55");
        rect.setAttribute("fill-opacity", "0.35");
        rect.setAttribute("stroke", "#c05621");
        rect.setAttribute("stroke-width", "1.5");
        rect.style.cursor = "pointer";
        rect.addEventListener("click", (e) => {
            e.stopPropagation();
            state.selectedFlowNodeId = item.nodeId;
            renderFlowNodeEditor();
            renderSpeedChart();
        });
        svg.appendChild(rect);

        const label = document.createElementNS(SVG_NS, "text");
        label.setAttribute("class", "motion-label");
        label.setAttribute("x", item.x + 4);
        label.setAttribute("y", 14);
        label.textContent = item.label;
        svg.appendChild(label);

        const timeLabel = document.createElementNS(SVG_NS, "text");
        timeLabel.setAttribute("class", "motion-label-time");
        timeLabel.setAttribute("x", item.x + 4);
        timeLabel.setAttribute("y", TRACK_HEIGHT - 3);
        timeLabel.textContent = item.timeLabel;
        svg.appendChild(timeLabel);
    }

    track.appendChild(svg);
    row.appendChild(track);
    timingGrid.appendChild(row);
}

function renderAxisCurveTrack(axis, track, totalSec, pxPerSec, trackWidth) {
    if (TimingGridControllerModule?.renderAxisCurveTrack) {
        return TimingGridControllerModule.renderAxisCurveTrack(document, {
            axis,
            track,
            state,
            pxPerSec,
            totalSec,
            trackWidth,
            trackHeight: TRACK_HEIGHT,
            applyTrackGridBackground,
            buildAxisCurveTrackState,
            renderTimingGrid,
            renderSpeedChart,
            distLabel: dist_label,
            formatDistance
        });
    }
    track.style.minWidth = trackWidth + "px";
    applyTrackGridBackground(track, pxPerSec);
    const curveState = buildAxisCurveTrackState(axis, pxPerSec);

    const svg = document.createElementNS(SVG_NS, "svg");
    svg.setAttribute("class", "curve-svg");
    svg.setAttribute("width", trackWidth);
    svg.setAttribute("height", TRACK_HEIGHT);

    // Background motion regions for hit-testing & visual emphasis
    for (const item of curveState.regions) {
        const { motion, times, err } = item;
        const x = item.x;
        const wMotion = item.motionWidth;
        const wWait = item.waitWidth;
        if (wMotion > 0) {
            const rect = document.createElementNS(SVG_NS, "rect");
            rect.setAttribute("class", "motion-region" + (motion.id === state.selectedMotionId ? " selected" : "") + (err ? " error-region" : ""));
            rect.setAttribute("x", x);
            rect.setAttribute("y", 0);
            rect.setAttribute("width", wMotion);
            rect.setAttribute("height", TRACK_HEIGHT);
            rect.addEventListener("click", (e) => {
                e.stopPropagation();
                state.selectedAxisId = axis.id;
                state.selectedMotionId = motion.id;
                renderTimingGrid();
                renderSpeedChart();
            });
            const titleEl = document.createElementNS(SVG_NS, "title");
            titleEl.textContent = err ? `${motion.name}\n${err}` : `${motion.name}\n${times.startSec.toFixed(3)}→${times.endSec.toFixed(3)}s\n移動距離: ${dist_label(axis, motion.distance)}`;
            rect.appendChild(titleEl);
            svg.appendChild(rect);
        }
        if (wWait > 0) {
            const wrect = document.createElementNS(SVG_NS, "rect");
            wrect.setAttribute("class", "wait-region");
            wrect.setAttribute("x", x + wMotion);
            wrect.setAttribute("y", 0);
            wrect.setAttribute("width", wWait);
            wrect.setAttribute("height", TRACK_HEIGHT);
            svg.appendChild(wrect);
        }
        // Motion name label at top-left of region
        const label = document.createElementNS(SVG_NS, "text");
        label.setAttribute("class", "motion-label");
        label.setAttribute("x", x + 4);
        label.setAttribute("y", 12);
        label.textContent = item.label;
        svg.appendChild(label);
        // Time label at bottom
        const timeLabel = document.createElementNS(SVG_NS, "text");
        timeLabel.setAttribute("class", "motion-label-time");
        timeLabel.setAttribute("x", x + 4);
        timeLabel.setAttribute("y", TRACK_HEIGHT - 3);
        timeLabel.textContent = item.timeLabel;
        svg.appendChild(timeLabel);
    }

    // Curve polyline
    if (curveState.polylinePoints) {
        const poly = document.createElementNS(SVG_NS, "polyline");
        poly.setAttribute("class", "pos-curve" + (state.selectedAxisId === axis.id ? " selected" : ""));
        poly.setAttribute("points", curveState.polylinePoints);
        svg.appendChild(poly);
    }

    track.appendChild(svg);

    // Y-axis scale labels (max at top, 0 at bottom) - DOM overlay for readability
    if (curveState.totalDist > 0) {
        const yMax = document.createElement("div");
        yMax.className = "y-scale";
        yMax.style.top = curveState.yScaleTopPx + "px";
        yMax.textContent = formatDistance(axis, curveState.totalDist);
        track.appendChild(yMax);
        const y0 = document.createElement("div");
        y0.className = "y-scale";
        y0.style.top = curveState.yScaleBottomPx + "px";
        y0.textContent = "0";
        track.appendChild(y0);
    }
}

function dist_label(axis, distance) {
    if (!isFiniteNonZero(distance)) return "—";
    return formatDistance(axis, distance);
}
function formatDistance(axis, distance) {
    if (axis.motionType === "linear") {
        return `${distance} ${state.project.units.distanceLinear}`;
    }
    return `${distance} ${state.project.units.angleRotary || "deg"}`;
}

function getAxisDistanceUnit(axis) {
    if (TimingGridControllerModule?.getAxisDistanceUnit) {
        return TimingGridControllerModule.getAxisDistanceUnit(axis, state.project.units);
    }
    return axis.motionType === "linear" ? state.project.units.distanceLinear : (state.project.units.angleRotary || "deg");
}

function getAxisAccelUnit(axis) {
    if (TimingGridControllerModule?.getAxisAccelUnit) {
        return TimingGridControllerModule.getAxisAccelUnit(axis, state.project.units);
    }
    return axis.motionType === "linear" ? state.project.units.accelLinear : (state.project.units.accelRotary || "deg/s2");
}

function getAxisSpeedUnit(axis) {
    if (TimingGridControllerModule?.getAxisSpeedUnit) {
        return TimingGridControllerModule.getAxisSpeedUnit(axis, state.project.units);
    }
    return axis.motionType === "linear" ? state.project.units.speedLinear : (state.project.units.speedRotary || "deg/s");
}

function applyTrackGridBackground(track, pxPerSec) {
    if (TimingGridControllerModule?.applyTrackGridBackground) {
        return TimingGridControllerModule.applyTrackGridBackground(track, pxPerSec, {
            buildTrackGridStyle
        });
    }
    if (!track) return;
    const style = buildTrackGridStyle(pxPerSec);
    track.style.backgroundImage = style.backgroundImage;
    track.style.backgroundSize = style.backgroundSize;
    track.style.backgroundPosition = style.backgroundPosition;
    track.style.backgroundRepeat = style.backgroundRepeat;
}

function buildAxisConfigSection(axis, speedUnitDetail) {
    if (TimingGridControllerModule?.buildAxisConfigSection) {
        return TimingGridControllerModule.buildAxisConfigSection(document, {
            axis,
            speedUnitDetail,
            buildActuatorTypeOptions,
            normalizeMotorControlType,
            isFinitePositive,
            rebuildAll,
            alertFn: alert
        });
    }

    const cfg = document.createElement("div");
    cfg.className = "axis-config";
    cfg.innerHTML = `
        <label>アクチュエータ種別: <select data-axis-field="actuatorType">
            ${buildActuatorTypeOptions(axis.actuatorType)}
        </select></label>
        <label>モータ制御種別: <select data-axis-field="motorControlType">
            <option value="" ${normalizeMotorControlType(axis.motorControlType, axis.actuatorType) === "" ? "selected" : ""}>—</option>
            <option value="speedControl" ${normalizeMotorControlType(axis.motorControlType, axis.actuatorType) === "speedControl" ? "selected" : ""}>速度指定モータ</option>
            <option value="pulseCommand" ${normalizeMotorControlType(axis.motorControlType, axis.actuatorType) === "pulseCommand" ? "selected" : ""}>パルスモータ（コマンド入力）</option>
            <option value="pulseTrain" ${normalizeMotorControlType(axis.motorControlType, axis.actuatorType) === "pulseTrain" ? "selected" : ""}>パルスモータ（パルス入力）</option>
            <option value="servo" ${normalizeMotorControlType(axis.motorControlType, axis.actuatorType) === "servo" ? "selected" : ""}>サーボモータ</option>
            <option value="inverter" ${normalizeMotorControlType(axis.motorControlType, axis.actuatorType) === "inverter" ? "selected" : ""}>インバータ</option>
        </select></label>
        <label>動作種別: <select data-axis-field="motionType">
            <option value="linear" ${axis.motionType === "linear" ? "selected" : ""}>直線</option>
            <option value="rotary" ${axis.motionType === "rotary" ? "selected" : ""}>回転</option>
        </select></label>
        <label>軸最高速度上限: <input type="number" data-axis-field="axisMaxSpeed" min="0" step="1" value="${axis.axisMaxSpeed ?? ""}"> ${speedUnitDetail}</label>
        <button id="applyAxisMaxBtn" type="button">この上限をこの軸の動作に反映</button>
    `;
    cfg.querySelectorAll("input,select").forEach(el => {
        el.addEventListener("change", () => {
            const v = el.tagName === "SELECT" ? el.value : (el.value === "" ? null : parseFloat(el.value));
            axis[el.dataset.axisField] = (typeof v === "string" || v == null) ? v : (isFinite(v) ? v : null);
            rebuildAll();
        });
    });
    cfg.querySelector("#applyAxisMaxBtn").addEventListener("click", () => {
        if (!isFinitePositive(axis.axisMaxSpeed)) { alert("最高速度を入力してください"); return; }
        for (const m of axis.motions) m.maxSpeed = axis.axisMaxSpeed;
        rebuildAll();
    });
    return cfg;
}

function buildCylinderPresetSection(axis) {
    if (TimingGridControllerModule?.buildCylinderPresetSection) {
        return TimingGridControllerModule.buildCylinderPresetSection(document, {
            axis,
            state,
            escapeHtml,
            detectCylinderPreset,
            applyCylinderPreset,
            renderTimingGrid,
            renderSpeedChart,
            rebuildAll
        });
    }

    if (axis.motionType !== "linear" || axis.motions.length === 0) return null;
    const selectedMotion = axis.motions.find(m => m.id === state.selectedMotionId) || axis.motions[0];
    const presetBox = document.createElement("div");
    presetBox.className = "axis-config";
    const selectedPreset = detectCylinderPreset(axis, selectedMotion);
    presetBox.innerHTML = `
        <label>対象動作: <select data-role="presetTarget">
            ${axis.motions.map(m => `<option value="${m.id}" ${m.id === selectedMotion.id ? "selected" : ""}>${escapeHtml(m.name)}</option>`).join("")}
        </select></label>
        <label>動作プリセット: <select data-role="cylinderPreset">
            <option value="">カスタム設定</option>
            <option value="slow" ${selectedPreset === "slow" ? "selected" : ""}>遅い</option>
            <option value="normal" ${selectedPreset === "normal" ? "selected" : ""}>普通</option>
            <option value="fast" ${selectedPreset === "fast" ? "selected" : ""}>速い</option>
        </select></label>
    `;
    presetBox.querySelector('[data-role="presetTarget"]').addEventListener("change", (e) => {
        state.selectedMotionId = e.target.value || null;
        renderTimingGrid();
        renderSpeedChart();
    });
    presetBox.querySelector('[data-role="cylinderPreset"]').addEventListener("change", (e) => {
        if (!e.target.value) return;
        applyCylinderPreset(selectedMotion, e.target.value);
        state.selectedMotionId = selectedMotion.id;
        rebuildAll();
    });
    return presetBox;
}

function buildMotionDetailTable(axis, speedUnitDetail, distanceUnitDetail, accelUnitDetail) {
    if (TimingGridControllerModule?.buildMotionDetailTable) {
        return TimingGridControllerModule.buildMotionDetailTable(document, {
            axis,
            speedUnitDetail,
            distanceUnitDetail,
            accelUnitDetail,
            state,
            escapeHtml,
            buildMotionDetailRowState,
            onMotionFieldChange,
            removeMotion,
            renderTimingGrid,
            renderSpeedChart
        });
    }

    const table = document.createElement("table");
    table.innerHTML = `
        <thead>
            <tr>
                <th>動作名</th>
                <th>開始条件</th>
                <th>参照元</th>
                <th>オフセット秒</th>
                <th>距離 (${distanceUnitDetail})</th>
                <th>加速度 (${accelUnitDetail})</th>
                <th>減速度 (${accelUnitDetail})</th>
                <th>最高速度 (${speedUnitDetail})</th>
                <th>待ち時間 (s)</th>
                <th>コメント</th>
                <th>所要 (s)</th>
                <th>開始 (s)</th>
                <th>終了 (s)</th>
                <th>算出最高 (${speedUnitDetail})</th>
                <th></th>
            </tr>
        </thead>
        <tbody></tbody>
    `;
    const tbody = table.querySelector("tbody");

    for (const motion of axis.motions) {
        const tr = document.createElement("tr");
        const rowState = buildMotionDetailRowState(axis, motion);
        const { times, err, startMode, sourceMotionOptions, adjustedMaxText } = rowState;
        const refOptions = sourceMotionOptions
            .map((option) => `<option value="${option.value}" ${option.selected ? "selected" : ""}>${option.label}</option>`)
            .join("");

        tr.innerHTML = `
            <td><input type="text" data-mfield="name" value="${escapeHtml(motion.name)}"></td>
            <td>
                <select data-mfield="startMode">
                    <option value="absolute" ${startMode === "absolute" ? "selected" : ""}>絶対時刻</option>
                    <option value="ref" ${startMode === "ref" ? "selected" : ""}>参照終了+α</option>
                </select>
                ${startMode === "absolute" ? `<input type="number" step="0.01" data-mfield="absoluteStartSec" value="${motion.absoluteStartSec ?? 0}" style="width:60px;margin-left:4px;">s` : ""}
                <div style="margin-top:4px;">
                    <input type="text" data-mfield="startConditionText" value="${escapeHtml(motion.startConditionText || "")}" placeholder="条件例: センサーON">
                </div>
            </td>
            <td>${startMode === "ref" ? `<select data-mfield="sourceMotionId"><option value="">(未選択)</option>${refOptions}</select>` : "—"}</td>
            <td>${startMode === "ref" ? `<input type="number" step="0.01" data-mfield="offsetSec" value="${motion.dependency?.offsetSec ?? 0}">` : "—"}</td>
            <td><input type="number" step="0.1" data-mfield="distance" value="${motion.distance ?? ""}"></td>
            <td><input type="number" min="0" step="0.01" data-mfield="accel" value="${motion.accel ?? ""}"></td>
            <td><input type="number" min="0" step="0.01" data-mfield="decel" value="${motion.decel ?? ""}"></td>
            <td><input type="number" min="0" step="1" data-mfield="maxSpeed" value="${motion.maxSpeed ?? ""}"></td>
            <td><input type="number" min="0" step="0.01" data-mfield="waitTime" value="${motion.waitTime ?? 0}"></td>
            <td><input type="text" data-mfield="comment" value="${escapeHtml(motion.comment || "")}"></td>
            <td class="computed">${times ? times.cycleTime.toFixed(3) : "—"}</td>
            <td class="computed">${times ? times.startSec.toFixed(3) : "—"}</td>
            <td class="computed">${times ? times.endSec.toFixed(3) : "—"}</td>
            <td class="computed">${adjustedMaxText || "—"}</td>
            <td class="row-actions"><button data-action="del">削除</button></td>
        `;
        if (err) {
            tr.style.background = "#fff5f5";
            tr.title = err;
        }
        if (motion.id === state.selectedMotionId) tr.style.outline = "2px solid #f6e05e";

        tr.addEventListener("click", (e) => {
            if (e.target.tagName !== "INPUT" && e.target.tagName !== "SELECT" && e.target.tagName !== "BUTTON") {
                state.selectedMotionId = motion.id;
                renderTimingGrid();
                renderSpeedChart();
            }
        });

        tr.querySelectorAll("[data-mfield]").forEach(el => {
            el.addEventListener("change", () => onMotionFieldChange(axis, motion, el));
            if (el.tagName === "INPUT" && el.type !== "number") {
                el.addEventListener("input", () => onMotionFieldChange(axis, motion, el));
            }
        });
        tr.querySelector("[data-action='del']").addEventListener("click", () => removeMotion(motion.id));

        tbody.appendChild(tr);
    }
    return table;
}

function buildDetailContent(axis) {
    if (TimingGridControllerModule?.buildDetailContent) {
        return TimingGridControllerModule.buildDetailContent(document, {
            axis,
            state,
            escapeHtml,
            getAxisSpeedUnit,
            getAxisDistanceUnit,
            getAxisAccelUnit,
            buildAxisConfigSection,
            buildCylinderPresetSection,
            buildMotionDetailTable
        });
    }
    const wrapper = document.createElement("details");
    wrapper.className = "detail-collapsible";
    if (state.openDetailAxes.has(axis.id)) wrapper.open = true;
    wrapper.addEventListener("toggle", () => {
        if (wrapper.open) state.openDetailAxes.add(axis.id);
        else state.openDetailAxes.delete(axis.id);
    });
    const summary = document.createElement("summary");
    summary.innerHTML = `<strong>詳細設定: ${escapeHtml(axis.name)}</strong> <span style="color:#666;font-size:0.85em;">（クリックで開閉）</span>`;
    wrapper.appendChild(summary);

    // Axis config
    const speedUnitDetail = getAxisSpeedUnit(axis);
    const distanceUnitDetail = getAxisDistanceUnit(axis);
    const accelUnitDetail = getAxisAccelUnit(axis);
    const cfg = buildAxisConfigSection(axis, speedUnitDetail);
    wrapper.appendChild(cfg);

    const presetBox = buildCylinderPresetSection(axis);
    if (presetBox) {
        wrapper.appendChild(presetBox);
    }

    // Motion table
    const table = buildMotionDetailTable(axis, speedUnitDetail, distanceUnitDetail, accelUnitDetail);
    wrapper.appendChild(table);
    return wrapper;
}

function onMotionFieldChange(axis, motion, el) {
    if (TimingGridControllerModule?.handleMotionFieldChange) {
        return TimingGridControllerModule.handleMotionFieldChange({
            axis,
            motion,
            element: el,
            updateAllFlowNodeLabels,
            recomputeAll,
            renderErrors,
            renderTimingGrid,
            renderSpeedChart,
            rebuildAll,
            removeFlowConnectionsToMotion,
            addFlowConnection
        });
    }
    const field = el.dataset.mfield;
    const val = el.value;
    if (field === "name" || field === "comment" || field === "startConditionText") {
        motion[field] = val;
        updateAllFlowNodeLabels();
        recomputeAll();
        renderErrors();
        renderTimingGrid();
        return;
    }
    if (field === "startMode") {
        if (val === "absolute") {
            motion.dependency = null;
            if (motion.absoluteStartSec == null) motion.absoluteStartSec = 0;
            // Remove flow connection from this node's input
            removeFlowConnectionsToMotion(motion.id);
        } else {
            motion.absoluteStartSec = null;
            if (!motion.dependency) motion.dependency = { sourceMotionId: "", anchor: "end", offsetSec: 0 };
        }
        rebuildAll();
        return;
    }
    if (field === "sourceMotionId") {
        if (!motion.dependency) motion.dependency = { sourceMotionId: "", anchor: "end", offsetSec: 0 };
        motion.dependency.sourceMotionId = val;
        // Sync flow
        removeFlowConnectionsToMotion(motion.id);
        if (val) addFlowConnection(val, motion.id);
        rebuildAll();
        return;
    }
    if (field === "offsetSec") {
        if (!motion.dependency) motion.dependency = { sourceMotionId: "", anchor: "end", offsetSec: 0 };
        motion.dependency.offsetSec = parseFloat(val) || 0;
        recomputeAll();
        renderErrors();
        renderTimingGrid();
        renderSpeedChart();
        return;
    }
    if (field === "absoluteStartSec") {
        motion.absoluteStartSec = parseFloat(val) || 0;
        recomputeAll();
        renderErrors();
        renderTimingGrid();
        renderSpeedChart();
        return;
    }
    // numeric fields
    const num = val === "" ? null : parseFloat(val);
    motion[field] = (num == null || !isFinite(num)) ? null : num;
    recomputeAll();
    renderErrors();
    renderTimingGrid();
    renderSpeedChart();
    updateAllFlowNodeLabels();
}

function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c]));
}

function renderErrors() {
    if (TimingGridControllerModule?.renderTimingErrors) {
        return TimingGridControllerModule.renderTimingErrors({
            errorPanel,
            errors: state.computed.errors,
            escapeHtml
        });
    }
    const errs = state.computed.errors;
    if (!errs.length) {
        errorPanel.style.display = "none";
        errorPanel.innerHTML = "";
        return;
    }
    errorPanel.style.display = "block";
    errorPanel.innerHTML = "<strong>エラー:</strong><ul>" + errs.map(e => `<li>${escapeHtml(e)}</li>`).join("") + "</ul>";
}

// =====================================================================
// Speed chart
// =====================================================================
let speedChartAdapter = null;
function initSpeedChart() {
    speedChartAdapter = window.__createSpeedChartAdapter({
        canvasElement: document.getElementById("speedChart"),
        labelElement: document.getElementById("speedChartLabel")
    });
    speedChartAdapter.init();
}

function renderSpeedChart() {
    speedChartAdapter.render({
        selectedAxisId: state.selectedAxisId,
        selectedFlowNodeId: state.selectedFlowNodeId,
        computed: state.computed,
        project: state.project,
        flowDataSnapshot: getMainFlowDataSnapshot(),
        getNodeData: FlowNodeHtmlModule?.getNodeData || getNodeData,
        getNodeTypeFromData: FlowNodeHtmlModule?.getNodeTypeFromData || getNodeTypeFromData,
        findAxis
    });
}

// =====================================================================
// Drawflow integration
// =====================================================================
function initDrawflow() {
    if (FlowControllerModule.initDrawflow) {
        return FlowControllerModule.initDrawflow({
            state,
            doc: document,
            syncFlowEditorHeight,
            initializeDrawflowController: FlowControllerModule.initializeDrawflowController,
            initializeDrawflowSurface: FlowControllerModule.initializeDrawflowSurface,
            DrawflowClass: Drawflow,
            ensureProjectFlowState,
            renderFlowTabs,
            bindDrawflowLifecycleEvents: FlowControllerModule.bindDrawflowLifecycleEvents,
            handleFlowConnectionCreated: FlowControllerModule.handleFlowConnectionCreated,
            handleFlowConnectionRemoved: FlowControllerModule.handleFlowConnectionRemoved,
            handleFlowNodeRemoved: FlowControllerModule.handleFlowNodeRemoved,
            getActiveFlowModuleName,
            getFlowExportSnapshot,
            getFlowModuleData,
            findMotion,
            countIncomingConnections,
            removeFlowConnectionsToMotion,
            rebuildStateFromFlow,
            scheduleAutoSave,
            alertMessage: (message) => alert(message),
            getNodeData,
            getNodeTypeFromData,
            pushTransientWarning,
            hasSubprocessReferenceNode,
            removeSubprocessMeta,
            initializeFlowEditorUi: FlowControllerModule.initializeFlowEditorUi,
            bindConditionNodeSelection,
            renderFlowNodeEditor,
            renderFlowConnectionAssist,
            bindFlowEditorInteractionEvents: FlowControllerModule.bindFlowEditorInteractionEvents,
            doc: document,
            viewport: window,
            handleFlowCanvasDoubleClick: FlowControllerModule.handleFlowCanvasDoubleClick,
            getActiveFlowDataSnapshot,
            renderTimingGrid,
            renderSpeedChart,
            bindFlowCanvasPointerEvents: FlowControllerModule.bindFlowCanvasPointerEvents,
            handleFlowConnectionPickMouseDown: FlowControllerModule.handleFlowConnectionPickMouseDown,
            handleFlowConnectionPickClick: FlowControllerModule.handleFlowConnectionPickClick,
            handleFlowCanvasClick: FlowControllerModule.handleFlowCanvasClick,
            getFlowNodeIdFromElement,
            getFlowPortName,
            beginFlowConnection,
            completeFlowConnection,
            selectFlowNodeByElement,
            closeFlowNodeEditor,
            bindFlowEditorViewportEvents: FlowControllerModule.bindFlowEditorViewportEvents,
            scheduleFlowConnectionRefresh,
            syncFlowEditorHeight,
            positionFlowNodeEditor,
            bindFlowEditorKeydownEvent: FlowControllerModule.bindFlowEditorKeydownEvent,
            handleFlowEditorKeydown: FlowControllerModule.handleFlowEditorKeydown,
            clearPendingFlowConnection
        });
    }
}


function addFlowNode(axis, motion, prevNodeId, options = {}) {
    if (FlowControllerModule.addFlowMotionNode) {
        return FlowControllerModule.addFlowMotionNode({
            state,
            axis,
            motion,
            prevNodeId,
            options,
            getActiveFlowDataSnapshot,
            buildMotionNodeData: ({ axis, motion }) => {
                const data = { axisId: axis?.id, motionId: motion?.id };
                ensureFlowMotionActuatorBinding(data, { axis, motion });
                return data;
            },
            buildFlowNodeHtml,
            getActiveFlowModuleName,
            bindConditionNodeSelection,
            doc: document
        });
    }
}

function addStartNode(options = {}) {
    if (FlowControllerModule.addStartFlowNode) {
        return FlowControllerModule.addStartFlowNode({
            state,
            options,
            getActiveFlowDataSnapshot,
            makeStartNodeData,
            buildFlowNodeHtml,
            bindConditionNodeSelection,
            doc: document,
            rebuildAll
        });
    }
}

function addConditionNode(options = {}) {
    if (FlowControllerModule.addConditionFlowNode) {
        return FlowControllerModule.addConditionFlowNode({
            state,
            options,
            getActiveFlowDataSnapshot,
            makeConditionNodeData,
            buildFlowNodeHtml,
            bindConditionNodeSelection,
            doc: document,
            rebuildAll
        });
    }
}

function addButtonNode(options = {}) {
    if (FlowControllerModule.addButtonFlowNode) {
        return FlowControllerModule.addButtonFlowNode({
            state,
            options,
            getActiveFlowDataSnapshot,
            makeButtonNodeData,
            buildFlowNodeHtml,
            bindConditionNodeSelection,
            doc: document,
            rebuildAll
        });
    }
}

function addErrorNode(options = {}) {
    if (FlowControllerModule.addErrorFlowNode) {
        return FlowControllerModule.addErrorFlowNode({
            state,
            options,
            getActiveFlowDataSnapshot,
            makeErrorNodeData,
            buildFlowNodeHtml,
            bindConditionNodeSelection,
            doc: document,
            rebuildAll
        });
    }
}

function addSignalOutputNode(options = {}) {
    if (FlowControllerModule.addSignalOutputFlowNode) {
        return FlowControllerModule.addSignalOutputFlowNode({
            state,
            options,
            getActiveFlowDataSnapshot,
            makeSignalOutputNodeData,
            buildFlowNodeHtml,
            bindConditionNodeSelection,
            doc: document,
            rebuildAll
        });
    }
}

function addWaitNode(options = {}) {
    if (FlowControllerModule.addWaitFlowNode) {
        return FlowControllerModule.addWaitFlowNode({
            state,
            options,
            getActiveFlowDataSnapshot,
            makeWaitNodeData,
            buildFlowNodeHtml,
            bindConditionNodeSelection,
            doc: document,
            rebuildAll
        });
    }
}

function addEndNode(options = {}) {
    if (FlowControllerModule.addEndFlowNode) {
        return FlowControllerModule.addEndFlowNode({
            state,
            options,
            getActiveFlowDataSnapshot,
            makeEndNodeData,
            buildFlowNodeHtml,
            bindConditionNodeSelection,
            doc: document,
            rebuildAll
        });
    }
}

function addSubprocessNode(options = {}) {
    if (FlowControllerModule.addSubprocessFlowNode) {
        return FlowControllerModule.addSubprocessFlowNode({
            state,
            options,
            getActiveFlowDataSnapshot,
            ensureSubprocessMeta,
            makeUniqueSubprocessName,
            ensureDrawflowModule,
            makeSubprocessNodeData,
            buildFlowNodeHtml,
            bindConditionNodeSelection,
            doc: document,
            renderFlowTabs,
            rebuildAll
        });
    }
}

function addFlowMotionFromToolbar() {
    if (FlowControllerModule.addFlowMotionFromToolbar) {
        return FlowControllerModule.addFlowMotionFromToolbar({
            state,
            doc: document,
            addMotion
        });
    }
}

function clearActiveFlowPalette() {
    if (FlowControllerModule.clearActiveFlowPalette) {
        return FlowControllerModule.clearActiveFlowPalette({
            state,
            getActiveFlowModuleName,
            getActiveFlowDataSnapshot,
            confirmClear: (message) => confirm(message),
            closeFlowNodeEditor,
            renderFlowConnectionAssist,
            rebuildStateFromFlow,
            renderFlowTabs,
            scheduleFlowConnectionRefresh,
            logWarn: (...args) => console.warn(...args)
        });
    }
}

function countIncomingConnections(node) {
    if (FlowControllerModule.countIncomingConnections) {
        return FlowControllerModule.countIncomingConnections({ node });
    }
}

function getNodeIncomingConnections(node) {
    if (FlowControllerModule.getNodeIncomingConnections) {
        return FlowControllerModule.getNodeIncomingConnections({ node });
    }
}

function formatConditionBranchText(data, outputName) {
    if (FlowControllerModule.formatConditionBranchText) {
        return FlowControllerModule.formatConditionBranchText({ data, outputName });
    }
}

function resolveFlowConnection(homeData, connection, visited = new Set()) {
    if (FlowControllerModule.resolveFlowConnection) {
        return FlowControllerModule.resolveFlowConnection({
            state,
            homeData,
            connection,
            visited,
            getNodeData,
            getNodeTypeFromData,
            getSubprocessNodeDuration,
            getWaitNodeDuration,
            getNodeIncomingConnections,
            formatConditionBranchText
        });
    }
}


function removeFlowNode(nodeId) {
    if (FlowControllerModule.removeFlowNode) {
        return FlowControllerModule.removeFlowNode({
            state,
            nodeId,
            getActiveFlowModuleName
        });
    }
}

function hasSubprocessReferenceNode(subprocessId, exported = getFlowExportSnapshot()) {
    if (FlowControllerModule.hasSubprocessReferenceNode) {
        return FlowControllerModule.hasSubprocessReferenceNode({
            subprocessId,
            exported,
            getNodeData,
            getNodeTypeFromData
        });
    }
}

function removeSubprocessMeta(subprocessId) {
    if (FlowControllerModule.removeSubprocessMeta) {
        return FlowControllerModule.removeSubprocessMeta({
            state,
            subprocessId,
            ensureProjectFlowState,
            findSubprocessById,
            getSubprocessList,
            getSubprocessModuleName,
            setActiveFlowModule,
            renderFlowTabs
        });
    }
}

function removeFlowConnectionsToMotion(motionId, exceptSourceNodeId) {
    if (FlowControllerModule.removeFlowConnectionsToMotion) {
        return FlowControllerModule.removeFlowConnectionsToMotion({
            state,
            motionId,
            exceptSourceNodeId,
            getFlowModuleData
        });
    }
}

function addFlowConnection(sourceMotionId, targetMotionId) {
    if (FlowControllerModule.addFlowConnection) {
        return FlowControllerModule.addFlowConnection({
            state,
            sourceMotionId,
            targetMotionId
        });
    }
}

function updateAllFlowNodeLabels(onlyAxisId) {
    if (FlowControllerModule.updateAllFlowNodeLabels) {
        return FlowControllerModule.updateAllFlowNodeLabels({
            state,
            onlyAxisId,
            doc: document,
            getFlowModuleData,
            getNodeData,
            getNodeTypeFromData,
            findMotion,
            buildFlowNodeHtml,
            bindConditionNodeSelection
        });
    }
}

function rebuildFlowFromState() {
    if (FlowControllerModule.rebuildFlowFromState) {
        return FlowControllerModule.rebuildFlowFromState({
            state,
            doc: document,
            getActiveFlowModuleName,
            setActiveFlowModule,
            clearCustomDrawflowModules,
            buildFlowNodeHtml,
            bindConditionNodeSelection,
            renderFlowTabs,
            getSubprocessList,
            scheduleFlowConnectionRefresh
        });
    }
}

function restoreFlowFromExport(exportedData) {
    if (FlowControllerModule.restoreFlowFromExport) {
        return FlowControllerModule.restoreFlowFromExport({
            state,
            exportedData,
            doc: document,
            ensureProjectFlowState,
            clearCustomDrawflowModules,
            getNodeData,
            getNodeTypeFromData,
            findMotion,
            buildFlowNodeHtml,
            getDrawflowAdapter,
            syncInternalFlagsFromFlowSnapshot,
            getSubprocessList: () => getSubprocessList().map((subprocess) => ({
                ...subprocess,
                moduleName: subprocess.moduleName || getSubprocessModuleName(subprocess.id)
            })),
            ensureDrawflowModule,
            refreshHomeMotionNodeMaps,
            bindConditionNodeSelection,
            renderFlowTabs,
            scheduleFlowConnectionRefresh
        });
    }
}

function rebuildStateFromFlow(options = {}) {
    if (FlowControllerModule.rebuildStateFromFlow) {
        return FlowControllerModule.rebuildStateFromFlow({
            state,
            options,
            getActiveFlowModuleName,
            getFlowExportSnapshot,
            getFlowModuleData,
            syncInternalFlagsFromFlowSnapshot,
            scheduleAutoSave,
            getNodeIncomingConnections,
            resolveFlowConnection,
            pushTransientWarning,
            rebuildAll
        });
    }
}

// =====================================================================
// Top-level rebuild
// =====================================================================
function rebuildAll(options = {}) {
    if (FlowControllerModule.rebuildAll) {
        return FlowControllerModule.rebuildAll({
            state,
            options,
            syncActiveFlowNodeEditorState,
            recomputeAll,
            refreshActuatorTypeSelectOptions,
            renderErrors,
            updateFlowAxisSelect,
            renderFlowNodeEditor,
            renderTimingGrid,
            renderSpeedChart,
            renderAssistantInsights,
            notifyAssistantUiChanged,
            scheduleAutoSave
        });
    }
}

// =====================================================================
// Import / Export
// =====================================================================
function exportData() {
    if (FlowControllerModule.exportData) {
        return FlowControllerModule.exportData({
            state,
            syncActiveFlowNodeEditorState,
            syncInternalFlagsFromFlowSnapshot,
            getPreferredProjectIo,
            BlobCtor: Blob,
            urlApi: URL,
            doc: document
        });
    }
}

function importData(data) {
    if (FlowControllerModule.importData) {
        return FlowControllerModule.importData({
            state,
            data,
            genId,
            getPreferredProjectIo,
            doc: document,
            applyUnitsToUI,
            getFlowModuleData,
            restoreFlowFromExport,
            rebuildFlowFromState,
            renderFlowTabs,
            rebuildAll,
            renderFlowConnectionAssist,
            clearAutoSaveTimer: () => clearTimeout(autoSaveTimer),
            setChatStatus,
            alertMessage: (message) => alert(message),
            logError: (error) => console.error(error)
        });
    }
}

// =====================================================================
// Phase 4 Step #10 後段: cloud save / load (uses /api/projects)
// =====================================================================

function refreshCloudAuthUi() {
    if (FlowControllerModule.refreshCloudAuthUi) {
        return FlowControllerModule.refreshCloudAuthUi({
            refs: getFrontendDomRefs(),
            state,
            apiProjectsClient,
            setCloudSignedIn: (value) => {
                cloudSignedIn = value;
            },
            getCloudProjectId: () => cloudProjectId,
            loadAccountSettingsFromCloud,
            warn: console.warn.bind(console)
        });
    }
}
if (typeof window !== "undefined") {
    window.__refreshCloudAuthUi = () => {
        try { refreshCloudAuthUi(); }
        catch (e) { console.warn("[phase4] refreshCloudAuthUi failed", e); }
    };
}

async function saveProjectToCloud() {
    if (FlowControllerModule.saveProjectToCloud) {
        return FlowControllerModule.saveProjectToCloud({
            apiProjectsClient,
            setChatStatus,
            syncActiveFlowNodeEditorState,
            state,
            syncInternalFlagsFromFlowSnapshot,
            getPreferredProjectIo,
            refreshCloudAuthUi,
            getCloudProjectId: () => cloudProjectId,
            setCloudProjectId: (value) => {
                cloudProjectId = value;
            },
            rememberLastCloudProjectId,
            updateCloudSaveStatus,
            warn: console.warn.bind(console)
        });
    }
}

async function refreshCloudProjectList() {
    if (FlowControllerModule.refreshCloudProjectList) {
        return FlowControllerModule.refreshCloudProjectList({
            apiProjectsClient,
            select: document.getElementById("cloudProjectSelect"),
            setChatStatus,
            refreshCloudAuthUi,
            createElement: document.createElement.bind(document),
            DateCtor: Date,
            warn: console.warn.bind(console)
        });
    }
}

async function loadProjectFromCloud(projectId) {
    if (FlowControllerModule.loadProjectFromCloud) {
        return FlowControllerModule.loadProjectFromCloud({
            apiProjectsClient,
            projectId,
            setChatStatus,
            refreshCloudAuthUi,
            importData,
            state,
            notifyAssistantUiChanged,
            loadAndRenderSkills,
            setCloudProjectId: (value) => {
                cloudProjectId = value;
            },
            rememberLastCloudProjectId,
            updateCloudSaveStatus,
            refs: getFrontendDomRefs(),
            warn: console.warn.bind(console)
        });
    }
}

// On boot, re-open the last cloud project so a reload does not lose work that
// was auto-saved while signed in. Skips if the user already has a project loaded.
async function bootRestoreLastCloudProject() {
    if (FlowControllerModule.bootRestoreLastCloudProject) {
        return FlowControllerModule.bootRestoreLastCloudProject({
            getBootProjectRestoreDone: () => bootProjectRestoreDone,
            setBootProjectRestoreDone: (value) => {
                bootProjectRestoreDone = value;
            },
            getCloudProjectId: () => cloudProjectId,
            projectHasContent,
            storage: localStorage,
            storageKey: LAST_CLOUD_PROJECT_ID_KEY,
            apiProjectsClient,
            setCloudSignedIn: (value) => {
                cloudSignedIn = value;
            },
            readStoredProjectId: ({ session }) => {
                const scopeKey = WorkspaceUiModule?.buildWorkspaceSessionScopeKey
                    ? WorkspaceUiModule.buildWorkspaceSessionScopeKey(session)
                    : "";
                return readRememberedLastCloudProjectId(scopeKey);
            },
            loadProjectFromCloud
        });
    }
}

async function signInWithEmail() {
    if (FlowControllerModule.signInWithEmail) {
        return FlowControllerModule.signInWithEmail({
            apiProjectsClient,
            promptEmail: () => window.prompt("ログイン用のメールアドレスを入力してください"),
            setChatStatus,
            warn: (...args) => console.warn(...args)
        });
    }
}

async function signOutCloud() {
    if (FlowControllerModule.signOutCloud) {
        return FlowControllerModule.signOutCloud({
            apiProjectsClient,
            setCloudProjectId: (value) => { cloudProjectId = value; },
            setCloudSignedIn: (value) => { cloudSignedIn = value; },
            setBootProjectRestoreDone: (value) => { bootProjectRestoreDone = value; },
            rememberLastCloudProjectId,
            setChatStatus,
            refreshCloudAuthUi,
            warn: (...args) => console.warn(...args)
        });
    }
}

function normalizeImportedAxis(a) {
    let axisMaxSpeed = a.axisMaxSpeed ?? null;
    if (axisMaxSpeed == null && a.leadMmPerRev && a.maxRpm) {
        axisMaxSpeed = a.leadMmPerRev * a.maxRpm / 60;
    }
    return {
        id: a.id || genId("axis"),
        name: a.name || "軸",
        actuatorType: normalizeAxisActuatorType(a.actuatorType),
        motorControlType: normalizeMotorControlType(a.motorControlType, a.actuatorType),
        motionType: a.motionType === "rotary" ? "rotary" : "linear",
        axisMaxSpeed,
        motions: (a.motions || []).map(normalizeImportedMotion)
    };
}
function normalizeImportedMotion(m) {
    return {
        id: m.id || genId("motion"),
        name: m.name || "動作",
        distance: numOrNull(m.distance),
        accel: numOrNull(m.accel),
        decel: numOrNull(m.decel),
        maxSpeed: numOrNull(m.maxSpeed),
        waitTime: numOrZero(m.waitTime),
        dependency: m.dependency ? {
            sourceMotionId: m.dependency.sourceMotionId || "",
            anchor: "end",
            offsetSec: numOrZero(m.dependency.offsetSec)
        } : null,
        absoluteStartSec: m.dependency ? null : (numOrNull(m.absoluteStartSec) ?? 0),
        startConditionText: m.startConditionText || "",
        comment: m.comment || ""
    };
}
function numOrNull(v) {
    if (v === "" || v == null) return null;
    const n = parseFloat(v);
    return isFinite(n) ? n : null;
}
function numOrZero(v) {
    const n = numOrNull(v);
    return n == null ? 0 : n;
}

function normalizeImportedAction(action) {
    const completionMode = String(action?.completionMode || "").trim();
    return {
        id: String(action?.id || "").trim() || "action",
        name: String(action?.name || action?.id || "アクション").trim() || "アクション",
        completionMode: completionMode === "sensor" || completionMode === "timer" || completionMode === "ignore"
            ? completionMode
            : "",
        timeoutSec: numOrZero(action?.timeoutSec),
        plcopen: normalizePlcopenActionMeta(action?.plcopen),
        command: normalizeActionCommand(action?.command),
        parameters: normalizeActionParameters(action?.parameters),
        status: normalizeActionStatus(action?.status),
        doneConditions: Array.isArray(action?.doneConditions) ? action.doneConditions : [],
        faultConditions: Array.isArray(action?.faultConditions) ? action.faultConditions : []
    };
}

function normalizeImportedActuator(actuator) {
    const actuatorType = normalizeAxisActuatorType(actuator?.actuatorType);
    return {
        id: String(actuator?.id || "").trim() || "actuator",
        name: String(actuator?.name || actuator?.id || "アクチュエータ").trim() || "アクチュエータ",
        actuatorType,
        motorControlType: normalizeMotorControlType(actuator?.motorControlType, actuator?.actuatorType),
        pneumaticValveMode: actuatorType === "pneumaticCylinder"
            ? (String(actuator?.pneumaticValveMode || "").trim() === "singleSolenoid" ? "singleSolenoid" : "doubleSolenoid")
            : "",
        controlMethod: normalizeActuatorControlMethod(actuator?.controlMethod, actuator?.actuatorType),
        axisRef: String(actuator?.axisRef || "").trim(),
        actions: Array.isArray(actuator?.actions) ? actuator.actions.map(normalizeImportedAction) : []
    };
}

function normalizeImportedSignal(signal) {
    return {
        id: String(signal?.id || "").trim() || "Signal",
        name: String(signal?.name || signal?.id || "信号").trim() || "信号",
        iecName: String(signal?.iecName || "").trim(),
        category: String(signal?.category || "").trim(),
        direction: ["input", "output", "internal"].includes(signal?.direction) ? signal.direction : "input",
        address: String(signal?.address || "").trim(),
        dataType: ControlSpecHelper.normalizeSignalDataType(signal?.dataType),
        activeState: String(signal?.activeState || "TRUE").trim() || "TRUE",
        actuatorId: String(signal?.actuatorId || "").trim(),
        description: String(signal?.description || "").trim()
    };
}

function migrateLegacyV1(data) {
    const project = emptyProject();
    if (data.units) {
        project.units.distanceLinear = data.units.distanceLinear || "mm";
        const ac = (data.units.accelLinear || "m/s²").replace("²", "2");
        project.units.accelLinear = ac;
        project.units.speedLinear = data.units.speedLinear || "mm/s";
    }
    const axis = {
        id: genId("axis"),
        name: "軸1",
        actuatorType: "pneumaticCylinder",
        motorControlType: "",
        motionType: "linear",
        axisMaxSpeed: null,
        motions: []
    };
    let cursor = 0;
    let prev = null;
    for (const r of data.rows) {
        const dist = parseFloat(r.distance);
        const accel = parseFloat(r.accel);
        const decel = parseFloat(r.decel);
        const maxSp = parseFloat(r.maxSpeed);
        if (!isFinite(dist) || dist <= 0) continue;
        const isRotary = r.rotary === true;
        if (isRotary) continue; // legacy rotary not migrated to linear axis
        const motion = {
            id: genId("motion"),
            name: r.comment || ("動作" + (axis.motions.length + 1)),
            distance: dist,
            accel: isFinite(accel) ? accel : null,
            decel: isFinite(decel) ? decel : null,
            maxSpeed: isFinite(maxSp) ? maxSp : null,
            waitTime: parseFloat(r.waitTime) || 0,
            dependency: prev ? { sourceMotionId: prev.id, anchor: "end", offsetSec: 0 } : null,
            absoluteStartSec: prev ? null : 0,
            startConditionText: "",
            comment: r.comment || ""
        };
        axis.motions.push(motion);
        prev = motion;
        cursor += 1;
    }
    project.axes.push(axis);
    return project;
}

// Phase 3 step #7: HTML mirrors of src/project/import-export.js entry points.
// Kept thin so the late-bind swap can replace them with the module versions
// without touching the call sites in importData() / exportData().
function normalizeProjectImportHtmlMirror(data) {
    if (data && data.version === 2 && Array.isArray(data.axes)) {
        return {
            version: 2,
            name: data.name || "新しいプロジェクト",
            selectedPlcProfileId: String(data.selectedPlcProfileId || "generic-st").trim() || "generic-st",
            units: Object.assign({ distanceLinear: "mm", accelLinear: "m/s2", speedLinear: "mm/s" }, data.units || {}),
            actuatorTypeCatalog: Array.isArray(data.actuatorTypeCatalog) && data.actuatorTypeCatalog.length
                ? data.actuatorTypeCatalog.map((entry, index) => normalizeActuatorTypeCatalogEntry(entry, index))
                : cloneDefaultActuatorTypeCatalog(),
            signalCategories: Array.isArray(data.signalCategories) && data.signalCategories.length
                ? data.signalCategories.map((entry, index) => normalizeSignalCategoryEntry(entry, index))
                : cloneDefaultSignalCategories(),
            plcProfiles: Array.isArray(data.plcProfiles)
                ? data.plcProfiles.map((entry, index) => normalizePlcProfile(entry, index))
                : [],
            plcProfilesSeeded: data.plcProfilesSeeded === true,
            axes: data.axes.map(normalizeImportedAxis),
            flow: {
                drawflowData: data.flow?.drawflowData || {},
                subprocesses: Array.isArray(data.flow?.subprocesses) ? data.flow.subprocesses.map((item) => {
                    const subprocessId = item.id || genId("subprocess");
                    return {
                        id: subprocessId,
                        name: item.name || "サブプロセス",
                        moduleName: item.moduleName || getSubprocessModuleName(subprocessId),
                        durationSec: Math.max(0, numOrZero(item.durationSec || 1))
                    };
                }) : []
            },
            actuators: Array.isArray(data.actuators) ? data.actuators.map(normalizeImportedActuator) : [],
            signals: Array.isArray(data.signals) ? data.signals.map(normalizeImportedSignal) : []
        };
    }
    if (data && Array.isArray(data.rows)) {
        return migrateLegacyV1(data);
    }
    throw new Error("無効なデータ形式");
}

function normalizeProjectImport(data) {
    return getPreferredProjectIo().normalizeProjectImport(data, { genId });
}

function serializeProjectExportHtmlMirror(project, drawflowSnapshot) {
    const flow = ensureProjectFlowState(project);
    const merged = {
        ...project,
        selectedPlcProfileId: String(project?.selectedPlcProfileId || state.chat?.plcProfileId || "generic-st").trim() || "generic-st",
        actuatorTypeCatalog: ensureActuatorTypeCatalog(project).map((entry) => ({
            id: entry.id,
            label: entry.label,
            defaultActionNames: Array.isArray(entry.defaultActionNames) ? entry.defaultActionNames.slice() : []
        })),
        plcProfiles: ensureProjectPlcProfiles(project).map((entry, index) => normalizePlcProfile(entry, index)),
        signalCategories: ensureSignalCategories(project).map((entry) => ({
            id: entry.id,
            name: entry.name,
            direction: entry.direction,
            addressPrefix: entry.addressPrefix,
            startByte: entry.startByte
        })),
        flow: {
            ...flow,
            drawflowData: drawflowSnapshot != null
                ? drawflowSnapshot
                : flow.drawflowData || {},
            subprocesses: getSubprocessList(project).map((item) => ({
                id: item.id,
                name: item.name,
                moduleName: item.moduleName || getSubprocessModuleName(item.id),
                durationSec: Math.max(0, numOrZero(item.durationSec || 1))
            }))
        }
    };
    return JSON.stringify(merged, null, 2);
}

function serializeProjectExport(project, drawflowSnapshot) {
    return getPreferredProjectIo().serializeProjectExport(project, drawflowSnapshot);
}

async function loadAccountSettingsFromCloud() {
    const refs = getFrontendDomRefs();
    return AccountSettingsApi.loadAccountSettingsFromCloud({
        apiProjectsClient,
        refs,
        getAccountSettingsImpl: AccountSettingsApi.getAccountSettings,
        setAccountSettings: (value) => {
            state.accountSettings = value;
        },
        warn: (...args) => console.warn(...args)
    });
}

function buildAccountSettingsJsonFromProject(project = state.project) {
    return AccountSettingsApi.buildAccountSettingsJsonFromProject({
        project,
        chat: state.chat,
        ensureProjectPlcProfiles,
        normalizePlcProfile,
        ensureSignalCategories,
        ensureActuatorTypeCatalog
    });
}

function applyAccountSettingsDefaultsToProject(project, settingsJson) {
    return AccountSettingsApi.applyAccountSettingsDefaultsToProject({
        project,
        settingsJson,
        normalizePlcProfile,
        normalizeSignalCategoryEntry,
        normalizeActuatorTypeCatalogEntry,
        setCurrentProjectPlcProfileId: (plcProfileId) => {
            if (project === state.project) {
                state.chat.plcProfileId = plcProfileId;
            }
        },
        syncChatDialectFromProfile: () => {
            if (project === state.project) {
                syncChatDialectFromProfile();
            }
        }
    });
}

async function saveAccountSettingsToCloud() {
    const refs = getFrontendDomRefs();
    return AccountSettingsApi.saveAccountSettingsToCloud({
        apiProjectsClient,
        refs,
        buildSettingsJson: () => buildAccountSettingsJsonFromProject(state.project),
        updateAccountSettingsImpl: AccountSettingsApi.updateAccountSettings,
        setAccountSettings: (value) => {
            state.accountSettings = value;
        },
        schemaVersion: 1,
        setTimeoutFn: (fn, delay) => setTimeout(fn, delay),
        error: (caughtError) => console.error(caughtError)
    });
}

function applyUnitsToUI() {
    document.getElementById("distanceLinearUnit").value = state.project.units.distanceLinear;
    document.getElementById("accelLinearUnit").value = state.project.units.accelLinear;
    document.getElementById("speedLinearUnit").value = state.project.units.speedLinear;
    document.getElementById("angleRotaryUnit").value = state.project.units.angleRotary || "deg";
    document.getElementById("accelRotaryUnit").value = state.project.units.accelRotary || "deg/s2";
    document.getElementById("speedRotaryUnit").value = state.project.units.speedRotary || "deg/s";
}

function saveChatConfig() {
    const external = AssistantConfigModule;
    if (typeof external?.writeAssistantChatConfig === "function") {
        external.writeAssistantChatConfig(localStorage, {
            mode: state.chat.mode,
            dialect: state.chat.dialect,
            plcProfileId: state.chat.plcProfileId
        });
        return;
    }
    try {
        localStorage.setItem(
            "timingChartChatConfig",
            JSON.stringify({
                mode: state.chat.mode,
                dialect: state.chat.dialect,
                plcProfileId: state.chat.plcProfileId
            })
        );
    } catch (e) {
        console.warn("chat config save failed", e);
    }
}

function loadChatConfig() {
    const external = AssistantConfigModule;
    if (
        typeof external?.readAssistantChatConfig === "function" &&
        typeof external?.applyAssistantChatConfig === "function"
    ) {
        external.applyAssistantChatConfig(state.chat, external.readAssistantChatConfig(localStorage));
        if (!state.chat.plcProfileId) state.chat.plcProfileId = state.chat.dialect || "generic-st";
        syncChatDialectFromProfile();
        return;
    }
    try {
        const raw = localStorage.getItem("timingChartChatConfig");
        if (!raw) return;
        const parsed = JSON.parse(raw);
        state.chat.mode = parsed.mode || state.chat.mode;
        state.chat.plcProfileId = parsed.plcProfileId || parsed.dialect || state.chat.plcProfileId || state.chat.dialect;
        syncChatDialectFromProfile();
    } catch (e) {
        console.warn("chat config load failed", e);
    }
}

function applyChatConfigToUi() {
    renderChatShell();
    renderAssistantInsights();
    notifyAssistantUiChanged();
}

function setChatVisibility(open) {
    state.chat.uiOpen = open;
    if (!open) state.chat.settingsOpen = false;
    renderChatShell();
    notifyAssistantUiChanged();
}

function toggleChatSettings() {
    state.chat.settingsOpen = !state.chat.settingsOpen;
    renderChatShell();
    notifyAssistantUiChanged();
}

function setChatModeValue(mode) {
    state.chat.mode = mode || "st";
    saveChatConfig();
    renderAssistantInsights();
    notifyAssistantUiChanged();
}

function setChatDialectValue(dialect) {
    state.chat.plcProfileId = PlcProfileModule.normalizePlcProfileSelectionValue(dialect);
    syncChatDialectFromProfile();
    saveChatConfig();
    renderAssistantInsights();
    notifyAssistantUiChanged();
}

function duplicateActivePlcProfile() {
    const next = PlcProfileModule.duplicateActivePlcProfile({
        baseProfile: getActivePlcProfile(),
        project: state.project,
        chat: state.chat,
        ensureProjectPlcProfilesImpl: ensureProjectPlcProfiles,
        buildDuplicatedPlcProfileImpl: PlcProfileModule.buildDuplicatedPlcProfile,
        buildOpenPlcProfileEditorStateImpl: PlcProfileModule.buildOpenPlcProfileEditorState,
        nextId: `profile-${Date.now()}`,
        applySavedProfileSelection: (profileId) => setChatDialectValue(profileId)
    });
    if (!next) return false;
    notifyAssistantUiChanged();
    return true;
}

function editActivePlcProfile() {
    const editState = PlcProfileModule.editActivePlcProfile(getActivePlcProfile(), PlcProfileModule.buildPlcProfileEditActionState);
    if (!editState.canEdit) {
        window.alert(editState.blockedMessage);
        return false;
    }
    state.chat.plcProfileEditor = editState.editorState;
    notifyAssistantUiChanged();
    return true;
}

function focusPlcProfileField(path = "") {
    const outcome = PlcProfileModule.focusPlcProfileField({
        path,
        activeProfile: getActivePlcProfile(),
        chat: state.chat,
        buildPlcProfileEditActionStateImpl: PlcProfileModule.buildPlcProfileEditActionState,
        buildPlcProfileFieldFocusStateImpl: PlcProfileModule.buildPlcProfileFieldFocusState
    });
    const { editState, focusState } = outcome;
    if (!focusState.normalizedPath) return;
    if (focusState.blocked) {
        window.alert(editState.blockedMessage);
        return;
    }
    notifyAssistantUiChanged();
    requestAnimationFrame(() => {
        const selector = `[data-plc-profile-field="${focusState.normalizedPath}"]`;
        const field = document.querySelector(selector);
        if (!field) return;
        const details = typeof field.closest === "function" ? field.closest("details") : null;
        if (details) details.open = true;
        if (typeof field.scrollIntoView === "function") {
            field.scrollIntoView({ block: "center", behavior: "smooth" });
        }
        if (typeof field.focus === "function") {
            field.focus({ preventScroll: true });
        }
    });
}

function deleteActivePlcProfile() {
    const active = getActivePlcProfile();
    if (!active.editable) return;
    if (!window.confirm(`PLC プロファイル「${active.name}」を削除しますか？`)) return;
    PlcProfileModule.deleteActivePlcProfile({
        activeProfile: active,
        project: state.project,
        ensureProjectPlcProfilesImpl: ensureProjectPlcProfiles,
        buildRemainingPlcProfilesAfterDeleteImpl: PlcProfileModule.buildRemainingPlcProfilesAfterDelete,
        applyFallbackSelection: (profileId) => setChatDialectValue(profileId)
    });
}

function cancelPlcProfileEdit() {
    PlcProfileModule.cancelPlcProfileEdit(state.chat, PlcProfileModule.buildClosedPlcProfileEditorState);
    notifyAssistantUiChanged();
}

function updatePlcProfileDraft(path, value) {
    PlcProfileModule.updatePlcProfileDraft(state.chat, path, value, PlcProfileModule.updatePlcProfileDraftValue);
    notifyAssistantUiChanged();
}

function savePlcProfileEdit() {
    const saved = PlcProfileModule.savePlcProfileEdit({
        chat: state.chat,
        project: state.project,
        ensureProjectPlcProfilesImpl: ensureProjectPlcProfiles,
        buildPlcProfileSaveCandidateImpl: PlcProfileModule.buildPlcProfileSaveCandidate,
        validatePlcProfileForSaveImpl: validatePlcProfileForSave,
        buildSavedPlcProfilesImpl: PlcProfileModule.buildSavedPlcProfiles,
        buildClosedPlcProfileEditorStateImpl: PlcProfileModule.buildClosedPlcProfileEditorState,
        focusPlcProfileField: (path) => focusPlcProfileField(path),
        applySavedProfileSelection: (profileId) => setChatDialectValue(profileId)
    });
    if (!saved) {
        notifyAssistantUiChanged();
        return false;
    }
    return true;
}

function setQuestionDraftInput(value) {
    AssistantUiRuntime.setQuestionDraftInput(state.chat, value, notifyAssistantUiChanged);
}

function toggleTaskHistoryPanel() {
    return AssistantUiRuntime.toggleTaskHistoryPanel({
        chat: state.chat,
        renderChatUi,
        loadHistory: () => showAgentSessionHistory(),
        onHistoryLoadError: (error) => {
            console.warn("[phase4] history load failed", error);
            setChatStatus("履歴取得に失敗");
        }
    });
}

function refreshTaskHistoryPanel() {
    return AssistantUiRuntime.refreshTaskHistoryPanel(() => showAgentSessionHistory(), (error) => {
        console.warn("[phase4] history refresh failed", error);
        setChatStatus("履歴取得に失敗");
    });
}

async function submitChatComposer(text) {
    return AssistantUiRuntime.submitChatComposer({
        text,
        chat: state.chat,
        question: getCurrentPlanQuestion(),
        agentSession: getAgentSession(),
        ensureActiveChatThread,
        buildFallbackThreadTitle,
        renderChatUi,
        requestThreadTitleFromAi,
        submitPlanAnswerFromInput,
        runChatAction,
        setChatStatus
    });
}

function requestThreadTitleFromAi(threadId, message) {
    return AssistantUiRuntime.requestThreadTitleFromAi({
        threadId,
        message,
        chat: state.chat,
        createAssistantTitle: apiAssistantTitleClient?.createAssistantTitle,
        renderChatUi,
        onError: (error) => {
            console.warn("assistant title generation failed", error);
        }
    });
}

async function handleAssistantAction(action) {
    if (!action?.kind) return;
    if (action.kind === "plan-answer" || action.kind === "agent-answer") {
        if (state.chat.busy) return;
        state.chat.busy = true;
        renderChatUi();
        try {
            await answerPlanQuestion(action.questionId, action.value, action.label || action.value);
        } finally {
            state.chat.busy = false;
            renderChatUi();
        }
        return;
    }
    if (action.kind === "plan-apply" || action.kind === "agent-apply") {
        applyPlanDraftNow();
        return;
    }
    if (action.kind === "plan-cancel" || action.kind === "agent-cancel") {
        cancelFlowPlanSession();
        return;
    }
    if (action.kind === "agent-history") {
        await refreshTaskHistoryPanel();
        return;
    }
    if (action.kind === "agent-resume" && action.sessionId) {
        try {
            await resumeAgentSessionFromHistory(action.sessionId);
        } catch (error) {
            console.warn("[phase4] history resume failed", error);
            setChatStatus("履歴再開に失敗");
        }
    }
}

function bindChatControls() {
    document.getElementById("btnAssistantToggle")?.addEventListener("click", () => {
        setChatVisibility(!state.chat.uiOpen);
    });
}

// =====================================================================
// Init
// =====================================================================
function bindFlowViewEvents() {
    if (FlowControllerModule.bindFlowViewEvents) {
        return FlowControllerModule.bindFlowViewEvents({
            refs: getFrontendDomRefs(),
            addFlowMotionFromToolbar,
            addStartNode,
            addConditionNode,
            addButtonNode,
            addErrorNode,
            addSignalOutputNode,
            addWaitNode,
            addSubprocessNode,
            addEndNode,
            setFlowConnectionHoverText,
            setFlowConnectionMode,
            clearActiveFlowPalette
        });
    }
}

function bindTimingViewEvents() {
    if (FlowControllerModule.bindTimingViewEvents) {
        return FlowControllerModule.bindTimingViewEvents({
            refs: getFrontendDomRefs(),
            timingGrid,
            state,
            renderTimingGrid,
            renderSpeedChart,
            convertProjectLinearUnits,
            convertProjectRotaryUnits,
            rebuildAll,
            saveAccountSettingsToCloud
        });
    }
}

function bindProjectEvents() {
    if (FlowControllerModule.bindProjectEvents) {
        return FlowControllerModule.bindProjectEvents({
            refs: getFrontendDomRefs(),
            state,
            exportData,
            importData,
            scheduleAutoSave,
            confirmFn: window.confirm.bind(window),
            clearTimeoutFn: window.clearTimeout.bind(window),
            getAutoSaveTimer: () => autoSaveTimer,
            setCloudProjectId: (value) => {
                cloudProjectId = value;
            },
            emptyProject,
            applyAccountSettingsDefaultsToProject,
            updateCloudSaveStatus,
            clearCustomDrawflowModules,
            setActiveFlowModule,
            refreshHomeMotionNodeMaps,
            rebuildAll,
            renderFlowTabs,
            renderFlowConnectionAssist,
            refreshCloudAuthUi,
            apiProjectsClient,
            createElement: document.createElement.bind(document),
            createReader: () => new FileReader(),
            loadProjectFromCloud,
            saveProjectToCloud,
            refreshCloudProjectList,
            signInWithEmail,
            signOutCloud,
            openCloudMemberManager: async () => {
                if (!apiProjectsClient || typeof apiProjectsClient.openOrganizationProfile !== "function") {
                    return false;
                }
                return apiProjectsClient.openOrganizationProfile();
            },
            escapeHtml,
            doc: document,
            DateCtor: Date
        });
    }
}

function bindToolbar() {
    if (FlowControllerModule.bindToolbar) {
        return FlowControllerModule.bindToolbar({
            bindFlowViewEvents,
            bindTimingViewEvents,
            bindProjectEvents
        });
    }
}

let _resizeTimer = null;
window.addEventListener("resize", () => {
    if (_resizeTimer) clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(() => { renderTimingGrid(); }, 80);
});

window.addEventListener("DOMContentLoaded", () => {
    // View templates are now injected; re-resolve the timing-view DOM refs that
    // were null when captured during the classic script's parse-time execution.
    ({ timingGrid, errorPanel } = getFrontendDomRefs());
    loadChatConfig();
    loadWorkspaceUiState();
    bindToolbar();
    bindChatControls();
    bindAxisAddPopover();
    bindFlowAxisSelector();
    bindWorkspaceUi();
    applyChatConfigToUi();
    setSidebarCollapsed(state.ui.sidebarCollapsed, { skipPersist: true });
    renderAxisAddPopover();
    initSpeedChart();
    initDrawflow();
    getDrawflowAdapter();
    window.__createTemplateGallery?.({
        refs: getFrontendDomRefs(),
        importData,
        confirm: window.confirm.bind(window),
        setStatus: setChatStatus,
        onBeforeImport: () => {
            clearTimeout(autoSaveTimer);
            cloudProjectId = null;
            updateCloudSaveStatus("");
        }
    });
    renderChatUi();
    setActiveView(state.ui.activeView, { skipPersist: true });
    setChatStatus("準備完了");
    Promise.resolve(bootRestoreLastCloudProject())
        .catch((error) => console.warn("cloud project restore failed", error));
    Promise.resolve(restoreAgentSessionFromStorage())
        .catch((error) => {
            console.warn("agent session restore failed", error);
            setChatStatus("準備完了");
        });
});

Object.assign(globalThis, {
    state, ensureActiveChatThread,
    GENERATION_INDEX_EXPECTED_EXPORTS, assistantUiSubscribers,
    preferredGenerationApi, preferredProjectIo,
    applyAccountSettingsDefaultsToProject, applyAnswersToDraft,
    bindAssistantResize, buildAccountSettingsJsonFromProject,
    buildFlowContext, buildFlowExtractionHelpers, buildInstructionWithPlcProfile,
    buildLocalFlowDraft, buildPlcProfileBaseLabel, buildPlcProfileEditorHints,
    buildPlcProfileOptions, buildPlcProfileRuleLines, buildPlcProfileSummary,
    cancelPlcProfileEdit, clearChatHistory, cloneJsonValue,
    collectActivePlcProfileWarnings, collectPlcProfileConsistencyHints, copyChatResult,
    createDefaultActuatorActions, createDefaultPlcopenPins, createDefaultUserPlcProfiles,
    createGenerationFacade, createProjectIoFacade, deleteActivePlcProfile,
    detectAfterMotionFromText, detectOffBranchFromText, duplicateActivePlcProfile,
    editActivePlcProfile, ensureProjectPlcProfiles, extractAxisNameFromText,
    extractErrorCodeFromText, extractFlowConnectionsFromSnapshot, extractTimeoutSecFromText,
    findDraftConditionNode, findDraftMotion, findDraftMotionById, findMotion,
    focusPlcProfileField, formatPlcProfileFieldLabel, genId, getActivePlcProfile,
    getAssistantMessageBlocks, getAssistantTaskHistoryItems, getDefaultActuatorActionTemplates,
    getDialectMeta, getFlowDataSnapshot, getFrontendDomRefs, getNodeData, getNodeTypeFromData,
    getPlcProfileOptions, getPreferredGenerationApi, hasDraftConnection, loadAssistantWidth,
    mergeActuatorActionsWithDefaults, normalizeActionCommand, normalizeActionParameters,
    normalizeActionStatus, normalizeActuatorControlMethod, normalizeActuatorTypeCatalogEntry,
    normalizeAxisActuatorType, normalizeMotorControlType, normalizePlcProfile,
    normalizePlcopenActionMeta, normalizeSignalCategoryEntry, normalizeSubprocessTimingMode,
    notifyAssistantUiChanged, presentCurrentPlanQuestion, pushPlanActivity,
    refreshCloudAuthUi, refreshTaskHistoryPanel, renderChatUi, requestThreadTitleFromAi,
    resolvePlcProfile, savePlcProfileEdit, setActiveView, setChatDialectValue,
    setChatModeValue, setChatVisibility, setQuestionDraftInput, startNewChatThread,
    syncAgentJobState, syncChatDialectFromProfile, toggleChatSettings,
    toggleTaskHistoryPanel, toggleWorklogGroup, updatePlcProfileDraft,
    validatePlcProfileForSave,
    handleAssistantAction, requestAgentFlowDraft, requestRemoteAgentSessionAnswer,
    requestRemoteAgentSessionCreate, restoreAgentSessionFromStorage,
    resumeAgentSessionFromHistory, runChatAction,
    answerPlanQuestion, loadAccountSettingsFromCloud, saveAccountSettingsToCloud,
    submitChatComposer, submitPlanAnswerFromInput,
});
